import React, { forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import {
  normalizeSlideTextFontFamily,
  parseSlideLayoutJson,
  PROGRAM_SLIDE_TEXT_FONT_OPTIONS,
  slideTextFontCssFamily,
  type ProgramSlideButtonBlockV1,
  type ProgramSlideClipboardImageBlockV1,
  type ProgramSlideScanImageBlockV1,
  type ProgramSlideImageBlockV1,
  type ProgramSlideImageCropV1,
  type ProgramSlideLayoutV1,
  type ProgramSlideLayoutPatch,
  type ProgramSlidePolylineBlockV1,
  type ProgramSlideTextFontFamilyV1,
  type ProgramSlideTextBoxV1,
  type ProgramSlideQuizChoiceOptionV1,
  type ProgramSlideQuizChoiceSetV1,
  type ProgramSlideQuizShortAnswerV1,
  type ProgramSlideShapeBlockV1,
  type ProgramSlideShapeKindV1,
  SLIDE_RULED_DEFAULT_SPACING_PX,
  SLIDE_RULED_MIN_SPACING_PX,
  SLIDE_RULED_MAX_SPACING_PX,
  type ProgramSlideTableBlockV1,
  type ProgramSlideVideoBlockV1,
  type ProgramSlideWebpageBlockV1,
  QUIZ_DEFAULT_TEXT_COLOR_HEX,
} from '../../types/programContentSlideCanvas';
import SlideCodeBlockPlaceholder from './SlideCodeBlockPlaceholder';
import SlideCodeStaticHtml from './SlideCodeStaticHtml';
import ProgramSlideWebpageBlockView from './ProgramSlideWebpageBlockView';
import ProgramSlideTableBlockView from './ProgramSlideTableBlockView';
import {
  PROGRAM_SLIDE_TEXT_ALIGN_ICONS,
  PROGRAM_SLIDE_TEXT_ALIGN_OPTIONS,
  programSlideTextAlignLabel,
  type ProgramSlideTextAlign,
} from './ProgramSlideTextDisplayToolbar';
import {
  fetchSlideWebpagePreview,
  findWebpageThumbSrcForUrl,
  localPlaceholderPreview,
  normalizeSlideWebpageUrl,
  thumbBase64ToWebpBlob,
} from '../../utils/slideWebpagePreview';
import {
  applyColorToPlainRange,
  compactSlideTextBoxContent,
  getPlainTextOffsetsFromSelection,
  getSlideTextBoxPlainText,
  parseRunsFromContentEditableRoot,
  runsToEditableHtml,
} from '../../utils/slideTextRuns';
import { createSlideDisplayColorMappers, slideDisplayHex } from '../../utils/slideDisplayColors';
import { isQuizChoiceAnswerCorrect } from '../../utils/slideQuizChoiceAnswer';
import { getQuizMatchedAnswer } from '../../utils/slideQuizAnswer';
import {
  createAddedQuizChoiceOption,
  createQuizChoiceSetBlocks,
  getQuizChoiceOptionsForSet,
  mergeSlideLayoutArrays,
  QUIZ_CHOICE_MAX_OPTIONS,
  QUIZ_CHOICE_MIN_OPTIONS,
  quizChoiceResizeWithSyncedSize,
  quizChoiceResizeSetFromCenter,
  remapQuizChoiceCorrectIndicesAfterRemove,
  toggleQuizChoiceCorrectIndex,
} from './slideQuizChoiceCanvas';
import { ProgramSlideQuizChoiceLayer } from './ProgramSlideQuizChoiceLayer';
import { ProgramSlideFloatedToolbar } from './ProgramSlideFloatedToolbar';
import { ProgramSlideTrashDeleteControl } from './ProgramSlideTrashDeleteControl';
import { SlideColorPickerField } from './SlideColorPickerField';
import EvaluationPlanSheetTableCreateModal from '../evaluationPlan/EvaluationPlanSheetTableCreateModal';
import EvaluationPlanSheetTableEditMiniModal from '../evaluationPlan/EvaluationPlanSheetTableEditMiniModal';
import {
  collectSlideLayoutColors,
  readSlideRecentColors,
  rememberSlideRecentColor,
} from '../../utils/slideColorHistory';
import {
  compressClipboardImage,
  readClipboardImageBlob,
  readClipboardImageBlobFromApi,
  type CompressedSlideImage,
} from '../../utils/slideImageCompress';
import {
  clonePendingClipboardImageForBlock,
  isPendingSlideImageSrc,
  registerPendingClipboardImage,
  registerPendingSlideImage,
  revokePendingForBlock,
  revokePendingSlideImageSrc,
} from '../../utils/slideImagePendingUpload';
import {
  clonePendingEvaluationSheetImageForBlock,
  isPendingEvaluationSheetImageSrc,
  registerPendingEvaluationSheetClipboardImage,
  registerPendingEvaluationSheetImage,
  revokePendingEvaluationSheetForBlock,
  revokePendingEvaluationSheetImageSrc,
} from '../../utils/evaluationSheetImagePendingUpload';
import {
  clonePendingLearningWorksheetImageForBlock,
  isPendingLearningWorksheetImageSrc,
  registerPendingLearningWorksheetClipboardImage,
  registerPendingLearningWorksheetImage,
  revokePendingLearningWorksheetForBlock,
  revokePendingLearningWorksheetImageSrc,
} from '../../utils/learningWorksheetImagePendingUpload';
import {
  EVALUATION_PLAN_SHEET_DEFAULT_POLYLINE_STROKE_WIDTH_PX,
  EVALUATION_PLAN_SHEET_DEFAULT_TEXT_FONT_SIZE_PX,
  EVALUATION_PLAN_SHEET_FLOATING_UI_Z,
  EVALUATION_PLAN_SHEET_HEADER_BAND_CONTENT_GAP_PX,
  EVALUATION_PLAN_SHEET_HEADER_BAND_NORM,
  type EvaluationPlanSheetImageScope,
} from '../../types/evaluationPlanSheetCanvas';
import {
  createTableBlock,
  expandSelectionToAnchorBounds,
  genSlideTableCellId,
  normalizeTableGrid,
  normalizeTableSelectionRect,
  selectionRectForAnchor,
  selectionRectFromEndpoints,
  type TableSelectionRect,
} from '../../utils/programSlideTableBlock';
import {
  bitmapImagesLayoutKey,
  findBitmapImage,
  getBitmapImages,
  isSlideBitmapImageKind,
  resolveSlideImageBlockKind,
  type SlideBitmapImageKind,
  type SlideImageBlockKind,
} from '../../utils/slideBitmapImageBlocks';
import {
  collectRefsWithGroupId,
  expandSlideRefsWithCoMembers,
  genSlideBlockGroupId,
  getSlideBlockGroupId,
  omitSlideBlockGroupId,
  patchAssignGroupIdToRefs,
  patchRemoveGroupIds,
} from '../../utils/slideBlockGroups';
import ScanImageBlockLiveCapture from './ScanImageBlockLiveCapture';
import { filterVideoInputDevices, simplifyVideoInputDeviceLabel } from '../../utils/programSlideWebcamQuality';
import {
  bakeFrameCropToBlob,
  coverCropFrameFrac,
  normFrameHeightForAspect,
} from '../../utils/scanImageFrame';
import {
  resolveSlideToolbarPlacement,
  SLIDE_TOOLBAR_CODE_BLOCK_HEIGHT_PX,
  SLIDE_TOOLBAR_DOUBLE_ROW_HEIGHT_PX,
  SLIDE_TOOLBAR_SHAPE_TWO_ROW_HEIGHT_PX,
  SLIDE_TOOLBAR_SINGLE_ROW_HEIGHT_PX,
} from '../../utils/slideToolbarPlacement';
import {
  isSlideBlockClipboardText,
  SLIDE_BLOCK_CLIP_PREFIX,
  SLIDE_BLOCKS_CLIP_PREFIX,
  shouldDeferToNativeSlideCopy,
  shouldDeferToNativeSlidePaste,
} from '../../utils/slideBlockClipboardGuard';
import { clientDeltaToElementNorm, clientPointToLayoutPoint, clientRectToLayoutRect } from '../../utils/pageZoom';
import { scalePolylinePointsFromBox, translatePolylinePoints } from '../../utils/slidePolylineTransform';

const SCAN_IMAGE_BLOCK_ASPECT = 16 / 9;

function getSlideBlockFrameAspect(
  block: Pick<ProgramSlideImageBlockV1, 'w' | 'h'>,
  rootW: number,
  rootH: number
): number {
  return (block.w * rootW) / Math.max(0.001, block.h * rootH);
}

function findSlideImageBlock(
  layout: ProgramSlideLayoutV1,
  blockKind: SlideImageBlockKind,
  id: string
): ProgramSlideImageBlockV1 | undefined {
  if (isSlideBitmapImageKind(blockKind)) return findBitmapImage(layout, blockKind, id);
  return layout.images.find((img) => img.id === id);
}

function probeImageNaturalSize(
  src: string,
  fallbackW: number,
  fallbackH: number
): Promise<{ naturalW: number; naturalH: number }> {
  return new Promise((resolve, reject) => {
    const probe = new Image();
    probe.onload = () =>
      resolve({
        naturalW: probe.naturalWidth || fallbackW,
        naturalH: probe.naturalHeight || fallbackH,
      });
    probe.onerror = () => reject(new Error('이미지를 불러오지 못했습니다.'));
    probe.src = src;
  });
}

function clampCropFrac(c: ProgramSlideImageCropV1): ProgramSlideImageCropV1 {
  const w = Math.min(1, Math.max(0.05, c.w));
  const h = Math.min(1, Math.max(0.05, c.h));
  return {
    x: Math.min(1 - w, Math.max(0, c.x)),
    y: Math.min(1 - h, Math.max(0, c.y)),
    w,
    h,
  };
}

/** 구 letterbox 정규화 크롭 → 프레임(몸통) 기준 0~1 */
function letterboxCropToFrameFrac(
  image: ProgramSlideImageBlockV1,
  getContainMetrics: (im: ProgramSlideImageBlockV1) => {
    dispW: number;
    dispH: number;
    offX: number;
    offY: number;
  }
): ProgramSlideImageCropV1 {
  const { dispW, dispH, offX, offY } = getContainMetrics(image);
  const c = image.crop;
  return clampCropFrac({
    x: offX + c.x * dispW,
    y: offY + c.y * dispH,
    w: c.w * dispW,
    h: c.h * dispH,
  });
}

/** 예전 소스코드 블럭 기본 예시 코드 — 이 값 그대로면 '코드 미입력'으로 간주(빗금 표시용) */
const DEFAULT_CODE_BLOCK_SOURCE =
  '<style>body{font-family:system-ui,sans-serif;padding:12px;background:#fff;color:#111;}button{padding:6px 10px;border:1px solid #ccc;border-radius:8px;background:#f5f5f5;cursor:pointer;}</style><h3>소스코드 블럭</h3><p>이 영역은 iframe으로 격리되어 실행됩니다.</p><button onclick="this.textContent=\'클릭됨\'">클릭</button>';

type ResizeSide = 'left' | 'right';
type ImageResizeCorner = 'nw' | 'ne' | 'sw' | 'se';
type SlideSelectionKind =
  | 'text'
  | 'image'
  | 'clipboardImage'
  | 'scanImage'
  | 'button'
  | 'shape'
  | 'polyline'
  | 'table'
  | 'video'
  | 'webpage'
  | 'quiz'
  | 'quizChoice';
type SelectedSlideBlockRef = { kind: SlideSelectionKind; id: string };
const SLIDE_TOOLBAR_BATCH_KINDS: ReadonlySet<SlideSelectionKind> = new Set<SlideSelectionKind>(['text', 'button', 'shape', 'polyline']);
type SelectedMoveBlockGeometry = SelectedSlideBlockRef & {
  x: number;
  y: number;
  w: number;
  h: number;
  /** polyline 그룹 이동·리사이즈용 시작 꼭짓점. 없으면 라이브 점에 누적 델타가 중복된다. */
  polylinePoints?: Array<{ x: number; y: number }>;
};
type SelectionBounds = { left: number; top: number; right: number; bottom: number; width: number; height: number };
type ObjectAlignActionId =
  | 'align-left'
  | 'align-center-x'
  | 'align-right'
  | 'align-top'
  | 'align-center-y'
  | 'align-bottom'
  | 'distribute-x'
  | 'distribute-y';

const OBJECT_ALIGN_ACTIONS: Array<{
  id: ObjectAlignActionId;
  label: string;
  minSelectionCount: number;
}> = [
  { id: 'align-left', label: '왼쪽정렬', minSelectionCount: 1 },
  { id: 'align-center-x', label: '중앙정렬', minSelectionCount: 1 },
  { id: 'align-right', label: '오른쪽정렬', minSelectionCount: 1 },
  { id: 'align-top', label: '위쪽정렬', minSelectionCount: 1 },
  { id: 'align-center-y', label: '상하중앙정렬', minSelectionCount: 1 },
  { id: 'align-bottom', label: '아래정렬', minSelectionCount: 1 },
  { id: 'distribute-x', label: '가로 등간격 정렬', minSelectionCount: 3 },
  { id: 'distribute-y', label: '세로 등간격 정렬', minSelectionCount: 3 },
];
const SLIDE_SMART_SNAP_STORAGE_KEY = 'cp:slide-smart-snap:v1';
const SLIDE_SMART_SNAP_THRESHOLD_PX = 8;
const SLIDE_HISTORY_LIMIT = 100;

function genSlideBoxId(idx: number): string {
  try {
    const c = globalThis.crypto?.randomUUID?.();
    if (c) return c;
  } catch {
    /* ignore */
  }
  return `box-${Date.now()}-${idx}`;
}

function maxSlideLayoutZIndex(layout: ProgramSlideLayoutV1): number {
  return Math.max(
    0,
    ...layout.boxes.map((b) => b.zIndex),
    ...layout.images.map((i) => i.zIndex),
    ...(layout.clipboardImages ?? []).map((i) => i.zIndex),
    ...(layout.scanImages ?? []).map((i) => i.zIndex),
    ...layout.buttons.map((b) => b.zIndex),
    ...(layout.shapes ?? []).map((s) => s.zIndex),
    ...(layout.polylines ?? []).map((p) => p.zIndex),
    ...(layout.tables ?? []).map((t) => t.zIndex),
    ...layout.videos.map((v) => v.zIndex),
    ...(layout.webpages ?? []).map((w) => w.zIndex),
    ...layout.quizzes.map((q) => q.zIndex),
    ...layout.quizChoiceOptions.map((o) => o.zIndex)
  );
}

function isSlideBlockLockedInLayout(
  layout: ProgramSlideLayoutV1,
  kind: string,
  id: string
): boolean {
  if (kind === 'text') return !!layout.boxes.find((b) => b.id === id)?.locked;
  if (kind === 'shape') return !!(layout.shapes ?? []).find((s) => s.id === id)?.locked;
  if (kind === 'table') return !!(layout.tables ?? []).find((t) => t.id === id)?.locked;
  if (kind === 'polyline') return !!(layout.polylines ?? []).find((p) => p.id === id)?.locked;
  if (kind === 'image') return !!layout.images.find((i) => i.id === id)?.locked;
  if (kind === 'clipboardImage') return !!(layout.clipboardImages ?? []).find((i) => i.id === id)?.locked;
  if (kind === 'scanImage') return !!(layout.scanImages ?? []).find((i) => i.id === id)?.locked;
  return false;
}

function SlideLockIcon({ locked }: { locked?: boolean }) {
  return (
    <img
      src={locked ? '/lock.svg' : '/unlock.svg'}
      alt=""
      aria-hidden
      className="cp-program-slide-canvas-lock-icon"
    />
  );
}

/** 텍스트 블록 1행 높이(패딩 포함)를 캔버스 높이 대비 비율로 반환 */
function textBoxOneLineHeightNorm(fontSizePx: number, lineHeight: number, rootHeightPx: number): number {
  const oneLineMinPx = Math.max(16, Math.round(fontSizePx * lineHeight + 6));
  const rootH = Math.max(1, rootHeightPx);
  return Math.round(Math.max(0.01, oneLineMinPx / rootH) * 10000) / 10000;
}


function cloneSlideTableBlockWithNewId(
  src: ProgramSlideTableBlockV1,
  newId: string,
  z: number
): ProgramSlideTableBlockV1 {
  const idMap = new Map<string, string>();
  const cells = src.cells.map((c, idx) => {
    const row = Math.floor(idx / Math.max(1, src.colCount));
    const col = idx % Math.max(1, src.colCount);
    const nextCellId = genSlideTableCellId(newId, row, col);
    idMap.set(c.id, nextCellId);
    return { ...c, id: nextCellId };
  });
  const coveredBy: Record<string, string> = {};
  Object.entries(src.coveredBy ?? {}).forEach(([k, v]) => {
    coveredBy[k] = idMap.get(v) ?? v;
  });
  const { groupId: _g, locked: _l, ...rest } = omitSlideBlockGroupId(src);
  return normalizeTableGrid({
    ...rest,
    id: newId,
    zIndex: z,
    cells,
    coveredBy,
  });
}

const PASTE_OFFSET_FRAC = 0.02;
const PASTE_OVERLAP_EPS = 0.001;

function frac4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

function polylineBBox(points: Array<{ x: number; y: number }>): { x: number; y: number; w: number; h: number } | null {
  if (!points.length) return null;
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const pad = 0.015;
  return {
    x: frac4(Math.max(0, minX - pad)),
    y: frac4(Math.max(0, minY - pad)),
    w: frac4(Math.min(1, maxX - minX + 2 * pad)),
    h: frac4(Math.min(1, maxY - minY + 2 * pad)),
  };
}

function blockGeometryFromRef(layout: ProgramSlideLayoutV1, item: SelectedSlideBlockRef): SelectedMoveBlockGeometry | null {
  if (item.kind === 'text') {
    const box = layout.boxes.find((value) => value.id === item.id);
    return box ? { kind: item.kind, id: item.id, x: box.x, y: box.y, w: box.w, h: box.h } : null;
  }
  if (item.kind === 'button') {
    const button = layout.buttons.find((value) => value.id === item.id);
    return button ? { kind: item.kind, id: item.id, x: button.x, y: button.y, w: button.w, h: button.h } : null;
  }
  if (item.kind === 'image') {
    const image = layout.images.find((value) => value.id === item.id);
    return image ? { kind: item.kind, id: item.id, x: image.x, y: image.y, w: image.w, h: image.h } : null;
  }
  if (isSlideBitmapImageKind(item.kind)) {
    const image = findBitmapImage(layout, item.kind, item.id);
    return image ? { kind: item.kind, id: item.id, x: image.x, y: image.y, w: image.w, h: image.h } : null;
  }
  if (item.kind === 'quiz') {
    const quiz = layout.quizzes.find((value) => value.id === item.id);
    return quiz ? { kind: item.kind, id: item.id, x: quiz.x, y: quiz.y, w: quiz.w, h: quiz.h } : null;
  }
  if (item.kind === 'quizChoice') {
    const opt = layout.quizChoiceOptions.find((value) => value.id === item.id);
    return opt ? { kind: item.kind, id: item.id, x: opt.x, y: opt.y, w: opt.w, h: opt.h } : null;
  }
  if (item.kind === 'shape') {
    const shape = (layout.shapes ?? []).find((value) => value.id === item.id);
    return shape ? { kind: item.kind, id: item.id, x: shape.x, y: shape.y, w: shape.w, h: shape.h } : null;
  }
  if (item.kind === 'table') {
    const table = (layout.tables ?? []).find((value) => value.id === item.id);
    return table ? { kind: item.kind, id: item.id, x: table.x, y: table.y, w: table.w, h: table.h } : null;
  }
  if (item.kind === 'polyline') {
    const pl = (layout.polylines ?? []).find((value) => value.id === item.id);
    if (pl && pl.points.length) {
      const b = polylineBBox(pl.points);
      if (b) {
        return {
          kind: item.kind,
          id: item.id,
          x: b.x,
          y: b.y,
          w: b.w,
          h: b.h,
          polylinePoints: pl.points.map((p) => ({ x: p.x, y: p.y })),
        };
      }
    }
    return null;
  }
  if (item.kind === 'webpage') {
    const webpage = (layout.webpages ?? []).find((value) => value.id === item.id);
    return webpage ? { kind: item.kind, id: item.id, x: webpage.x, y: webpage.y, w: webpage.w, h: webpage.h } : null;
  }
  const video = layout.videos.find((value) => value.id === item.id);
  return video ? { kind: item.kind, id: item.id, x: video.x, y: video.y, w: video.w, h: video.h } : null;
}

type SlideInsertPeerKind =
  | 'text'
  | 'image'
  | 'clipboardImage'
  | 'scanImage'
  | 'video'
  | 'webpage'
  | 'quiz'
  | 'quizChoiceSet'
  | 'polyline'
  | { t: 'button'; buttonKind: ProgramSlideButtonBlockV1['kind'] }
  | { t: 'shape'; shape: ProgramSlideShapeKindV1 };

function insertRectOverlap(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
  margin = 0.002
): boolean {
  return (
    a.x + a.w + margin > b.x &&
    a.x < b.x + b.w + margin &&
    a.y + a.h + margin > b.y &&
    a.y < b.y + b.h + margin
  );
}

/** 같은 종류 끼워넣기: 안쪽 영역만 겹침으로 보면 살짝 비켜도 해소 → 테두리가 얇게만 비치는 스택 */
function insertRectOverlapForSameKindStack(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number }
): boolean {
  const m = Math.min(a.w, a.h, b.w, b.h);
  const inset = Math.min(0.021, Math.max(0.004, m * 0.36));
  const shrink = (r: { x: number; y: number; w: number; h: number }) => ({
    x: frac4(r.x + inset),
    y: frac4(r.y + inset),
    w: frac4(Math.max(0.00005, r.w - 2 * inset)),
    h: frac4(Math.max(0.00005, r.h - 2 * inset)),
  });
  return insertRectOverlap(shrink(a), shrink(b), 0);
}

function quizChoiceSetUnionRect(
  layout: ProgramSlideLayoutV1,
  setId: string
): { x: number; y: number; w: number; h: number } | null {
  const opts = getQuizChoiceOptionsForSet(layout, setId);
  if (!opts.length) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxR = -Infinity;
  let maxB = -Infinity;
  for (const o of opts) {
    minX = Math.min(minX, o.x);
    minY = Math.min(minY, o.y);
    maxR = Math.max(maxR, o.x + o.w);
    maxB = Math.max(maxB, o.y + o.h);
  }
  return { x: minX, y: minY, w: frac4(maxR - minX), h: frac4(maxB - minY) };
}

function collectInsertPeerRects(layout: ProgramSlideLayoutV1, pk: SlideInsertPeerKind): Array<{ x: number; y: number; w: number; h: number }> {
  const out: Array<{ x: number; y: number; w: number; h: number }> = [];
  if (pk === 'text') {
    layout.boxes.forEach((b) => out.push({ x: b.x, y: b.y, w: b.w, h: b.h }));
  } else if (pk === 'image') {
    layout.images.forEach((b) => out.push({ x: b.x, y: b.y, w: b.w, h: b.h }));
  } else if (pk === 'clipboardImage') {
    (layout.clipboardImages ?? []).forEach((b) => out.push({ x: b.x, y: b.y, w: b.w, h: b.h }));
  } else if (pk === 'scanImage') {
    (layout.scanImages ?? []).forEach((b) => out.push({ x: b.x, y: b.y, w: b.w, h: b.h }));
  } else if (pk === 'video') {
    layout.videos.forEach((b) => out.push({ x: b.x, y: b.y, w: b.w, h: b.h }));
  } else if (pk === 'webpage') {
    (layout.webpages ?? []).forEach((b) => out.push({ x: b.x, y: b.y, w: b.w, h: b.h }));
  } else if (pk === 'quiz') {
    layout.quizzes.forEach((b) => out.push({ x: b.x, y: b.y, w: b.w, h: b.h }));
  } else if (pk === 'quizChoiceSet') {
    (layout.quizChoiceSets ?? []).forEach((set) => {
      const u = quizChoiceSetUnionRect(layout, set.id);
      if (u) out.push(u);
    });
  } else if (pk === 'polyline') {
    (layout.polylines ?? []).forEach((pl) => {
      const b = polylineBBox(pl.points);
      if (b) out.push({ x: b.x, y: b.y, w: b.w, h: b.h });
    });
  } else if (typeof pk === 'object' && pk.t === 'button') {
    layout.buttons.filter((b) => b.kind === pk.buttonKind).forEach((b) => out.push({ x: b.x, y: b.y, w: b.w, h: b.h }));
  } else if (typeof pk === 'object' && pk.t === 'shape') {
    (layout.shapes ?? []).filter((s) => s.shape === pk.shape).forEach((b) => out.push({ x: b.x, y: b.y, w: b.w, h: b.h }));
  }
  return out;
}

/** (cx,cy) 기준 가운데 배치 후 같은 종류 개체와 겹치면 오른쪽·아래로 조금씩 이동(살짝 덮이는 스택) */
function resolveInsertTopLeftAvoidPeerRects(
  w: number,
  h: number,
  peers: Array<{ x: number; y: number; w: number; h: number }>,
  cx = 0.5,
  cy = 0.5,
  stepX = 0.0026,
  stepY = 0.003,
  maxSteps = 120,
  contentBounds?: CanvasContentBoundsNorm
): { x: number; y: number } {
  const yMin = contentBounds?.top ?? 0;
  const yMax = contentBounds?.bottom ?? 1;
  const clampY = (value: number) => frac4(Math.max(yMin, Math.min(yMax - h, value)));
  let x = frac4(Math.max(0, Math.min(1 - w, cx - w / 2)));
  let y = clampY(cy - h / 2);
  const me = (): { x: number; y: number; w: number; h: number } => ({ x, y, w, h });
  const hits = () => peers.some((p) => insertRectOverlapForSameKindStack(me(), p));
  let n = 0;
  while (hits() && n < maxSteps) {
    n += 1;
    x = frac4(Math.max(0, Math.min(1 - w, x + stepX)));
    y = clampY(y + stepY);
  }
  return { x, y };
}

/**
 * 선 끝·시작 꼬깔(chevron). segmentTowardApex: apex 직전 점 → apex 방향(정규화 좌표 델타).
 * 캔버스가 preserveAspectRatio="none"이면 norm에서의 각도·길이가 화면에서 깨지므로, 픽셀 등가(W·H)로 회전 후 다시 norm으로 환산한다.
 */
function polylineChevronCapPath(
  apex: { x: number; y: number },
  segmentTowardApex: { x: number; y: number },
  swPx: number,
  canvasW: number,
  canvasH: number
): string {
  const W = Math.max(1, canvasW);
  const H = Math.max(1, canvasH);
  const sx = segmentTowardApex.x * W;
  const sy = segmentTowardApex.y * H;
  const slen = Math.hypot(sx, sy);
  if (slen < 1e-9) return '';
  const ux = sx / slen;
  const uy = sy / slen;
  const bx = -ux;
  const by = -uy;
  const halfOpen = 0.48;
  const rot = (px: number, py: number, ang: number) => {
    const c = Math.cos(ang);
    const s = Math.sin(ang);
    return { x: px * c - py * s, y: px * s + py * c };
  };
  const w1 = rot(bx, by, halfOpen);
  const w2 = rot(bx, by, -halfOpen);
  const Lpx = Math.min(110, Math.max(3.5, 2.9 * swPx));
  const axs = apex.x * W;
  const ays = apex.y * H;
  const t1x = (axs + Lpx * w1.x) / W;
  const t1y = (ays + Lpx * w1.y) / H;
  const t2x = (axs + Lpx * w2.x) / W;
  const t2y = (ays + Lpx * w2.y) / H;
  return `M ${frac4(t1x)} ${frac4(t1y)} L ${frac4(apex.x)} ${frac4(apex.y)} L ${frac4(t2x)} ${frac4(t2y)}`;
}

/** Shift: 캔버스 픽셀 기준으로 수평·수직(직각) 중 더 가까운 쪽에 맞춤 */
function snapLineDraftPointToShiftOrtho(
  anchor: { x: number; y: number },
  raw: { x: number; y: number },
  shiftKey: boolean,
  innerWidthPx: number,
  innerHeightPx: number
): { x: number; y: number } {
  if (!shiftKey) return { x: frac4(raw.x), y: frac4(raw.y) };
  const w = Math.max(1, innerWidthPx);
  const h = Math.max(1, innerHeightPx);
  const dx = (raw.x - anchor.x) * w;
  const dy = (raw.y - anchor.y) * h;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return { x: frac4(raw.x), y: frac4(anchor.y) };
  }
  return { x: frac4(anchor.x), y: frac4(raw.y) };
}

function clampCenteredRectInCanvas(
  cx: number,
  cy: number,
  w: number,
  h: number,
  minW: number,
  minH: number
): { x: number; y: number; w: number; h: number } {
  /** 가로·세로가 같을 때는 한 변으로 통일해 캔버스에 맞춤 (원+Shift 중심 리사이즈 등에서 타원이 되는 것 방지) */
  const lockSquare = Math.abs(w - h) < 1e-8;
  if (lockSquare) {
    let s = Math.max(minW, Math.min(w, h));
    s = Math.min(s, 1, 2 * cx, 2 * (1 - cx), 2 * cy, 2 * (1 - cy));
    s = Math.max(minW, s);
    return {
      x: frac4(Math.max(0, Math.min(1 - s, cx - s / 2))),
      y: frac4(Math.max(0, Math.min(1 - s, cy - s / 2))),
      w: frac4(s),
      h: frac4(s),
    };
  }
  let nextW = Math.max(minW, w);
  let nextH = Math.max(minH, h);
  nextW = Math.min(1, nextW);
  nextH = Math.min(1, nextH);
  if (nextW > 2 * cx) nextW = Math.max(minW, 2 * cx);
  if (nextW > 2 * (1 - cx)) nextW = Math.max(minW, 2 * (1 - cx));
  if (nextH > 2 * cy) nextH = Math.max(minH, 2 * cy);
  if (nextH > 2 * (1 - cy)) nextH = Math.max(minH, 2 * (1 - cy));
  return {
    x: Math.max(0, Math.min(1 - nextW, cx - nextW / 2)),
    y: Math.max(0, Math.min(1 - nextH, cy - nextH / 2)),
    w: nextW,
    h: nextH,
  };
}

function resizeHorizontalSideFromCenter(
  startX: number,
  startW: number,
  dx: number,
  side: ResizeSide,
  minW: number
): { x: number; w: number } {
  const cx = startX + startW / 2;
  const nextW = side === 'right' ? Math.max(minW, startW + 2 * dx) : Math.max(minW, startW - 2 * dx);
  const clamped = clampCenteredRectInCanvas(cx, 0.5, nextW, 0.001, minW, 0.001);
  return { x: clamped.x, w: clamped.w };
}

function resizeCornerFromCenter(
  startX: number,
  startY: number,
  startW: number,
  startH: number,
  dx: number,
  dy: number,
  corner: ImageResizeCorner,
  minW: number,
  minH: number
): { x: number; y: number; w: number; h: number } {
  const cx = startX + startW / 2;
  const cy = startY + startH / 2;
  const nextW =
    corner === 'nw' || corner === 'sw' ? Math.max(minW, startW - 2 * dx) : Math.max(minW, startW + 2 * dx);
  const nextH =
    corner === 'nw' || corner === 'ne' ? Math.max(minH, startH - 2 * dy) : Math.max(minH, startH + 2 * dy);
  return clampCenteredRectInCanvas(cx, cy, nextW, nextH, minW, minH);
}

function resizeCornerFromCenterAspect(
  startX: number,
  startY: number,
  startW: number,
  startH: number,
  dx: number,
  dy: number,
  corner: ImageResizeCorner,
  minW: number,
  minH: number
): { x: number; y: number; w: number; h: number } {
  const cx = startX + startW / 2;
  const cy = startY + startH / 2;
  const aspect = Math.max(0.1, startW / Math.max(0.001, startH));
  const horizontalW =
    corner === 'nw' || corner === 'sw'
      ? Math.max(minW, startW - 2 * dx)
      : Math.max(minW, startW + 2 * dx);
  const verticalH =
    corner === 'nw' || corner === 'ne'
      ? Math.max(minH, startH - 2 * dy)
      : Math.max(minH, startH + 2 * dy);
  let nextW = Math.max(horizontalW, verticalH * aspect);
  let nextH = nextW / aspect;
  if (nextH < minH) {
    nextH = minH;
    nextW = nextH * aspect;
  }
  return clampCenteredRectInCanvas(cx, cy, nextW, nextH, minW, minH);
}

/** 코너 리사이즈 + Shift: 가로·세로 길이 동일(정사각 bbox). 잡은 모서리 반대편 고정. */
function squareCornerResizeFromProposedRect(
  corner: ImageResizeCorner,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  nextX: number,
  nextY: number,
  nextW: number,
  nextH: number,
  minS: number
): { x: number; y: number; w: number; h: number } {
  let s = Math.max(minS, Math.max(nextW, nextH));
  if (corner === 'se') {
    s = Math.min(s, 1 - sx, 1 - sy);
    const fs = Math.max(minS, s);
    return { x: sx, y: sy, w: fs, h: fs };
  }
  if (corner === 'nw') {
    const fx = sx + sw;
    const fy = sy + sh;
    s = Math.min(s, fx, fy);
    const fs = Math.max(minS, s);
    return { x: fx - fs, y: fy - fs, w: fs, h: fs };
  }
  if (corner === 'ne') {
    const fy = sy + sh;
    s = Math.min(s, 1 - sx, fy);
    const fs = Math.max(minS, s);
    return { x: sx, y: fy - fs, w: fs, h: fs };
  }
  const fx = sx + sw;
  s = Math.min(s, fx, 1 - sy);
  const fs = Math.max(minS, s);
  return { x: fx - fs, y: sy, w: fs, h: fs };
}

/**
 * 슬라이드 좌표: w는 캔버스 너비·h는 높이에 대한 비율이라 w===h여도 화면 픽셀은 직사각형이 됨.
 * 원+Shift는 픽셀에서 한 변이 같도록 맞춘 뒤 다시 (w,h)로 환산한다.
 */
function squareCornerResizeFromProposedRectVisualSquare(
  corner: ImageResizeCorner,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  nextX: number,
  nextY: number,
  nextW: number,
  nextH: number,
  rootW: number,
  rootH: number,
  minW: number,
  minH: number
): { x: number; y: number; w: number; h: number } {
  const rw = Math.max(1, rootW);
  const rh = Math.max(1, rootH);
  const sxPx = sx * rw;
  const syPx = sy * rh;
  const swPx = sw * rw;
  const shPx = sh * rh;
  const nwPx = nextW * rw;
  const nhPx = nextH * rh;
  const minSpx = Math.max(minW * rw, minH * rh);

  let s = Math.max(minSpx, Math.max(nwPx, nhPx));
  if (corner === 'se') {
    s = Math.min(s, rw - sxPx, rh - syPx);
    const fs = Math.max(minSpx, s);
    return { x: frac4(sxPx / rw), y: frac4(syPx / rh), w: frac4(fs / rw), h: frac4(fs / rh) };
  }
  if (corner === 'nw') {
    const fxPx = sxPx + swPx;
    const fyPx = syPx + shPx;
    s = Math.min(s, fxPx, fyPx);
    const fs = Math.max(minSpx, s);
    return {
      x: frac4((fxPx - fs) / rw),
      y: frac4((fyPx - fs) / rh),
      w: frac4(fs / rw),
      h: frac4(fs / rh),
    };
  }
  if (corner === 'ne') {
    const fyPx = syPx + shPx;
    s = Math.min(s, rw - sxPx, fyPx);
    const fs = Math.max(minSpx, s);
    return { x: frac4(sxPx / rw), y: frac4((fyPx - fs) / rh), w: frac4(fs / rw), h: frac4(fs / rh) };
  }
  const fxPx = sxPx + swPx;
  s = Math.min(s, fxPx, rh - syPx);
  const fs = Math.max(minSpx, s);
  return { x: frac4((fxPx - fs) / rw), y: frac4(syPx / rh), w: frac4(fs / rw), h: frac4(fs / rh) };
}

/** 중심 고정 + 픽셀 정사각 bbox (Alt+Shift+원 등) */
function clampPixelCenteredSquareToNorm(
  cxN: number,
  cyN: number,
  sPx: number,
  rootW: number,
  rootH: number,
  minSpx: number
): { x: number; y: number; w: number; h: number } {
  const rw = Math.max(1, rootW);
  const rh = Math.max(1, rootH);
  const cxPx = cxN * rw;
  const cyPx = cyN * rh;
  let s = Math.max(minSpx, sPx);
  s = Math.min(s, 2 * cxPx, 2 * (rw - cxPx), 2 * cyPx, 2 * (rh - cyPx));
  s = Math.max(minSpx, s);
  const xPx = Math.max(0, Math.min(rw - s, cxPx - s / 2));
  const yPx = Math.max(0, Math.min(rh - s, cyPx - s / 2));
  return {
    x: frac4(xPx / rw),
    y: frac4(yPx / rh),
    w: frac4(s / rw),
    h: frac4(s / rh),
  };
}

/** 비율 무시: 코너 드래그로 bbox만 조절 (Shift 정사각 전 자유 변형 단계) */
function freeCornerResizeNoAspect(
  corner: ImageResizeCorner,
  startX: number,
  startY: number,
  w: number,
  h: number,
  dx: number,
  dy: number,
  minW: number,
  minH: number
): { x: number; y: number; w: number; h: number } {
  const right = startX + w;
  const bottom = startY + h;
  let nextX = startX;
  let nextY = startY;
  let nextW = w;
  let nextH = h;
  if (corner === 'nw' || corner === 'sw') {
    nextX = Math.min(right - minW, Math.max(0, startX + dx));
    nextW = right - nextX;
  } else {
    nextW = Math.min(1 - startX, Math.max(minW, w + dx));
  }
  if (corner === 'nw' || corner === 'ne') {
    nextY = Math.min(bottom - minH, Math.max(0, startY + dy));
    nextH = bottom - nextY;
  } else {
    nextH = Math.min(1 - startY, Math.max(minH, h + dy));
  }
  return { x: nextX, y: nextY, w: nextW, h: nextH };
}

/** 포인터 이동 중에도 Shift 감지가 안정적으로 동작하도록 보강 */
function isShiftResizeModifier(e: PointerEvent): boolean {
  return e.shiftKey || (typeof e.getModifierState === 'function' && e.getModifierState('Shift'));
}

function clamp01(v: number): number {
  return Math.min(1, Math.max(0, v));
}

function rectsIntersectNorm(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number }
): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function getSelectionBounds(items: SelectedMoveBlockGeometry[]): SelectionBounds | null {
  if (!items.length) return null;
  const left = Math.min(...items.map((item) => item.x));
  const top = Math.min(...items.map((item) => item.y));
  const right = Math.max(...items.map((item) => item.x + item.w));
  const bottom = Math.max(...items.map((item) => item.y + item.h));
  return {
    left,
    top,
    right,
    bottom,
    width: right - left,
    height: bottom - top,
  };
}

function getBlockMinSize(
  kind: SlideSelectionKind,
  opts?: { slideShapeKind?: ProgramSlideShapeKindV1 }
): { minW: number; minH: number } {
  /** 원은 픽셀 정사각 bbox라 minW·minH 중 큰 쪽이 지름 하한이 됨 → 둘 다 작게 맞춰 더 작은 원 허용 */
  if (kind === 'shape' && opts?.slideShapeKind === 'circle') {
    return { minW: 0.022, minH: 0.022 };
  }
  if (kind === 'button' || kind === 'shape' || kind === 'table') return { minW: 0.08, minH: 0.04 };
  if (kind === 'polyline') return { minW: 0.02, minH: 0.02 };
  if (kind === 'quiz') return { minW: 0.08, minH: 0.04 };
  if (kind === 'quizChoice') return { minW: 0.06, minH: 0.06 };
  if (kind === 'image' || isSlideBitmapImageKind(kind)) return { minW: 0.06, minH: 0.06 };
  if (kind === 'video') return { minW: 0.08, minH: 0.06 };
  if (kind === 'webpage') return { minW: 0.12, minH: 0.1 };
  return { minW: 0.06, minH: 0.01 };
}

function slideSelectionKey(kind: SlideSelectionKind, id: string): string {
  return `${kind}:${id}`;
}

function slideSelectionRefKey(ref: SelectedSlideBlockRef): string {
  return slideSelectionKey(ref.kind, ref.id);
}

type CanvasContentBoundsNorm = { top: number; bottom: number };

function computeBoundedArrowDelta(
  moveGroup: SelectedMoveBlockGeometry[],
  rawDx: number,
  rawDy: number,
  contentBounds?: CanvasContentBoundsNorm
): { boundedDx: number; boundedDy: number } {
  if (!moveGroup.length || (rawDx === 0 && rawDy === 0)) return { boundedDx: 0, boundedDy: 0 };
  const yMin = contentBounds?.top ?? 0;
  const yMax = contentBounds?.bottom ?? 1;
  if (moveGroup.length === 1) {
    const g = moveGroup[0]!;
    const boundedDx = Math.min(1 - g.x - g.w, Math.max(-g.x, rawDx));
    const boundedDy = Math.min(yMax - g.y - g.h, Math.max(yMin - g.y, rawDy));
    return { boundedDx, boundedDy };
  }
  const minX = Math.min(...moveGroup.map((i) => i.x));
  const minY = Math.min(...moveGroup.map((i) => i.y));
  const maxRight = Math.max(...moveGroup.map((i) => i.x + i.w));
  const maxBottom = Math.max(...moveGroup.map((i) => i.y + i.h));
  const boundedDx = Math.min(1 - maxRight, Math.max(-minX, rawDx));
  const boundedDy = Math.min(yMax - maxBottom, Math.max(yMin - minY, rawDy));
  return { boundedDx, boundedDy };
}

function buildTranslatePatchFromMoveGroup(
  current: ProgramSlideLayoutV1,
  moveGroup: SelectedMoveBlockGeometry[],
  boundedDx: number,
  boundedDy: number
): ProgramSlideLayoutPatch | null {
  if (!moveGroup.length || (boundedDx === 0 && boundedDy === 0)) return null;

  const moveMap = new Map(
    moveGroup.map((item) => [
      slideSelectionKey(item.kind, item.id),
      { x: frac4(item.x + boundedDx), y: frac4(item.y + boundedDy) },
    ])
  );
  const moveKeys = new Set(moveGroup.map((item) => slideSelectionKey(item.kind, item.id)));

  return {
    v: 1,
    boxes: current.boxes.map((box) => {
      const next = moveMap.get(slideSelectionKey('text', box.id));
      return next ? { ...box, x: next.x, y: next.y } : box;
    }),
    images: current.images.map((image) => {
      const next = moveMap.get(slideSelectionKey('image', image.id));
      return next ? { ...image, x: next.x, y: next.y } : image;
    }),
    clipboardImages: (current.clipboardImages ?? []).map((image) => {
      const next = moveMap.get(slideSelectionKey('clipboardImage', image.id));
      return next ? { ...image, x: next.x, y: next.y } : image;
    }),
    scanImages: (current.scanImages ?? []).map((image) => {
      const next = moveMap.get(slideSelectionKey('scanImage', image.id));
      return next ? { ...image, x: next.x, y: next.y } : image;
    }),
    buttons: current.buttons.map((button) => {
      const next = moveMap.get(slideSelectionKey('button', button.id));
      return next ? { ...button, x: next.x, y: next.y } : button;
    }),
    shapes: (current.shapes ?? []).map((shape) => {
      const next = moveMap.get(slideSelectionKey('shape', shape.id));
      return next ? { ...shape, x: next.x, y: next.y } : shape;
    }),
    polylines: (current.polylines ?? []).map((pl) => {
      const key = slideSelectionKey('polyline', pl.id);
      if (!moveKeys.has(key)) return pl;
      const item = moveGroup.find((g) => slideSelectionKey(g.kind, g.id) === key);
      const startPoints = item?.polylinePoints ?? pl.points;
      return {
        ...pl,
        points: translatePolylinePoints(startPoints, boundedDx, boundedDy),
      };
    }),
    tables: (current.tables ?? []).map((table) => {
      const next = moveMap.get(slideSelectionKey('table', table.id));
      return next ? { ...table, x: next.x, y: next.y } : table;
    }),
    videos: current.videos.map((video) => {
      const next = moveMap.get(slideSelectionKey('video', video.id));
      return next ? { ...video, x: next.x, y: next.y } : video;
    }),
    webpages: (current.webpages ?? []).map((wp) => {
      const next = moveMap.get(slideSelectionKey('webpage', wp.id));
      return next ? { ...wp, x: next.x, y: next.y } : wp;
    }),
    quizzes: current.quizzes.map((quiz) => {
      const next = moveMap.get(slideSelectionKey('quiz', quiz.id));
      return next ? { ...quiz, x: next.x, y: next.y } : quiz;
    }),
    quizChoiceOptions: current.quizChoiceOptions.map((opt) => {
      const next = moveMap.get(slideSelectionKey('quizChoice', opt.id));
      return next ? { ...opt, x: next.x, y: next.y } : opt;
    }),
  };
}

function getObjectAlignIconSrc(actionId: ObjectAlignActionId): string {
  switch (actionId) {
    case 'align-left':
      return '/align/Align%20Left.svg';
    case 'align-center-x':
      return '/align/Align%20Center%20Vertical.svg';
    case 'align-right':
      return '/align/Align%20Right.svg';
    case 'align-top':
      return '/align/Align%20Top.svg';
    case 'align-center-y':
      return '/align/Align%20Center%20Horizontal.svg';
    case 'align-bottom':
      return '/align/Align%20Bottom.svg';
    case 'distribute-x':
      return '/align/Align%20Horizontal%20Space%20Around.svg';
    case 'distribute-y':
      return '/align/Align%20Vertical%20Space%20Around.svg';
    default:
      return '';
  }
}

function renderSmartSnapIcon(): React.ReactNode {
  return (
    <>
      <path fill="currentColor" d="M7 4h3v7a2 2 0 104 0V4h3v7a5 5 0 11-10 0V4z" />
      <path fill="currentColor" d="M4 18h16v2H4v-2z" />
    </>
  );
}

function areLayoutsEqual(a: ProgramSlideLayoutV1, b: ProgramSlideLayoutV1): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function offsetNormRect<T extends { x: number; y: number; w: number; h: number }>(b: T): T {
  const nx = Math.min(1 - b.w, Math.max(0, b.x + PASTE_OFFSET_FRAC));
  const ny = Math.min(1 - b.h, Math.max(0, b.y + PASTE_OFFSET_FRAC));
  return { ...b, x: frac4(nx), y: frac4(ny) };
}

function shiftXYWH<T extends { x: number; y: number; w: number; h: number }>(b: T, dx: number, dy: number): T {
  const nx = Math.min(1 - b.w, Math.max(0, b.x + dx));
  const ny = Math.min(1 - b.h, Math.max(0, b.y + dy));
  return { ...b, x: frac4(nx), y: frac4(ny) };
}

function normRectsNearlyEqual(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
  eps = PASTE_OVERLAP_EPS
): boolean {
  return (
    Math.abs(a.x - b.x) < eps &&
    Math.abs(a.y - b.y) < eps &&
    Math.abs(a.w - b.w) < eps &&
    Math.abs(a.h - b.h) < eps
  );
}

function layoutContainsNearlyIdenticalRect(
  layout: ProgramSlideLayoutV1,
  rect: { x: number; y: number; w: number; h: number }
): boolean {
  if (rect.w <= 0 || rect.h <= 0) return false;
  const hit = (x: number, y: number, w: number, h: number) => normRectsNearlyEqual(rect, { x, y, w, h });
  for (const b of layout.boxes) if (hit(b.x, b.y, b.w, b.h)) return true;
  for (const b of layout.images) if (hit(b.x, b.y, b.w, b.h)) return true;
  for (const b of layout.clipboardImages ?? []) if (hit(b.x, b.y, b.w, b.h)) return true;
  for (const b of layout.scanImages ?? []) if (hit(b.x, b.y, b.w, b.h)) return true;
  for (const b of layout.buttons) if (hit(b.x, b.y, b.w, b.h)) return true;
  for (const b of layout.shapes ?? []) if (hit(b.x, b.y, b.w, b.h)) return true;
  for (const b of layout.videos) if (hit(b.x, b.y, b.w, b.h)) return true;
  for (const b of layout.webpages ?? []) if (hit(b.x, b.y, b.w, b.h)) return true;
  for (const b of layout.quizzes) if (hit(b.x, b.y, b.w, b.h)) return true;
  for (const pl of layout.polylines ?? []) {
    const bb = polylineBBox(pl.points);
    if (bb && hit(bb.x, bb.y, bb.w, bb.h)) return true;
  }
  return false;
}

function computePasteNudge(
  layout: ProgramSlideLayoutV1,
  rects: { x: number; y: number; w: number; h: number }[]
): number {
  if (!rects.length) return 0;
  let nd = 0;
  for (let step = 0; step < 100; step += 1) {
    const overlaps = rects.some((r) => layoutContainsNearlyIdenticalRect(layout, shiftXYWH(r, nd, nd)));
    if (!overlaps) return nd;
    nd += PASTE_OFFSET_FRAC;
  }
  return nd;
}

function formatVideoBookmarkTime(timeSec: number): string {
  const total = Math.max(0, Math.floor(timeSec));
  const mm = Math.floor(total / 60);
  const ss = total % 60;
  return `${mm}분${String(ss).padStart(2, '0')}초`;
}

type VideoRenderSource =
  | { kind: 'video'; src: string }
  | { kind: 'iframe'; src: string; provider: 'youtube' | 'vimeo' };

function getVideoRenderSource(rawSrc: string, startSec?: number): VideoRenderSource | null {
  const src = rawSrc.trim();
  if (!src) return null;
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return null;
  }
  const host = url.hostname.replace(/^www\./, '');
  const start = Number.isFinite(startSec) && startSec && startSec > 0 ? Math.floor(startSec) : 0;
  if (host === 'youtu.be' || host.endsWith('youtube.com')) {
    const id =
      host === 'youtu.be'
        ? url.pathname.split('/').filter(Boolean)[0]
        : url.pathname.startsWith('/shorts/')
        ? url.pathname.split('/').filter(Boolean)[1]
        : url.pathname.startsWith('/embed/')
        ? url.pathname.split('/').filter(Boolean)[1]
        : url.searchParams.get('v');
    if (id) {
      const embed = new URL(`https://www.youtube.com/embed/${id}`);
      embed.searchParams.set('rel', '0');
      embed.searchParams.set('enablejsapi', '1');
      if (globalThis.location?.origin) embed.searchParams.set('origin', globalThis.location.origin);
      if (start > 0) {
        embed.searchParams.set('start', String(start));
        embed.searchParams.set('autoplay', '1');
      }
      return { kind: 'iframe', src: embed.toString(), provider: 'youtube' };
    }
  }
  if (host.endsWith('vimeo.com')) {
    const id = url.pathname.split('/').filter(Boolean).find((part) => /^\d+$/.test(part));
    if (id) {
      const embed = new URL(`https://player.vimeo.com/video/${id}`);
      if (start > 0) embed.hash = `t=${start}s`;
      return { kind: 'iframe', src: embed.toString(), provider: 'vimeo' };
    }
  }
  if (host === 'drive.google.com') {
    const parts = url.pathname.split('/').filter(Boolean);
    const fileId = parts[0] === 'file' && parts[1] === 'd' ? parts[2] : url.searchParams.get('id');
    if (fileId) return { kind: 'video', src: `https://drive.google.com/uc?export=download&id=${fileId}` };
  }
  return { kind: 'video', src: url.toString() };
}

type ParsedSlideClipboard =
  | { kind: 'text'; box: ProgramSlideTextBoxV1 }
  | { kind: 'image'; image: ProgramSlideImageBlockV1 }
  | { kind: 'clipboardImage'; image: ProgramSlideClipboardImageBlockV1 }
  | { kind: 'scanImage'; image: ProgramSlideScanImageBlockV1 }
  | { kind: 'button'; button: ProgramSlideButtonBlockV1 }
  | { kind: 'shape'; shape: ProgramSlideShapeBlockV1 }
  | { kind: 'polyline'; polyline: ProgramSlidePolylineBlockV1 }
  | { kind: 'table'; table: ProgramSlideTableBlockV1 }
  | { kind: 'video'; video: ProgramSlideVideoBlockV1 }
  | { kind: 'webpage'; webpage: ProgramSlideWebpageBlockV1 }
  | { kind: 'quiz'; quiz: ProgramSlideQuizShortAnswerV1 }
  | { kind: 'quizChoiceSet'; set: ProgramSlideQuizChoiceSetV1; options: ProgramSlideQuizChoiceOptionV1[] };

function boundingRectFromParsed(parsed: ParsedSlideClipboard): { x: number; y: number; w: number; h: number } {
  if (parsed.kind === 'text') {
    const b = omitSlideBlockGroupId(parsed.box);
    return { x: b.x, y: b.y, w: b.w, h: b.h };
  }
  if (parsed.kind === 'image') {
    const b = omitSlideBlockGroupId(parsed.image);
    return { x: b.x, y: b.y, w: b.w, h: b.h };
  }
  if (parsed.kind === 'clipboardImage' || parsed.kind === 'scanImage') {
    const b = omitSlideBlockGroupId(parsed.image);
    return { x: b.x, y: b.y, w: b.w, h: b.h };
  }
  if (parsed.kind === 'video') {
    const b = omitSlideBlockGroupId(parsed.video);
    return { x: b.x, y: b.y, w: b.w, h: b.h };
  }
  if (parsed.kind === 'webpage') {
    const b = omitSlideBlockGroupId(parsed.webpage);
    return { x: b.x, y: b.y, w: b.w, h: b.h };
  }
  if (parsed.kind === 'quiz') {
    const b = omitSlideBlockGroupId(parsed.quiz);
    return { x: b.x, y: b.y, w: b.w, h: b.h };
  }
  if (parsed.kind === 'shape') {
    const b = omitSlideBlockGroupId(parsed.shape);
    return { x: b.x, y: b.y, w: b.w, h: b.h };
  }
  if (parsed.kind === 'button') {
    const b = omitSlideBlockGroupId(parsed.button);
    return { x: b.x, y: b.y, w: b.w, h: b.h };
  }
  if (parsed.kind === 'table') {
    const b = omitSlideBlockGroupId(parsed.table);
    return { x: b.x, y: b.y, w: b.w, h: b.h };
  }
  if (parsed.kind === 'quizChoiceSet') {
    if (!parsed.options.length) return { x: 0, y: 0, w: 0, h: 0 };
    let minX = Infinity;
    let minY = Infinity;
    let maxR = -Infinity;
    let maxB = -Infinity;
    for (const o of parsed.options) {
      const b = omitSlideBlockGroupId(o);
      minX = Math.min(minX, b.x);
      minY = Math.min(minY, b.y);
      maxR = Math.max(maxR, b.x + b.w);
      maxB = Math.max(maxB, b.y + b.h);
    }
    return { x: minX, y: minY, w: frac4(maxR - minX), h: frac4(maxB - minY) };
  }
  const pl0 = omitSlideBlockGroupId(parsed.polyline);
  const b0 = polylineBBox(pl0.points);
  return b0 ? { x: b0.x, y: b0.y, w: b0.w, h: b0.h } : { x: 0, y: 0, w: 0, h: 0 };
}

function polylinePastedWithNudge(
  pl0: ProgramSlidePolylineBlockV1,
  nudgeDx: number,
  nudgeDy: number,
  newId: string,
  z: number
): ProgramSlidePolylineBlockV1 | null {
  const b0 = polylineBBox(pl0.points);
  if (!b0) return null;
  const shifted = shiftXYWH({ x: b0.x, y: b0.y, w: b0.w, h: b0.h }, nudgeDx, nudgeDy);
  const ddx = shifted.x - b0.x;
  const ddy = shifted.y - b0.y;
  return {
    ...pl0,
    id: newId,
    zIndex: z,
    points: pl0.points.map((p) => ({ x: frac4(p.x + ddx), y: frac4(p.y + ddy) })),
  };
}

function serializeSlideBlockSelection(
  kind: SlideSelectionKind,
  id: string,
  layout: ProgramSlideLayoutV1
): string | null {
  if (kind === 'text') {
    const box = layout.boxes.find((b) => b.id === id);
    if (!box) return null;
    return SLIDE_BLOCK_CLIP_PREFIX + JSON.stringify({ k: 'text', box });
  }
  if (kind === 'image') {
    const image = layout.images.find((img) => img.id === id);
    if (!image) return null;
    return SLIDE_BLOCK_CLIP_PREFIX + JSON.stringify({ k: 'image', image });
  }
  if (kind === 'clipboardImage' || kind === 'scanImage') {
    const image = findBitmapImage(layout, kind, id);
    if (!image) return null;
    return SLIDE_BLOCK_CLIP_PREFIX + JSON.stringify({ k: kind, image });
  }
  if (kind === 'video') {
    const video = layout.videos.find((v) => v.id === id);
    if (!video) return null;
    return SLIDE_BLOCK_CLIP_PREFIX + JSON.stringify({ k: 'video', video });
  }
  if (kind === 'webpage') {
    const webpage = (layout.webpages ?? []).find((w) => w.id === id);
    if (!webpage) return null;
    return SLIDE_BLOCK_CLIP_PREFIX + JSON.stringify({ k: 'webpage', webpage });
  }
  if (kind === 'quiz') {
    const quiz = layout.quizzes.find((q) => q.id === id);
    if (!quiz) return null;
    return SLIDE_BLOCK_CLIP_PREFIX + JSON.stringify({ k: 'quiz', quiz });
  }
  if (kind === 'shape') {
    const shape = (layout.shapes ?? []).find((s) => s.id === id);
    if (!shape) return null;
    return SLIDE_BLOCK_CLIP_PREFIX + JSON.stringify({ k: 'shape', shape });
  }
  if (kind === 'polyline') {
    const polyline = (layout.polylines ?? []).find((p) => p.id === id);
    if (!polyline) return null;
    return SLIDE_BLOCK_CLIP_PREFIX + JSON.stringify({ k: 'polyline', polyline });
  }
  if (kind === 'table') {
    const table = (layout.tables ?? []).find((t) => t.id === id);
    if (!table) return null;
    return SLIDE_BLOCK_CLIP_PREFIX + JSON.stringify({ k: 'table', table });
  }
  if (kind === 'quizChoice') {
    const opt = layout.quizChoiceOptions.find((o) => o.id === id);
    if (!opt) return null;
    const set = layout.quizChoiceSets.find((s) => s.id === opt.setId);
    if (!set) return null;
    const options = getQuizChoiceOptionsForSet(layout, set.id);
    if (!options.length) return null;
    return SLIDE_BLOCK_CLIP_PREFIX + JSON.stringify({ k: 'quizChoiceSet', set, options });
  }
  if (kind === 'button') {
    const button = layout.buttons.find((btn) => btn.id === id);
    if (!button) return null;
    return SLIDE_BLOCK_CLIP_PREFIX + JSON.stringify({ k: 'button', button });
  }
  return null;
}

function parseSlideClipboardPayload(raw: unknown): ParsedSlideClipboard | null {
  if (!raw || typeof raw !== 'object') return null;
  const k = (raw as { k?: unknown }).k;
  if (k === 'text') {
    const boxRaw = (raw as { box?: unknown }).box;
    const L = parseSlideLayoutJson({ v: 1, boxes: boxRaw ? [boxRaw] : [], images: [], buttons: [], videos: [], quizzes: [] });
    const box = L.boxes[0];
    return box ? { kind: 'text', box } : null;
  }
  if (k === 'image') {
    const imageRaw = (raw as { image?: unknown }).image;
    const L = parseSlideLayoutJson({
      v: 1,
      boxes: [],
      images: imageRaw ? [imageRaw] : [],
      clipboardImages: [],
      buttons: [],
      videos: [],
      quizzes: [],
    });
    const image = L.images[0];
    return image ? { kind: 'image', image } : null;
  }
  if (k === 'clipboardImage' || k === 'scanImage') {
    const imageRaw = (raw as { image?: unknown }).image;
    const layoutKey = k === 'scanImage' ? 'scanImages' : 'clipboardImages';
    const L = parseSlideLayoutJson({
      v: 1,
      boxes: [],
      images: [],
      clipboardImages: [],
      scanImages: [],
      [layoutKey]: imageRaw ? [imageRaw] : [],
      buttons: [],
      videos: [],
      quizzes: [],
    });
    const image = getBitmapImages(L, k)[0];
    return image ? { kind: k, image } : null;
  }
  if (k === 'button') {
    const buttonRaw = (raw as { button?: unknown }).button;
    const L = parseSlideLayoutJson({ v: 1, boxes: [], images: [], buttons: buttonRaw ? [buttonRaw] : [], videos: [], quizzes: [] });
    const button = L.buttons[0];
    return button ? { kind: 'button', button } : null;
  }
  if (k === 'video') {
    const videoRaw = (raw as { video?: unknown }).video;
    const L = parseSlideLayoutJson({ v: 1, boxes: [], images: [], buttons: [], videos: videoRaw ? [videoRaw] : [], quizzes: [] });
    const video = L.videos[0];
    return video ? { kind: 'video', video } : null;
  }
  if (k === 'webpage') {
    const webpageRaw = (raw as { webpage?: unknown }).webpage;
    const L = parseSlideLayoutJson({
      v: 1,
      boxes: [],
      images: [],
      buttons: [],
      videos: [],
      webpages: webpageRaw ? [webpageRaw] : [],
      quizzes: [],
    });
    const webpage = (L.webpages ?? [])[0];
    return webpage ? { kind: 'webpage', webpage } : null;
  }
  if (k === 'quiz') {
    const quizRaw = (raw as { quiz?: unknown }).quiz;
    const L = parseSlideLayoutJson({
      v: 1,
      boxes: [],
      images: [],
      buttons: [],
      shapes: [],
      polylines: [],
      videos: [],
      quizzes: quizRaw ? [quizRaw] : [],
    });
    const quiz = L.quizzes[0];
    return quiz ? { kind: 'quiz', quiz } : null;
  }
  if (k === 'shape') {
    const shapeRaw = (raw as { shape?: unknown }).shape;
    const L = parseSlideLayoutJson({
      v: 1,
      boxes: [],
      images: [],
      buttons: [],
      shapes: shapeRaw ? [shapeRaw] : [],
      polylines: [],
      videos: [],
      quizzes: [],
    });
    const shape = (L.shapes ?? [])[0];
    return shape ? { kind: 'shape', shape } : null;
  }
  if (k === 'polyline') {
    const polylineRaw = (raw as { polyline?: unknown }).polyline;
    const L = parseSlideLayoutJson({
      v: 1,
      boxes: [],
      images: [],
      buttons: [],
      shapes: [],
      polylines: polylineRaw ? [polylineRaw] : [],
      videos: [],
      quizzes: [],
    });
    const polyline = (L.polylines ?? [])[0];
    return polyline ? { kind: 'polyline', polyline } : null;
  }
  if (k === 'table') {
    const tableRaw = (raw as { table?: unknown }).table;
    const L = parseSlideLayoutJson({
      v: 1,
      boxes: [],
      images: [],
      buttons: [],
      videos: [],
      quizzes: [],
      tables: tableRaw ? [tableRaw] : [],
    });
    const table = (L.tables ?? [])[0];
    return table ? { kind: 'table', table } : null;
  }
  if (k === 'quizChoiceSet') {
    const setRaw = (raw as { set?: unknown }).set;
    const optionsRaw = (raw as { options?: unknown }).options;
    const L = parseSlideLayoutJson({
      v: 1,
      boxes: [],
      images: [],
      buttons: [],
      videos: [],
      quizzes: [],
      quizChoiceSets: setRaw ? [setRaw] : [],
      quizChoiceOptions: Array.isArray(optionsRaw) ? optionsRaw : [],
    });
    const set = L.quizChoiceSets[0];
    if (!set) return null;
    const options = L.quizChoiceOptions.filter((o) => o.setId === set.id);
    return options.length ? { kind: 'quizChoiceSet', set, options } : null;
  }
  return null;
}

function tryParseSlideBlockClipboard(text: string): ParsedSlideClipboard | null {
  const t = (text ?? '').trim();
  if (!t.startsWith(SLIDE_BLOCK_CLIP_PREFIX)) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(t.slice(SLIDE_BLOCK_CLIP_PREFIX.length));
  } catch {
    return null;
  }
  return parseSlideClipboardPayload(raw);
}

function tryParseSlideBlocksClipboard(text: string): ParsedSlideClipboard[] | null {
  const t = (text ?? '').trim();
  if (!t.startsWith(SLIDE_BLOCKS_CLIP_PREFIX)) return null;
  let raw: unknown;
  try {
    raw = JSON.parse(t.slice(SLIDE_BLOCKS_CLIP_PREFIX.length));
  } catch {
    return null;
  }
  if (!raw || typeof raw !== 'object') return null;
  const items = (raw as { items?: unknown }).items;
  if (!Array.isArray(items)) return null;
  const out: ParsedSlideClipboard[] = [];
  for (const it of items) {
    const p = parseSlideClipboardPayload(it);
    if (p) out.push(p);
  }
  return out.length ? out : null;
}

function serializeSlideMultiBlockClipboard(
  refs: SelectedSlideBlockRef[],
  layout: ProgramSlideLayoutV1
): string | null {
  const items: unknown[] = [];
  const seenQuizSets = new Set<string>();
  for (const ref of refs) {
    if (ref.kind === 'quizChoice') {
      const opt = layout.quizChoiceOptions.find((o) => o.id === ref.id);
      const setId = opt?.setId;
      if (setId) {
        if (seenQuizSets.has(setId)) continue;
        seenQuizSets.add(setId);
      }
    }
    const s = serializeSlideBlockSelection(ref.kind, ref.id, layout);
    if (!s || !s.startsWith(SLIDE_BLOCK_CLIP_PREFIX)) continue;
    try {
      items.push(JSON.parse(s.slice(SLIDE_BLOCK_CLIP_PREFIX.length)));
    } catch {
      /* skip */
    }
  }
  if (!items.length) return null;
  return SLIDE_BLOCKS_CLIP_PREFIX + JSON.stringify({ v: 1, items });
}

/** 페이지 오버레이 캔버스 블록 z-index 하한(배경 레이아웃보다 항상 위) */
const SLIDE_LAYOUT_FOREGROUND_Z_BASE = 100_000;

export type ProgramSlideCanvasToolbarPreset = 'full' | 'evaluation-sheet';
export type ProgramSlideCanvasAspectRatio = 'presentation' | 'a4';

export interface ProgramSlideCanvasProps {
  layout: ProgramSlideLayoutV1;
  onLayoutChange: (next: ProgramSlideLayoutV1) => void;
  readOnly: boolean;
  viewMode?: boolean;
  /** 레이아웃 샌드위치에서 배경/페이지 레이어 z-index 구간 분리 */
  layoutLayerRole?: 'background' | 'foreground';
  /** 웹캠이 캔버스 뒤에 있을 때 루트의 빈 영역 클릭·포커스가 아래 웹캠으로 전달되도록 함 */
  webcamHitThrough?: boolean;
  /** 동일 URL 썸네일 공유 조회용(다른 슬라이드 포함) */
  allLayoutsForWebpageThumbCache?: Record<number, ProgramSlideLayoutV1>;
  /** 개체 복사·잘라내기로 시스템 클립보드가 갱신될 때(페이지 클립보드 무효화 등) */
  onBlockClipboardCopy?: () => void;
  /** 새 개체 생성 시 기본 텍스트 색 */
  defaultTextColorHex?: string;
  /** 캔버스 배경색(페이지별 배경/전체 기본 배경 반영) */
  canvasBackgroundColorHex?: string;
  toolbarPreset?: ProgramSlideCanvasToolbarPreset;
  canvasAspectRatio?: ProgramSlideCanvasAspectRatio;
  fixedHeaderBand?: {
    title: string;
    academicYearLabel?: string;
    /** 2페이지 이상일 때 타이틀 회색 뱃지 오른쪽에 표시할 페이지 번호(1부터) */
    pageNumber?: number;
    titleEditable?: boolean;
    onTitleChange?: (title: string) => void;
  };
  contentBoundsNorm?: { top?: number; bottom?: number };
  imageStorageKind?: 'slide' | 'evaluation-sheet' | 'learning-worksheet';
  evaluationSheetImageScope?: EvaluationPlanSheetImageScope;
  evaluationSheetWorkspaceId?: string | null;
  /** 지정 시 왼쪽 블럭 삽입 툴바를 캔버스 밖 호스트에 portal */
  externalToolbarLeftHost?: HTMLElement | null;
  /** 지정 시 오른쪽 편집 툴바를 캔버스 밖 호스트에 portal */
  externalToolbarRightHost?: HTMLElement | null;
  /** evaluation-sheet: 표 삽입 모달 요청 */
  onRequestCreateTable?: () => void;
  /** 표 블록·셀 선택 변경 */
  onTableSelectionChange?: (tableId: string | null, selection: TableSelectionRect | null) => void;
  /** 표 블록 DOM 위치(미니 모달 앵커) */
  onTableAnchorRectChange?: (rect: DOMRect | null) => void;
  /** 스크롤·리사이즈 시 표 앵커 재계산용 루트(평가지 캔버스 scroll 등) */
  positionSyncRoot?: HTMLElement | null;
  /** viewport portal 미니 툴바가 벗어나지 않도록 할 컨테이너 rect(평가지 모달) */
  viewportContainerRect?: DOMRect | null;
  /** evaluation-sheet viewport portal 툴바 z-index (기본: 평가지 모달 위) */
  floatedToolbarZIndex?: number;
  /** 생성 직후 포커스할 표 id */
  focusTableId?: string | null;
  onFocusTableHandled?: () => void;
  /** 부모(미니 모달 등)에서 갱신한 셀 선택을 캔버스에 반영 */
  syncedTableCellSelection?: (TableSelectionRect & { tableId: string }) | null;
  /**
   * 지정 시(보통 viewMode) 글자 크기를 캔버스 실제 너비 / 이 값 비율로 스케일.
   * 랜딩 서비스 소개처럼 좁은 화면에서 absolute px 폰트가 잘리지 않게 함.
   */
  scaleTypographyFromDesignWidthPx?: number;
}

export interface ProgramSlideCanvasHandle {
  /** 선택 블록이 있으면 화살표로 미세 이동하고 true(페이지 전환 등 상위 처리 생략). */
  tryConsumeArrowKeyForSelection(e: KeyboardEvent): boolean;
  /** 복사 가능한 블록 선택이 있으면 true(페이지 복사 대신 개체 복사). */
  hasEffectiveBlockSelection(): boolean;
}

const ProgramSlideCanvas = forwardRef<ProgramSlideCanvasHandle, ProgramSlideCanvasProps>(function ProgramSlideCanvas(
  {
    layout,
    onLayoutChange,
    readOnly,
    viewMode = false,
    layoutLayerRole,
    webcamHitThrough = false,
    allLayoutsForWebpageThumbCache,
    onBlockClipboardCopy,
    defaultTextColorHex,
    canvasBackgroundColorHex,
    toolbarPreset = 'full',
    canvasAspectRatio = 'presentation',
    fixedHeaderBand,
    contentBoundsNorm,
    imageStorageKind = 'slide',
    evaluationSheetImageScope = 'admin',
    evaluationSheetWorkspaceId = null,
    externalToolbarLeftHost = null,
    externalToolbarRightHost = null,
    onRequestCreateTable,
    onTableSelectionChange,
    onTableAnchorRectChange,
    positionSyncRoot = null,
    viewportContainerRect = null,
    floatedToolbarZIndex: floatedToolbarZIndexProp,
    focusTableId = null,
    onFocusTableHandled,
    syncedTableCellSelection = null,
    scaleTypographyFromDesignWidthPx,
  },
  ref
) {
  const isEvaluationSheetPreset = toolbarPreset === 'evaluation-sheet';
  const usesLearningWorksheetImages = imageStorageKind === 'learning-worksheet';
  const usesEvaluationSheetImages = imageStorageKind === 'evaluation-sheet';
  const [typographyScale, setTypographyScale] = React.useState(1);
  const resolveFontPx = React.useCallback(
    (fontSizePx: number): number => {
      if (!scaleTypographyFromDesignWidthPx || typographyScale >= 0.999) return fontSizePx;
      return Math.max(6, Math.round(fontSizePx * typographyScale * 100) / 100);
    },
    [scaleTypographyFromDesignWidthPx, typographyScale]
  );
  /** 폰트만 줄이고 패딩이 고정이면 모바일에서 마지막 줄이 잘림 → 패딩도 동일 비율로 */
  const typographyBoxPadding =
    scaleTypographyFromDesignWidthPx && typographyScale < 0.999
      ? `${Math.max(0.5, 6 * typographyScale)}px ${Math.max(0.5, 8 * typographyScale)}px`
      : undefined;
  const headerBandElRef = React.useRef<HTMLDivElement | null>(null);
  const worksheetTitleBeforeEditRef = React.useRef('');
  const [headerBandTopNorm, setHeaderBandTopNorm] = React.useState<number | null>(null);
  const contentBoundsNormResolved = React.useMemo<CanvasContentBoundsNorm>(
    () => ({
      top:
        headerBandTopNorm ??
        contentBoundsNorm?.top ??
        (fixedHeaderBand ? EVALUATION_PLAN_SHEET_HEADER_BAND_NORM : 0),
      bottom: contentBoundsNorm?.bottom ?? 1,
    }),
    [headerBandTopNorm, contentBoundsNorm?.top, contentBoundsNorm?.bottom, fixedHeaderBand]
  );
  const contentBoundsRef = React.useRef(contentBoundsNormResolved);
  contentBoundsRef.current = contentBoundsNormResolved;
  const defaultInsertCenterY = (contentBoundsNormResolved.top + contentBoundsNormResolved.bottom) / 2;
  const defaultNewTextFontSizePx = isEvaluationSheetPreset
    ? EVALUATION_PLAN_SHEET_DEFAULT_TEXT_FONT_SIZE_PX
    : 72;
  const defaultShapeTextFontSizePx = isEvaluationSheetPreset
    ? EVALUATION_PLAN_SHEET_DEFAULT_TEXT_FONT_SIZE_PX
    : 32;
  const defaultPolylineStrokeWidthPx = isEvaluationSheetPreset
    ? EVALUATION_PLAN_SHEET_DEFAULT_POLYLINE_STROKE_WIDTH_PX
    : 12;

  const registerPendingClipboardImageForCanvas = React.useCallback(
    (blockId: string, blob: Blob, mimeType: string) => {
      if (usesLearningWorksheetImages) return registerPendingLearningWorksheetClipboardImage(blockId, blob, mimeType);
      if (usesEvaluationSheetImages) return registerPendingEvaluationSheetClipboardImage(blockId, blob, mimeType);
      return registerPendingClipboardImage(blockId, blob, mimeType);
    },
    [usesEvaluationSheetImages, usesLearningWorksheetImages]
  );

  const registerPendingImageForCanvas = React.useCallback(
    (blockId: string, blob: Blob, mimeType: string) => {
      if (usesLearningWorksheetImages) return registerPendingLearningWorksheetImage(blockId, blob, mimeType);
      if (usesEvaluationSheetImages) return registerPendingEvaluationSheetImage(blockId, blob, mimeType);
      return registerPendingSlideImage(blockId, blob, mimeType);
    },
    [usesEvaluationSheetImages, usesLearningWorksheetImages]
  );

  const clonePendingClipboardImageForCanvas = React.useCallback(
    (src: string, newBlockId: string) => {
      if (usesLearningWorksheetImages) return clonePendingLearningWorksheetImageForBlock(src, newBlockId);
      if (usesEvaluationSheetImages) return clonePendingEvaluationSheetImageForBlock(src, newBlockId);
      return clonePendingClipboardImageForBlock(src, newBlockId);
    },
    [usesEvaluationSheetImages, usesLearningWorksheetImages]
  );

  const isPendingImageSrcForCanvas = React.useCallback(
    (src: string) => {
      if (usesLearningWorksheetImages) return isPendingLearningWorksheetImageSrc(src);
      if (usesEvaluationSheetImages) return isPendingEvaluationSheetImageSrc(src);
      return isPendingSlideImageSrc(src);
    },
    [usesEvaluationSheetImages, usesLearningWorksheetImages]
  );

  const revokePendingForBlockForCanvas = React.useCallback(
    (blockId: string) => {
      if (usesLearningWorksheetImages) return revokePendingLearningWorksheetForBlock(blockId);
      if (usesEvaluationSheetImages) return revokePendingEvaluationSheetForBlock(blockId);
      return revokePendingForBlock(blockId);
    },
    [usesEvaluationSheetImages, usesLearningWorksheetImages]
  );

  const revokePendingImageSrcForCanvas = React.useCallback(
    (src: string) => {
      if (usesLearningWorksheetImages) return revokePendingLearningWorksheetImageSrc(src);
      if (usesEvaluationSheetImages) return revokePendingEvaluationSheetImageSrc(src);
      return revokePendingSlideImageSrc(src);
    },
    [usesEvaluationSheetImages, usesLearningWorksheetImages]
  );
  /** 슬라이드 본문은 항상 라이트 표면 — 앱 다크모드와 무관하게 표시색 보정 */
  const slideSurfaceTheme = 'light' as const;
  const slideDisplayColors = React.useMemo(
    () => createSlideDisplayColorMappers(slideSurfaceTheme),
    []
  );
  const resolvedDefaultTextColorHex = React.useMemo(
    () =>
      defaultTextColorHex && /^#[0-9a-f]{6}$/i.test(defaultTextColorHex)
        ? defaultTextColorHex.toLowerCase()
        : '#141414',
    [defaultTextColorHex]
  );
  const resolvedCanvasBackgroundColorHex = React.useMemo(
    () =>
      canvasBackgroundColorHex && /^#[0-9a-f]{6}$/i.test(canvasBackgroundColorHex)
        ? canvasBackgroundColorHex.toLowerCase()
        : '#ffffff',
    [canvasBackgroundColorHex]
  );
  /** 웹캠 오버·레이아웃 전경 캔버스는 inner 배경을 투명하게 두어 아래 레이어(웹캠·배경)가 보이게 함 */
  const shouldPaintCanvasInnerBackground =
    !webcamHitThrough && layoutLayerRole !== 'foreground';
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [canvasInnerEl, setCanvasInnerEl] = React.useState<HTMLDivElement | null>(null);
  const [canvasCoordRect, setCanvasCoordRect] = React.useState<DOMRect | null>(null);
  const lastCanvasCoordRectKeyRef = React.useRef('');

  React.useEffect(() => {
    if (!scaleTypographyFromDesignWidthPx) {
      setTypographyScale(1);
      return;
    }
    const root = rootRef.current;
    if (!root) return;
    const sync = () => {
      const w = root.getBoundingClientRect().width;
      const next = w > 0 ? w / scaleTypographyFromDesignWidthPx : 1;
      setTypographyScale((prev) => (Math.abs(prev - next) < 0.002 ? prev : next));
    };
    sync();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(sync);
    ro.observe(root);
    return () => ro.disconnect();
  }, [scaleTypographyFromDesignWidthPx]);

  React.useLayoutEffect(() => {
    if (!fixedHeaderBand) {
      setHeaderBandTopNorm(null);
      return;
    }
    const bandEl = headerBandElRef.current;
    const innerEl = canvasInnerEl;
    if (!bandEl || !innerEl) return;

    const measure = () => {
      const innerH = innerEl.getBoundingClientRect().height;
      if (innerH <= 0) return;
      const bandH = bandEl.getBoundingClientRect().height;
      const topNorm = frac4(
        Math.min(
          0.35,
          Math.max(0.05, (bandH + EVALUATION_PLAN_SHEET_HEADER_BAND_CONTENT_GAP_PX) / innerH)
        )
      );
      setHeaderBandTopNorm((prev) => {
        if (prev != null && Math.abs(prev - topNorm) < 0.001) return prev;
        return topNorm;
      });
    };

    const ro = new ResizeObserver(measure);
    ro.observe(bandEl);
    ro.observe(innerEl);
    measure();
    return () => ro.disconnect();
  }, [fixedHeaderBand, fixedHeaderBand?.title, fixedHeaderBand?.academicYearLabel, canvasInnerEl]);
  /** 텍스트 상자(contenteditable) */
  const textBlockRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const textareaRefs = React.useRef<Record<string, HTMLTextAreaElement | null>>({});
  const quizPlaceholderInputRefs = React.useRef<Record<string, HTMLInputElement | null>>({});
  const videoRefs = React.useRef<Record<string, HTMLVideoElement | null>>({});
  const iframeRefs = React.useRef<Record<string, HTMLIFrameElement | null>>({});
  const layoutRef = React.useRef(layout);
  const onLayoutChangeRef = React.useRef(onLayoutChange);
  const onBlockClipboardCopyRef = React.useRef(onBlockClipboardCopy);
  onBlockClipboardCopyRef.current = onBlockClipboardCopy;
  const deleteHoldTimerRef = React.useRef<number | null>(null);
  const deleteHoldCommittedRef = React.useRef(false);
  const deleteHoldTargetRef = React.useRef<{ id: string; kind: SlideSelectionKind } | null>(null);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [selectedKind, setSelectedKind] = React.useState<SlideSelectionKind | null>(null);
  const [selectedItems, setSelectedItems] = React.useState<SelectedSlideBlockRef[]>([]);
  /** 그룹 단위 선택(멤버별 is-selected 테두리 없이 묶음 테두리만) */
  const [selectedGroupId, setSelectedGroupId] = React.useState<string | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [tableCellSelection, setTableCellSelection] = React.useState<TableSelectionRect & { tableId: string } | null>(
    null
  );
  const tableCellSelectionRef = React.useRef<TableSelectionRect & { tableId: string } | null>(null);
  const commitTableCellSelection = React.useCallback(
    (next: (TableSelectionRect & { tableId: string }) | null) => {
      tableCellSelectionRef.current = next;
      setTableCellSelection(next);
    },
    []
  );
  const [editingTableCellId, setEditingTableCellId] = React.useState<string | null>(null);
  const tableSelectionAnchorRef = React.useRef<{ tableId: string; row: number; col: number } | null>(null);
  const tableCellDragRef = React.useRef<{
    tableId: string;
    anchorRow: number;
    anchorCol: number;
    cellId: string;
    active: boolean;
    startClientX: number;
    startClientY: number;
  } | null>(null);
  const tableCellPointerDownRef = React.useRef(false);
  const suppressTableCellBlurRef = React.useRef(false);
  const tableBlockElRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
  const onTableSelectionChangeRef = React.useRef(onTableSelectionChange);
  const onTableAnchorRectChangeRef = React.useRef(onTableAnchorRectChange);
  const lastTableAnchorRectKeyRef = React.useRef('');
  /** 수업 프레젠테이션 등 호스트가 표 UI를 주지 않을 때: 캔버스 안에서 표 만들기·편집 패널을 직접 관리 */
  const useInternalTableUi = !onRequestCreateTable;
  const [innerTableCreateOpen, setInnerTableCreateOpen] = React.useState(false);
  const [innerTableId, setInnerTableId] = React.useState<string | null>(null);
  const [innerTableSel, setInnerTableSel] = React.useState<TableSelectionRect | null>(null);
  const [innerTableAnchor, setInnerTableAnchor] = React.useState<DOMRect | null>(null);
  const [innerTableEditOpen, setInnerTableEditOpen] = React.useState(false);
  const [innerFocusTableId, setInnerFocusTableId] = React.useState<string | null>(null);
  onTableSelectionChangeRef.current = useInternalTableUi
    ? (tableId, selection) => {
        setInnerTableId(tableId);
        setInnerTableSel(selection);
        if (tableId) setInnerTableEditOpen(true);
      }
    : onTableSelectionChange;
  onTableAnchorRectChangeRef.current = useInternalTableUi ? (rect) => setInnerTableAnchor(rect) : onTableAnchorRectChange;

  const syncTableAnchorRect = React.useCallback((tableId: string | null) => {
    if (!tableId) {
      if (lastTableAnchorRectKeyRef.current === '') return;
      lastTableAnchorRectKeyRef.current = '';
      onTableAnchorRectChangeRef.current?.(null);
      return;
    }
    const el = tableBlockElRefs.current[tableId];
    const rect = el ? el.getBoundingClientRect() : null;
    const rectKey = rect
      ? `${Math.round(rect.top)}:${Math.round(rect.left)}:${Math.round(rect.width)}:${Math.round(rect.height)}`
      : '';
    if (rectKey === lastTableAnchorRectKeyRef.current) return;
    lastTableAnchorRectKeyRef.current = rectKey;
    onTableAnchorRectChangeRef.current?.(rect);
  }, []);

  const notifyTableSelection = React.useCallback(
    (tableId: string | null, selection: TableSelectionRect | null) => {
      onTableSelectionChangeRef.current?.(tableId, selection);
      syncTableAnchorRect(tableId);
    },
    [syncTableAnchorRect]
  );
  const [editingChoiceLabelOptionId, setEditingChoiceLabelOptionId] = React.useState<string | null>(null);
  const [marqueeViewportRect, setMarqueeViewportRect] = React.useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const marqueeRef = React.useRef<{
    innerEl: HTMLDivElement;
    startClientX: number;
    startClientY: number;
    rootW: number;
    rootH: number;
    shiftKey: boolean;
    baseItems: SelectedSlideBlockRef[];
  } | null>(null);
  const [holdingDeleteId, setHoldingDeleteId] = React.useState<string | null>(null);
  const [deleteConfirmTarget, setDeleteConfirmTarget] = React.useState<{
    id: string;
    kind: SlideSelectionKind;
  } | null>(null);
  const [imageUrlDrafts, setImageUrlDrafts] = React.useState<Record<string, string>>({});
  const [imageLoadErrors, setImageLoadErrors] = React.useState<Record<string, string>>({});
  const [clipboardImageUploading, setClipboardImageUploading] = React.useState<Record<string, boolean>>({});
  const [clipboardImageLoadErrors, setClipboardImageLoadErrors] = React.useState<Record<string, string>>({});
  const [clipboardImageToast, setClipboardImageToast] = React.useState('');
  const [scanLiveBlockId, setScanLiveBlockId] = React.useState<string | null>(null);
  /** 스캔 웹캠 미리보기 입력(툴바「캠 변경」·미리보기 공통) */
  const [scanLiveCaptureMode, setScanLiveCaptureMode] = React.useState<'camera' | 'screen'>('camera');
  const [scanLiveCameraDeviceId, setScanLiveCameraDeviceId] = React.useState<string | null>(null);
  const [scanLiveInputMenuBlockId, setScanLiveInputMenuBlockId] = React.useState<string | null>(null);
  const [scanLiveInputDeviceList, setScanLiveInputDeviceList] = React.useState<MediaDeviceInfo[]>([]);
  const scanLiveInputMenuWrapRef = React.useRef<HTMLDivElement | null>(null);
  type PlaceCanvasTool = null | 'line';
  const [placeCanvasTool, setPlaceCanvasTool] = React.useState<PlaceCanvasTool>(null);
  const [shapeAddFlyoutOpen, setShapeAddFlyoutOpen] = React.useState(false);
  const [quizAddFlyoutOpen, setQuizAddFlyoutOpen] = React.useState(false);
  const quizAddMainButtonPointerTypeRef = React.useRef<string>('');
  /** 도형 메인 버튼에서 마우스로 눌렀는지 — 호버로 연 뒤 같은 클릭에서 바로 닫히지 않게 구분 */
  const shapeAddMainButtonPointerTypeRef = React.useRef<string>('');
  const [lineDraftPoints, setLineDraftPoints] = React.useState<Array<{ x: number; y: number }> | null>(null);
  /** 직선 그리기: 마지막 확정 점에서 커서까지 보조 가이드(정규화 좌표) */
  const [linePointerNorm, setLinePointerNorm] = React.useState<{ x: number; y: number } | null>(null);
  const placeCanvasToolRef = React.useRef<PlaceCanvasTool>(null);
  const lineDraftPointsRef = React.useRef<Array<{ x: number; y: number }> | null>(null);
  React.useEffect(() => {
    placeCanvasToolRef.current = placeCanvasTool;
  }, [placeCanvasTool]);
  React.useEffect(() => {
    lineDraftPointsRef.current = lineDraftPoints;
  }, [lineDraftPoints]);
  const [videoUrlDrafts, setVideoUrlDrafts] = React.useState<Record<string, string>>({});
  const [webpageUrlDrafts, setWebpageUrlDrafts] = React.useState<Record<string, string>>({});
  const [webpageCodeDrafts, setWebpageCodeDrafts] = React.useState<Record<string, string>>({});
  const [webpagePreviewLoadingId, setWebpagePreviewLoadingId] = React.useState<string | null>(null);
  const [webpagePreviewErrorByBlockId, setWebpagePreviewErrorByBlockId] = React.useState<Record<string, string>>({});
  const webpageToolbarInputFocusIdRef = React.useRef<string | null>(null);
  const [webpageEmbedFailed, setWebpageEmbedFailed] = React.useState<Record<string, boolean>>({});
  const allLayoutsForWebpageThumbCacheRef = React.useRef(allLayoutsForWebpageThumbCache);
  React.useEffect(() => {
    allLayoutsForWebpageThumbCacheRef.current = allLayoutsForWebpageThumbCache;
  }, [allLayoutsForWebpageThumbCache]);
  const [videoLoadErrors, setVideoLoadErrors] = React.useState<Record<string, string>>({});
  const [videoEmbedStarts, setVideoEmbedStarts] = React.useState<Record<string, number>>({});
  const [videoEmbedCurrentTimes, setVideoEmbedCurrentTimes] = React.useState<Record<string, number>>({});
  const [smartSnapEnabled, setSmartSnapEnabled] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(SLIDE_SMART_SNAP_STORAGE_KEY) !== 'off';
  });
  const [smartSnapGuides, setSmartSnapGuides] = React.useState<{ x: number | null; y: number | null }>({
    x: null,
    y: null,
  });
  const undoStackRef = React.useRef<ProgramSlideLayoutV1[]>([]);
  const redoStackRef = React.useRef<ProgramSlideLayoutV1[]>([]);
  const gestureHistoryStartRef = React.useRef<ProgramSlideLayoutV1 | null>(null);
  const [canUndoHistory, setCanUndoHistory] = React.useState(false);
  const [canRedoHistory, setCanRedoHistory] = React.useState(false);
  const [videoControlEnabledId, setVideoControlEnabledId] = React.useState<string | null>(null);
  const videoControlEnabledIdRef = React.useRef<string | null>(null);
  const stopVideoPlaybackInEditModeRef = React.useRef<() => void>(() => {});
  React.useEffect(() => {
    videoControlEnabledIdRef.current = videoControlEnabledId;
  }, [videoControlEnabledId]);
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SLIDE_SMART_SNAP_STORAGE_KEY, smartSnapEnabled ? 'on' : 'off');
  }, [smartSnapEnabled]);
  React.useEffect(() => {
    if (smartSnapEnabled) return;
    setSmartSnapGuides((prev) => (prev.x == null && prev.y == null ? prev : { x: null, y: null }));
  }, [smartSnapEnabled]);
  const [cropEditingId, setCropEditingId] = React.useState<string | null>(null);
  const [fadingDismissButtonIds, setFadingDismissButtonIds] = React.useState<Set<string>>(() => new Set());
  const [hiddenDismissButtonIds, setHiddenDismissButtonIds] = React.useState<Set<string>>(() => new Set());
  const [quizDraftById, setQuizDraftById] = React.useState<Record<string, string>>({});
  const [quizSucceededIds, setQuizSucceededIds] = React.useState<Set<string>>(() => new Set());
  const [quizWrongFlashIds, setQuizWrongFlashIds] = React.useState<Set<string>>(() => new Set());
  const [quizCorrectOverlay, setQuizCorrectOverlay] = React.useState(false);
  const [quizResultOverlay, setQuizResultOverlay] = React.useState<'o' | 'x' | null>(null);
  const [choicePickedBySetId, setChoicePickedBySetId] = React.useState<Record<string, Set<number>>>({});
  const [choiceSucceededSetIds, setChoiceSucceededSetIds] = React.useState<Set<string>>(() => new Set());
  const [choiceWrongFlashSetIds, setChoiceWrongFlashSetIds] = React.useState<Set<string>>(() => new Set());
  const dragRef = React.useRef<{
    mode:
      | 'move'
      | 'group-resize'
      | 'resize'
      | 'button-resize'
      | 'button-radius'
      | 'image-resize'
      | 'image-resize-x'
      | 'image-resize-y'
      | 'video-resize'
      | 'crop-move'
      | 'crop-resize';
    kind: SlideSelectionKind;
    id: string;
    side?: ResizeSide;
    /** 'image-resize-y'(소스코드 블럭 세로 리사이즈) */
    vSide?: 'top' | 'bottom';
    corner?: ImageResizeCorner;
    /** 가로 리사이즈 시 초기 aspectWMultiplier */
    startAspectWMult?: number;
    startRadiusPx?: number;
    /** 선택형 퀴즈 세트 동기 리사이즈·둥글기용 */
    choiceSetId?: string;
    choiceSetBBox?: { left: number; top: number; right: number; bottom: number };
    /** `kind === 'shape'` 코너 리사이즈 시작 시점의 도형 종류(gesture 중 layout과 무관) */
    shapeBlockKind?: ProgramSlideShapeKindV1;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
    w: number;
    h: number;
    crop?: ProgramSlideImageCropV1;
    moveGroup?: SelectedMoveBlockGeometry[];
    moveGroupBounds?: { minX: number; minY: number; maxRight: number; maxBottom: number };
    rootW: number;
    rootH: number;
    latestX: number;
    latestY: number;
    latestW: number;
    latestH: number;
    /** `kind === 'polyline'` 단일 이동 시 드래그 시작 시점의 꼭짓점(정규화 좌표) */
    polylinePoints0?: Array<{ x: number; y: number }>;
    /** 그룹 단위 선택 유지한 채 전체 이동(포인터 업 시 무이동이면 멤버 단일 선택으로 드릴) */
    keptGroupUnitSelection?: boolean;
    /** Ctrl/Cmd 복제 드래그로 전환됨(시작 시 또는 드래그 중) */
    ctrlCopyActive?: boolean;
  } | null>(null);

  /** 리사이즈 중 Shift — 포인터 이벤트에修饰键이 없을 때 대비해 키보드로 동기화 */
  const resizeShiftKeyboardRef = React.useRef(false);
  const resizeShiftKeyListenerCleanupRef = React.useRef<(() => void) | null>(null);
  /** 이동 드래그 중 Ctrl/Cmd로 복제 전환용 키 리스너 */
  const ctrlCopyKeyListenerCleanupRef = React.useRef<(() => void) | null>(null);

  const editingIdRef = React.useRef(editingId);
  const selectionRef = React.useRef({ selectedId, selectedKind, selectedItems, selectedGroupId });
  /** 툴바 클릭 등으로 live Selection이 사라져도 부분 색 적용에 쓸 평문 구간 */
  const slideTextPlainSelectionRef = React.useRef<{ boxId: string; start: number; end: number } | null>(null);
  React.useEffect(() => {
    editingIdRef.current = editingId;
  }, [editingId]);
  React.useEffect(() => {
    selectionRef.current = { selectedId, selectedKind, selectedItems, selectedGroupId };
  }, [selectedId, selectedKind, selectedItems, selectedGroupId]);

  const captureSlideTextPlainSelection = React.useCallback((boxId: string) => {
    const root = textBlockRefs.current[boxId];
    if (!root || editingIdRef.current !== boxId || typeof document === 'undefined') return;
    const sel = document.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const off = getPlainTextOffsetsFromSelection(root, sel);
    if (!off) return;
    slideTextPlainSelectionRef.current = { boxId, start: off.start, end: off.end };
  }, []);

  React.useEffect(() => {
    if (!editingId || typeof document === 'undefined') return;
    const onSelectionChange = () => captureSlideTextPlainSelection(editingId);
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [captureSlideTextPlainSelection, editingId]);

  const keepTextEditFocusFromToolbar = React.useCallback(
    (boxId: string) => (e: React.MouseEvent | React.PointerEvent) => {
      captureSlideTextPlainSelection(boxId);
      e.stopPropagation();
      const t = e.target as HTMLElement | null;
      if (t?.closest('input, select, textarea')) return;
      e.preventDefault();
    },
    [captureSlideTextPlainSelection]
  );

  const isFocusInsideTextBoxToolbar = React.useCallback((boxContainer: HTMLElement | null) => {
    if (!boxContainer || typeof document === 'undefined') return false;
    const ae = document.activeElement;
    return !!(ae && boxContainer.contains(ae) && ae.closest('.cp-program-slide-canvas-toolbar'));
  }, []);

  const boxes = layout.boxes.length ? [...layout.boxes].sort((a, b) => a.zIndex - b.zIndex) : [];
  const images = layout.images.length ? [...layout.images].sort((a, b) => a.zIndex - b.zIndex) : [];
  const clipboardImages = (layout.clipboardImages ?? []).length
    ? [...(layout.clipboardImages ?? [])].sort((a, b) => a.zIndex - b.zIndex)
    : [];
  const scanImages = (layout.scanImages ?? []).length
    ? [...(layout.scanImages ?? [])].sort((a, b) => a.zIndex - b.zIndex)
    : [];
  const buttons = layout.buttons.length ? [...layout.buttons].sort((a, b) => a.zIndex - b.zIndex) : [];
  const shapes = (layout.shapes ?? []).length ? [...(layout.shapes ?? [])].sort((a, b) => a.zIndex - b.zIndex) : [];
  const tables = (layout.tables ?? []).length ? [...(layout.tables ?? [])].sort((a, b) => a.zIndex - b.zIndex) : [];
  const polylines = (layout.polylines ?? []).length ? [...(layout.polylines ?? [])].sort((a, b) => a.zIndex - b.zIndex) : [];
  const videos = layout.videos.length ? [...layout.videos].sort((a, b) => a.zIndex - b.zIndex) : [];
  const webpages = (layout.webpages ?? []).length
    ? [...(layout.webpages ?? [])].sort((a, b) => a.zIndex - b.zIndex)
    : [];
  const quizzes = layout.quizzes?.length ? [...layout.quizzes].sort((a, b) => a.zIndex - b.zIndex) : [];
  const quizChoiceSets = layout.quizChoiceSets?.length ? [...layout.quizChoiceSets] : [];
  const quizChoiceOptions = layout.quizChoiceOptions?.length
    ? [...layout.quizChoiceOptions].sort((a, b) => a.zIndex - b.zIndex || a.optionIndex - b.optionIndex)
    : [];
  const [slideRecentColors, setSlideRecentColors] = React.useState(readSlideRecentColors);
  const slideUsedColors = React.useMemo(() => collectSlideLayoutColors(layout), [layout]);
  const canEdit = !readOnly && !viewMode;
  const selectedItemKeys = React.useMemo(() => new Set(selectedItems.map((item) => slideSelectionRefKey(item))), [selectedItems]);
  const selectionCount = selectedItems.length;
  const groupMemberRefs = React.useMemo(() => {
    if (!selectedGroupId) return [];
    const fromLayout = collectRefsWithGroupId(layout, selectedGroupId);
    if (fromLayout.length > 1) {
      return fromLayout.map((r) => ({ kind: r.kind as SlideSelectionKind, id: r.id }));
    }
    const fromRef = collectRefsWithGroupId(layoutRef.current, selectedGroupId);
    if (fromRef.length > 1) {
      return fromRef.map((r) => ({ kind: r.kind as SlideSelectionKind, id: r.id }));
    }
    const src = fromLayout.length ? fromLayout : fromRef;
    return src.map((r) => ({ kind: r.kind as SlideSelectionKind, id: r.id }));
  }, [layout, selectedGroupId]);
  const hasGroupAsUnitSelection = groupMemberRefs.length > 1;
  const hasMultiSelection = selectionCount > 1 || hasGroupAsUnitSelection;
  const isDrilledGroupMemberSelection = React.useMemo(() => {
    if (selectedGroupId || selectedItems.length !== 1) return false;
    const it = selectedItems[0];
    if (!it) return false;
    const gid = getSlideBlockGroupId(layout, it.kind, it.id);
    if (!gid) return false;
    return collectRefsWithGroupId(layout, gid).length > 1;
  }, [layout, selectedGroupId, selectedItems]);
  const showMultiSelectionBoundsOverlay = hasMultiSelection || isDrilledGroupMemberSelection;
  const plainMultiGroupResizeClass = hasGroupAsUnitSelection ? '' : ' cp-program-slide-canvas-group-resize--plain-multi';
  const hasSingleSelection = selectionCount === 1 && !!selectedId && !!selectedKind && !selectedGroupId;
  /** 같은 종류 개체만 여러 개 선택했을 때 대표 개체의 미니 툴바로 일괄 수정할 대상 */
  const toolbarBatchTargets = React.useMemo<{ kind: SlideSelectionKind; ids: string[] } | null>(() => {
    if (selectedGroupId || selectedItems.length < 2) return null;
    const kind = selectedItems[0]!.kind;
    if (!SLIDE_TOOLBAR_BATCH_KINDS.has(kind)) return null;
    if (selectedItems.some((item) => item.kind !== kind)) return null;
    return { kind, ids: selectedItems.map((item) => item.id) };
  }, [selectedGroupId, selectedItems]);
  const toolbarBatchTargetsRef = React.useRef(toolbarBatchTargets);
  toolbarBatchTargetsRef.current = toolbarBatchTargets;
  const canOpenToolbarFor = (kind: SlideSelectionKind) =>
    !hasMultiSelection || toolbarBatchTargets?.kind === kind;
  const isToolbarBatchFor = (kind: SlideSelectionKind) => toolbarBatchTargets?.kind === kind;
  const blockZIndexBase = layoutLayerRole === 'foreground' ? SLIDE_LAYOUT_FOREGROUND_Z_BASE : 0;
  const blockLayerZ = React.useCallback(
    (storedZIndex: number) => blockZIndexBase + 10 + storedZIndex,
    [blockZIndexBase]
  );
  const maxCanvasBlockLayer = React.useMemo(() => {
    const maxStoredZ = Math.max(
      0,
      ...layout.boxes.map((box) => box.zIndex),
      ...layout.buttons.map((button) => button.zIndex),
      ...(layout.shapes ?? []).map((s) => s.zIndex),
      ...(layout.polylines ?? []).map((p) => p.zIndex),
      ...layout.images.map((image) => image.zIndex),
      ...layout.videos.map((video) => video.zIndex),
      ...(layout.webpages ?? []).map((wp) => wp.zIndex),
      ...(layout.quizzes ?? []).map((quiz) => quiz.zIndex),
      ...(layout.quizChoiceOptions ?? []).map((o) => o.zIndex)
    );
    return blockZIndexBase + 10 + maxStoredZ;
  }, [
    blockZIndexBase,
    layout.boxes,
    layout.buttons,
    layout.shapes,
    layout.polylines,
    layout.images,
    layout.videos,
    layout.webpages,
    layout.quizzes,
    layout.quizChoiceOptions,
  ]);
  const activeToolbarBlockLayer = maxCanvasBlockLayer + 1000;
  const activeToolbarHandleLayer = activeToolbarBlockLayer + 10;
  const quizResultOverlayZIndex = activeToolbarHandleLayer + 100;
  const resolveToolbarPlacement = React.useCallback(
    (
      blockY: number,
      blockH: number,
      toolbarHeightPx: number,
      options?: { disallowInside?: boolean }
    ) => {
      const rootRect = rootRef.current?.getBoundingClientRect();
      const rootHeight = rootRect ? clientRectToLayoutRect(rootRect).height : 720;
      return resolveSlideToolbarPlacement(blockY, blockH, toolbarHeightPx, rootHeight, options);
    },
    []
  );
  const getBlockLayerZIndex = React.useCallback(
    (baseZIndex: number, toolbarOpen: boolean) => (toolbarOpen ? activeToolbarBlockLayer : baseZIndex),
    [activeToolbarBlockLayer]
  );
  const floatedToolbarZIndex = isEvaluationSheetPreset
    ? (floatedToolbarZIndexProp ?? EVALUATION_PLAN_SHEET_FLOATING_UI_Z)
    : activeToolbarHandleLayer + 5;
  const floatedToolbarPortalProps = isEvaluationSheetPreset
    ? {
        coordinateRect: canvasCoordRect,
        viewportContainerRect,
        viewportSurface: 'sheet-light' as const,
      }
    : { host: canvasInnerEl };

  const syncCanvasCoordRect = React.useCallback(() => {
    if (!isEvaluationSheetPreset) return;
    const root = rootRef.current;
    if (!root) return;
    const rect = root.getBoundingClientRect();
    const key = `${Math.round(rect.top)}:${Math.round(rect.left)}:${Math.round(rect.width)}:${Math.round(rect.height)}`;
    if (key === lastCanvasCoordRectKeyRef.current) return;
    lastCanvasCoordRectKeyRef.current = key;
    setCanvasCoordRect(rect);
  }, [isEvaluationSheetPreset]);

  const applySelectionState = React.useCallback((items: SelectedSlideBlockRef[], primary?: SelectedSlideBlockRef | null) => {
    setSelectedGroupId(null);
    const nextItems = items.filter(
      (item, index, arr) => arr.findIndex((candidate) => slideSelectionRefKey(candidate) === slideSelectionRefKey(item)) === index
    );
    const nextPrimary =
      primary && nextItems.some((item) => slideSelectionRefKey(item) === slideSelectionRefKey(primary))
        ? primary
        : nextItems[nextItems.length - 1] ?? null;
    setSelectedItems(nextItems);
    setSelectedId(nextPrimary?.id ?? null);
    setSelectedKind(nextPrimary?.kind ?? null);
  }, []);

  const clearSelection = React.useCallback(() => {
    setSelectedGroupId(null);
    applySelectionState([], null);
    setEditingId(null);
    commitTableCellSelection(null);
    setEditingTableCellId(null);
    notifyTableSelection(null, null);
    setDeleteConfirmTarget(null);
  }, [applySelectionState, commitTableCellSelection, notifyTableSelection]);

  const selectSingleBlock = React.useCallback(
    (kind: SlideSelectionKind, id: string) => {
      const cur = layoutRef.current;
      const gid = getSlideBlockGroupId(cur, kind, id);
      if (gid) {
        const members = collectRefsWithGroupId(cur, gid);
        if (members.length > 1) {
          setSelectedGroupId(gid);
          setSelectedItems([]);
          const primary = members[0]!;
          setSelectedId(primary.id);
          setSelectedKind(primary.kind as SlideSelectionKind);
          return;
        }
      }
      applySelectionState([{ kind, id }], { kind, id });
      commitTableCellSelection(null);
      setEditingTableCellId(null);
      if (kind === 'table') {
        notifyTableSelection(id, null);
      } else {
        notifyTableSelection(null, null);
      }
    },
    [applySelectionState, commitTableCellSelection, notifyTableSelection]
  );

  React.useLayoutEffect(() => {
    if (!isEvaluationSheetPreset) return;
    syncCanvasCoordRect();
  }, [isEvaluationSheetPreset, layout, syncCanvasCoordRect]);

  React.useEffect(() => {
    if (!isEvaluationSheetPreset) return;
    const sync = () => syncCanvasCoordRect();
    const scrollRoot = positionSyncRoot;
    scrollRoot?.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    let ro: ResizeObserver | null = null;
    const root = rootRef.current;
    if (typeof ResizeObserver !== 'undefined' && root) {
      ro = new ResizeObserver(sync);
      ro.observe(root);
    }
    return () => {
      scrollRoot?.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      ro?.disconnect();
    };
  }, [isEvaluationSheetPreset, positionSyncRoot, syncCanvasCoordRect]);

  React.useLayoutEffect(() => {
    if (!isEvaluationSheetPreset || selectedKind !== 'table' || !selectedId) return;
    syncTableAnchorRect(selectedId);
    const tableEl = tableBlockElRefs.current[selectedId];
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && tableEl) {
      ro = new ResizeObserver(() => syncTableAnchorRect(selectedId));
      ro.observe(tableEl);
    }
    return () => ro?.disconnect();
  }, [isEvaluationSheetPreset, layout.tables, selectedId, selectedKind, syncTableAnchorRect]);

  React.useEffect(() => {
    if (!isEvaluationSheetPreset || selectedKind !== 'table' || !selectedId) return;
    const sel =
      tableCellSelectionRef.current && tableCellSelectionRef.current.tableId === selectedId
        ? {
            rowStart: tableCellSelectionRef.current.rowStart,
            colStart: tableCellSelectionRef.current.colStart,
            rowEnd: tableCellSelectionRef.current.rowEnd,
            colEnd: tableCellSelectionRef.current.colEnd,
          }
        : null;
    notifyTableSelection(selectedId, sel);
  }, [isEvaluationSheetPreset, notifyTableSelection, selectedId, selectedKind]);

  React.useEffect(() => {
    if (!isEvaluationSheetPreset || selectedKind !== 'table' || !selectedId) return;
    const sync = () => syncTableAnchorRect(selectedId);
    const scrollRoot = positionSyncRoot;
    scrollRoot?.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return () => {
      scrollRoot?.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [isEvaluationSheetPreset, positionSyncRoot, selectedId, selectedKind, syncTableAnchorRect]);

  /** 2명 이상 그룹에 속한 블록도 묶음 단위로 승격하지 않음(드릴 유지·인라인 편집 진입용). */
  const selectSingleBlockWithoutGroupUnitPromotion = React.useCallback(
    (kind: SlideSelectionKind, id: string) => {
      const cur = layoutRef.current;
      const gid = getSlideBlockGroupId(cur, kind, id);
      if (gid && collectRefsWithGroupId(cur, gid).length > 1) {
        applySelectionState([{ kind, id }], { kind, id });
        return;
      }
      selectSingleBlock(kind, id);
    },
    [applySelectionState, selectSingleBlock]
  );

  const toggleBlockSelection = React.useCallback(
    (kind: SlideSelectionKind, id: string) => {
      const cur = layoutRef.current;
      const prevGroupId = selectionRef.current.selectedGroupId;
      let prevItems = selectionRef.current.selectedItems;
      if (prevGroupId) {
        prevItems = collectRefsWithGroupId(cur, prevGroupId).map((r) => ({
          kind: r.kind as SlideSelectionKind,
          id: r.id,
        }));
      }
      const toggledRefs = expandSlideRefsWithCoMembers(cur, [{ kind, id }]);
      const toggleKeys = new Set(toggledRefs.map(slideSelectionRefKey));
      const anyIn = prevItems.some((item) => toggleKeys.has(slideSelectionRefKey(item)));
      let nextItems: SelectedSlideBlockRef[];
      if (anyIn) {
        nextItems = prevItems.filter((item) => !toggleKeys.has(slideSelectionRefKey(item)));
      } else {
        const merged = [...prevItems];
        toggledRefs.forEach((r) => {
          const ref: SelectedSlideBlockRef = { kind: r.kind as SlideSelectionKind, id: r.id };
          if (!merged.some((m) => slideSelectionRefKey(m) === slideSelectionRefKey(ref))) merged.push(ref);
        });
        nextItems = merged;
      }
      const nextPrimary = nextItems[nextItems.length - 1] ?? null;
      applySelectionState(nextItems, nextPrimary ? { kind: nextPrimary.kind, id: nextPrimary.id } : null);
      setEditingId(null);
    },
    [applySelectionState]
  );

  const isBlockSelected = React.useCallback(
    (kind: SlideSelectionKind, id: string) => {
      if (selectedGroupId) {
        const g = getSlideBlockGroupId(layoutRef.current, kind, id);
        if (g && g === selectedGroupId) return false;
      }
      return selectedItemKeys.has(slideSelectionKey(kind, id));
    },
    [selectedItemKeys, selectedGroupId]
  );

  const getSelectedBlockGeometrySnapshot = React.useCallback(
    (opts?: { keyboardNudge?: boolean }): SelectedMoveBlockGeometry[] => {
      const current = layoutRef.current;
      const sg = selectionRef.current.selectedGroupId;
      if (sg) {
        const members = collectRefsWithGroupId(current, sg);
        const out: SelectedMoveBlockGeometry[] = [];
        const seen = new Set<string>();
        members.forEach((m) => {
          const g = blockGeometryFromRef(current, { kind: m.kind as SlideSelectionKind, id: m.id });
          if (!g) return;
          const k = slideSelectionKey(g.kind, g.id);
          if (seen.has(k)) return;
          seen.add(k);
          out.push(g);
        });
        return out;
      }
      const selected = selectionRef.current.selectedItems;
      /** 화살표 미세 이동: 그룹 묶음이 아닌데 멤버 1개만 고른 경우(드릴)에는 해당 개체만 */
      if (
        opts?.keyboardNudge &&
        selected.length === 1 &&
        !selectionRef.current.selectedGroupId
      ) {
        const sole = selected[0]!;
        const soleGid = getSlideBlockGroupId(current, sole.kind, sole.id);
        if (soleGid && collectRefsWithGroupId(current, soleGid).length > 1) {
          const g = blockGeometryFromRef(current, sole);
          return g ? [g] : [];
        }
      }
      const expanded = expandSlideRefsWithCoMembers(current, selected);
      const out: SelectedMoveBlockGeometry[] = [];
      const seen = new Set<string>();
      expanded.forEach((item) => {
        const g = blockGeometryFromRef(current, { kind: item.kind as SlideSelectionKind, id: item.id });
        if (!g) return;
        const k = slideSelectionKey(g.kind, g.id);
        if (seen.has(k)) return;
        seen.add(k);
        out.push(g);
      });
      return out;
    },
    []
  );

  const getSelectedMoveGroup = React.useCallback(
    (kind: SlideSelectionKind, id: string): SelectedMoveBlockGeometry[] => {
      const current = layoutRef.current;
      const sg = selectionRef.current.selectedGroupId;
      if (sg) {
        const members = collectRefsWithGroupId(current, sg);
        if (!members.some((m) => m.kind === kind && m.id === id)) return [];
        const geoms: SelectedMoveBlockGeometry[] = [];
        const seen = new Set<string>();
        members.forEach((m) => {
          const g = blockGeometryFromRef(current, { kind: m.kind as SlideSelectionKind, id: m.id });
          if (!g) return;
          const k = slideSelectionKey(g.kind, g.id);
          if (seen.has(k)) return;
          seen.add(k);
          geoms.push(g);
        });
        return geoms.length > 0 && members.length > 1 ? geoms : [];
      }
      const selected = selectionRef.current.selectedItems;
      if (selected.length === 1) {
        const sole = selected[0]!;
        const soleGid = getSlideBlockGroupId(current, sole.kind, sole.id);
        if (soleGid && collectRefsWithGroupId(current, soleGid).length > 1) {
          if (sole.kind === kind && sole.id === id) {
            return [];
          }
        }
      }
      const primaryKey = slideSelectionKey(kind, id);
      if (!selected.some((item) => slideSelectionRefKey(item) === primaryKey)) return [];

      const expanded = expandSlideRefsWithCoMembers(current, selected);
      const geoms: SelectedMoveBlockGeometry[] = [];
      const seen = new Set<string>();
      expanded.forEach((item) => {
        const g = blockGeometryFromRef(current, { kind: item.kind as SlideSelectionKind, id: item.id });
        if (!g) return;
        const k = slideSelectionKey(g.kind, g.id);
        if (seen.has(k)) return;
        seen.add(k);
        geoms.push(g);
      });
      return selected.length > 1 || expanded.length > 1 ? geoms : geoms.length > 1 ? geoms : [];
    },
    []
  );

  const shouldKeepSlideGroupUnitSelectionOnDrag = React.useCallback((kind: SlideSelectionKind, id: string) => {
    const cur = layoutRef.current;
    const gid = getSlideBlockGroupId(cur, kind, id);
    const sg = selectionRef.current.selectedGroupId;
    return !!(gid && sg && gid === sg);
  }, []);

  const shouldSkipSelectSingleOnDragStart = React.useCallback((kind: SlideSelectionKind, id: string) => {
    const selected = selectionRef.current.selectedItems;
    if (selected.length !== 1) return false;
    const sole = selected[0]!;
    if (sole.kind !== kind || sole.id !== id) return false;
    const cur = layoutRef.current;
    const gid = getSlideBlockGroupId(cur, kind, id);
    return !!(gid && collectRefsWithGroupId(cur, gid).length > 1 && !selectionRef.current.selectedGroupId);
  }, []);

  const getEffectiveSelectedRefs = React.useCallback((): SelectedSlideBlockRef[] => {
    const cur = layoutRef.current;
    const sg = selectionRef.current.selectedGroupId;
    if (sg) {
      return collectRefsWithGroupId(cur, sg).map((r) => ({ kind: r.kind as SlideSelectionKind, id: r.id }));
    }
    return selectionRef.current.selectedItems;
  }, []);

  React.useEffect(() => {
    const gid = selectedGroupId;
    if (!gid) return;
    const nLayout = collectRefsWithGroupId(layout, gid).length;
    const nRef = collectRefsWithGroupId(layoutRef.current, gid).length;
    const n = Math.max(nLayout, nRef);
    if (n > 1) return;
    if (n === 1) {
      const pick = collectRefsWithGroupId(layout, gid)[0] ?? collectRefsWithGroupId(layoutRef.current, gid)[0];
      if (!pick) return;
      const kind = pick.kind as SlideSelectionKind;
      applySelectionState([{ kind, id: pick.id }], { kind, id: pick.id });
    } else if (nLayout === 0 && nRef === 0) {
      applySelectionState([], null);
    }
  }, [layout, selectedGroupId, applySelectionState]);

  const updateSmartSnapGuides = React.useCallback((x: number | null, y: number | null) => {
    setSmartSnapGuides((prev) => (prev.x === x && prev.y === y ? prev : { x, y }));
  }, []);

  const getAllBlockGeometrySnapshot = React.useCallback((): SelectedMoveBlockGeometry[] => {
    const current = layoutRef.current;
    return [
      ...current.boxes.map((box) => ({ kind: 'text' as const, id: box.id, x: box.x, y: box.y, w: box.w, h: box.h })),
      ...current.images.map((image) => ({ kind: 'image' as const, id: image.id, x: image.x, y: image.y, w: image.w, h: image.h })),
      ...(current.clipboardImages ?? []).map((image) => ({
        kind: 'clipboardImage' as const,
        id: image.id,
        x: image.x,
        y: image.y,
        w: image.w,
        h: image.h,
      })),
      ...(current.scanImages ?? []).map((image) => ({
        kind: 'scanImage' as const,
        id: image.id,
        x: image.x,
        y: image.y,
        w: image.w,
        h: image.h,
      })),
      ...current.buttons.map((button) => ({ kind: 'button' as const, id: button.id, x: button.x, y: button.y, w: button.w, h: button.h })),
      ...(current.shapes ?? []).map((shape) => ({ kind: 'shape' as const, id: shape.id, x: shape.x, y: shape.y, w: shape.w, h: shape.h })),
      ...(current.tables ?? []).map((table) => ({ kind: 'table' as const, id: table.id, x: table.x, y: table.y, w: table.w, h: table.h })),
      ...(current.polylines ?? []).flatMap((pl) => {
        const b = polylineBBox(pl.points);
        return b
          ? [
              {
                kind: 'polyline' as const,
                id: pl.id,
                x: b.x,
                y: b.y,
                w: b.w,
                h: b.h,
                polylinePoints: pl.points.map((p) => ({ x: p.x, y: p.y })),
              },
            ]
          : [];
      }),
      ...current.videos.map((video) => ({ kind: 'video' as const, id: video.id, x: video.x, y: video.y, w: video.w, h: video.h })),
      ...(current.webpages ?? []).map((wp) => ({
        kind: 'webpage' as const,
        id: wp.id,
        x: wp.x,
        y: wp.y,
        w: wp.w,
        h: wp.h,
      })),
      ...current.quizzes.map((quiz) => ({ kind: 'quiz' as const, id: quiz.id, x: quiz.x, y: quiz.y, w: quiz.w, h: quiz.h })),
      ...(current.quizChoiceOptions ?? []).map((opt) => ({
        kind: 'quizChoice' as const,
        id: opt.id,
        x: opt.x,
        y: opt.y,
        w: opt.w,
        h: opt.h,
      })),
    ];
  }, []);

  const getSmartSnappedMoveBounds = React.useCallback(
    (
      bounds: SelectionBounds,
      excludedKeys: Set<string>,
      rootW: number,
      rootH: number
    ): { left: number; top: number; guideX: number | null; guideY: number | null } => {
      const xThreshold = SLIDE_SMART_SNAP_THRESHOLD_PX / Math.max(1, rootW);
      const yThreshold = SLIDE_SMART_SNAP_THRESHOLD_PX / Math.max(1, rootH);
      const others = getAllBlockGeometrySnapshot().filter((item) => !excludedKeys.has(slideSelectionKey(item.kind, item.id)));

      let bestDeltaX = 0;
      let bestGuideX: number | null = null;
      let bestAbsDeltaX = Number.POSITIVE_INFINITY;
      let bestDeltaY = 0;
      let bestGuideY: number | null = null;
      let bestAbsDeltaY = Number.POSITIVE_INFINITY;
      const boundsCenterX = bounds.left + bounds.width / 2;
      const boundsCenterY = bounds.top + bounds.height / 2;
      const pageCenterXDelta = 0.5 - boundsCenterX;
      const pageCenterYDelta = 0.5 - boundsCenterY;

      if (Math.abs(pageCenterXDelta) <= xThreshold) {
        bestAbsDeltaX = Math.abs(pageCenterXDelta);
        bestDeltaX = pageCenterXDelta;
        bestGuideX = 0.5;
      }
      if (Math.abs(pageCenterYDelta) <= yThreshold) {
        bestAbsDeltaY = Math.abs(pageCenterYDelta);
        bestDeltaY = pageCenterYDelta;
        bestGuideY = 0.5;
      }

      others.forEach((item) => {
        const itemRight = item.x + item.w;
        const itemBottom = item.y + item.h;
        [item.x, itemRight].forEach((line) => {
          [line - bounds.left, line - bounds.right].forEach((delta) => {
            const abs = Math.abs(delta);
            if (abs <= xThreshold && abs < bestAbsDeltaX) {
              bestAbsDeltaX = abs;
              bestDeltaX = delta;
              bestGuideX = line;
            }
          });
        });
        [item.y, itemBottom].forEach((line) => {
          [line - bounds.top, line - bounds.bottom].forEach((delta) => {
            const abs = Math.abs(delta);
            if (abs <= yThreshold && abs < bestAbsDeltaY) {
              bestAbsDeltaY = abs;
              bestDeltaY = delta;
              bestGuideY = line;
            }
          });
        });
      });

      const nextLeft = frac4(Math.min(1 - bounds.width, Math.max(0, bounds.left + bestDeltaX)));
      const nextTop = frac4(Math.min(1 - bounds.height, Math.max(0, bounds.top + bestDeltaY)));
      return {
        left: nextLeft,
        top: nextTop,
        guideX: bestGuideX,
        guideY: bestGuideY,
      };
    },
    [getAllBlockGeometrySnapshot]
  );

  const getSmartSnappedXLine = React.useCallback(
    (
      line: number,
      excludedKeys: Set<string>,
      rootW: number
    ): { line: number; guideX: number | null; delta: number } => {
      const threshold = SLIDE_SMART_SNAP_THRESHOLD_PX / Math.max(1, rootW);
      const others = getAllBlockGeometrySnapshot().filter((item) => !excludedKeys.has(slideSelectionKey(item.kind, item.id)));
      let bestGuideX: number | null = null;
      let bestDelta = 0;
      let bestAbsDelta = Number.POSITIVE_INFINITY;
      others.forEach((item) => {
        [item.x, item.x + item.w].forEach((candidate) => {
          const delta = candidate - line;
          const abs = Math.abs(delta);
          if (abs <= threshold && abs < bestAbsDelta) {
            bestAbsDelta = abs;
            bestDelta = delta;
            bestGuideX = candidate;
          }
        });
      });
      return {
        line: frac4(Math.min(1, Math.max(0, line + bestDelta))),
        guideX: bestGuideX,
        delta: bestGuideX == null ? 0 : bestDelta,
      };
    },
    [getAllBlockGeometrySnapshot]
  );

  const getSmartSnappedYLine = React.useCallback(
    (
      line: number,
      excludedKeys: Set<string>,
      rootH: number
    ): { line: number; guideY: number | null; delta: number } => {
      const threshold = SLIDE_SMART_SNAP_THRESHOLD_PX / Math.max(1, rootH);
      const others = getAllBlockGeometrySnapshot().filter((item) => !excludedKeys.has(slideSelectionKey(item.kind, item.id)));
      let bestGuideY: number | null = null;
      let bestDelta = 0;
      let bestAbsDelta = Number.POSITIVE_INFINITY;
      others.forEach((item) => {
        [item.y, item.y + item.h].forEach((candidate) => {
          const delta = candidate - line;
          const abs = Math.abs(delta);
          if (abs <= threshold && abs < bestAbsDelta) {
            bestAbsDelta = abs;
            bestDelta = delta;
            bestGuideY = candidate;
          }
        });
      });
      return {
        line: frac4(Math.min(1, Math.max(0, line + bestDelta))),
        guideY: bestGuideY,
        delta: bestGuideY == null ? 0 : bestDelta,
      };
    },
    [getAllBlockGeometrySnapshot]
  );

  const getSmartSnappedAspectResize = React.useCallback(
    (params: {
      rawX: number;
      rawY: number;
      rawW: number;
      rawH: number;
      corner: ImageResizeCorner;
      startLeft: number;
      startTop: number;
      startRight: number;
      startBottom: number;
      aspect: number;
      minW: number;
      excludedKeys: Set<string>;
      rootW: number;
      rootH: number;
    }): { x: number; y: number; w: number; h: number; guideX: number | null; guideY: number | null } => {
      const { rawX, rawY, rawW, rawH, corner, startLeft, startTop, startRight, startBottom, aspect, minW, excludedKeys, rootW, rootH } =
        params;
      const minH = minW / Math.max(0.001, aspect);
      const candidates: Array<{
        score: number;
        x: number;
        y: number;
        w: number;
        h: number;
        guideX: number | null;
        guideY: number | null;
      }> = [];

      const movingLeft = corner === 'nw' || corner === 'sw';
      const movingTop = corner === 'nw' || corner === 'ne';

      const xSnap = getSmartSnappedXLine(movingLeft ? rawX : rawX + rawW, excludedKeys, rootW);
      if (xSnap.guideX != null) {
        const candidateW = movingLeft ? startRight - xSnap.line : xSnap.line - startLeft;
        if (candidateW >= minW) {
          const candidateH = candidateW / aspect;
          const candidateX = movingLeft ? startRight - candidateW : startLeft;
          const candidateY = movingTop ? startBottom - candidateH : startTop;
          if (candidateX >= 0 && candidateY >= 0 && candidateX + candidateW <= 1 && candidateY + candidateH <= 1) {
            candidates.push({
              score: Math.abs(xSnap.delta),
              x: frac4(candidateX),
              y: frac4(candidateY),
              w: frac4(candidateW),
              h: frac4(candidateH),
              guideX: xSnap.guideX,
              guideY: null,
            });
          }
        }
      }

      const ySnap = getSmartSnappedYLine(movingTop ? rawY : rawY + rawH, excludedKeys, rootH);
      if (ySnap.guideY != null) {
        const candidateH = movingTop ? startBottom - ySnap.line : ySnap.line - startTop;
        if (candidateH >= minH) {
          const candidateW = candidateH * aspect;
          const candidateX = movingLeft ? startRight - candidateW : startLeft;
          const candidateY = movingTop ? startBottom - candidateH : startTop;
          if (candidateX >= 0 && candidateY >= 0 && candidateX + candidateW <= 1 && candidateY + candidateH <= 1) {
            candidates.push({
              score: Math.abs(ySnap.delta),
              x: frac4(candidateX),
              y: frac4(candidateY),
              w: frac4(candidateW),
              h: frac4(candidateH),
              guideX: null,
              guideY: ySnap.guideY,
            });
          }
        }
      }

      if (!candidates.length) {
        return {
          x: rawX,
          y: rawY,
          w: rawW,
          h: rawH,
          guideX: null,
          guideY: null,
        };
      }
      candidates.sort((a, b) => a.score - b.score);
      return candidates[0];
    },
    [getSmartSnappedXLine, getSmartSnappedYLine]
  );

  const selectedBlockGeometries = React.useMemo(() => {
    const refs =
      selectedGroupId && groupMemberRefs.length > 1
        ? groupMemberRefs
        : selectedItems.length > 1
          ? selectedItems
          : [];
    if (refs.length === 0) return [];
    const out: SelectedMoveBlockGeometry[] = [];
    refs.forEach((item) => {
      const g = blockGeometryFromRef(layout, item);
      if (g) out.push(g);
    });
    return out;
  }, [layout, selectedGroupId, groupMemberRefs, selectedItems]);

  /** 그룹 속 한 개체만 선택(드릴) 시 회색 점선 묶음 = 그룹 전체 외곽 */
  const drilledGroupOuterGeometries = React.useMemo((): SelectedMoveBlockGeometry[] => {
    if (!isDrilledGroupMemberSelection || !selectedItems[0]) return [];
    const it = selectedItems[0];
    const gid = getSlideBlockGroupId(layout, it.kind, it.id);
    if (!gid) return [];
    const refs = collectRefsWithGroupId(layout, gid).map((r) => ({
      kind: r.kind as SlideSelectionKind,
      id: r.id,
    }));
    const out: SelectedMoveBlockGeometry[] = [];
    refs.forEach((item) => {
      const g = blockGeometryFromRef(layout, item);
      if (g) out.push(g);
    });
    return out;
  }, [layout, isDrilledGroupMemberSelection, selectedItems]);

  const multiSelectionBounds = React.useMemo(() => {
    if (isDrilledGroupMemberSelection && drilledGroupOuterGeometries.length > 1) {
      return getSelectionBounds(drilledGroupOuterGeometries);
    }
    if (!selectedBlockGeometries.length) return null;
    if (selectedBlockGeometries.length > 1) return getSelectionBounds(selectedBlockGeometries);
    return null;
  }, [selectedBlockGeometries, isDrilledGroupMemberSelection, drilledGroupOuterGeometries]);

  React.useEffect(() => {
    const sg = selectedGroupId;
    if (sg) {
      const inProp = collectRefsWithGroupId(layout, sg).length;
      const inRef = collectRefsWithGroupId(layoutRef.current, sg).length;
      if (inProp < inRef && inRef > 1) {
        onLayoutChangeRef.current = onLayoutChange;
        return;
      }
    }
    layoutRef.current = layout;
    onLayoutChangeRef.current = onLayoutChange;
  }, [layout, onLayoutChange, selectedGroupId]);

  const syncHistoryAvailability = React.useCallback(() => {
    setCanUndoHistory(undoStackRef.current.length > 0);
    setCanRedoHistory(redoStackRef.current.length > 0);
  }, []);

  const pushUndoSnapshot = React.useCallback(
    (snapshot: ProgramSlideLayoutV1) => {
      undoStackRef.current.push(cloneSlideLayout(snapshot));
      if (undoStackRef.current.length > SLIDE_HISTORY_LIMIT) {
        undoStackRef.current.splice(0, undoStackRef.current.length - SLIDE_HISTORY_LIMIT);
      }
      redoStackRef.current = [];
      syncHistoryAvailability();
    },
    [syncHistoryAvailability]
  );

  const beginGestureHistory = React.useCallback((snapshotOverride?: ProgramSlideLayoutV1 | null) => {
    if (gestureHistoryStartRef.current) return;
    gestureHistoryStartRef.current = cloneSlideLayout(snapshotOverride ?? layoutRef.current);
  }, []);

  const commitGestureHistory = React.useCallback(() => {
    const started = gestureHistoryStartRef.current;
    gestureHistoryStartRef.current = null;
    if (!started) return;
    if (areLayoutsEqual(started, layoutRef.current)) return;
    pushUndoSnapshot(started);
  }, [pushUndoSnapshot]);

  const previewSlideColor = React.useCallback(
    (apply: (hex: string) => void) => (hex: string) => {
      beginGestureHistory();
      apply(hex);
    },
    [beginGestureHistory]
  );

  const confirmSlideColor = React.useCallback(
    (apply: (hex: string) => void) => (hex: string) => {
      beginGestureHistory();
      apply(hex);
      commitGestureHistory();
      setSlideRecentColors(rememberSlideRecentColor(hex));
    },
    [beginGestureHistory, commitGestureHistory]
  );

  const applyLayoutChange = React.useCallback(
    (nextLayout: ProgramSlideLayoutPatch, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
      const current = layoutRef.current;
      const merged = mergeSlideLayoutArrays(current, nextLayout);
      if (areLayoutsEqual(current, merged)) return;
      if (historyMode === 'record' || (historyMode === 'auto' && !gestureHistoryStartRef.current)) {
        pushUndoSnapshot(current);
      }
      const next = cloneSlideLayout(merged);
      layoutRef.current = next;
      onLayoutChangeRef.current(next);
    },
    [pushUndoSnapshot]
  );

  useImperativeHandle(
    ref,
    () => ({
      tryConsumeArrowKeyForSelection(e: KeyboardEvent): boolean {
        if (readOnly || viewMode || !canEdit) return false;
        if (e.ctrlKey || e.altKey || e.metaKey) return false;
        if (dragRef.current) return false;
        if (editingId != null || editingChoiceLabelOptionId != null) return false;
        if (cropEditingId) return false;
        if (placeCanvasToolRef.current != null || lineDraftPointsRef.current != null || shapeAddFlyoutOpen) return false;
        if (marqueeRef.current) {
          if (
            e.key === 'ArrowUp' ||
            e.key === 'ArrowDown' ||
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight'
          ) {
            e.preventDefault();
            return true;
          }
          return false;
        }
        const key = e.key;
        if (key !== 'ArrowUp' && key !== 'ArrowDown' && key !== 'ArrowLeft' && key !== 'ArrowRight') return false;

        const moveGroup = getSelectedBlockGeometrySnapshot({ keyboardNudge: true });
        if (!moveGroup.length) return false;

        const step = e.shiftKey ? 0.004 : 0.001;
        let rawDx = 0;
        let rawDy = 0;
        if (key === 'ArrowLeft') rawDx = -step;
        else if (key === 'ArrowRight') rawDx = step;
        else if (key === 'ArrowUp') rawDy = -step;
        else rawDy = step;

        const { boundedDx, boundedDy } = computeBoundedArrowDelta(
          moveGroup,
          rawDx,
          rawDy,
          contentBoundsRef.current
        );
        if (boundedDx === 0 && boundedDy === 0) {
          e.preventDefault();
          return true;
        }
        const patch = buildTranslatePatchFromMoveGroup(layoutRef.current, moveGroup, boundedDx, boundedDy);
        if (!patch) {
          e.preventDefault();
          return true;
        }
        e.preventDefault();
        beginGestureHistory();
        applyLayoutChange(patch, 'skip');
        commitGestureHistory();
        return true;
      },
      hasEffectiveBlockSelection(): boolean {
        if (readOnly || viewMode || !canEdit || editingIdRef.current) return false;
        const refs = getEffectiveSelectedRefs();
        return refs.some((r) => !!serializeSlideBlockSelection(r.kind, r.id, layoutRef.current));
      },
    }),
    [
      readOnly,
      viewMode,
      canEdit,
      cropEditingId,
      editingChoiceLabelOptionId,
      editingId,
      shapeAddFlyoutOpen,
      getSelectedBlockGeometrySnapshot,
      getEffectiveSelectedRefs,
      beginGestureHistory,
      applyLayoutChange,
      commitGestureHistory,
    ]
  );

  React.useEffect(() => {
    if (!canEdit) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.repeat) return;
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      if (e.code !== 'KeyG') return;
      if (dragRef.current) return;
      if (marqueeRef.current) return;
      if (placeCanvasToolRef.current != null || lineDraftPointsRef.current != null || shapeAddFlyoutOpen) return;
      if (editingId != null || editingChoiceLabelOptionId != null) return;
      if (cropEditingId) return;

      const cur = layoutRef.current;
      const sg = selectionRef.current.selectedGroupId;
      const seedRefs: SelectedSlideBlockRef[] = sg
        ? collectRefsWithGroupId(cur, sg).map((r) => ({ kind: r.kind as SlideSelectionKind, id: r.id }))
        : selectionRef.current.selectedItems;
      const expanded = expandSlideRefsWithCoMembers(cur, seedRefs);

      if (e.shiftKey) {
        const gids = new Set<string>();
        expanded.forEach((r) => {
          const g = getSlideBlockGroupId(cur, r.kind, r.id);
          if (g) gids.add(g);
        });
        if (!gids.size) return;
        e.preventDefault();
        const ungroupedRefs: SelectedSlideBlockRef[] = [];
        const seenUngroupKeys = new Set<string>();
        gids.forEach((gid) => {
          collectRefsWithGroupId(cur, gid).forEach((r) => {
            const ref: SelectedSlideBlockRef = { kind: r.kind as SlideSelectionKind, id: r.id };
            const k = slideSelectionRefKey(ref);
            if (seenUngroupKeys.has(k)) return;
            seenUngroupKeys.add(k);
            ungroupedRefs.push(ref);
          });
        });
        beginGestureHistory();
        applyLayoutChange(patchRemoveGroupIds(cur, gids), 'skip');
        commitGestureHistory();
        if (ungroupedRefs.length) {
          const primary = ungroupedRefs[ungroupedRefs.length - 1] ?? null;
          applySelectionState(ungroupedRefs, primary);
        }
        return;
      }

      if (expanded.length < 2) return;
      e.preventDefault();
      const newGid = genSlideBlockGroupId();
      const keySet = new Set(expanded.map((r) => `${r.kind}:${r.id}`));
      const firstSel = expanded[0]!;
      beginGestureHistory();
      applyLayoutChange(patchAssignGroupIdToRefs(cur, keySet, newGid), 'skip');
      commitGestureHistory();
      setSelectedGroupId(newGid);
      setSelectedItems([]);
      setSelectedId(firstSel.id);
      setSelectedKind(firstSel.kind as SlideSelectionKind);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [
    applyLayoutChange,
    applySelectionState,
    beginGestureHistory,
    canEdit,
    commitGestureHistory,
    cropEditingId,
    editingChoiceLabelOptionId,
    editingId,
    shapeAddFlyoutOpen,
  ]);

  const handleUndoHistory = React.useCallback(() => {
    if (gestureHistoryStartRef.current) commitGestureHistory();
    const previous = undoStackRef.current.pop();
    if (!previous) return;
    redoStackRef.current.push(cloneSlideLayout(layoutRef.current));
    gestureHistoryStartRef.current = null;
    syncHistoryAvailability();
    onLayoutChangeRef.current(cloneSlideLayout(previous));
  }, [commitGestureHistory, syncHistoryAvailability]);

  const handleRedoHistory = React.useCallback(() => {
    if (gestureHistoryStartRef.current) commitGestureHistory();
    const next = redoStackRef.current.pop();
    if (!next) return;
    undoStackRef.current.push(cloneSlideLayout(layoutRef.current));
    gestureHistoryStartRef.current = null;
    syncHistoryAvailability();
    onLayoutChangeRef.current(cloneSlideLayout(next));
  }, [commitGestureHistory, syncHistoryAvailability]);

  const patchBox = React.useCallback(
    (id: string, patch: Partial<ProgramSlideTextBoxV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
      const current = layoutRef.current;
      applyLayoutChange({
        v: 1,
        images: current.images,
        buttons: current.buttons,
        videos: current.videos,
        quizzes: current.quizzes,
        boxes: current.boxes.map((b) => (b.id === id ? { ...b, ...patch } : b)),
      }, historyMode);
    },
    [applyLayoutChange]
  );

  const applySlideTextColorFromToolbar = React.useCallback(
    (boxId: string, hex: string, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
      const root = textBlockRefs.current[boxId];
      const cur = layoutRef.current.boxes.find((b) => b.id === boxId);
      if (!cur) {
        patchBox(boxId, { colorHex: hex }, historyMode);
        return;
      }
      if (editingIdRef.current === boxId && root && typeof document !== 'undefined') {
        let start: number | null = null;
        let end: number | null = null;
        const sel = document.getSelection();
        if (sel && sel.rangeCount > 0) {
          const off = getPlainTextOffsetsFromSelection(root, sel);
          if (off && off.start !== off.end) {
            start = off.start;
            end = off.end;
          }
        }
        const saved = slideTextPlainSelectionRef.current;
        if ((start == null || start === end) && saved?.boxId === boxId && saved.start !== saved.end) {
          start = saved.start;
          end = saved.end;
        }
        if (start != null && end != null && start !== end) {
          const next = applyColorToPlainRange(cur, start, end, hex);
          patchBox(boxId, next, historyMode);
          root.innerHTML = runsToEditableHtml(next.textRuns, next.text, cur.colorHex, slideDisplayColors.fg);
          slideTextPlainSelectionRef.current = { boxId, start, end };
          return;
        }
      }
      patchBox(boxId, { colorHex: hex }, historyMode);
      if (editingIdRef.current === boxId && root) {
        root.innerHTML = runsToEditableHtml(cur.textRuns, cur.text, hex, slideDisplayColors.fg);
      }
    },
    [patchBox, slideDisplayColors.fg]
  );

  const patchImage = React.useCallback((id: string, patch: Partial<ProgramSlideImageBlockV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
    const current = layoutRef.current;
    applyLayoutChange({
      v: 1,
      boxes: current.boxes,
      buttons: current.buttons,
      videos: current.videos,
        quizzes: current.quizzes,
      images: current.images.map((img) => (img.id === id ? { ...img, ...patch } : img)),
    }, historyMode);
  }, [applyLayoutChange]);

  const patchBitmapImage = React.useCallback(
    (
      kind: SlideBitmapImageKind,
      id: string,
      patch: Partial<ProgramSlideImageBlockV1>,
      historyMode: 'auto' | 'record' | 'skip' = 'auto'
    ) => {
      const current = layoutRef.current;
      const key = bitmapImagesLayoutKey(kind);
      applyLayoutChange(
        {
          v: 1,
          boxes: current.boxes,
          images: current.images,
          buttons: current.buttons,
          videos: current.videos,
          quizzes: current.quizzes,
          [key]: getBitmapImages(current, kind).map((img) => (img.id === id ? { ...img, ...patch } : img)),
        },
        historyMode
      );
    },
    [applyLayoutChange]
  );

  const patchClipboardImage = React.useCallback(
    (id: string, patch: Partial<ProgramSlideClipboardImageBlockV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') =>
      patchBitmapImage('clipboardImage', id, patch, historyMode),
    [patchBitmapImage]
  );

  const patchScanImage = React.useCallback(
    (id: string, patch: Partial<ProgramSlideScanImageBlockV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') =>
      patchBitmapImage('scanImage', id, patch, historyMode),
    [patchBitmapImage]
  );

  const patchSlideImageByKind = React.useCallback(
    (
      id: string,
      kind: SlideImageBlockKind,
      patch: Partial<ProgramSlideImageBlockV1>,
      historyMode: 'auto' | 'record' | 'skip' = 'auto'
    ) => {
      if (isSlideBitmapImageKind(kind)) patchBitmapImage(kind, id, patch, historyMode);
      else patchImage(id, patch, historyMode);
    },
    [patchBitmapImage, patchImage]
  );

  const showClipboardImageToast = React.useCallback((msg: string) => {
    setClipboardImageToast(msg);
    window.setTimeout(() => setClipboardImageToast(''), 4200);
  }, []);

  const applyBitmapImageSrc = React.useCallback(
    (kind: SlideBitmapImageKind, id: string, src: string, naturalW: number, naturalH: number) => {
      const current = layoutRef.current;
      const block = findBitmapImage(current, kind, id);
      if (!block || !naturalW || !naturalH) return;
      patchBitmapImage(kind, id, {
        src,
        naturalW,
        naturalH,
        aspectWMultiplier: 1,
        cropUsesFrameFrac: true,
        crop: { x: 0, y: 0, w: 1, h: 1 },
      });
      setClipboardImageLoadErrors((cur) => ({ ...cur, [id]: '' }));
    },
    [patchBitmapImage]
  );

  const applyScanImageSrc = React.useCallback(
    (id: string, src: string, naturalW: number, naturalH: number) => {
      const block = findBitmapImage(layoutRef.current, 'scanImage', id);
      if (!block || !naturalW || !naturalH) return;
      patchScanImage(id, {
        src,
        naturalW,
        naturalH,
        aspectWMultiplier: 1,
        cropUsesFrameFrac: true,
        crop: { x: 0, y: 0, w: 1, h: 1 },
      });
      setClipboardImageLoadErrors((cur) => ({ ...cur, [id]: '' }));
    },
    [patchScanImage]
  );

  const applyCompressedBitmapToBlock = React.useCallback(
    async (kind: SlideBitmapImageKind, id: string, compressed: CompressedSlideImage) => {
      const current = layoutRef.current;
      const block = findBitmapImage(current, kind, id);
      if (!block) return;
      const oldSrc = block.src?.trim();
      if (oldSrc && isPendingImageSrcForCanvas(oldSrc)) {
        revokePendingImageSrcForCanvas(oldSrc);
      }
      const previewUrl = registerPendingClipboardImageForCanvas(id, compressed.blob, compressed.mimeType);
      const { naturalW, naturalH } = await probeImageNaturalSize(
        previewUrl,
        compressed.width,
        compressed.height
      );
      if (kind === 'scanImage') {
        applyScanImageSrc(id, previewUrl, naturalW, naturalH);
      } else {
        applyBitmapImageSrc(kind, id, previewUrl, naturalW, naturalH);
      }
    },
    [applyBitmapImageSrc, applyScanImageSrc]
  );

  const applyCompressedClipboardToBlock = React.useCallback(
    (id: string, compressed: CompressedSlideImage) => applyCompressedBitmapToBlock('clipboardImage', id, compressed),
    [applyCompressedBitmapToBlock]
  );

  const patchButton = React.useCallback((id: string, patch: Partial<ProgramSlideButtonBlockV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
    const current = layoutRef.current;
    applyLayoutChange({
      v: 1,
      boxes: current.boxes,
      images: current.images,
      videos: current.videos,
        quizzes: current.quizzes,
      buttons: current.buttons.map((btn) => (btn.id === id ? { ...btn, ...patch } : btn)),
    }, historyMode);
  }, [applyLayoutChange]);

  const patchShape = React.useCallback((id: string, patch: Partial<ProgramSlideShapeBlockV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
    const current = layoutRef.current;
    applyLayoutChange(
      {
        v: 1,
        boxes: current.boxes,
        images: current.images,
        buttons: current.buttons,
        shapes: (current.shapes ?? []).map((s) => (s.id === id ? { ...s, ...patch } : s)),
        videos: current.videos,
        quizzes: current.quizzes,
      },
      historyMode
    );
  }, [applyLayoutChange]);

  const patchTable = React.useCallback(
    (id: string, patch: Partial<ProgramSlideTableBlockV1> | ProgramSlideTableBlockV1, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
      const current = layoutRef.current;
      applyLayoutChange(
        {
          v: 1,
          boxes: current.boxes,
          images: current.images,
          buttons: current.buttons,
          shapes: current.shapes ?? [],
          polylines: current.polylines ?? [],
          tables: (current.tables ?? []).map((tbl) => {
            if (tbl.id !== id) return tbl;
            const merged = { ...tbl, ...patch };
            return 'cells' in patch || 'coveredBy' in patch || 'rowCount' in patch || 'colCount' in patch
              ? normalizeTableGrid(merged as ProgramSlideTableBlockV1)
              : merged;
          }),
          videos: current.videos,
          quizzes: current.quizzes,
        },
        historyMode
      );
    },
    [applyLayoutChange]
  );

  const replaceTable = React.useCallback(
    (table: ProgramSlideTableBlockV1, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
      patchTable(table.id, normalizeTableGrid(table), historyMode);
    },
    [patchTable]
  );

  /** Ctrl/Cmd+드래그용: 원본은 두고 같은 위치에 복제본을 만든 뒤 복제본 id를 반환 */
  const duplicateBlockInPlaceForDrag = React.useCallback(
    (kind: SlideSelectionKind, id: string): { kind: SlideSelectionKind; id: string } | null => {
      const cur = layoutRef.current;
      const newId = genSlideBoxId(Date.now());
      const z = maxSlideLayoutZIndex(cur) + 1;
      const base = {
        v: 1 as const,
        boxes: cur.boxes,
        images: cur.images,
        buttons: cur.buttons,
        videos: cur.videos,
        quizzes: cur.quizzes,
      };
      if (kind === 'text') {
        const src = cur.boxes.find((b) => b.id === id);
        if (!src) return null;
        const { groupId: _g, locked: _l, ...rest } = src;
        applyLayoutChange(
          { ...base, boxes: [...cur.boxes, { ...rest, id: newId, zIndex: z }] },
          'skip'
        );
        return { kind: 'text', id: newId };
      }
      if (kind === 'shape') {
        const src = (cur.shapes ?? []).find((s) => s.id === id);
        if (!src) return null;
        const { groupId: _g, locked: _l, ...rest } = src;
        applyLayoutChange(
          { ...base, shapes: [...(cur.shapes ?? []), { ...rest, id: newId, zIndex: z }] },
          'skip'
        );
        return { kind: 'shape', id: newId };
      }
      if (kind === 'table') {
        const src = (cur.tables ?? []).find((t) => t.id === id);
        if (!src) return null;
        const nextTable = cloneSlideTableBlockWithNewId(src, newId, z);
        applyLayoutChange({ ...base, tables: [...(cur.tables ?? []), nextTable] }, 'skip');
        return { kind: 'table', id: newId };
      }
      if (kind === 'image' || kind === 'clipboardImage' || kind === 'scanImage') {
        const src = findSlideImageBlock(cur, kind, id);
        if (!src) return null;
        const { groupId: _g, locked: _l, ...rest } = src;
        const next: ProgramSlideImageBlockV1 = {
          ...rest,
          id: newId,
          zIndex: z,
          crop: { ...src.crop },
        };
        if (kind === 'image') {
          applyLayoutChange({ ...base, images: [...cur.images, next] }, 'skip');
        } else if (kind === 'clipboardImage') {
          applyLayoutChange(
            { ...base, clipboardImages: [...(cur.clipboardImages ?? []), next] },
            'skip'
          );
        } else {
          applyLayoutChange({ ...base, scanImages: [...(cur.scanImages ?? []), next] }, 'skip');
        }
        return { kind, id: newId };
      }
      if (kind === 'polyline') {
        const src = (cur.polylines ?? []).find((p) => p.id === id);
        if (!src) return null;
        const { groupId: _g, locked: _l, ...rest } = src;
        applyLayoutChange(
          {
            ...base,
            polylines: [
              ...(cur.polylines ?? []),
              {
                ...rest,
                id: newId,
                zIndex: z,
                points: src.points.map((p) => ({ x: p.x, y: p.y })),
              },
            ],
          },
          'skip'
        );
        return { kind: 'polyline', id: newId };
      }
      return null;
    },
    [applyLayoutChange]
  );

  const toggleBlockLocked = React.useCallback(
    (kind: SlideSelectionKind, id: string) => {
      if (!canEdit) return;
      const cur = layoutRef.current;
      if (kind === 'text') {
        const src = cur.boxes.find((b) => b.id === id);
        if (!src) return;
        patchBox(id, { locked: src.locked ? undefined : true }, 'record');
        return;
      }
      if (kind === 'shape') {
        const src = (cur.shapes ?? []).find((s) => s.id === id);
        if (!src) return;
        patchShape(id, { locked: src.locked ? undefined : true }, 'record');
        return;
      }
      if (kind === 'table') {
        const src = (cur.tables ?? []).find((t) => t.id === id);
        if (!src) return;
        patchTable(id, { locked: src.locked ? undefined : true }, 'record');
        return;
      }
      if (kind === 'image' || kind === 'clipboardImage' || kind === 'scanImage') {
        const src = findSlideImageBlock(cur, kind, id);
        if (!src) return;
        patchSlideImageByKind(id, kind, { locked: src.locked ? undefined : true }, 'record');
        return;
      }
      if (kind === 'polyline') {
        const src = (cur.polylines ?? []).find((p) => p.id === id);
        if (!src) return;
        applyLayoutChange(
          {
            v: 1,
            boxes: cur.boxes,
            images: cur.images,
            buttons: cur.buttons,
            videos: cur.videos,
            quizzes: cur.quizzes,
            polylines: (cur.polylines ?? []).map((p) =>
              p.id === id ? { ...p, locked: src.locked ? undefined : true } : p
            ),
          },
          'record'
        );
      }
    },
    [applyLayoutChange, canEdit, patchBox, patchShape, patchSlideImageByKind, patchTable]
  );

  const patchPolyline = React.useCallback(
    (id: string, patch: Partial<ProgramSlidePolylineBlockV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
      const current = layoutRef.current;
      applyLayoutChange(
        {
          v: 1,
          boxes: current.boxes,
          images: current.images,
          buttons: current.buttons,
          shapes: current.shapes ?? [],
          polylines: (current.polylines ?? []).map((p) => (p.id === id ? { ...p, ...patch } : p)),
          videos: current.videos,
          quizzes: current.quizzes,
        },
        historyMode
      );
    },
    [applyLayoutChange]
  );

  /**
   * 미니 툴바 조작을 대표 개체 + 같은 종류로 함께 선택된 개체에 적용한다(실행 취소 1단계).
   * 잠긴 개체는 includeLocked 가 아니면 건너뛴다.
   */
  const forToolbarTargets = React.useCallback(
    (
      kind: SlideSelectionKind,
      primaryId: string,
      fn: (id: string) => void,
      opts?: { includeLocked?: boolean }
    ) => {
      const batch = toolbarBatchTargetsRef.current;
      if (!batch || batch.kind !== kind || !batch.ids.includes(primaryId)) {
        fn(primaryId);
        return;
      }
      const layoutNow = layoutRef.current;
      const ids = [primaryId, ...batch.ids.filter((id) => id !== primaryId)].filter(
        (id) => opts?.includeLocked || id === primaryId || !isSlideBlockLockedInLayout(layoutNow, kind, id)
      );
      const ownsGesture = !gestureHistoryStartRef.current;
      if (ownsGesture) beginGestureHistory();
      try {
        for (const id of ids) fn(id);
      } finally {
        if (ownsGesture) commitGestureHistory();
      }
    },
    [beginGestureHistory, commitGestureHistory]
  );

  const toggleLockTargets = React.useCallback(
    (kind: SlideSelectionKind, primaryId: string) => {
      const batch = toolbarBatchTargetsRef.current;
      if (!batch || batch.kind !== kind) {
        toggleBlockLocked(kind, primaryId);
        return;
      }
      const nextLocked = !isSlideBlockLockedInLayout(layoutRef.current, kind, primaryId);
      const stackBefore = undoStackRef.current.length;
      const start = cloneSlideLayout(layoutRef.current);
      forToolbarTargets(
        kind,
        primaryId,
        (id) => {
          if (isSlideBlockLockedInLayout(layoutRef.current, kind, id) !== nextLocked) toggleBlockLocked(kind, id);
        },
        { includeLocked: true }
      );
      if (undoStackRef.current.length > stackBefore + 1) {
        undoStackRef.current.length = stackBefore;
        pushUndoSnapshot(start);
      }
    },
    [forToolbarTargets, toggleBlockLocked, pushUndoSnapshot]
  );

  const patchVideo = React.useCallback((id: string, patch: Partial<ProgramSlideVideoBlockV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
    const current = layoutRef.current;
    applyLayoutChange({
      v: 1,
      boxes: current.boxes,
      images: current.images,
      buttons: current.buttons,
      quizzes: current.quizzes,
      videos: current.videos.map((video) => (video.id === id ? { ...video, ...patch } : video)),
    }, historyMode);
  }, [applyLayoutChange]);

  const patchWebpage = React.useCallback(
    (id: string, patch: Partial<ProgramSlideWebpageBlockV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
      const current = layoutRef.current;
      applyLayoutChange(
        {
          v: 1,
          boxes: current.boxes,
          images: current.images,
          buttons: current.buttons,
          videos: current.videos,
          quizzes: current.quizzes,
          webpages: (current.webpages ?? []).map((wp) => (wp.id === id ? { ...wp, ...patch } : wp)),
        },
        historyMode
      );
    },
    [applyLayoutChange]
  );

  const patchQuiz = React.useCallback((id: string, patch: Partial<ProgramSlideQuizShortAnswerV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
    const current = layoutRef.current;
    applyLayoutChange({
      v: 1,
      boxes: current.boxes,
      images: current.images,
      buttons: current.buttons,
      videos: current.videos,
      quizzes: current.quizzes.map((quiz) => (quiz.id === id ? { ...quiz, ...patch } : quiz)),
    }, historyMode);
  }, [applyLayoutChange]);

  const patchQuizChoiceOption = React.useCallback(
    (id: string, patch: Partial<ProgramSlideQuizChoiceOptionV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
      const current = layoutRef.current;
      applyLayoutChange({
        v: 1,
        boxes: current.boxes,
        images: current.images,
        buttons: current.buttons,
        videos: current.videos,
        quizzes: current.quizzes,
        quizChoiceSets: current.quizChoiceSets,
        quizChoiceOptions: current.quizChoiceOptions.map((opt) => (opt.id === id ? { ...opt, ...patch } : opt)),
      }, historyMode);
    },
    [applyLayoutChange]
  );

  const patchQuizChoiceSetOptionGeometry = React.useCallback(
    (
      setId: string,
      draggedOptionId: string,
      geom: { x: number; y: number; w: number; h: number },
      corner: 'nw' | 'ne' | 'sw' | 'se',
      historyMode: 'auto' | 'record' | 'skip' = 'skip'
    ) => {
      const current = layoutRef.current;
      applyLayoutChange(
        {
          v: 1,
          boxes: current.boxes,
          images: current.images,
          buttons: current.buttons,
          videos: current.videos,
          quizzes: current.quizzes,
          quizChoiceSets: current.quizChoiceSets,
          quizChoiceOptions: current.quizChoiceOptions.map((opt) => {
            if (opt.setId !== setId) return opt;
            if (opt.id === draggedOptionId) {
              return { ...opt, x: geom.x, y: geom.y, w: geom.w, h: geom.h };
            }
            return { ...opt, ...quizChoiceResizeWithSyncedSize(opt, geom.w, geom.h, corner) };
          }),
        },
        historyMode
      );
    },
    [applyLayoutChange]
  );

  const patchQuizChoiceSetRadius = React.useCallback(
    (setId: string, cornerRadiusPx: number, historyMode: 'auto' | 'record' | 'skip' = 'skip') => {
      const current = layoutRef.current;
      applyLayoutChange(
        {
          v: 1,
          boxes: current.boxes,
          images: current.images,
          buttons: current.buttons,
          videos: current.videos,
          quizzes: current.quizzes,
          quizChoiceSets: current.quizChoiceSets,
          quizChoiceOptions: current.quizChoiceOptions.map((opt) =>
            opt.setId === setId ? { ...opt, cornerRadiusPx } : opt
          ),
        },
        historyMode
      );
    },
    [applyLayoutChange]
  );

  const patchQuizChoiceSetFromCenter = React.useCallback(
    (
      setId: string,
      newW: number,
      newH: number,
      setBBox: { left: number; top: number; right: number; bottom: number },
      startW: number,
      startH: number,
      historyMode: 'auto' | 'record' | 'skip' = 'skip'
    ) => {
      const current = layoutRef.current;
      applyLayoutChange(
        {
          v: 1,
          boxes: current.boxes,
          images: current.images,
          buttons: current.buttons,
          videos: current.videos,
          quizzes: current.quizzes,
          quizChoiceSets: current.quizChoiceSets,
          quizChoiceOptions: quizChoiceResizeSetFromCenter(
            current.quizChoiceOptions,
            setId,
            setBBox,
            newW,
            newH,
            startW,
            startH
          ),
        },
        historyMode
      );
    },
    [applyLayoutChange]
  );

  const patchQuizChoiceSet = React.useCallback(
    (setId: string, patch: Partial<{ correctOptionIndices: number[]; zIndex: number }>, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
      const current = layoutRef.current;
      applyLayoutChange({
        v: 1,
        boxes: current.boxes,
        images: current.images,
        buttons: current.buttons,
        videos: current.videos,
        quizzes: current.quizzes,
        quizChoiceSets: current.quizChoiceSets.map((set) => (set.id === setId ? { ...set, ...patch } : set)),
        quizChoiceOptions: current.quizChoiceOptions,
      }, historyMode);
    },
    [applyLayoutChange]
  );

  const deleteQuizChoiceSet = React.useCallback(
    (setId: string) => {
      const current = layoutRef.current;
      applyLayoutChange({
        v: 1,
        boxes: current.boxes,
        images: current.images,
        buttons: current.buttons,
        videos: current.videos,
        quizzes: current.quizzes,
        quizChoiceSets: current.quizChoiceSets.filter((set) => set.id !== setId),
        quizChoiceOptions: current.quizChoiceOptions.filter((opt) => opt.setId !== setId),
      });
      applySelectionState(
        selectionRef.current.selectedItems.filter((item) => {
          if (item.kind !== 'quizChoice') return true;
          const opt = current.quizChoiceOptions.find((o) => o.id === item.id);
          return opt?.setId !== setId;
        }),
        null
      );
      setChoicePickedBySetId((cur) => {
        const next = { ...cur };
        delete next[setId];
        return next;
      });
      setChoiceSucceededSetIds((cur) => {
        const next = new Set(cur);
        next.delete(setId);
        return next;
      });
    },
    [applyLayoutChange, applySelectionState]
  );

  const getNextZIndex = React.useCallback(() => {
    const current = layoutRef.current;
    const zs = [
      ...current.boxes.map((b) => b.zIndex),
      ...current.images.map((img) => img.zIndex),
      ...(current.clipboardImages ?? []).map((img) => img.zIndex),
      ...(current.scanImages ?? []).map((img) => img.zIndex),
      ...current.buttons.map((btn) => btn.zIndex),
      ...(current.shapes ?? []).map((s) => s.zIndex),
      ...(current.tables ?? []).map((tbl) => tbl.zIndex),
      ...current.videos.map((video) => video.zIndex),
      ...(current.webpages ?? []).map((wp) => wp.zIndex),
      ...current.quizzes.map((quiz) => quiz.zIndex),
      ...current.quizChoiceOptions.map((opt) => opt.zIndex),
    ];
    return zs.length ? Math.max(...zs) + 1 : 0;
  }, []);

  const insertBitmapImageFromBlob = React.useCallback(
    async (kind: SlideBitmapImageKind, file: Blob, targetId: string | null) => {
      let blockId = targetId?.trim() || null;
      const cur = layoutRef.current;
      if (blockId && !findBitmapImage(cur, kind, blockId)) {
        blockId = null;
      }
      if (blockId) {
        setClipboardImageUploading((u) => ({ ...u, [blockId!]: true }));
        setClipboardImageLoadErrors((u) => ({ ...u, [blockId!]: '' }));
      }
      try {
        const compressed = await compressClipboardImage(file);
        if (!blockId) {
          const id = genSlideBoxId(
            cur.boxes.length +
              cur.images.length +
              (cur.clipboardImages ?? []).length +
              (cur.scanImages ?? []).length +
              cur.buttons.length +
              cur.videos.length
          );
          const previewUrl = registerPendingClipboardImageForCanvas(id, compressed.blob, compressed.mimeType);
          const { naturalW, naturalH } = await probeImageNaturalSize(
            previewUrl,
            compressed.width,
            compressed.height
          );
          const root = rootRef.current;
          const rect = root?.getBoundingClientRect();
          const rootW = Math.max(1, rect?.width ?? 1280);
          const rootH = Math.max(1, rect?.height ?? 720);
          const frameW = 0.34;
          let frameX: number;
          let frameY: number;
          let frameH: number;
          if (kind === 'scanImage') {
            frameX = 0.16;
            frameY = 0.16;
            frameH = normFrameHeightForAspect(frameW, frameY, SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH);
          } else {
            const peers = collectInsertPeerRects(cur, 'clipboardImage');
            const probeY = 0.5 - normFrameHeightForAspect(frameW, 0.5, SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH) / 2;
            const probeH = normFrameHeightForAspect(frameW, Math.max(0, Math.min(1, probeY)), SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH);
            const pos = resolveInsertTopLeftAvoidPeerRects(frameW, probeH, peers, 0.5, 0.5);
            frameX = pos.x;
            frameY = pos.y;
            frameH = normFrameHeightForAspect(frameW, frameY, SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH);
          }
          const frameDims = { w: frameW, h: frameH };
          const nb: ProgramSlideImageBlockV1 = {
            id,
            x: frameX,
            y: frameY,
            w: frameDims.w,
            h: frameDims.h,
            src: previewUrl,
            naturalW,
            naturalH,
            aspectWMultiplier: 1,
            cropUsesFrameFrac: true,
            crop: { x: 0, y: 0, w: 1, h: 1 },
            zIndex: getNextZIndex(),
          };
          const key = bitmapImagesLayoutKey(kind);
          applyLayoutChange({
            v: 1,
            boxes: cur.boxes,
            images: cur.images,
            [key]: [...getBitmapImages(cur, kind), nb],
            buttons: cur.buttons,
            videos: cur.videos,
            quizzes: cur.quizzes,
          });
          selectSingleBlock(kind, id);
          setClipboardImageLoadErrors((u) => ({ ...u, [id]: '' }));
          return;
        }
        await applyCompressedBitmapToBlock(kind, blockId, compressed);
      } catch (e) {
        const msg = e instanceof Error ? e.message : '이미지 처리에 실패했습니다.';
        if (msg === 'QUOTA_EXCEEDED') {
          showClipboardImageToast('이미지 저장 용량을 초과했습니다. 쓰지 않는 이미지를 지운 뒤 다시 시도해 주세요.');
        } else if (blockId) {
          const errId = blockId;
          setClipboardImageLoadErrors((u) => ({ ...u, [errId]: msg }));
        } else {
          showClipboardImageToast(msg);
        }
      } finally {
        if (blockId) {
          const doneId = blockId;
          setClipboardImageUploading((u) => {
            const next = { ...u };
            delete next[doneId];
            return next;
          });
        }
      }
    },
    [
      applyCompressedBitmapToBlock,
      applyLayoutChange,
      getNextZIndex,
      selectSingleBlock,
      showClipboardImageToast,
    ]
  );

  const insertClipboardImageFromBlob = React.useCallback(
    (file: Blob, targetId: string | null) => insertBitmapImageFromBlob('clipboardImage', file, targetId),
    [insertBitmapImageFromBlob]
  );

  const handleClipboardImagePaste = React.useCallback(
    async (kind: SlideBitmapImageKind, targetId: string | null, clipboardData: DataTransfer | null) => {
      const blob = readClipboardImageBlob(clipboardData);
      if (!blob) {
        showClipboardImageToast('클립보드에 이미지가 없습니다.');
        return;
      }
      await insertBitmapImageFromBlob(kind, blob, targetId);
    },
    [insertBitmapImageFromBlob, showClipboardImageToast]
  );

  const pasteClipboardImageToBlock = React.useCallback(
    async (targetId: string) => {
      const blob = await readClipboardImageBlobFromApi();
      if (!blob) {
        showClipboardImageToast('클립보드에 이미지가 없습니다.');
        return;
      }
      await insertClipboardImageFromBlob(blob, targetId);
    },
    [insertClipboardImageFromBlob, showClipboardImageToast]
  );

  const ensureCanvasPasteFocus = React.useCallback(() => {
    rootRef.current?.focus({ preventScroll: true });
  }, []);

  const applyObjectAlignment = React.useCallback(
    (action: ObjectAlignActionId) => {
      const current = layoutRef.current;
      const selected = getEffectiveSelectedRefs();
      if (selected.length < 1) return;
      const lookup = new Map<
        string,
        { kind: SlideSelectionKind; id: string; x: number; y: number; w: number; h: number }
      >();
      current.boxes.forEach((box) => lookup.set(slideSelectionKey('text', box.id), { kind: 'text', id: box.id, x: box.x, y: box.y, w: box.w, h: box.h }));
      current.images.forEach((image) =>
        lookup.set(slideSelectionKey('image', image.id), { kind: 'image', id: image.id, x: image.x, y: image.y, w: image.w, h: image.h })
      );
      (current.clipboardImages ?? []).forEach((image) =>
        lookup.set(slideSelectionKey('clipboardImage', image.id), {
          kind: 'clipboardImage',
          id: image.id,
          x: image.x,
          y: image.y,
          w: image.w,
          h: image.h,
        })
      );
      (current.scanImages ?? []).forEach((image) =>
        lookup.set(slideSelectionKey('scanImage', image.id), {
          kind: 'scanImage',
          id: image.id,
          x: image.x,
          y: image.y,
          w: image.w,
          h: image.h,
        })
      );
      current.buttons.forEach((button) =>
        lookup.set(slideSelectionKey('button', button.id), { kind: 'button', id: button.id, x: button.x, y: button.y, w: button.w, h: button.h })
      );
      (current.shapes ?? []).forEach((shape) =>
        lookup.set(slideSelectionKey('shape', shape.id), { kind: 'shape', id: shape.id, x: shape.x, y: shape.y, w: shape.w, h: shape.h })
      );
      (current.polylines ?? []).forEach((pl) => {
        const b = polylineBBox(pl.points);
        if (b) lookup.set(slideSelectionKey('polyline', pl.id), { kind: 'polyline', id: pl.id, x: b.x, y: b.y, w: b.w, h: b.h });
      });
      current.videos.forEach((video) =>
        lookup.set(slideSelectionKey('video', video.id), { kind: 'video', id: video.id, x: video.x, y: video.y, w: video.w, h: video.h })
      );
      (current.webpages ?? []).forEach((wp) =>
        lookup.set(slideSelectionKey('webpage', wp.id), { kind: 'webpage', id: wp.id, x: wp.x, y: wp.y, w: wp.w, h: wp.h })
      );
      current.quizzes.forEach((quiz) =>
        lookup.set(slideSelectionKey('quiz', quiz.id), { kind: 'quiz', id: quiz.id, x: quiz.x, y: quiz.y, w: quiz.w, h: quiz.h })
      );
      current.quizChoiceOptions.forEach((opt) =>
        lookup.set(slideSelectionKey('quizChoice', opt.id), {
          kind: 'quizChoice',
          id: opt.id,
          x: opt.x,
          y: opt.y,
          w: opt.w,
          h: opt.h,
        })
      );
      const targets = selected
        .map((item) => lookup.get(slideSelectionRefKey(item)))
        .filter((item): item is NonNullable<typeof item> => !!item);
      const minRequired = action === 'distribute-x' || action === 'distribute-y' ? 3 : 1;
      if (targets.length < minRequired) return;

      const referenceBounds =
        targets.length === 1
          ? { left: 0, top: 0, right: 1, bottom: 1 }
          : {
              left: Math.min(...targets.map((item) => item.x)),
              top: Math.min(...targets.map((item) => item.y)),
              right: Math.max(...targets.map((item) => item.x + item.w)),
              bottom: Math.max(...targets.map((item) => item.y + item.h)),
            };
      const { left, top, right, bottom } = referenceBounds;
      const nextPositions = new Map<string, { x?: number; y?: number }>();
      const clampX = (x: number, w: number) => frac4(Math.min(1 - w, Math.max(0, x)));
      const clampY = (y: number, h: number) => frac4(Math.min(1 - h, Math.max(0, y)));

      if (action === 'align-left' || action === 'align-center-x' || action === 'align-right') {
        targets.forEach((item) => {
          const nextX =
            action === 'align-left' ? left : action === 'align-center-x' ? (left + right - item.w) / 2 : right - item.w;
          nextPositions.set(slideSelectionKey(item.kind, item.id), { x: clampX(nextX, item.w) });
        });
      } else if (action === 'align-top' || action === 'align-center-y' || action === 'align-bottom') {
        targets.forEach((item) => {
          const nextY =
            action === 'align-top' ? top : action === 'align-center-y' ? (top + bottom - item.h) / 2 : bottom - item.h;
          nextPositions.set(slideSelectionKey(item.kind, item.id), { y: clampY(nextY, item.h) });
        });
      } else if (action === 'distribute-x') {
        const ordered = [...targets].sort((a, b) => a.x - b.x || a.y - b.y);
        const totalWidth = ordered.reduce((sum, item) => sum + item.w, 0);
        const gap = ordered.length > 1 ? (right - left - totalWidth) / (ordered.length - 1) : 0;
        let cursor = left;
        ordered.forEach((item) => {
          nextPositions.set(slideSelectionKey(item.kind, item.id), { x: clampX(cursor, item.w) });
          cursor += item.w + gap;
        });
      } else {
        const ordered = [...targets].sort((a, b) => a.y - b.y || a.x - b.x);
        const totalHeight = ordered.reduce((sum, item) => sum + item.h, 0);
        const gap = ordered.length > 1 ? (bottom - top - totalHeight) / (ordered.length - 1) : 0;
        let cursor = top;
        ordered.forEach((item) => {
          nextPositions.set(slideSelectionKey(item.kind, item.id), { y: clampY(cursor, item.h) });
          cursor += item.h + gap;
        });
      }

      applyLayoutChange({
        v: 1,
        boxes: current.boxes.map((box) => {
          const next = nextPositions.get(slideSelectionKey('text', box.id));
          if (!next) return box;
          return { ...box, ...(typeof next.x === 'number' ? { x: next.x } : {}), ...(typeof next.y === 'number' ? { y: next.y } : {}) };
        }),
        images: current.images.map((image) => {
          const next = nextPositions.get(slideSelectionKey('image', image.id));
          if (!next) return image;
          return {
            ...image,
            ...(typeof next.x === 'number' ? { x: next.x } : {}),
            ...(typeof next.y === 'number' ? { y: next.y } : {}),
          };
        }),
        clipboardImages: (current.clipboardImages ?? []).map((image) => {
          const next = nextPositions.get(slideSelectionKey('clipboardImage', image.id));
          if (!next) return image;
          return {
            ...image,
            ...(typeof next.x === 'number' ? { x: next.x } : {}),
            ...(typeof next.y === 'number' ? { y: next.y } : {}),
          };
        }),
        scanImages: (current.scanImages ?? []).map((image) => {
          const next = nextPositions.get(slideSelectionKey('scanImage', image.id));
          if (!next) return image;
          return {
            ...image,
            ...(typeof next.x === 'number' ? { x: next.x } : {}),
            ...(typeof next.y === 'number' ? { y: next.y } : {}),
          };
        }),
        buttons: current.buttons.map((button) => {
          const next = nextPositions.get(slideSelectionKey('button', button.id));
          if (!next) return button;
          return {
            ...button,
            ...(typeof next.x === 'number' ? { x: next.x } : {}),
            ...(typeof next.y === 'number' ? { y: next.y } : {}),
          };
        }),
        shapes: (current.shapes ?? []).map((shape) => {
          const next = nextPositions.get(slideSelectionKey('shape', shape.id));
          if (!next) return shape;
          return {
            ...shape,
            ...(typeof next.x === 'number' ? { x: next.x } : {}),
            ...(typeof next.y === 'number' ? { y: next.y } : {}),
          };
        }),
        polylines: (current.polylines ?? []).map((pl) => {
          const key = slideSelectionKey('polyline', pl.id);
          const next = nextPositions.get(key);
          const oldB = lookup.get(key);
          if (!next || !oldB) return pl;
          const deltaX = typeof next.x === 'number' ? next.x - oldB.x : 0;
          const deltaY = typeof next.y === 'number' ? next.y - oldB.y : 0;
          if (!deltaX && !deltaY) return pl;
          return {
            ...pl,
            points: pl.points.map((p) => ({ x: frac4(p.x + deltaX), y: frac4(p.y + deltaY) })),
          };
        }),
        videos: current.videos.map((video) => {
          const next = nextPositions.get(slideSelectionKey('video', video.id));
          if (!next) return video;
          return {
            ...video,
            ...(typeof next.x === 'number' ? { x: next.x } : {}),
            ...(typeof next.y === 'number' ? { y: next.y } : {}),
          };
        }),
        quizzes: current.quizzes.map((quiz) => {
          const next = nextPositions.get(slideSelectionKey('quiz', quiz.id));
          if (!next) return quiz;
          return {
            ...quiz,
            ...(typeof next.x === 'number' ? { x: next.x } : {}),
            ...(typeof next.y === 'number' ? { y: next.y } : {}),
          };
        }),
        quizChoiceOptions: current.quizChoiceOptions.map((opt) => {
          const next = nextPositions.get(slideSelectionKey('quizChoice', opt.id));
          if (!next) return opt;
          return {
            ...opt,
            ...(typeof next.x === 'number' ? { x: next.x } : {}),
            ...(typeof next.y === 'number' ? { y: next.y } : {}),
          };
        }),
      });
    },
    [applyLayoutChange, getEffectiveSelectedRefs]
  );

  const applyObjectStackOrder = React.useCallback(
    (direction: 'front' | 'back') => {
      const current = layoutRef.current;
      const selected = getEffectiveSelectedRefs();
      if (selected.length < 1) return;
      const selectedKeySet = new Set(selected.map((item) => slideSelectionRefKey(item)));
      const allItems = [
        ...current.boxes.map((box) => ({ kind: 'text' as const, id: box.id, zIndex: box.zIndex })),
        ...current.images.map((image) => ({ kind: 'image' as const, id: image.id, zIndex: image.zIndex })),
        ...(current.clipboardImages ?? []).map((image) => ({
          kind: 'clipboardImage' as const,
          id: image.id,
          zIndex: image.zIndex,
        })),
        ...(current.scanImages ?? []).map((image) => ({
          kind: 'scanImage' as const,
          id: image.id,
          zIndex: image.zIndex,
        })),
        ...current.buttons.map((button) => ({ kind: 'button' as const, id: button.id, zIndex: button.zIndex })),
        ...(current.shapes ?? []).map((shape) => ({ kind: 'shape' as const, id: shape.id, zIndex: shape.zIndex })),
        ...(current.polylines ?? []).map((pl) => ({ kind: 'polyline' as const, id: pl.id, zIndex: pl.zIndex })),
        ...current.videos.map((video) => ({ kind: 'video' as const, id: video.id, zIndex: video.zIndex })),
        ...(current.webpages ?? []).map((wp) => ({ kind: 'webpage' as const, id: wp.id, zIndex: wp.zIndex })),
        ...current.quizzes.map((quiz) => ({ kind: 'quiz' as const, id: quiz.id, zIndex: quiz.zIndex })),
      ].sort((a, b) => a.zIndex - b.zIndex);
      const selectedItemsOrdered = allItems.filter((item) => selectedKeySet.has(slideSelectionKey(item.kind, item.id)));
      if (!selectedItemsOrdered.length) return;
      const nonSelectedItems = allItems.filter((item) => !selectedKeySet.has(slideSelectionKey(item.kind, item.id)));
      const nextOrderedItems =
        direction === 'front'
          ? [...nonSelectedItems, ...selectedItemsOrdered]
          : [...selectedItemsOrdered, ...nonSelectedItems];
      const nextZIndexByKey = new Map(
        nextOrderedItems.map((item, index) => [slideSelectionKey(item.kind, item.id), index])
      );

      applyLayoutChange({
        v: 1,
        boxes: current.boxes.map((box) => ({
          ...box,
          zIndex: nextZIndexByKey.get(slideSelectionKey('text', box.id)) ?? box.zIndex,
        })),
        images: current.images.map((image) => ({
          ...image,
          zIndex: nextZIndexByKey.get(slideSelectionKey('image', image.id)) ?? image.zIndex,
        })),
        clipboardImages: (current.clipboardImages ?? []).map((image) => ({
          ...image,
          zIndex: nextZIndexByKey.get(slideSelectionKey('clipboardImage', image.id)) ?? image.zIndex,
        })),
        scanImages: (current.scanImages ?? []).map((image) => ({
          ...image,
          zIndex: nextZIndexByKey.get(slideSelectionKey('scanImage', image.id)) ?? image.zIndex,
        })),
        buttons: current.buttons.map((button) => ({
          ...button,
          zIndex: nextZIndexByKey.get(slideSelectionKey('button', button.id)) ?? button.zIndex,
        })),
        shapes: (current.shapes ?? []).map((shape) => ({
          ...shape,
          zIndex: nextZIndexByKey.get(slideSelectionKey('shape', shape.id)) ?? shape.zIndex,
        })),
        polylines: (current.polylines ?? []).map((pl) => ({
          ...pl,
          zIndex: nextZIndexByKey.get(slideSelectionKey('polyline', pl.id)) ?? pl.zIndex,
        })),
        videos: current.videos.map((video) => ({
          ...video,
          zIndex: nextZIndexByKey.get(slideSelectionKey('video', video.id)) ?? video.zIndex,
        })),
        webpages: (current.webpages ?? []).map((wp) => ({
          ...wp,
          zIndex: nextZIndexByKey.get(slideSelectionKey('webpage', wp.id)) ?? wp.zIndex,
        })),
        quizzes: current.quizzes.map((quiz) => ({
          ...quiz,
          zIndex: nextZIndexByKey.get(slideSelectionKey('quiz', quiz.id)) ?? quiz.zIndex,
        })),
      });
    },
    [applyLayoutChange, getEffectiveSelectedRefs]
  );

  const applyPastedBlocksFromParsed = React.useCallback(
    (parsedList: ParsedSlideClipboard[]) => {
      if (!parsedList.length) return;
      const cur = layoutRef.current;
      const rects = parsedList.map(boundingRectFromParsed).filter((r) => r.w > 0 && r.h > 0);
      const nd = computePasteNudge(cur, rects);

      const maxZ = Math.max(
        0,
        ...cur.boxes.map((b) => b.zIndex),
        ...cur.images.map((i) => i.zIndex),
        ...(cur.clipboardImages ?? []).map((i) => i.zIndex),
        ...(cur.scanImages ?? []).map((i) => i.zIndex),
        ...cur.buttons.map((b) => b.zIndex),
        ...(cur.shapes ?? []).map((s) => s.zIndex),
        ...(cur.polylines ?? []).map((p) => p.zIndex),
        ...cur.videos.map((v) => v.zIndex),
        ...(cur.webpages ?? []).map((w) => w.zIndex),
        ...(cur.tables ?? []).map((t) => t.zIndex),
        ...cur.quizzes.map((q) => q.zIndex),
        ...cur.quizChoiceOptions.map((o) => o.zIndex)
      );
      const baseZ = maxZ + 1;

      let boxes = [...cur.boxes];
      let images = [...cur.images];
      let clipboardImages = [...(cur.clipboardImages ?? [])];
      let scanImages = [...(cur.scanImages ?? [])];
      let buttons = [...cur.buttons];
      let shapes = [...(cur.shapes ?? [])];
      let polylines = [...(cur.polylines ?? [])];
      let tables = [...(cur.tables ?? [])];
      let videos = [...cur.videos];
      let webpages = [...(cur.webpages ?? [])];
      let quizzes = [...cur.quizzes];
      let quizChoiceSets = [...cur.quizChoiceSets];
      let quizChoiceOptions = [...cur.quizChoiceOptions];

      const newSelection: SelectedSlideBlockRef[] = [];
      let zi = 0;

      for (const parsed of parsedList) {
        const z = baseZ + zi;
        zi += 1;
        const newId = genSlideBoxId(zi * 7919 + parsedList.length * 13);

        if (parsed.kind === 'text') {
          const base = shiftXYWH(omitSlideBlockGroupId(parsed.box), nd, nd);
          boxes = [...boxes, { ...base, id: newId, zIndex: z }];
          newSelection.push({ kind: 'text', id: newId });
          continue;
        }
        if (parsed.kind === 'image') {
          const srcImg = omitSlideBlockGroupId(parsed.image);
          const base = shiftXYWH(srcImg, nd, nd);
          const next: ProgramSlideImageBlockV1 = {
            ...base,
            id: newId,
            zIndex: z,
            crop: { ...srcImg.crop },
          };
          images = [...images, next];
          newSelection.push({ kind: 'image', id: newId });
          continue;
        }
        if (parsed.kind === 'clipboardImage' || parsed.kind === 'scanImage') {
          const bitmapKind = parsed.kind;
          const srcImg = omitSlideBlockGroupId(parsed.image);
          const base = shiftXYWH(srcImg, nd, nd);
          let src = srcImg.src?.trim() ?? '';
          if (isPendingImageSrcForCanvas(src)) {
            const cloned = clonePendingClipboardImageForCanvas(src, newId);
            src = cloned ?? '';
          }
          const next: ProgramSlideImageBlockV1 = {
            ...base,
            id: newId,
            zIndex: z,
            src,
            crop: { ...srcImg.crop },
          };
          if (bitmapKind === 'clipboardImage') clipboardImages = [...clipboardImages, next];
          else scanImages = [...scanImages, next];
          newSelection.push({ kind: bitmapKind, id: newId });
          continue;
        }
        if (parsed.kind === 'video') {
          const srcVideo = omitSlideBlockGroupId(parsed.video);
          const next: ProgramSlideVideoBlockV1 = {
            ...shiftXYWH(srcVideo, nd, nd),
            id: newId,
            zIndex: z,
            bookmarks: srcVideo.bookmarks.map((bookmark) => ({ ...bookmark })),
          };
          videos = [...videos, next];
          newSelection.push({ kind: 'video', id: newId });
          continue;
        }
        if (parsed.kind === 'webpage') {
          const srcWp = omitSlideBlockGroupId(parsed.webpage);
          const base = shiftXYWH(srcWp, nd, nd);
          let thumbSrc = srcWp.thumbSrc?.trim() ?? '';
          if (thumbSrc && isPendingImageSrcForCanvas(thumbSrc)) {
            thumbSrc = clonePendingClipboardImageForCanvas(thumbSrc, newId) ?? '';
          }
          const next: ProgramSlideWebpageBlockV1 = {
            ...base,
            id: newId,
            zIndex: z,
            url: srcWp.url,
            title: srcWp.title,
            thumbSrc,
            embedSrc: srcWp.embedSrc,
          };
          webpages = [...webpages, next];
          newSelection.push({ kind: 'webpage', id: newId });
          continue;
        }
        if (parsed.kind === 'quiz') {
          const next: ProgramSlideQuizShortAnswerV1 = {
            ...shiftXYWH(omitSlideBlockGroupId(parsed.quiz), nd, nd),
            id: newId,
            zIndex: z,
          };
          quizzes = [...quizzes, next];
          newSelection.push({ kind: 'quiz', id: newId });
          continue;
        }
        if (parsed.kind === 'shape') {
          const next: ProgramSlideShapeBlockV1 = {
            ...shiftXYWH(omitSlideBlockGroupId(parsed.shape), nd, nd),
            id: newId,
            zIndex: z,
          };
          shapes = [...shapes, next];
          newSelection.push({ kind: 'shape', id: newId });
          continue;
        }
        if (parsed.kind === 'polyline') {
          const pl0 = omitSlideBlockGroupId(parsed.polyline);
          const nextPl = polylinePastedWithNudge(pl0, nd, nd, newId, z);
          if (!nextPl) continue;
          polylines = [...polylines, nextPl];
          newSelection.push({ kind: 'polyline', id: newId });
          continue;
        }
        if (parsed.kind === 'table') {
          const srcTable = shiftXYWH(omitSlideBlockGroupId(parsed.table), nd, nd);
          tables = [...tables, cloneSlideTableBlockWithNewId(srcTable, newId, z)];
          newSelection.push({ kind: 'table', id: newId });
          continue;
        }
        if (parsed.kind === 'quizChoiceSet') {
          const newSetId = newId;
          const nextOptions = parsed.options.map((opt, i) => {
            const { groupId: _g, ...rest } = omitSlideBlockGroupId(opt);
            return {
              ...shiftXYWH(rest, nd, nd),
              id: genSlideBoxId(zi * 7919 + i + 101),
              setId: newSetId,
              zIndex: z,
            };
          });
          quizChoiceSets = [...quizChoiceSets, { ...parsed.set, id: newSetId, zIndex: z }];
          quizChoiceOptions = [...quizChoiceOptions, ...nextOptions];
          for (const opt of nextOptions) {
            newSelection.push({ kind: 'quizChoice', id: opt.id });
          }
          zi += Math.max(0, nextOptions.length - 1);
          continue;
        }
        if (parsed.kind !== 'button') continue;
        const next: ProgramSlideButtonBlockV1 = {
          ...shiftXYWH(omitSlideBlockGroupId(parsed.button), nd, nd),
          id: newId,
          zIndex: z,
        };
        buttons = [...buttons, next];
        newSelection.push({ kind: 'button', id: newId });
      }

      setEditingId(null);
      setCropEditingId(null);
      applyLayoutChange({
        v: 1,
        boxes,
        images,
        clipboardImages,
        scanImages,
        buttons,
        shapes,
        polylines,
        tables,
        videos,
        webpages,
        quizzes,
        quizChoiceSets,
        quizChoiceOptions,
      });

      if (!newSelection.length) return;
      const primary = newSelection[newSelection.length - 1] ?? null;
      if (newSelection.length === 1 && primary) {
        selectSingleBlock(primary.kind, primary.id);
      } else {
        applySelectionState(newSelection, primary);
      }
    },
    [applyLayoutChange, applySelectionState, selectSingleBlock]
  );

  const applyPastedBlock = React.useCallback(
    (parsed: ParsedSlideClipboard) => {
      applyPastedBlocksFromParsed([parsed]);
    },
    [applyPastedBlocksFromParsed]
  );

  const handleBlockCopy = React.useCallback(
    (e: ClipboardEvent | React.ClipboardEvent<HTMLDivElement>) => {
      if (!canEdit) return;
      if (shouldDeferToNativeSlideCopy(e.target)) return;
      const refs = getEffectiveSelectedRefs();
      const okRefs = refs.filter((r) => !!serializeSlideBlockSelection(r.kind, r.id, layoutRef.current));
      if (!okRefs.length) return;
      const payload =
        okRefs.length === 1
          ? serializeSlideBlockSelection(okRefs[0]!.kind, okRefs[0]!.id, layoutRef.current)
          : serializeSlideMultiBlockClipboard(okRefs, layoutRef.current);
      if (!payload) return;
      e.preventDefault();
      e.clipboardData?.setData('text/plain', payload);
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(payload).catch(() => undefined);
      }
      onBlockClipboardCopyRef.current?.();
    },
    [canEdit, getEffectiveSelectedRefs]
  );

  const lastBlockPasteRef = React.useRef<{ text: string; at: number } | null>(null);

  const onCanvasPaste = React.useCallback(
    (e: ClipboardEvent | React.ClipboardEvent<HTMLDivElement>) => {
      if (!canEdit) return;

      const text = e.clipboardData?.getData('text/plain') ?? '';
      // 툴바 URL·정답 칸에 포커스가 있어도 블록 클립보드는 개체로 붙인다.
      if (isSlideBlockClipboardText(text)) {
        /** 같은 붙여넣기 이벤트가 짧은 간격으로 두 번 들어와도(키 반복·중복 전달) 개체는 한 번만 만든다 */
        const now = Date.now();
        const last = lastBlockPasteRef.current;
        if (last && last.text === text && now - last.at < 350) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        lastBlockPasteRef.current = { text, at: now };
        const multi = tryParseSlideBlocksClipboard(text);
        if (multi?.length) {
          e.preventDefault();
          e.stopPropagation();
          applyPastedBlocksFromParsed(multi);
          return;
        }
        const parsed = tryParseSlideBlockClipboard(text);
        if (parsed) {
          e.preventDefault();
          e.stopPropagation();
          applyPastedBlock(parsed);
          return;
        }
      }

      if (shouldDeferToNativeSlidePaste(e.target)) return;

      const root = rootRef.current;
      if (!root) return;
      const active = document.activeElement;
      if (active && active !== root && !root.contains(active)) {
        root.focus({ preventScroll: true });
      }

      const imageBlob = readClipboardImageBlob(e.clipboardData);
      if (!imageBlob) return;
      e.preventDefault();
      e.stopPropagation();
      const { selectedId: sid, selectedKind: skid } = selectionRef.current;
      const targetId =
        sid && (skid === 'clipboardImage' || skid === 'scanImage') ? sid : null;
      const targetKind: SlideBitmapImageKind =
        skid === 'scanImage' ? 'scanImage' : 'clipboardImage';
      void handleClipboardImagePaste(targetKind, targetId, e.clipboardData);
    },
    [applyPastedBlock, applyPastedBlocksFromParsed, canEdit, handleClipboardImagePaste]
  );

  React.useEffect(() => {
    if (!canEdit) return;
    const id = window.requestAnimationFrame(() => {
      ensureCanvasPasteFocus();
    });
    return () => window.cancelAnimationFrame(id);
  }, [canEdit, ensureCanvasPasteFocus]);

  React.useEffect(() => {
    if (!canEdit) return;
    const onDocPaste = (e: ClipboardEvent) => {
      const root = rootRef.current;
      if (!root) return;
      const active = document.activeElement;
      if (active && (active === root || root.contains(active))) return;
      onCanvasPaste(e);
    };
    document.addEventListener('paste', onDocPaste, true);
    return () => document.removeEventListener('paste', onDocPaste, true);
  }, [canEdit, onCanvasPaste]);

  React.useEffect(() => {
    if (!canEdit) return;
    /** ⌘/Ctrl+V를 길게 누르면 브라우저가 붙여넣기를 반복 발생시킨다 — 글 입력칸 밖에서는 반복분을 막는다 */
    const onDocKeyDown = (e: KeyboardEvent) => {
      if (!e.repeat || !(e.metaKey || e.ctrlKey) || e.altKey) return;
      if (e.key.toLowerCase() !== 'v') return;
      const t = e.target;
      if (t instanceof HTMLElement) {
        const tag = t.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t.isContentEditable) return;
      }
      e.preventDefault();
    };
    document.addEventListener('keydown', onDocKeyDown, true);
    return () => document.removeEventListener('keydown', onDocKeyDown, true);
  }, [canEdit]);

  React.useEffect(() => {
    if (!canEdit) return;
    const onDocCopy = (e: ClipboardEvent) => {
      const root = rootRef.current;
      if (!root) return;
      const active = document.activeElement;
      if (active && (active === root || root.contains(active))) return;
      handleBlockCopy(e);
    };
    document.addEventListener('copy', onDocCopy, true);
    return () => document.removeEventListener('copy', onDocCopy, true);
  }, [canEdit, handleBlockCopy]);

  React.useEffect(() => {
    if (!canEdit) return;
    const onDocCut = (e: ClipboardEvent) => {
      const root = rootRef.current;
      if (!root) return;
      const active = document.activeElement;
      if (active && (active === root || root.contains(active))) return;
      if (shouldDeferToNativeSlideCopy(e.target)) return;
      const refs = getEffectiveSelectedRefs();
      const okRefs = refs.filter(
        (r) =>
          !isSlideBlockLockedInLayout(layoutRef.current, r.kind, r.id) &&
          !!serializeSlideBlockSelection(r.kind, r.id, layoutRef.current)
      );
      if (!okRefs.length) return;
      const payload =
        okRefs.length === 1
          ? serializeSlideBlockSelection(okRefs[0]!.kind, okRefs[0]!.id, layoutRef.current)
          : serializeSlideMultiBlockClipboard(okRefs, layoutRef.current);
      if (!payload) return;
      e.preventDefault();
      e.stopPropagation();
      e.clipboardData?.setData('text/plain', payload);
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        void navigator.clipboard.writeText(payload).catch(() => undefined);
      }
      onBlockClipboardCopyRef.current?.();
      if (refs.length > 1) deleteSelectedBlocks();
      else {
        const r = okRefs[0]!;
        deleteSelectedBlock(r.kind, r.id);
      }
    };
    document.addEventListener('cut', onDocCut, true);
    return () => document.removeEventListener('cut', onDocCut, true);
  }, [canEdit, getEffectiveSelectedRefs]);

  const measureBoxHeightPx = React.useCallback((box: ProgramSlideTextBoxV1): number | null => {
    const root = rootRef.current;
    if (!root) return null;
    const rootRect = root.getBoundingClientRect();
    const widthPx = Math.max(1, box.w * rootRect.width);
    const mirror = document.createElement('div');
    mirror.className = 'cp-program-slide-canvas-text-measure';
    mirror.style.width = `${widthPx}px`;
    mirror.style.fontSize = `${box.fontSizePx}px`;
    mirror.style.fontFamily = slideTextFontCssFamily(box.fontFamily);
    mirror.style.fontWeight = box.bold ? '700' : '400';
    mirror.style.lineHeight = String(box.lineHeight ?? 1.05);
    mirror.style.textAlign = box.textAlign ?? 'left';
    mirror.innerHTML = runsToEditableHtml(box.textRuns, box.text, box.colorHex);
    root.appendChild(mirror);
    const height = mirror.scrollHeight;
    root.removeChild(mirror);
    return height;
  }, []);

  const adjustBoxHeightToContent = React.useCallback(
    (id: string) => {
      const root = rootRef.current;
      const current = layoutRef.current;
      const box = current.boxes.find((b) => b.id === id);
      if (!root || !box) return;
      const rootH = Math.max(1, root.getBoundingClientRect().height);
      const oneLineMinPx = Math.max(16, Math.round(box.fontSizePx * (box.lineHeight ?? 1.05) + 6));
      const minH = Math.max(0.01, oneLineMinPx / rootH);
      const measuredPx = measureBoxHeightPx(box);
      if (measuredPx == null) return;
      const neededPx = measuredPx + 2;
      const nextH = Math.min(1 - box.y, Math.max(minH, neededPx / rootH));
      const nextHRounded = Math.round(nextH * 10000) / 10000;
      if (Math.abs(nextHRounded - box.h) < 0.002) return;
      patchBox(id, { h: nextHRounded }, 'skip');
    },
    [measureBoxHeightPx, patchBox]
  );

  const patchBoxAndAutofit = React.useCallback(
    (id: string, patch: Partial<ProgramSlideTextBoxV1>, historyMode: 'auto' | 'record' | 'skip' = 'auto') => {
      const root = rootRef.current;
      const current = layoutRef.current;
      const rootH = root ? Math.max(1, root.getBoundingClientRect().height) : 1;
      applyLayoutChange({
        v: 1,
        images: current.images,
        buttons: current.buttons,
        videos: current.videos,
        quizzes: current.quizzes,
        boxes: current.boxes.map((b) => {
          if (b.id !== id) return b;
          const next = { ...b, ...patch };
          if (!root) return next;
          const oneLineMinPx = Math.max(16, Math.round(next.fontSizePx * (next.lineHeight ?? 1.05) + 6));
          const measuredPx = measureBoxHeightPx(next);
          if (measuredPx == null) return next;
          const nextH = Math.min(1 - next.y, Math.max(Math.max(0.01, oneLineMinPx / rootH), (measuredPx + 2) / rootH));
          return { ...next, h: Math.round(nextH * 10000) / 10000 };
        }),
      }, historyMode);
    },
    [applyLayoutChange, measureBoxHeightPx]
  );

  const applyFontSizeAndAutofit = React.useCallback(
    (id: string, nextSize: number) => {
      const clamped = Math.min(96, Math.max(8, Math.round(nextSize)));
      patchBoxAndAutofit(id, { fontSizePx: clamped });
    },
    [patchBoxAndAutofit]
  );

  const applyLineHeightAndAutofit = React.useCallback(
    (id: string, nextLineHeight: number) => {
      const clamped = Math.min(2.4, Math.max(1, Math.round(nextLineHeight * 100) / 100));
      patchBoxAndAutofit(id, { lineHeight: clamped });
    },
    [patchBoxAndAutofit]
  );

  const deleteImage = React.useCallback((id: string) => {
    const current = layoutRef.current;
    applyLayoutChange({
      v: 1,
      boxes: current.boxes,
      images: current.images.filter((img) => img.id !== id),
      buttons: current.buttons,
      videos: current.videos,
        quizzes: current.quizzes,
    });
    applySelectionState(
      selectionRef.current.selectedItems.filter((item) => slideSelectionRefKey(item) !== slideSelectionKey('image', id)),
      null
    );
    setCropEditingId((cur) => (cur === id ? null : cur));
    setImageUrlDrafts((cur) => {
      const next = { ...cur };
      delete next[id];
      return next;
    });
    setImageLoadErrors((cur) => {
      const next = { ...cur };
      delete next[id];
      return next;
    });
  }, [applyLayoutChange, applySelectionState]);

  const deleteBitmapImage = React.useCallback(
    (kind: SlideBitmapImageKind, id: string) => {
      if (kind === 'scanImage') {
        setScanLiveBlockId((liveId) => (liveId === id ? null : liveId));
      }
      const current = layoutRef.current;
      const block = findBitmapImage(current, kind, id);
      const src = block?.src?.trim();
      if (src && isPendingImageSrcForCanvas(src)) {
        revokePendingImageSrcForCanvas(src);
      } else {
        revokePendingForBlockForCanvas(id);
      }
      const key = bitmapImagesLayoutKey(kind);
      applyLayoutChange({
        v: 1,
        boxes: current.boxes,
        images: current.images,
        [key]: getBitmapImages(current, kind).filter((img) => img.id !== id),
        buttons: current.buttons,
        videos: current.videos,
        quizzes: current.quizzes,
      });
      applySelectionState(
        selectionRef.current.selectedItems.filter(
          (item) => slideSelectionRefKey(item) !== slideSelectionKey(kind, id)
        ),
        null
      );
      setCropEditingId((cur) => (cur === id ? null : cur));
      setClipboardImageLoadErrors((cur) => {
        const next = { ...cur };
        delete next[id];
        return next;
      });
    },
    [applyLayoutChange, applySelectionState]
  );

  const deleteClipboardImage = React.useCallback(
    (id: string) => deleteBitmapImage('clipboardImage', id),
    [deleteBitmapImage]
  );

  const deleteScanImage = React.useCallback(
    (id: string) => deleteBitmapImage('scanImage', id),
    [deleteBitmapImage]
  );

  const deleteVideo = React.useCallback((id: string) => {
    const current = layoutRef.current;
    applyLayoutChange({
      v: 1,
      boxes: current.boxes,
      images: current.images,
      buttons: current.buttons,
      videos: current.videos.filter((video) => video.id !== id),
      quizzes: current.quizzes,
    });
    applySelectionState(
      selectionRef.current.selectedItems.filter((item) => slideSelectionRefKey(item) !== slideSelectionKey('video', id)),
      null
    );
    setVideoUrlDrafts((cur) => {
      const next = { ...cur };
      delete next[id];
      return next;
    });
    setVideoLoadErrors((cur) => {
      const next = { ...cur };
      delete next[id];
      return next;
    });
    setVideoEmbedStarts((cur) => {
      const next = { ...cur };
      delete next[id];
      return next;
    });
    setVideoEmbedCurrentTimes((cur) => {
      const next = { ...cur };
      delete next[id];
      return next;
    });
    setVideoControlEnabledId((cur) => (cur === id ? null : cur));
    delete videoRefs.current[id];
    delete iframeRefs.current[id];
  }, [applyLayoutChange, applySelectionState]);

  const deleteWebpage = React.useCallback(
    (id: string) => {
      const current = layoutRef.current;
      const target = (current.webpages ?? []).find((wp) => wp.id === id);
      const thumb = target?.thumbSrc?.trim() ?? '';
      if (thumb && isPendingImageSrcForCanvas(thumb)) revokePendingImageSrcForCanvas(thumb);
      applyLayoutChange({
        v: 1,
        boxes: current.boxes,
        images: current.images,
        buttons: current.buttons,
        videos: current.videos,
        quizzes: current.quizzes,
        webpages: (current.webpages ?? []).filter((wp) => wp.id !== id),
      });
      applySelectionState(
        selectionRef.current.selectedItems.filter((item) => slideSelectionRefKey(item) !== slideSelectionKey('webpage', id)),
        null
      );
      setWebpageEmbedFailed((cur) => {
        if (!cur[id]) return cur;
        const next = { ...cur };
        delete next[id];
        return next;
      });
    },
    [applyLayoutChange, applySelectionState]
  );

  const deleteBox = React.useCallback((id: string) => {
    const current = layoutRef.current;
    applyLayoutChange({
      v: 1,
      boxes: current.boxes.filter((b) => b.id !== id),
      images: current.images,
      buttons: current.buttons,
      videos: current.videos,
        quizzes: current.quizzes,
    });
    applySelectionState(
      selectionRef.current.selectedItems.filter((item) => slideSelectionRefKey(item) !== slideSelectionKey('text', id)),
      null
    );
    setEditingId((cur) => (cur === id ? null : cur));
  }, [applyLayoutChange, applySelectionState]);

  const deleteButton = React.useCallback((id: string) => {
    const current = layoutRef.current;
    applyLayoutChange({
      v: 1,
      boxes: current.boxes,
      images: current.images,
      buttons: current.buttons.filter((btn) => btn.id !== id),
      videos: current.videos,
        quizzes: current.quizzes,
    });
    applySelectionState(
      selectionRef.current.selectedItems.filter((item) => slideSelectionRefKey(item) !== slideSelectionKey('button', id)),
      null
    );
    setEditingId((cur) => (cur === id ? null : cur));
  }, [applyLayoutChange, applySelectionState]);

  const deleteShape = React.useCallback((id: string) => {
    const current = layoutRef.current;
    applyLayoutChange({
      v: 1,
      boxes: current.boxes,
      images: current.images,
      buttons: current.buttons,
      shapes: (current.shapes ?? []).filter((s) => s.id !== id),
      videos: current.videos,
      quizzes: current.quizzes,
    });
    applySelectionState(
      selectionRef.current.selectedItems.filter((item) => slideSelectionRefKey(item) !== slideSelectionKey('shape', id)),
      null
    );
    setEditingId((cur) => (cur === id ? null : cur));
  }, [applyLayoutChange, applySelectionState]);

  const deleteTable = React.useCallback(
    (id: string) => {
      const current = layoutRef.current;
      applyLayoutChange({
        v: 1,
        boxes: current.boxes,
        images: current.images,
        buttons: current.buttons,
        shapes: current.shapes ?? [],
        polylines: current.polylines ?? [],
        tables: (current.tables ?? []).filter((tbl) => tbl.id !== id),
        videos: current.videos,
        quizzes: current.quizzes,
      });
      applySelectionState(
        selectionRef.current.selectedItems.filter(
          (item) => slideSelectionRefKey(item) !== slideSelectionKey('table', id)
        ),
        null
      );
      if (tableCellSelectionRef.current?.tableId === id) {
        commitTableCellSelection(null);
      }
      setEditingTableCellId(null);
      notifyTableSelection(null, null);
    },
    [applyLayoutChange, applySelectionState, notifyTableSelection]
  );

  const deletePolyline = React.useCallback((id: string) => {
    const current = layoutRef.current;
    applyLayoutChange({
      v: 1,
      boxes: current.boxes,
      images: current.images,
      buttons: current.buttons,
      shapes: current.shapes ?? [],
      polylines: (current.polylines ?? []).filter((p) => p.id !== id),
      videos: current.videos,
      quizzes: current.quizzes,
    });
    applySelectionState(
      selectionRef.current.selectedItems.filter((item) => slideSelectionRefKey(item) !== slideSelectionKey('polyline', id)),
      null
    );
    setEditingId((cur) => (cur === id ? null : cur));
  }, [applyLayoutChange, applySelectionState]);

  const deleteQuiz = React.useCallback((id: string) => {
    const current = layoutRef.current;
    applyLayoutChange({
      v: 1,
      boxes: current.boxes,
      images: current.images,
      buttons: current.buttons,
      videos: current.videos,
      quizzes: current.quizzes.filter((quiz) => quiz.id !== id),
    });
    applySelectionState(
      selectionRef.current.selectedItems.filter((item) => slideSelectionRefKey(item) !== slideSelectionKey('quiz', id)),
      null
    );
    setEditingId((cur) => (cur === id ? null : cur));
    setQuizDraftById((cur) => {
      const next = { ...cur };
      delete next[id];
      return next;
    });
    setQuizSucceededIds((cur) => {
      const next = new Set(cur);
      next.delete(id);
      return next;
    });
  }, [applyLayoutChange, applySelectionState]);

  const deleteSelectedBlock = React.useCallback((kind: SlideSelectionKind, id: string) => {
    if (isSlideBlockLockedInLayout(layoutRef.current, kind, id)) return;
    if (kind === 'image') {
      deleteImage(id);
      return;
    }
    if (kind === 'clipboardImage') {
      deleteClipboardImage(id);
      return;
    }
    if (kind === 'scanImage') {
      deleteScanImage(id);
      return;
    }
    if (kind === 'video') {
      deleteVideo(id);
      return;
    }
    if (kind === 'webpage') {
      deleteWebpage(id);
      return;
    }
    if (kind === 'quiz') {
      deleteQuiz(id);
      return;
    }
    if (kind === 'quizChoice') {
      const opt = layoutRef.current.quizChoiceOptions.find((o) => o.id === id);
      if (opt) deleteQuizChoiceSet(opt.setId);
      return;
    }
    if (kind === 'button') {
      deleteButton(id);
      return;
    }
    if (kind === 'shape') {
      deleteShape(id);
      return;
    }
    if (kind === 'table') {
      deleteTable(id);
      return;
    }
    if (kind === 'polyline') {
      deletePolyline(id);
      return;
    }
    deleteBox(id);
  }, [deleteBox, deleteButton, deleteClipboardImage, deleteImage, deletePolyline, deleteQuiz, deleteScanImage, deleteShape, deleteTable, deleteVideo, deleteWebpage]);

  const deleteSelectedBlocks = React.useCallback(() => {
    const current = layoutRef.current;
    const items = getEffectiveSelectedRefs().filter(
      (item) => !isSlideBlockLockedInLayout(current, item.kind, item.id)
    );
    if (!items.length) return;
    const keys = new Set(items.map((item) => slideSelectionRefKey(item)));
    const setIdsToRemove = new Set<string>();
    items.forEach((item) => {
      if (item.kind !== 'quizChoice') return;
      const opt = current.quizChoiceOptions.find((o) => o.id === item.id);
      if (opt) setIdsToRemove.add(opt.setId);
    });
    applyLayoutChange({
      v: 1,
      boxes: current.boxes.filter((b) => !keys.has(slideSelectionKey('text', b.id))),
      images: current.images.filter((img) => !keys.has(slideSelectionKey('image', img.id))),
      clipboardImages: (current.clipboardImages ?? []).filter(
        (img) => !keys.has(slideSelectionKey('clipboardImage', img.id))
      ),
      scanImages: (current.scanImages ?? []).filter((img) => !keys.has(slideSelectionKey('scanImage', img.id))),
      buttons: current.buttons.filter((btn) => !keys.has(slideSelectionKey('button', btn.id))),
      shapes: (current.shapes ?? []).filter((s) => !keys.has(slideSelectionKey('shape', s.id))),
      tables: (current.tables ?? []).filter((tbl) => !keys.has(slideSelectionKey('table', tbl.id))),
      polylines: (current.polylines ?? []).filter((p) => !keys.has(slideSelectionKey('polyline', p.id))),
      videos: current.videos.filter((v) => !keys.has(slideSelectionKey('video', v.id))),
      webpages: (current.webpages ?? []).filter((wp) => !keys.has(slideSelectionKey('webpage', wp.id))),
      quizzes: current.quizzes.filter((q) => !keys.has(slideSelectionKey('quiz', q.id))),
      quizChoiceSets: current.quizChoiceSets.filter((s) => !setIdsToRemove.has(s.id)),
      quizChoiceOptions: current.quizChoiceOptions.filter((opt) => !setIdsToRemove.has(opt.setId)),
    });
    setChoicePickedBySetId((cur) => {
      const next = { ...cur };
      setIdsToRemove.forEach((setId) => delete next[setId]);
      return next;
    });
    setChoiceSucceededSetIds((cur) => {
      const next = new Set(cur);
      setIdsToRemove.forEach((setId) => next.delete(setId));
      return next;
    });
    setCropEditingId(null);
    clearSelection();
    slideTextPlainSelectionRef.current = null;
  }, [applyLayoutChange, clearSelection, getEffectiveSelectedRefs]);

  const clearDeleteHoldTimer = React.useCallback(() => {
    if (deleteHoldTimerRef.current == null) return;
    window.clearTimeout(deleteHoldTimerRef.current);
    deleteHoldTimerRef.current = null;
    setHoldingDeleteId(null);
  }, []);

  const executeDeleteBlock = React.useCallback(
    (id: string, kind: SlideSelectionKind) => {
      if (kind === 'image') {
        deleteImage(id);
      } else if (kind === 'clipboardImage') {
        deleteClipboardImage(id);
      } else if (kind === 'scanImage') {
        deleteScanImage(id);
      } else if (kind === 'video') {
        deleteVideo(id);
      } else if (kind === 'quiz') {
        deleteQuiz(id);
      } else if (kind === 'quizChoice') {
        deleteQuizChoiceSet(id);
      } else if (kind === 'button') {
        deleteButton(id);
      } else if (kind === 'shape') {
        deleteShape(id);
      } else if (kind === 'polyline') {
        deletePolyline(id);
      } else if (kind === 'webpage') {
        deleteWebpage(id);
      } else {
        deleteBox(id);
      }
    },
    [
      deleteBox,
      deleteButton,
      deleteClipboardImage,
      deleteImage,
      deletePolyline,
      deleteQuiz,
      deleteQuizChoiceSet,
      deleteScanImage,
      deleteShape,
      deleteVideo,
      deleteWebpage,
    ]
  );

  /** 짧게 떼면 확인 모달, 길게 누르면 이미 삭제된 뒤라 모달 없음. leave/cancel은 모달 없이 취소. */
  const endDeleteHold = React.useCallback(
    (openConfirmIfShort: boolean) => {
      const hadPendingTimer = deleteHoldTimerRef.current != null;
      if (deleteHoldTimerRef.current != null) {
        window.clearTimeout(deleteHoldTimerRef.current);
        deleteHoldTimerRef.current = null;
      }
      setHoldingDeleteId(null);
      if (
        openConfirmIfShort &&
        hadPendingTimer &&
        !deleteHoldCommittedRef.current &&
        deleteHoldTargetRef.current
      ) {
        setDeleteConfirmTarget(deleteHoldTargetRef.current);
      }
      deleteHoldTargetRef.current = null;
    },
    []
  );

  React.useEffect(() => {
    if (!viewMode) return;
    clearSelection();
    setCropEditingId(null);
    clearDeleteHoldTimer();
    setDeleteConfirmTarget(null);
  }, [clearDeleteHoldTimer, clearSelection, viewMode]);

  React.useEffect(() => {
    if (viewMode) return;
    setFadingDismissButtonIds(new Set());
    setHiddenDismissButtonIds(new Set());
  }, [viewMode]);

  const startDeleteHold = React.useCallback(
    (e: React.PointerEvent, id: string, kind: SlideSelectionKind = 'text') => {
      if (!canEdit) return;
      if (isSlideBlockLockedInLayout(layoutRef.current, kind, id)) return;
      e.preventDefault();
      e.stopPropagation();
      clearDeleteHoldTimer();
      setDeleteConfirmTarget(null);
      deleteHoldCommittedRef.current = false;
      deleteHoldTargetRef.current = { id, kind };
      setHoldingDeleteId(id);
      deleteHoldTimerRef.current = window.setTimeout(() => {
        deleteHoldTimerRef.current = null;
        deleteHoldCommittedRef.current = true;
        setHoldingDeleteId(null);
        const target = deleteHoldTargetRef.current;
        deleteHoldTargetRef.current = null;
        if (target) executeDeleteBlock(target.id, target.kind);
      }, 1000);
    },
    [canEdit, clearDeleteHoldTimer, executeDeleteBlock]
  );

  React.useEffect(() => {
    if (!deleteConfirmTarget) return undefined;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        ev.stopPropagation();
        setDeleteConfirmTarget(null);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [deleteConfirmTarget]);

  const addBox = React.useCallback(() => {
    const z = getNextZIndex();
    const fontSizePx = defaultNewTextFontSizePx;
    const lineHeight = 1.05;
    const rootH = rootRef.current
      ? Math.max(1, rootRef.current.getBoundingClientRect().height)
      : 720;
    const w = 0.55;
    const h = textBoxOneLineHeightNorm(fontSizePx, lineHeight, rootH);
    const cur = layoutRef.current;
    const peers = collectInsertPeerRects(cur, 'text');
    const { x, y } = resolveInsertTopLeftAvoidPeerRects(
      w,
      h,
      peers,
      0.5,
      defaultInsertCenterY,
      0.0026,
      0.003,
      120,
      contentBoundsNormResolved
    );
    const nb: ProgramSlideTextBoxV1 = {
      id: genSlideBoxId(layout.boxes.length),
      x,
      y,
      w,
      h,
      text: '',
      fontSizePx,
      fontFamily: 'pretendard',
      colorHex: resolvedDefaultTextColorHex,
      bold: false,
      textAlign: 'center',
      lineHeight,
      zIndex: z,
    };
    const nextLayout: ProgramSlideLayoutPatch = {
      v: 1,
      boxes: [...layout.boxes, nb],
      images: layout.images,
      buttons: layout.buttons,
      videos: layout.videos,
      quizzes: layout.quizzes,
    };
    applyLayoutChange(nextLayout);
    beginGestureHistory(cloneSlideLayout(mergeSlideLayoutArrays(layout, nextLayout)));
    selectSingleBlock('text', nb.id);
    setEditingId(nb.id);
    setCropEditingId(null);
    window.requestAnimationFrame(() => {
      adjustBoxHeightToContent(nb.id);
      textBlockRefs.current[nb.id]?.focus();
    });
  }, [
    adjustBoxHeightToContent,
    applyLayoutChange,
    beginGestureHistory,
    getNextZIndex,
    layout.boxes,
    layout.buttons,
    layout.images,
    layout.videos,
    resolvedDefaultTextColorHex,
    selectSingleBlock,
    defaultNewTextFontSizePx,
  ]);

  const addButtonBlock = React.useCallback(() => {
    const id = genSlideBoxId(layout.boxes.length + layout.images.length + layout.buttons.length + layout.videos.length);
    const bw = 0.24;
    const bh = 0.09;
    const cur = layoutRef.current;
    const peers = collectInsertPeerRects(cur, { t: 'button', buttonKind: 'link' });
    const { x, y } = resolveInsertTopLeftAvoidPeerRects(bw, bh, peers, 0.5, 0.5);
    const nb: ProgramSlideButtonBlockV1 = {
      id,
      kind: 'link',
      x,
      y,
      w: bw,
      h: bh,
      text: '링크 버튼',
      href: '',
      fontSizePx: 32,
      fontFamily: 'pretendard',
      colorHex: resolvedDefaultTextColorHex,
      backgroundColorHex: '#ffffff',
      bold: true,
      cornerRadiusPx: 999,
      zIndex: getNextZIndex(),
    };
    applyLayoutChange({ v: 1, boxes: layout.boxes, images: layout.images, buttons: [...layout.buttons, nb], videos: layout.videos, quizzes: layout.quizzes });
    selectSingleBlock('button', id);
    setEditingId(null);
    setCropEditingId(null);
  }, [
    applyLayoutChange,
    getNextZIndex,
    layout.boxes,
    layout.buttons,
    layout.images,
    layout.quizzes,
    layout.videos,
    resolvedDefaultTextColorHex,
    selectSingleBlock,
  ]);

  const addDismissButtonBlock = React.useCallback(() => {
    const id = genSlideBoxId(layout.boxes.length + layout.images.length + layout.buttons.length + layout.videos.length);
    const bw = 0.18;
    const bh = 0.09;
    const cur = layoutRef.current;
    const peers = collectInsertPeerRects(cur, { t: 'button', buttonKind: 'dismiss' });
    const { x, y } = resolveInsertTopLeftAvoidPeerRects(bw, bh, peers, 0.5, 0.5);
    const nb: ProgramSlideButtonBlockV1 = {
      id,
      kind: 'dismiss',
      x,
      y,
      w: bw,
      h: bh,
      text: '?',
      href: '',
      fontSizePx: 32,
      fontFamily: 'pretendard',
      colorHex: resolvedDefaultTextColorHex,
      backgroundColorHex: '#ffffff',
      bold: true,
      cornerRadiusPx: 999,
      zIndex: getNextZIndex(),
    };
    applyLayoutChange({ v: 1, boxes: layout.boxes, images: layout.images, buttons: [...layout.buttons, nb], videos: layout.videos, quizzes: layout.quizzes });
    selectSingleBlock('button', id);
    setEditingId(null);
    setCropEditingId(null);
  }, [
    applyLayoutChange,
    getNextZIndex,
    layout.boxes,
    layout.buttons,
    layout.images,
    layout.quizzes,
    layout.videos,
    resolvedDefaultTextColorHex,
    selectSingleBlock,
  ]);

  const placeShapeFromCanvasClick = React.useCallback(
    (cx: number, cy: number, shapeKind: ProgramSlideShapeKindV1) => {
      const current = layoutRef.current;
      const root = rootRef.current;
      const rr = root?.getBoundingClientRect();
      const rw = Math.max(1, rr?.width ?? 1280);
      const rh = Math.max(1, rr?.height ?? 720);
      const id = genSlideBoxId(
        current.boxes.length +
          current.images.length +
          current.buttons.length +
          (current.shapes ?? []).length +
          (current.polylines ?? []).length +
          current.videos.length
      );
      let w: number;
      let h: number;
      if (isEvaluationSheetPreset) {
        const oneLinePx = Math.max(
          16,
          Math.round(EVALUATION_PLAN_SHEET_DEFAULT_TEXT_FONT_SIZE_PX * 1.05 + 6)
        );
        const shapePadPx = 20;
        if (shapeKind === 'circle') {
          const dPx = oneLinePx + shapePadPx;
          w = frac4(dPx / rw);
          h = frac4(dPx / rh);
        } else if (shapeKind === 'ruled') {
          // 4줄 분량, 본문 폭 대부분
          w = frac4(0.8);
          h = frac4((SLIDE_RULED_DEFAULT_SPACING_PX * 4 + 10) * (rw / 794) / rh);
        } else {
          w = frac4(0.18);
          h = frac4((oneLinePx + shapePadPx) / rh);
        }
      } else {
        const dw = 0.26;
        if (shapeKind === 'ruled') {
          w = 0.7;
          h = frac4(Math.min(0.6, (44 * 4 + 10) / rh));
        } else if (shapeKind === 'circle') {
          const dPx = dw * Math.min(rw, rh);
          w = frac4(dPx / rw);
          h = frac4(dPx / rh);
        } else {
          w = dw;
          h = 0.12;
        }
      }
      const peers = collectInsertPeerRects(current, { t: 'shape', shape: shapeKind });
      const { x, y } = resolveInsertTopLeftAvoidPeerRects(
        w,
        h,
        peers,
        cx,
        cy,
        0.0026,
        0.003,
        120,
        contentBoundsRef.current
      );
      const nb: ProgramSlideShapeBlockV1 = {
        id,
        x,
        y,
        w,
        h,
        shape: shapeKind,
        fillColorHex: shapeKind === 'ruled' ? '' : '#ffffff',
        strokeColorHex: shapeKind === 'ruled' ? '#9aa1a8' : '#bdbdbd',
        strokeWidthPx: shapeKind === 'ruled' ? 1 : 2,
        cornerRadiusPx: shapeKind === 'rect' ? 14 : shapeKind === 'ruled' ? 8 : 0,
        ...(shapeKind === 'ruled'
          ? { ruledLineSpacingPx: isEvaluationSheetPreset ? SLIDE_RULED_DEFAULT_SPACING_PX : 44, ruledLineColorHex: '#c8c8c8', ruledLineDashed: false }
          : {}),
        text: '',
        fontSizePx: defaultShapeTextFontSizePx,
        fontFamily: 'pretendard',
        colorHex: resolvedDefaultTextColorHex,
        bold: true,
        textAlign: 'center',
        zIndex: getNextZIndex(),
      };
      beginGestureHistory();
      applyLayoutChange({
        v: 1,
        boxes: current.boxes,
        images: current.images,
        buttons: current.buttons,
        shapes: [...(current.shapes ?? []), nb],
        videos: current.videos,
        quizzes: current.quizzes,
      });
      commitGestureHistory();
      selectSingleBlock('shape', id);
      setEditingId(null);
      setCropEditingId(null);
    },
    [
      applyLayoutChange,
      beginGestureHistory,
      commitGestureHistory,
      defaultShapeTextFontSizePx,
      getNextZIndex,
      isEvaluationSheetPreset,
      resolvedDefaultTextColorHex,
      selectSingleBlock,
    ]
  );

  const commitPolylineDraft = React.useCallback(() => {
    const pts = lineDraftPointsRef.current;
    if (!pts || pts.length < 2) return;
    const current = layoutRef.current;
    const id = genSlideBoxId(
      current.boxes.length +
        current.images.length +
        current.buttons.length +
        (current.shapes ?? []).length +
        (current.polylines ?? []).length +
        current.videos.length
    );
    const ply: ProgramSlidePolylineBlockV1 = {
      id,
      points: pts.map((p) => ({ x: frac4(p.x), y: frac4(p.y) })),
      strokeColorHex: '#000000',
      strokeWidthPx: defaultPolylineStrokeWidthPx,
      zIndex: getNextZIndex(),
    };
    beginGestureHistory();
    applyLayoutChange({
      v: 1,
      boxes: current.boxes,
      images: current.images,
      buttons: current.buttons,
      shapes: current.shapes ?? [],
      polylines: [...(current.polylines ?? []), ply],
      videos: current.videos,
      quizzes: current.quizzes,
    });
    commitGestureHistory();
    setLineDraftPoints(null);
    setLinePointerNorm(null);
    setPlaceCanvasTool(null);
    selectSingleBlock('polyline', id);
    setEditingId(null);
    setCropEditingId(null);
  }, [
    applyLayoutChange,
    beginGestureHistory,
    commitGestureHistory,
    defaultPolylineStrokeWidthPx,
    getNextZIndex,
    selectSingleBlock,
  ]);

  const addImageBlock = React.useCallback(() => {
    const currentImages = layout.images;
    const id = genSlideBoxId(layout.boxes.length + currentImages.length + layout.buttons.length + layout.videos.length);
    const iw = 0.34;
    const ih = 0.24;
    const cur = layoutRef.current;
    const peers = collectInsertPeerRects(cur, 'image');
    const { x, y } = resolveInsertTopLeftAvoidPeerRects(
      iw,
      ih,
      peers,
      0.5,
      defaultInsertCenterY,
      0.0026,
      0.003,
      120,
      contentBoundsNormResolved
    );
    const nb: ProgramSlideImageBlockV1 = {
      id,
      x,
      y,
      w: iw,
      h: ih,
      src: '',
      naturalW: 0,
      naturalH: 0,
      aspectWMultiplier: 1,
      cropUsesFrameFrac: true,
      crop: { x: 0, y: 0, w: 1, h: 1 },
      zIndex: getNextZIndex(),
    };
    applyLayoutChange({ v: 1, boxes: layout.boxes, images: [...currentImages, nb], buttons: layout.buttons, videos: layout.videos, quizzes: layout.quizzes });
    selectSingleBlock('image', id);
    setEditingId(null);
    setCropEditingId(null);
    setImageUrlDrafts((cur) => ({ ...cur, [id]: '' }));
  }, [applyLayoutChange, getNextZIndex, layout.boxes, layout.buttons, layout.images, layout.quizzes, layout.videos, selectSingleBlock]);

  const addClipboardImageBlock = React.useCallback(() => {
    const currentImages = layout.clipboardImages ?? [];
    const id = genSlideBoxId(
      layout.boxes.length + layout.images.length + currentImages.length + layout.buttons.length + layout.videos.length
    );
    const root = rootRef.current;
    const rect = root?.getBoundingClientRect();
    const rootW = Math.max(1, rect?.width ?? 1280);
    const rootH = Math.max(1, rect?.height ?? 720);
    const frameW = 0.34;
    const cur = layoutRef.current;
    const peers = collectInsertPeerRects(cur, 'clipboardImage');
    const probeY = 0.5 - normFrameHeightForAspect(frameW, 0.5, SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH) / 2;
    const probeH = normFrameHeightForAspect(frameW, Math.max(0, Math.min(1, probeY)), SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH);
    const { x: frameX, y: frameY } = resolveInsertTopLeftAvoidPeerRects(
      frameW,
      probeH,
      peers,
      0.5,
      defaultInsertCenterY,
      0.0026,
      0.003,
      120,
      contentBoundsNormResolved
    );
    const frameH = normFrameHeightForAspect(frameW, frameY, SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH);
    const nb: ProgramSlideClipboardImageBlockV1 = {
      id,
      x: frameX,
      y: frameY,
      w: frameW,
      h: frameH,
      src: '',
      naturalW: 0,
      naturalH: 0,
      aspectWMultiplier: 1,
      cropUsesFrameFrac: true,
      crop: { x: 0, y: 0, w: 1, h: 1 },
      zIndex: getNextZIndex(),
    };
    applyLayoutChange({
      v: 1,
      boxes: layout.boxes,
      images: layout.images,
      clipboardImages: [...currentImages, nb],
      buttons: layout.buttons,
      videos: layout.videos,
      quizzes: layout.quizzes,
    });
    selectSingleBlock('clipboardImage', id);
    setEditingId(null);
    setCropEditingId(null);
    ensureCanvasPasteFocus();
  }, [
    applyLayoutChange,
    ensureCanvasPasteFocus,
    getNextZIndex,
    layout.boxes,
    layout.buttons,
    layout.clipboardImages,
    layout.images,
    layout.quizzes,
    layout.videos,
    selectSingleBlock,
  ]);

  const addScanImageBlock = React.useCallback(() => {
    const currentImages = layout.scanImages ?? [];
    const id = genSlideBoxId(
      layout.boxes.length +
        layout.images.length +
        (layout.clipboardImages ?? []).length +
        currentImages.length +
        layout.buttons.length +
        layout.videos.length
    );
    const root = rootRef.current;
    const rect = root?.getBoundingClientRect();
    const rootW = Math.max(1, rect?.width ?? 1280);
    const rootH = Math.max(1, rect?.height ?? 720);
    const frameW = 0.34;
    const cur = layoutRef.current;
    const peers = collectInsertPeerRects(cur, 'scanImage');
    const probeY = 0.5 - normFrameHeightForAspect(frameW, 0.5, SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH) / 2;
    const probeH = normFrameHeightForAspect(frameW, Math.max(0, Math.min(1, probeY)), SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH);
    const { x: frameX, y: frameY } = resolveInsertTopLeftAvoidPeerRects(
      frameW,
      probeH,
      peers,
      0.5,
      defaultInsertCenterY,
      0.0026,
      0.003,
      120,
      contentBoundsNormResolved
    );
    const frameH = normFrameHeightForAspect(frameW, frameY, SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH);
    const nb: ProgramSlideScanImageBlockV1 = {
      id,
      x: frameX,
      y: frameY,
      w: frameW,
      h: frameH,
      src: '',
      naturalW: 0,
      naturalH: 0,
      aspectWMultiplier: 1,
      cropUsesFrameFrac: true,
      crop: { x: 0, y: 0, w: 1, h: 1 },
      zIndex: getNextZIndex(),
    };
    applyLayoutChange({
      v: 1,
      boxes: layout.boxes,
      images: layout.images,
      scanImages: [...currentImages, nb],
      buttons: layout.buttons,
      videos: layout.videos,
      quizzes: layout.quizzes,
    });
    selectSingleBlock('scanImage', id);
    setEditingId(null);
    setCropEditingId(null);
  }, [
    applyLayoutChange,
    getNextZIndex,
    layout.boxes,
    layout.buttons,
    layout.clipboardImages,
    layout.images,
    layout.quizzes,
    layout.scanImages,
    layout.videos,
    selectSingleBlock,
  ]);

  const addVideoBlock = React.useCallback(() => {
    const id = genSlideBoxId(layout.boxes.length + layout.images.length + layout.buttons.length + layout.videos.length);
    const root = rootRef.current;
    const rect = root?.getBoundingClientRect();
    const w = 0.34;
    const h = rect ? Math.min(0.5, Math.max(0.08, (w * Math.max(1, rect.width)) / (16 / 9) / Math.max(1, rect.height))) : w;
    const vh = frac4(h);
    const cur = layoutRef.current;
    const peers = collectInsertPeerRects(cur, 'video');
    const { x, y } = resolveInsertTopLeftAvoidPeerRects(w, vh, peers, 0.5, 0.5);
    const nb: ProgramSlideVideoBlockV1 = {
      id,
      x,
      y,
      w,
      h: vh,
      src: '',
      bookmarks: [],
      zIndex: getNextZIndex(),
    };
    applyLayoutChange({
      v: 1,
      boxes: layout.boxes,
      images: layout.images,
      buttons: layout.buttons,
      videos: [...layout.videos, nb],
      quizzes: layout.quizzes,
    });
    selectSingleBlock('video', id);
    setEditingId(null);
    setCropEditingId(null);
    setVideoUrlDrafts((cur) => ({ ...cur, [id]: '' }));
  }, [applyLayoutChange, getNextZIndex, layout.boxes, layout.buttons, layout.images, layout.videos, layout.quizzes, selectSingleBlock]);

  const applyWebpageUrl = React.useCallback(
    async (blockId: string, rawUrl: string) => {
      const normalized = normalizeSlideWebpageUrl(rawUrl);
      if (!normalized) throw new Error('올바른 http(s) URL을 입력해 주세요.');
      const current = layoutRef.current;
      const block = (current.webpages ?? []).find((wp) => wp.id === blockId);
      if (!block) return;

      const prevNorm = normalizeSlideWebpageUrl(block.url);
      if (prevNorm === normalized && block.thumbSrc.trim()) {
        patchWebpage(blockId, { mode: 'url', url: normalized, sourceCode: undefined });
        return;
      }

      const cacheMap: Record<number, ProgramSlideLayoutV1> = {
        ...(allLayoutsForWebpageThumbCacheRef.current ?? {}),
        0: current,
      };
      const cachedThumb = findWebpageThumbSrcForUrl(cacheMap, normalized);
      if (cachedThumb) {
        const cachedBlock = Object.values(cacheMap)
          .flatMap((l) => l?.webpages ?? [])
          .find((wp) => normalizeSlideWebpageUrl(wp.url) === normalized && wp.thumbSrc.trim() === cachedThumb);
        patchWebpage(blockId, {
          mode: 'url',
          url: normalized,
          title: cachedBlock?.title || block.title,
          thumbSrc: cachedThumb,
          embedSrc: cachedBlock?.embedSrc,
          sourceCode: undefined,
        });
        setWebpageEmbedFailed((cur) => ({ ...cur, [blockId]: false }));
        return;
      }

      let preview;
      try {
        preview = await fetchSlideWebpagePreview(normalized);
      } catch {
        preview = localPlaceholderPreview(rawUrl);
      }
      const webp = await thumbBase64ToWebpBlob(preview.thumbMime, preview.thumbBase64);
      const oldThumb = block.thumbSrc?.trim() ?? '';
      if (oldThumb && isPendingImageSrcForCanvas(oldThumb)) revokePendingImageSrcForCanvas(oldThumb);
      const blobUrl = registerPendingImageForCanvas(blockId, webp, 'image/webp');
      patchWebpage(blockId, {
        mode: 'url',
        url: preview.normalizedUrl || normalized,
        title: preview.title,
        thumbSrc: blobUrl,
        embedSrc: preview.embedSrc ?? undefined,
        sourceCode: undefined,
      });
      setWebpageEmbedFailed((cur) => ({ ...cur, [blockId]: false }));
    },
    [patchWebpage]
  );

  const submitWebpageUrlForBlock = React.useCallback(
    async (blockId: string, rawUrl: string) => {
      const trimmed = rawUrl.trim();
      if (!trimmed) return;
      setWebpagePreviewErrorByBlockId((cur) => {
        if (!cur[blockId]) return cur;
        const next = { ...cur };
        delete next[blockId];
        return next;
      });
      setWebpagePreviewLoadingId(blockId);
      try {
        await applyWebpageUrl(blockId, trimmed);
        setWebpageUrlDrafts((cur) => ({ ...cur, [blockId]: trimmed }));
      } catch (err: unknown) {
        setWebpagePreviewErrorByBlockId((cur) => ({
          ...cur,
          [blockId]: err instanceof Error ? err.message : '미리보기에 실패했습니다.',
        }));
      } finally {
        setWebpagePreviewLoadingId(null);
      }
    },
    [applyWebpageUrl]
  );

  const addWebpageBlock = React.useCallback(() => {
    const id = genSlideBoxId(
      layout.boxes.length +
        layout.images.length +
        (layout.clipboardImages ?? []).length +
        layout.buttons.length +
        layout.videos.length +
        (layout.webpages ?? []).length
    );
    const root = rootRef.current;
    const rect = root?.getBoundingClientRect();
    const rootW = Math.max(1, rect?.width ?? 1280);
    const rootH = Math.max(1, rect?.height ?? 720);
    const frameW = 0.34;
    const cur = layoutRef.current;
    const peers = collectInsertPeerRects(cur, 'webpage');
    const probeY = 0.5 - normFrameHeightForAspect(frameW, 0.5, SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH) / 2;
    const probeH = normFrameHeightForAspect(frameW, Math.max(0, Math.min(1, probeY)), SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH);
    const { x: frameX, y: frameY } = resolveInsertTopLeftAvoidPeerRects(frameW, probeH, peers, 0.5, 0.5);
    const frameH = normFrameHeightForAspect(frameW, frameY, SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH);
    const nb: ProgramSlideWebpageBlockV1 = {
      id,
      x: frameX,
      y: frameY,
      w: frameW,
      h: frameH,
      mode: 'url',
      url: '',
      title: '',
      thumbSrc: '',
      zIndex: getNextZIndex(),
    };
    applyLayoutChange({
      v: 1,
      boxes: layout.boxes,
      images: layout.images,
      buttons: layout.buttons,
      videos: layout.videos,
      quizzes: layout.quizzes,
      webpages: [...(cur.webpages ?? []), nb],
    });
    selectSingleBlock('webpage', id);
    setEditingId(null);
    setCropEditingId(null);
    setWebpageUrlDrafts((cur) => ({ ...cur, [id]: '' }));
    webpageToolbarInputFocusIdRef.current = id;
    ensureCanvasPasteFocus();
  }, [
    applyLayoutChange,
    ensureCanvasPasteFocus,
    getNextZIndex,
    layout.boxes,
    layout.buttons,
    layout.clipboardImages,
    layout.images,
    layout.quizzes,
    layout.videos,
    selectSingleBlock,
  ]);

  const addCodeWebpageBlock = React.useCallback(() => {
    const id = genSlideBoxId(
      layout.boxes.length +
        layout.images.length +
        (layout.clipboardImages ?? []).length +
        layout.buttons.length +
        layout.videos.length +
        (layout.webpages ?? []).length
    );
    const root = rootRef.current;
    const rect = root?.getBoundingClientRect();
    const rootW = Math.max(1, rect?.width ?? 1280);
    const rootH = Math.max(1, rect?.height ?? 720);
    const frameW = 0.34;
    const cur = layoutRef.current;
    const peers = collectInsertPeerRects(cur, 'webpage');
    const probeY = 0.5 - normFrameHeightForAspect(frameW, 0.5, SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH) / 2;
    const probeH = normFrameHeightForAspect(frameW, Math.max(0, Math.min(1, probeY)), SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH);
    const { x: frameX, y: frameY } = resolveInsertTopLeftAvoidPeerRects(frameW, probeH, peers, 0.5, 0.5);
    const frameH = normFrameHeightForAspect(frameW, frameY, SCAN_IMAGE_BLOCK_ASPECT, rootW, rootH);
    // 새 블럭은 빈 코드로 시작 (DEFAULT_CODE_BLOCK_SOURCE는 예전 예시 코드 판별용으로만 유지)
    const defaultSourceCode = '';
    const nb: ProgramSlideWebpageBlockV1 = {
      id,
      x: frameX,
      y: frameY,
      w: frameW,
      h: frameH,
      mode: 'code',
      url: '',
      title: '소스코드 블럭',
      thumbSrc: '',
      sourceCode: defaultSourceCode,
      zIndex: getNextZIndex(),
    };
    applyLayoutChange({
      v: 1,
      boxes: layout.boxes,
      images: layout.images,
      buttons: layout.buttons,
      videos: layout.videos,
      quizzes: layout.quizzes,
      webpages: [...(cur.webpages ?? []), nb],
    });
    selectSingleBlock('webpage', id);
    setEditingId(null);
    setCropEditingId(null);
    setWebpageCodeDrafts((prev) => ({ ...prev, [id]: defaultSourceCode }));
    ensureCanvasPasteFocus();
  }, [
    applyLayoutChange,
    ensureCanvasPasteFocus,
    getNextZIndex,
    layout.boxes,
    layout.buttons,
    layout.clipboardImages,
    layout.images,
    layout.quizzes,
    layout.videos,
    selectSingleBlock,
  ]);

  const addQuizBlock = React.useCallback(() => {
    const id = genSlideBoxId(
      layout.boxes.length + layout.images.length + layout.buttons.length + layout.videos.length + layout.quizzes.length
    );
    const qw = 0.36;
    const qh = 0.13;
    const cur = layoutRef.current;
    const peers = collectInsertPeerRects(cur, 'quiz');
    const { x, y } = resolveInsertTopLeftAvoidPeerRects(qw, qh, peers, 0.5, 0.5);
    const nb: ProgramSlideQuizShortAnswerV1 = {
      id,
      x,
      y,
      w: qw,
      h: qh,
      correctAnswer: '',
      placeholder: '?',
      fontSizePx: 72,
      fontFamily: 'pretendard',
      colorHex: resolvedDefaultTextColorHex,
      backgroundColorHex: '#ffffff',
      bold: false,
      cornerRadiusPx: 999,
      zIndex: getNextZIndex(),
    };
    applyLayoutChange({
      v: 1,
      boxes: layout.boxes,
      images: layout.images,
      buttons: layout.buttons,
      videos: layout.videos,
      quizzes: [...layout.quizzes, nb],
    });
    selectSingleBlock('quiz', id);
    setEditingId(null);
    setCropEditingId(null);
  }, [
    applyLayoutChange,
    getNextZIndex,
    layout.boxes,
    layout.buttons,
    layout.images,
    layout.quizzes,
    layout.videos,
    resolvedDefaultTextColorHex,
    selectSingleBlock,
  ]);

  const addQuizChoiceSet = React.useCallback(() => {
    const setId = genSlideBoxId(
      layout.quizChoiceSets.length + layout.quizzes.length + layout.boxes.length
    );
    const z = getNextZIndex();
    const { set, options } = createQuizChoiceSetBlocks(
      setId,
      z,
      genSlideBoxId,
      layout.quizChoiceOptions.length
    );
    const optionsWithDefaultText = options.map((option) => ({
      ...option,
      colorHex: resolvedDefaultTextColorHex,
    }));
    let minX = Infinity;
    let minY = Infinity;
    let maxR = -Infinity;
    let maxB = -Infinity;
    for (const o of optionsWithDefaultText) {
      minX = Math.min(minX, o.x);
      minY = Math.min(minY, o.y);
      maxR = Math.max(maxR, o.x + o.w);
      maxB = Math.max(maxB, o.y + o.h);
    }
    const uw = frac4(maxR - minX);
    const uh = frac4(maxB - minY);
    const cur = layoutRef.current;
    const peers = collectInsertPeerRects(cur, 'quizChoiceSet');
    const { x: tx, y: ty } = resolveInsertTopLeftAvoidPeerRects(uw, uh, peers, 0.5, 0.5);
    const dx = frac4(tx - minX);
    const dy = frac4(ty - minY);
    const shifted = optionsWithDefaultText.map((o) => ({
      ...o,
      x: frac4(o.x + dx),
      y: frac4(o.y + dy),
    }));
    applyLayoutChange({
      v: 1,
      boxes: layout.boxes,
      images: layout.images,
      buttons: layout.buttons,
      videos: layout.videos,
      quizzes: layout.quizzes,
      quizChoiceSets: [...layout.quizChoiceSets, set],
      quizChoiceOptions: [...layout.quizChoiceOptions, ...shifted],
    });
    selectSingleBlock('quizChoice', shifted[0].id);
    setEditingId(null);
    setCropEditingId(null);
  }, [
    applyLayoutChange,
    getNextZIndex,
    layout.boxes,
    layout.buttons,
    layout.images,
    layout.quizChoiceOptions.length,
    layout.quizChoiceSets.length,
    layout.quizzes,
    layout.videos,
    resolvedDefaultTextColorHex,
    selectSingleBlock,
  ]);

  const toggleQuizChoiceCorrect = React.useCallback(
    (setId: string, index: number) => {
      const set = layoutRef.current.quizChoiceSets.find((s) => s.id === setId);
      if (!set) return;
      patchQuizChoiceSet(setId, { correctOptionIndices: toggleQuizChoiceCorrectIndex(set, index) });
    },
    [patchQuizChoiceSet]
  );

  const addQuizChoiceOption = React.useCallback(
    (setId: string) => {
      const current = layoutRef.current;
      const setOpts = getQuizChoiceOptionsForSet(current, setId);
      if (setOpts.length >= QUIZ_CHOICE_MAX_OPTIONS) return;
      const newOpt = createAddedQuizChoiceOption(
        setId,
        setOpts,
        genSlideBoxId,
        current.quizChoiceOptions.length + current.boxes.length
      );
      applyLayoutChange({
        v: 1,
        boxes: current.boxes,
        images: current.images,
        buttons: current.buttons,
        videos: current.videos,
        quizzes: current.quizzes,
        quizChoiceSets: current.quizChoiceSets,
        quizChoiceOptions: [...current.quizChoiceOptions, newOpt],
      });
      selectSingleBlock('quizChoice', newOpt.id);
      setEditingChoiceLabelOptionId(null);
    },
    [applyLayoutChange, selectSingleBlock]
  );

  const removeQuizChoiceOption = React.useCallback(
    (setId: string) => {
      const current = layoutRef.current;
      const setOpts = getQuizChoiceOptionsForSet(current, setId);
      if (setOpts.length <= QUIZ_CHOICE_MIN_OPTIONS) return;
      const removed = setOpts[setOpts.length - 1];
      const set = current.quizChoiceSets.find((s) => s.id === setId);
      if (!set) return;
      const remaining = setOpts.slice(0, -1).map((opt, i) => ({ ...opt, optionIndex: i }));
      const remainingIds = new Set(remaining.map((o) => o.id));
      applyLayoutChange({
        v: 1,
        boxes: current.boxes,
        images: current.images,
        buttons: current.buttons,
        videos: current.videos,
        quizzes: current.quizzes,
        quizChoiceSets: current.quizChoiceSets.map((s) =>
          s.id === setId
            ? {
                ...s,
                correctOptionIndices: remapQuizChoiceCorrectIndicesAfterRemove(
                  s.correctOptionIndices ?? [],
                  removed.optionIndex
                ),
              }
            : s
        ),
        quizChoiceOptions: [
          ...current.quizChoiceOptions.filter((opt) => opt.setId !== setId),
          ...remaining,
        ],
      });
      applySelectionState(
        selectionRef.current.selectedItems.filter(
          (item) => item.kind !== 'quizChoice' || item.id !== removed.id
        ),
        null
      );
      setEditingChoiceLabelOptionId((cur) => (cur === removed.id ? null : cur));
    },
    [applyLayoutChange, applySelectionState]
  );

  const showQuizResultOverlay = React.useCallback((kind: 'o' | 'x') => {
    setQuizResultOverlay(kind);
    window.setTimeout(() => setQuizResultOverlay(null), 1000);
  }, []);

  const submitChoiceQuizzes = React.useCallback(() => {
    if (!viewMode) return;
    const sets = layoutRef.current.quizChoiceSets;
    let showed = false;
    for (const set of sets) {
      if (choiceSucceededSetIds.has(set.id)) continue;
      const picked = choicePickedBySetId[set.id];
      if (!picked || picked.size === 0) continue;
      const selected = Array.from(picked);
      if (isQuizChoiceAnswerCorrect(selected, set.correctOptionIndices ?? [])) {
        setChoiceSucceededSetIds((prev) => {
          const next = new Set(prev);
          next.add(set.id);
          return next;
        });
        if (!showed) {
          setQuizCorrectOverlay(true);
          window.setTimeout(() => setQuizCorrectOverlay(false), 1000);
          showed = true;
        }
      } else {
        setChoiceWrongFlashSetIds((prev) => {
          const next = new Set(prev);
          next.add(set.id);
          return next;
        });
        if (!showed) {
          showQuizResultOverlay('x');
          showed = true;
        }
        window.setTimeout(() => {
          setChoiceWrongFlashSetIds((prev) => {
            if (!prev.has(set.id)) return prev;
            const next = new Set(prev);
            next.delete(set.id);
            return next;
          });
        }, 1000);
      }
    }
  }, [choicePickedBySetId, choiceSucceededSetIds, showQuizResultOverlay, viewMode]);

  const toggleChoiceViewPick = React.useCallback((setId: string, index: number) => {
    if (!viewMode || choiceSucceededSetIds.has(setId)) return;
    setChoicePickedBySetId((prev) => {
      const cur = new Set(prev[setId] ?? []);
      if (cur.has(index)) cur.delete(index);
      else cur.add(index);
      return { ...prev, [setId]: cur };
    });
  }, [choiceSucceededSetIds, viewMode]);

  const showQuizCorrectOverlay = React.useCallback(() => {
    setQuizCorrectOverlay(true);
    window.setTimeout(() => setQuizCorrectOverlay(false), 1000);
  }, []);

  const submitQuizAnswer = React.useCallback(
    (quizId: string) => {
      if (!viewMode) return;
      const quiz = layoutRef.current.quizzes.find((q) => q.id === quizId);
      if (!quiz || quizSucceededIds.has(quizId)) return;
      const draft = quizDraftById[quizId] ?? '';
      const matched = getQuizMatchedAnswer(draft, quiz.correctAnswer);
      if (matched) {
        setQuizSucceededIds((prev) => {
          const next = new Set(prev);
          next.add(quizId);
          return next;
        });
        setQuizDraftById((prev) => ({ ...prev, [quizId]: matched }));
        showQuizCorrectOverlay();
        return;
      }
      setQuizWrongFlashIds((prev) => {
        const next = new Set(prev);
        next.add(quizId);
        return next;
      });
      showQuizResultOverlay('x');
      window.setTimeout(() => {
        setQuizWrongFlashIds((prev) => {
          if (!prev.has(quizId)) return prev;
          const next = new Set(prev);
          next.delete(quizId);
          return next;
        });
      }, 450);
    },
    [quizDraftById, quizSucceededIds, showQuizCorrectOverlay, showQuizResultOverlay, viewMode]
  );

  const quizIdsKey = React.useMemo(
    () =>
      [
        ...(layout.quizzes ?? []).map((q) => q.id),
        ...(layout.quizChoiceSets ?? []).map((s) => s.id),
      ].join('|'),
    [layout.quizChoiceSets, layout.quizzes]
  );
  React.useEffect(() => {
    setQuizDraftById({});
    setQuizSucceededIds(new Set());
    setQuizWrongFlashIds(new Set());
    setQuizCorrectOverlay(false);
    setChoicePickedBySetId({});
    setChoiceSucceededSetIds(new Set());
    setChoiceWrongFlashSetIds(new Set());
    setQuizResultOverlay(null);
  }, [quizIdsKey, viewMode]);

  const loadImageIntoBlock = React.useCallback(
    (id: string) => {
      const raw = (imageUrlDrafts[id] ?? '').trim();
      let src = '';
      try {
        const url = new URL(raw);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new Error('unsupported protocol');
        src = url.toString();
      } catch {
        setImageLoadErrors((cur) => ({ ...cur, [id]: 'http 또는 https 이미지 주소를 입력해 주세요.' }));
        return;
      }

      setImageLoadErrors((cur) => ({ ...cur, [id]: '' }));
      const probe = new Image();
      probe.onload = () => {
        const naturalW = probe.naturalWidth || probe.width;
        const naturalH = probe.naturalHeight || probe.height;
        const root = rootRef.current;
        const current = layoutRef.current;
        const block = current.images.find((img) => img.id === id);
        if (!root || !block || !naturalW || !naturalH) return;
        const rect = root.getBoundingClientRect();
        const rootW = Math.max(1, rect.width);
        const rootH = Math.max(1, rect.height);
        const imageRatio = naturalW / naturalH;
        let nextW = block.w;
        let nextH = (nextW * rootW) / imageRatio / rootH;
        const maxH = Math.max(0.05, 1 - block.y);
        if (nextH > maxH) {
          nextH = maxH;
          nextW = Math.min(1 - block.x, (nextH * imageRatio * rootH) / rootW);
        }
        patchImage(id, {
          src,
          naturalW,
          naturalH,
          aspectWMultiplier: 1,
          cropUsesFrameFrac: true,
          w: Math.round(nextW * 10000) / 10000,
          h: Math.round(nextH * 10000) / 10000,
          crop: { x: 0, y: 0, w: 1, h: 1 },
        });
      };
      probe.onerror = () => {
        setImageLoadErrors((cur) => ({ ...cur, [id]: '이미지를 불러오지 못했습니다.' }));
      };
      probe.src = src;
    },
    [imageUrlDrafts, patchImage]
  );

  const loadVideoIntoBlock = React.useCallback(
    (id: string) => {
      const raw = (videoUrlDrafts[id] ?? '').trim();
      let src = '';
      try {
        const url = new URL(raw);
        if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new Error('unsupported protocol');
        src = url.toString();
      } catch {
        setVideoLoadErrors((cur) => ({ ...cur, [id]: 'http 또는 https 동영상 주소를 입력해 주세요.' }));
        return;
      }
      const renderSource = getVideoRenderSource(src);
      if (!renderSource) {
        setVideoLoadErrors((cur) => ({ ...cur, [id]: '지원하는 동영상 주소를 입력해 주세요.' }));
        return;
      }
      setVideoLoadErrors((cur) => ({ ...cur, [id]: '' }));
      patchVideo(id, { src });
      setVideoUrlDrafts((cur) => ({ ...cur, [id]: src }));
      setVideoEmbedStarts((cur) => {
        const next = { ...cur };
        delete next[id];
        return next;
      });
    },
    [patchVideo, videoUrlDrafts]
  );

  const applySlideImageFrameToCrop = React.useCallback(
    (id: string, blockKind: SlideImageBlockKind) => {
      const current = layoutRef.current;
      const block = findSlideImageBlock(current, blockKind, id);
      if (!block || !block.src) return;
      const crop = block.crop;
      if (!crop) return;
      const nextX = block.x + block.w * crop.x;
      const nextY = block.y + block.h * crop.y;
      const nextW = block.w * crop.w;
      const nextH = block.h * crop.h;
      patchSlideImageByKind(id, blockKind, {
        x: Math.round(nextX * 10000) / 10000,
        y: Math.round(nextY * 10000) / 10000,
        w: Math.round(nextW * 10000) / 10000,
        h: Math.round(nextH * 10000) / 10000,
      });
    },
    [patchSlideImageByKind]
  );

  /** 크롭 편집 진입: 보이던 영역 픽셀 크기 유지, 프레임만 원본 전체를 담도록 확장 */
  const applySlideImageFrameToOriginal = React.useCallback(
    (id: string, blockKind: SlideImageBlockKind) => {
      const current = layoutRef.current;
      const block = findSlideImageBlock(current, blockKind, id);
      if (!block?.src || block.naturalW <= 0 || block.naturalH <= 0) return;
      const crop = block.crop;
      const nextW = block.w / Math.max(0.001, crop.w);
      const nextH = block.h / Math.max(0.001, crop.h);
      patchSlideImageByKind(id, blockKind, {
        x: Math.round((block.x - nextW * crop.x) * 10000) / 10000,
        y: Math.round((block.y - nextH * crop.y) * 10000) / 10000,
        w: Math.round(nextW * 10000) / 10000,
        h: Math.round(nextH * 10000) / 10000,
      });
    },
    [patchSlideImageByKind]
  );

  const getContainMetrics = React.useCallback((image: ProgramSlideImageBlockV1) => {
    const root = rootRef.current;
    const rootRect = root?.getBoundingClientRect();
    const framePxW = Math.max(1, image.w * (rootRect?.width ?? 1));
    const framePxH = Math.max(1, image.h * (rootRect?.height ?? 1));
    const mult =
      image.naturalW > 0 && image.naturalH > 0 && image.aspectWMultiplier > 0 ? image.aspectWMultiplier : 1;
    const naturalW = (image.naturalW > 0 ? image.naturalW : 1) * mult;
    const naturalH = image.naturalH > 0 ? image.naturalH : 1;
    const imageAspect = naturalW / naturalH;
    const frameAspect = framePxW / framePxH;
    const dispW = frameAspect > imageAspect ? imageAspect / frameAspect : 1;
    const dispH = frameAspect > imageAspect ? 1 : frameAspect / imageAspect;
    return {
      dispW,
      dispH,
      offX: (1 - dispW) / 2,
      offY: (1 - dispH) / 2,
    };
  }, []);

  const exitCropMode = React.useCallback(() => {
    const id = cropEditingId;
    if (!id) return;
    const blockKind = resolveSlideImageBlockKind(layoutRef.current, id);
    const block = findSlideImageBlock(layoutRef.current, blockKind, id);

    const clearCropEditing = () => {
      setCropEditingId(null);
      if (blockKind !== 'scanImage') {
        window.requestAnimationFrame(() => applySlideImageFrameToCrop(id, blockKind));
      }
    };

    if (
      blockKind === 'scanImage' &&
      block?.src &&
      block.naturalW > 0 &&
      block.naturalH > 0
    ) {
      const crop = clampCropFrac(block.crop);
      const nextX = Math.round((block.x + block.w * crop.x) * 10000) / 10000;
      const nextY = Math.round((block.y + block.h * crop.y) * 10000) / 10000;
      const nextW = Math.round(block.w * crop.w * 10000) / 10000;
      const nextH = Math.round(block.h * crop.h * 10000) / 10000;

      void (async () => {
        try {
          const bakedBlob = await bakeFrameCropToBlob(block.src, block.naturalW, block.naturalH, crop);
          const compressed = await compressClipboardImage(bakedBlob);
          const oldSrc = block.src?.trim();
          if (oldSrc && isPendingImageSrcForCanvas(oldSrc)) {
            revokePendingImageSrcForCanvas(oldSrc);
          }
          const previewUrl = registerPendingClipboardImageForCanvas(id, compressed.blob, compressed.mimeType);
          const { naturalW, naturalH } = await probeImageNaturalSize(
            previewUrl,
            compressed.width,
            compressed.height
          );
          patchScanImage(id, {
            src: previewUrl,
            naturalW,
            naturalH,
            aspectWMultiplier: 1,
            cropUsesFrameFrac: true,
            crop: { x: 0, y: 0, w: 1, h: 1 },
            x: nextX,
            y: nextY,
            w: nextW,
            h: nextH,
          });
          setClipboardImageLoadErrors((cur) => ({ ...cur, [id]: '' }));
        } catch (e) {
          const msg = e instanceof Error ? e.message : '이미지 처리에 실패했습니다.';
          showClipboardImageToast(msg);
          patchScanImage(id, {
            x: nextX,
            y: nextY,
            w: nextW,
            h: nextH,
            crop: { x: 0, y: 0, w: 1, h: 1 },
          });
        } finally {
          clearCropEditing();
        }
      })();
      return;
    }

    clearCropEditing();
  }, [
    applySlideImageFrameToCrop,
    cropEditingId,
    patchScanImage,
    showClipboardImageToast,
  ]);

  const beginScanCropAfterCapture = React.useCallback(
    (blockId: string) => {
      if (!canEdit) return;
      const block = findBitmapImage(layoutRef.current, 'scanImage', blockId);
      if (!block?.src || block.naturalW <= 0 || block.naturalH <= 0) return;

      const root = rootRef.current;
      const rect = root?.getBoundingClientRect();
      const rootW = Math.max(1, rect?.width ?? 1280);
      const rootH = Math.max(1, rect?.height ?? 720);
      const frameAspect = getSlideBlockFrameAspect(block, rootW, rootH);
      const initialCrop = clampCropFrac(coverCropFrameFrac(block.naturalW, block.naturalH, frameAspect));

      patchScanImage(blockId, { crop: initialCrop, cropUsesFrameFrac: true }, 'skip');
      setEditingId(null);
      selectSingleBlock('scanImage', blockId);
      setCropEditingId(blockId);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => applySlideImageFrameToOriginal(blockId, 'scanImage'));
      });
    },
    [applySlideImageFrameToOriginal, canEdit, patchScanImage, selectSingleBlock]
  );

  const stopScanLivePreview = React.useCallback(() => {
    setScanLiveBlockId(null);
    setScanLiveInputMenuBlockId(null);
    setScanLiveCaptureMode('camera');
    setScanLiveCameraDeviceId(null);
  }, []);

  React.useEffect(() => {
    if (!scanLiveInputMenuBlockId) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return;
    void navigator.mediaDevices.enumerateDevices().then((ds) => {
      setScanLiveInputDeviceList(filterVideoInputDevices(ds));
    });
  }, [scanLiveInputMenuBlockId]);

  React.useEffect(() => {
    if (!scanLiveInputMenuBlockId) return;
    const onDown = (e: MouseEvent) => {
      const el = scanLiveInputMenuWrapRef.current;
      if (el && !el.contains(e.target as Node)) setScanLiveInputMenuBlockId(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [scanLiveInputMenuBlockId]);

  React.useEffect(() => {
    if (!scanLiveInputMenuBlockId) return;
    const toolbarScanOpen =
      canEdit && !hasMultiSelection && selectedKind === 'scanImage' && selectedId === scanLiveInputMenuBlockId;
    if (!toolbarScanOpen) setScanLiveInputMenuBlockId(null);
  }, [scanLiveInputMenuBlockId, canEdit, hasMultiSelection, selectedKind, selectedId]);

  const startScanLivePreview = React.useCallback(
    (blockId: string) => {
      if (!canEdit) return;
      if (cropEditingId) exitCropMode();
      setScanLiveInputMenuBlockId(null);
      setScanLiveBlockId(blockId);
      selectSingleBlock('scanImage', blockId);
    },
    [canEdit, cropEditingId, exitCropMode, selectSingleBlock]
  );

  const handleScanLiveCapture = React.useCallback(
    (blockId: string, blob: Blob) => {
      setScanLiveBlockId(null);
      setScanLiveInputMenuBlockId(null);
      void (async () => {
        await insertBitmapImageFromBlob('scanImage', blob, blockId);
        beginScanCropAfterCapture(blockId);
      })();
    },
    [beginScanCropAfterCapture, insertBitmapImageFromBlob]
  );

  const handleScanLivePreviewError = React.useCallback(
    (msg: string) => {
      showClipboardImageToast(msg);
      stopScanLivePreview();
    },
    [showClipboardImageToast, stopScanLivePreview]
  );

  const enterChoiceLabelEditMode = React.useCallback(
    (optionId: string) => {
      if (!canEdit || viewMode) return;
      setSelectedGroupId(null);
      applySelectionState([{ kind: 'quizChoice', id: optionId }], { kind: 'quizChoice', id: optionId });
      setEditingChoiceLabelOptionId(optionId);
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
    },
    [applySelectionState, canEdit, exitCropMode, viewMode]
  );

  const applyMarqueeHitSelection = React.useCallback(
    (
      m: NonNullable<typeof marqueeRef.current>,
      left: number,
      top: number,
      w: number,
      h: number,
    ) => {
      const minNorm = 4 / Math.max(m.rootW, m.rootH);
      if (w < minNorm && h < minNorm) return;
      const marquee = { x: left, y: top, w, h };
      const hitItemsRaw: SelectedSlideBlockRef[] = getAllBlockGeometrySnapshot()
        .filter((block) => rectsIntersectNorm(marquee, block))
        .map((block) => ({ kind: block.kind, id: block.id }));
      const cur = layoutRef.current;
      const hitItems = expandSlideRefsWithCoMembers(cur, hitItemsRaw);

      const trySelectAsWholeGroup = (refs: SelectedSlideBlockRef[]): boolean => {
        if (refs.length < 2) return false;
        const first = refs[0];
        if (!first) return false;
        const gid = getSlideBlockGroupId(cur, first.kind, first.id);
        if (!gid) return false;
        const full = collectRefsWithGroupId(cur, gid);
        if (full.length !== refs.length) return false;
        const keys = new Set(refs.map(slideSelectionRefKey));
        if (!full.every((f) => keys.has(slideSelectionRefKey({ kind: f.kind as SlideSelectionKind, id: f.id })))) {
          return false;
        }
        setSelectedGroupId(gid);
        setSelectedItems([]);
        const p = full[0]!;
        setSelectedId(p.id);
        setSelectedKind(p.kind as SlideSelectionKind);
        setEditingId(null);
        return true;
      };

      if (m.shiftKey) {
        const merged = new Map<string, SelectedSlideBlockRef>();
        m.baseItems.forEach((item) => merged.set(slideSelectionRefKey(item), item));
        hitItems.forEach((item) => merged.set(slideSelectionRefKey(item), item));
        const nextItems = Array.from(merged.values());
        const mergedExpanded = expandSlideRefsWithCoMembers(cur, nextItems);
        if (trySelectAsWholeGroup(mergedExpanded)) return;
        applySelectionState(mergedExpanded, mergedExpanded[mergedExpanded.length - 1] ?? null);
      } else if (!hitItems.length) {
        applySelectionState([], null);
      } else {
        if (trySelectAsWholeGroup(hitItems)) return;
        applySelectionState(hitItems, hitItems[hitItems.length - 1]);
      }
    },
    [applySelectionState, getAllBlockGeometrySnapshot],
  );

  const updateMarqueeFromClient = React.useCallback(
    (m: NonNullable<typeof marqueeRef.current>, clientX: number, clientY: number) => {
      const r = m.innerEl.getBoundingClientRect();
      const sx = (m.startClientX - r.left) / Math.max(1, r.width);
      const sy = (m.startClientY - r.top) / Math.max(1, r.height);
      const cx = (clientX - r.left) / Math.max(1, r.width);
      const cy = (clientY - r.top) / Math.max(1, r.height);
      const left = Math.min(sx, cx);
      const top = Math.min(sy, cy);
      const w = Math.abs(cx - sx);
      const h = Math.abs(cy - sy);

      const a = clientPointToLayoutPoint(m.startClientX, m.startClientY);
      const b = clientPointToLayoutPoint(clientX, clientY);
      let box = {
        left: Math.min(a.x, b.x),
        top: Math.min(a.y, b.y),
        width: Math.abs(b.x - a.x),
        height: Math.abs(b.y - a.y),
      };
      const clipEl = positionSyncRoot ?? m.innerEl;
      const clip = clientRectToLayoutRect(clipEl.getBoundingClientRect());
      const nextLeft = Math.max(box.left, clip.left);
      const nextTop = Math.max(box.top, clip.top);
      const nextRight = Math.min(box.left + box.width, clip.right);
      const nextBottom = Math.min(box.top + box.height, clip.bottom);
      box = {
        left: nextLeft,
        top: nextTop,
        width: Math.max(0, nextRight - nextLeft),
        height: Math.max(0, nextBottom - nextTop),
      };
      setMarqueeViewportRect(box);
      applyMarqueeHitSelection(m, left, top, w, h);
    },
    [applyMarqueeHitSelection, positionSyncRoot],
  );

  const onMarqueePointerMove = React.useCallback(
    (e: PointerEvent) => {
      const m = marqueeRef.current;
      if (!m) return;
      updateMarqueeFromClient(m, e.clientX, e.clientY);
    },
    [updateMarqueeFromClient],
  );

  const endMarqueeSelection = React.useCallback(
    (e: PointerEvent) => {
      const m = marqueeRef.current;
      marqueeRef.current = null;
      window.removeEventListener('pointermove', onMarqueePointerMove);
      window.removeEventListener('pointerup', endMarqueeSelection);
      window.removeEventListener('pointercancel', endMarqueeSelection);
      setMarqueeViewportRect(null);
      if (!m) return;
      const r = m.innerEl.getBoundingClientRect();
      const sx = (m.startClientX - r.left) / Math.max(1, r.width);
      const sy = (m.startClientY - r.top) / Math.max(1, r.height);
      const cx = (e.clientX - r.left) / Math.max(1, r.width);
      const cy = (e.clientY - r.top) / Math.max(1, r.height);
      const left = Math.min(sx, cx);
      const top = Math.min(sy, cy);
      const w = Math.abs(cx - sx);
      const h = Math.abs(cy - sy);
      const minNorm = 4 / Math.max(m.rootW, m.rootH);
      if (w < minNorm && h < minNorm) {
        if (!m.shiftKey) clearSelection();
        return;
      }
      applyMarqueeHitSelection(m, left, top, w, h);
      setEditingId(null);
      setEditingChoiceLabelOptionId(null);
    },
    [applyMarqueeHitSelection, clearSelection, onMarqueePointerMove],
  );

  /** 꺾은선 그리기 모드: 캡처 단계에서 처리해 다른 블록의 pointerdown/stopPropagation에 가리지 않음 */
  const tryConsumeLinePlacePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!canEdit || viewMode || placeCanvasToolRef.current !== 'line' || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      ensureCanvasPasteFocus();
      exitCropMode();
      clearDeleteHoldTimer();
      stopVideoPlaybackInEditModeRef.current();
      setEditingId(null);
      setEditingChoiceLabelOptionId(null);
      const inner = e.currentTarget;
      const rect = inner.getBoundingClientRect();
      const startNormX = clamp01((e.clientX - rect.left) / Math.max(1, rect.width));
      const startNormY = clamp01((e.clientY - rect.top) / Math.max(1, rect.height));
      const prevPts = lineDraftPointsRef.current;
      let nx = startNormX;
      let ny = startNormY;
      if (prevPts && prevPts.length > 0) {
        const anchor = prevPts[prevPts.length - 1];
        const snapped = snapLineDraftPointToShiftOrtho(
          anchor,
          { x: startNormX, y: startNormY },
          e.shiftKey,
          rect.width,
          rect.height
        );
        nx = snapped.x;
        ny = snapped.y;
      }
      setLineDraftPoints((p) => [...(p ?? []), { x: nx, y: ny }]);
      setLinePointerNorm({ x: nx, y: ny });
    },
    [canEdit, viewMode, clearDeleteHoldTimer, ensureCanvasPasteFocus, exitCropMode]
  );

  const updateLineDraftHoverFromInnerPointer = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!canEdit || viewMode || placeCanvasToolRef.current !== 'line') return;
      const pts = lineDraftPointsRef.current;
      if (!pts?.length) return;
      const inner = e.currentTarget;
      const rect = inner.getBoundingClientRect();
      const x = clamp01((e.clientX - rect.left) / Math.max(1, rect.width));
      const y = clamp01((e.clientY - rect.top) / Math.max(1, rect.height));
      const anchor = pts[pts.length - 1];
      const snapped = snapLineDraftPointToShiftOrtho(anchor, { x, y }, e.shiftKey, rect.width, rect.height);
      setLinePointerNorm(snapped);
    },
    [canEdit, viewMode]
  );

  const beginMarqueeSelectionAt = React.useCallback(
    (clientX: number, clientY: number, shiftKey: boolean) => {
      if (!canEdit) return false;
      if (placeCanvasToolRef.current === 'line') return false;
      const inner = canvasInnerEl;
      if (!inner) return false;
      const rect = inner.getBoundingClientRect();

      ensureCanvasPasteFocus();
      exitCropMode();
      clearDeleteHoldTimer();
      stopVideoPlaybackInEditModeRef.current();
      setEditingId(null);
      setEditingChoiceLabelOptionId(null);
      const nextMarquee = {
        innerEl: inner,
        startClientX: clientX,
        startClientY: clientY,
        rootW: Math.max(1, rect.width),
        rootH: Math.max(1, rect.height),
        shiftKey,
        baseItems: shiftKey
          ? [
              ...(selectionRef.current.selectedGroupId
                ? collectRefsWithGroupId(layoutRef.current, selectionRef.current.selectedGroupId).map((r) => ({
                    kind: r.kind as SlideSelectionKind,
                    id: r.id,
                  }))
                : selectionRef.current.selectedItems),
            ]
          : [],
      };
      marqueeRef.current = nextMarquee;
      updateMarqueeFromClient(nextMarquee, clientX, clientY);
      window.addEventListener('pointermove', onMarqueePointerMove);
      window.addEventListener('pointerup', endMarqueeSelection);
      window.addEventListener('pointercancel', endMarqueeSelection);
      return true;
    },
    [
      canEdit,
      canvasInnerEl,
      clearDeleteHoldTimer,
      endMarqueeSelection,
      ensureCanvasPasteFocus,
      exitCropMode,
      onMarqueePointerMove,
      updateMarqueeFromClient,
    ]
  );

  const startMarqueeSelection = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!canEdit || e.button !== 0) return;
      if (placeCanvasToolRef.current === 'line') return;
      const target = e.target as HTMLElement | null;
      if (
        target?.closest(
          '.cp-program-slide-canvas-table, .cp-program-slide-table-cell, .cp-program-slide-table-drag-edge, .cp-program-slide-scan-live, .cp-program-slide-scan-live-hit'
        )
      ) {
        return;
      }
      e.preventDefault();
      beginMarqueeSelectionAt(e.clientX, e.clientY, e.shiftKey);
    },
    [beginMarqueeSelectionAt, canEdit]
  );

  React.useEffect(() => {
    const host = positionSyncRoot;
    if (!host || !canEdit || viewMode) return;
    const onHostPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (placeCanvasToolRef.current === 'line') return;
      const t = e.target as HTMLElement | null;
      if (!t || !host.contains(t)) return;
      if (canvasInnerEl?.contains(t)) return;
      if (
        t.closest(
          'button, input, textarea, select, a, .cp-program-slide-canvas-toolbar-wrap, .cp-slide-color-picker-modal'
        )
      ) {
        return;
      }
      e.preventDefault();
      beginMarqueeSelectionAt(e.clientX, e.clientY, e.shiftKey);
    };
    host.addEventListener('pointerdown', onHostPointerDown);
    return () => host.removeEventListener('pointerdown', onHostPointerDown);
  }, [beginMarqueeSelectionAt, canEdit, canvasInnerEl, positionSyncRoot, viewMode]);

  const handleMultiSelectPointerDown = React.useCallback(
    (e: React.PointerEvent, kind: SlideSelectionKind, id: string): boolean => {
      if (!canEdit || !e.shiftKey) return false;
      e.preventDefault();
      e.stopPropagation();
      rootRef.current?.focus({ preventScroll: true });
      clearDeleteHoldTimer();
      toggleBlockSelection(kind, id);
      stopVideoPlaybackInEditModeRef.current();
      if (cropEditingId) exitCropMode();
      return true;
    },
    [canEdit, clearDeleteHoldTimer, cropEditingId, exitCropMode, toggleBlockSelection]
  );

  React.useEffect(() => {
    const existingKeys = new Set<string>();
    layout.boxes.forEach((box) => existingKeys.add(slideSelectionKey('text', box.id)));
    layout.images.forEach((image) => existingKeys.add(slideSelectionKey('image', image.id)));
    (layout.clipboardImages ?? []).forEach((image) =>
      existingKeys.add(slideSelectionKey('clipboardImage', image.id))
    );
    (layout.scanImages ?? []).forEach((image) => existingKeys.add(slideSelectionKey('scanImage', image.id)));
    layout.buttons.forEach((button) => existingKeys.add(slideSelectionKey('button', button.id)));
    (layout.shapes ?? []).forEach((shape) => existingKeys.add(slideSelectionKey('shape', shape.id)));
    (layout.tables ?? []).forEach((table) => existingKeys.add(slideSelectionKey('table', table.id)));
    (layout.polylines ?? []).forEach((pl) => existingKeys.add(slideSelectionKey('polyline', pl.id)));
    layout.videos.forEach((video) => existingKeys.add(slideSelectionKey('video', video.id)));
    (layout.webpages ?? []).forEach((wp) => existingKeys.add(slideSelectionKey('webpage', wp.id)));
    layout.quizzes.forEach((quiz) => existingKeys.add(slideSelectionKey('quiz', quiz.id)));
    layout.quizChoiceOptions.forEach((opt) => existingKeys.add(slideSelectionKey('quizChoice', opt.id)));

    const filtered = selectedItems.filter((item) => existingKeys.has(slideSelectionRefKey(item)));
    const selectedPrimaryKey = selectedId && selectedKind ? slideSelectionKey(selectedKind, selectedId) : '';
    const primaryStillExists = selectedPrimaryKey ? existingKeys.has(selectedPrimaryKey) : false;
    const shouldUpdateItems =
      filtered.length !== selectedItems.length ||
      filtered.some((item, index) => slideSelectionRefKey(item) !== slideSelectionRefKey(selectedItems[index] ?? item));

    if (shouldUpdateItems) {
      applySelectionState(filtered, primaryStillExists && selectedId && selectedKind ? { kind: selectedKind, id: selectedId } : null);
      if (!filtered.length) {
        setEditingId(null);
        setCropEditingId(null);
      }
      return;
    }
    if (filtered.length === 0 && (selectedId || selectedKind) && !selectedGroupId) {
      setSelectedId(null);
      setSelectedKind(null);
      setEditingId(null);
      setCropEditingId(null);
      return;
    }
    if (filtered.length > 0 && !primaryStillExists) {
      const fallback = filtered[filtered.length - 1];
      setSelectedId(fallback.id);
      setSelectedKind(fallback.kind);
      setEditingId(null);
    }
  }, [
    applySelectionState,
    layout.boxes,
    layout.buttons,
    layout.shapes,
    layout.tables,
    layout.clipboardImages,
    layout.scanImages,
    layout.images,
    layout.quizChoiceOptions,
    layout.quizzes,
    layout.videos,
    layout.webpages,
    selectedGroupId,
    selectedId,
    selectedItems,
    selectedKind,
  ]);

  const autofitAllBoxesToViewport = React.useCallback(() => {
    const root = rootRef.current;
    const current = layoutRef.current;
    if (!root || !current.boxes.length) return;
    const rootH = Math.max(1, root.getBoundingClientRect().height);
    let changed = false;
    const nextBoxes = current.boxes.map((box) => {
      const oneLineMinPx = Math.max(16, Math.round(box.fontSizePx * (box.lineHeight ?? 1.05) + 6));
      const measuredPx = measureBoxHeightPx(box);
      if (measuredPx == null) return box;
      const nextH = Math.min(1 - box.y, Math.max(Math.max(0.01, oneLineMinPx / rootH), (measuredPx + 2) / rootH));
      const nextHRounded = Math.round(nextH * 10000) / 10000;
      if (Math.abs(nextHRounded - box.h) < 0.002) return box;
      changed = true;
      return { ...box, h: nextHRounded };
    });
    if (!changed) return;
    applyLayoutChange({ v: 1, boxes: nextBoxes, images: current.images, buttons: current.buttons, videos: current.videos, quizzes: current.quizzes }, 'skip');
  }, [applyLayoutChange, measureBoxHeightPx]);

  React.useEffect(() => {
    // viewMode/readOnly(랜딩 소개 등)에서는 레이아웃을 변형하지 않음
    if (viewMode || readOnly) return;
    const root = rootRef.current;
    if (!root) return;
    let rafId: number | null = null;
    const scheduleAutofit = () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        autofitAllBoxesToViewport();
      });
    };
    scheduleAutofit();
    const observer = new ResizeObserver(() => {
      scheduleAutofit();
    });
    observer.observe(root);
    return () => {
      observer.disconnect();
      if (rafId != null) window.cancelAnimationFrame(rafId);
    };
  }, [autofitAllBoxesToViewport, viewMode, readOnly]);

  const enterEditMode = React.useCallback(
    (id: string) => {
      if (!canEdit) return;
      if (isSlideBlockLockedInLayout(layoutRef.current, 'text', id)) return;
      selectSingleBlockWithoutGroupUnitPromotion('text', id);
      setEditingId(id);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      beginGestureHistory();
      slideTextPlainSelectionRef.current = null;
      window.requestAnimationFrame(() => {
        const el = textBlockRefs.current[id];
        if (!el) return;
        el.focus();
        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      });
    },
    [beginGestureHistory, canEdit, exitCropMode, selectSingleBlockWithoutGroupUnitPromotion]
  );

  const prevTextEditSessionIdRef = React.useRef<string | null>(null);
  const prevTextEditThemeRef = React.useRef<typeof slideSurfaceTheme | null>(null);
  React.useLayoutEffect(() => {
    if (!canEdit || !editingId) {
      prevTextEditSessionIdRef.current = null;
      prevTextEditThemeRef.current = null;
      return;
    }
    if (!layout.boxes.some((b) => b.id === editingId)) {
      prevTextEditSessionIdRef.current = null;
      prevTextEditThemeRef.current = null;
      return;
    }
    if (prevTextEditSessionIdRef.current === editingId && prevTextEditThemeRef.current === slideSurfaceTheme) return;
    prevTextEditSessionIdRef.current = editingId;
    prevTextEditThemeRef.current = slideSurfaceTheme;
    const box = layout.boxes.find((b) => b.id === editingId);
    const el = textBlockRefs.current[editingId];
    if (box && el) {
      el.innerHTML = runsToEditableHtml(box.textRuns, box.text, box.colorHex, slideDisplayColors.fg);
    }
  }, [canEdit, editingId, layout, slideDisplayColors.fg]);

  const enterButtonEditMode = React.useCallback(
    (id: string) => {
      if (!canEdit) return;
      selectSingleBlockWithoutGroupUnitPromotion('button', id);
      setEditingId(id);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.requestAnimationFrame(() => {
        const el = textareaRefs.current[id];
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      });
    },
    [canEdit, exitCropMode, selectSingleBlockWithoutGroupUnitPromotion]
  );

  const enterShapeEditMode = React.useCallback(
    (id: string) => {
      if (!canEdit) return;
      if (isSlideBlockLockedInLayout(layoutRef.current, 'shape', id)) return;
      selectSingleBlockWithoutGroupUnitPromotion('shape', id);
      setEditingId(id);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.requestAnimationFrame(() => {
        const el = textareaRefs.current[id];
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      });
    },
    [canEdit, exitCropMode, selectSingleBlockWithoutGroupUnitPromotion]
  );

  const enterQuizPlaceholderEditMode = React.useCallback(
    (id: string) => {
      if (!canEdit) return;
      selectSingleBlockWithoutGroupUnitPromotion('quiz', id);
      setEditingId(id);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.requestAnimationFrame(() => {
        const el = quizPlaceholderInputRefs.current[id];
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      });
    },
    [canEdit, exitCropMode, selectSingleBlockWithoutGroupUnitPromotion]
  );

  const tryActivateCtrlCopyDuringMove = React.useCallback((): boolean => {
    const d = dragRef.current;
    if (!d || d.mode !== 'move' || d.ctrlCopyActive) return false;
    if (d.moveGroup && d.moveGroup.length > 1) return false;
    const kind = d.kind;
    if (
      kind !== 'text' &&
      kind !== 'shape' &&
      kind !== 'table' &&
      kind !== 'image' &&
      kind !== 'clipboardImage' &&
      kind !== 'scanImage' &&
      kind !== 'polyline'
    ) {
      return false;
    }
    const sourceId = d.id;
    if (kind === 'polyline' && d.polylinePoints0) {
      patchPolyline(
        sourceId,
        { points: d.polylinePoints0.map((p) => ({ x: p.x, y: p.y })) },
        'skip'
      );
    } else if (kind === 'image' || isSlideBitmapImageKind(kind)) {
      patchSlideImageByKind(sourceId, kind, { x: d.startX, y: d.startY }, 'skip');
    } else if (kind === 'shape') {
      patchShape(sourceId, { x: d.startX, y: d.startY }, 'skip');
    } else if (kind === 'table') {
      patchTable(sourceId, { x: d.startX, y: d.startY }, 'skip');
    } else if (kind === 'text') {
      patchBox(sourceId, { x: d.startX, y: d.startY }, 'skip');
    } else {
      return false;
    }
    const dup = duplicateBlockInPlaceForDrag(kind, sourceId);
    if (!dup) return false;
    d.id = dup.id;
    d.ctrlCopyActive = true;
    d.moveGroup = undefined;
    d.moveGroupBounds = undefined;
    d.keptGroupUnitSelection = false;
    const nx = d.latestX;
    const ny = d.latestY;
    if (kind === 'polyline' && d.polylinePoints0) {
      const ddx = nx - d.startX;
      const ddy = ny - d.startY;
      patchPolyline(
        d.id,
        {
          points: d.polylinePoints0.map((p) => ({
            x: frac4(p.x + ddx),
            y: frac4(p.y + ddy),
          })),
        },
        'skip'
      );
    } else if (kind === 'image' || isSlideBitmapImageKind(kind)) {
      patchSlideImageByKind(d.id, kind, { x: nx, y: ny }, 'skip');
    } else if (kind === 'shape') {
      patchShape(d.id, { x: nx, y: ny }, 'skip');
    } else if (kind === 'table') {
      patchTable(d.id, { x: nx, y: ny }, 'skip');
    } else {
      patchBox(d.id, { x: nx, y: ny }, 'skip');
    }
    selectSingleBlock(dup.kind, dup.id);
    return true;
  }, [
    duplicateBlockInPlaceForDrag,
    patchBox,
    patchPolyline,
    patchShape,
    patchSlideImageByKind,
    patchTable,
    selectSingleBlock,
  ]);

  const onPointerMove = React.useCallback(
    (e: PointerEvent) => {
      const d = dragRef.current;
      const root = rootRef.current;
      if (!d || !root) return;
      if (d.mode === 'move' && !ctrlCopyKeyListenerCleanupRef.current) {
        const onCtrlCopyKeyDown = (ev: KeyboardEvent) => {
          if (!(ev.ctrlKey || ev.metaKey) || ev.altKey) return;
          tryActivateCtrlCopyDuringMove();
        };
        window.addEventListener('keydown', onCtrlCopyKeyDown, true);
        ctrlCopyKeyListenerCleanupRef.current = () => {
          window.removeEventListener('keydown', onCtrlCopyKeyDown, true);
          ctrlCopyKeyListenerCleanupRef.current = null;
        };
      }
      if (d.mode === 'move' && (e.ctrlKey || e.metaKey) && !e.altKey) {
        tryActivateCtrlCopyDuringMove();
      }
      if (
        (d.mode === 'button-resize' || d.mode === 'image-resize' || d.mode === 'video-resize') &&
        typeof window !== 'undefined' &&
        !resizeShiftKeyListenerCleanupRef.current
      ) {
        const syncShiftFromKeyboard = (ev: KeyboardEvent) => {
          resizeShiftKeyboardRef.current =
            ev.shiftKey || (typeof ev.getModifierState === 'function' && ev.getModifierState('Shift'));
        };
        resizeShiftKeyboardRef.current = isShiftResizeModifier(e);
        window.addEventListener('keydown', syncShiftFromKeyboard, true);
        window.addEventListener('keyup', syncShiftFromKeyboard, true);
        resizeShiftKeyListenerCleanupRef.current = () => {
          window.removeEventListener('keydown', syncShiftFromKeyboard, true);
          window.removeEventListener('keyup', syncShiftFromKeyboard, true);
          resizeShiftKeyListenerCleanupRef.current = null;
          resizeShiftKeyboardRef.current = false;
        };
      }
      const shiftSq = isShiftResizeModifier(e) || resizeShiftKeyboardRef.current;
      const coordEl = canvasInnerEl ?? root;
      const { dx, dy } = clientDeltaToElementNorm(
        e.clientX,
        e.clientY,
        d.startClientX,
        d.startClientY,
        coordEl
      );
      if (d.mode !== 'move') {
        updateSmartSnapGuides(null, null);
      }
      if (d.mode === 'resize') {
        const minW = 0.06;
        const right = d.startX + d.w;
        let nextX = d.startX;
        let nextW = d.w;
        if (e.altKey && d.side) {
          const centered = resizeHorizontalSideFromCenter(d.startX, d.w, dx, d.side, minW);
          nextX = centered.x;
          nextW = centered.w;
        } else if (d.side === 'right') {
          nextW = Math.min(1 - d.startX, Math.max(minW, d.w + dx));
        } else if (d.side === 'left') {
          nextX = Math.min(right - minW, Math.max(0, d.startX + dx));
          nextW = right - nextX;
        }
        let guideX: number | null = null;
        if (smartSnapEnabled && d.side && !e.altKey) {
          const excludedKeys = new Set([slideSelectionKey('text', d.id)]);
          if (d.side === 'right') {
            const snap = getSmartSnappedXLine(nextX + nextW, excludedKeys, d.rootW);
            if (snap.guideX != null) {
              nextW = Math.max(minW, snap.line - nextX);
              guideX = snap.guideX;
            }
          } else {
            const snap = getSmartSnappedXLine(nextX, excludedKeys, d.rootW);
            if (snap.guideX != null) {
              nextX = Math.min(right - minW, Math.max(0, snap.line));
              nextW = right - nextX;
              guideX = snap.guideX;
            }
          }
        }
        d.latestX = nextX;
        d.latestW = nextW;
        updateSmartSnapGuides(guideX, null);
        patchBox(d.id, { x: nextX, w: nextW });
        return;
      }
      if (d.mode === 'button-resize' && d.corner) {
        const liveShapeForResize =
          d.kind === 'shape' ? (layoutRef.current.shapes ?? []).find((s) => s.id === d.id) : undefined;
        const minSize = getBlockMinSize(
          d.kind,
          d.kind === 'shape'
            ? { slideShapeKind: liveShapeForResize?.shape ?? d.shapeBlockKind }
            : undefined
        );
        const minW = minSize.minW;
        const minH = minSize.minH;
        const resizeShapeCircleShiftSquare =
          d.kind === 'shape' &&
          shiftSq &&
          (d.shapeBlockKind === 'circle' || liveShapeForResize?.shape === 'circle');
        const right = d.startX + d.w;
        const bottom = d.startY + d.h;
        let nextX = d.startX;
        let nextY = d.startY;
        let nextW = d.w;
        let nextH = d.h;
        if (e.altKey) {
          let centered: { x: number; y: number; w: number; h: number };
          if (resizeShapeCircleShiftSquare) {
            /** 화면 픽셀 기준 정사각(가로·세로 정규화 축이 달라 norm에서 w===h는 직사각형이 됨) */
            const rw = Math.max(1, d.rootW);
            const rh = Math.max(1, d.rootH);
            const cxN = d.startX + d.w / 2;
            const cyN = d.startY + d.h / 2;
            const minSpx = Math.max(minW * rw, minH * rh);
            const tentativeW =
              d.corner === 'nw' || d.corner === 'sw' ? Math.max(minW, d.w - 2 * dx) : Math.max(minW, d.w + 2 * dx);
            const tentativeH =
              d.corner === 'nw' || d.corner === 'ne' ? Math.max(minH, d.h - 2 * dy) : Math.max(minH, d.h + 2 * dy);
            const sPx = Math.max(minSpx, Math.max(tentativeW * rw, tentativeH * rh));
            centered = clampPixelCenteredSquareToNorm(cxN, cyN, sPx, rw, rh, minSpx);
          } else {
            centered = resizeCornerFromCenter(d.startX, d.startY, d.w, d.h, dx, dy, d.corner, minW, minH);
          }
          nextX = centered.x;
          nextY = centered.y;
          nextW = centered.w;
          nextH = centered.h;
        } else {
          if (d.corner === 'nw' || d.corner === 'sw') {
            nextX = Math.min(right - minW, Math.max(0, d.startX + dx));
            nextW = right - nextX;
          } else {
            nextW = Math.min(1 - d.startX, Math.max(minW, d.w + dx));
          }
          if (d.corner === 'nw' || d.corner === 'ne') {
            nextY = Math.min(bottom - minH, Math.max(0, d.startY + dy));
            nextH = bottom - nextY;
          } else {
            nextH = Math.min(1 - d.startY, Math.max(minH, d.h + dy));
          }
        }
        let guideX: number | null = null;
        let guideY: number | null = null;
        if (smartSnapEnabled && !e.altKey) {
          const excludedKeys = new Set([slideSelectionKey(d.kind, d.id)]);
          if (d.corner === 'nw' || d.corner === 'sw') {
            const snap = getSmartSnappedXLine(nextX, excludedKeys, d.rootW);
            if (snap.guideX != null) {
              nextX = Math.min(right - minW, Math.max(0, snap.line));
              nextW = right - nextX;
              guideX = snap.guideX;
            }
          } else {
            const snap = getSmartSnappedXLine(nextX + nextW, excludedKeys, d.rootW);
            if (snap.guideX != null) {
              nextW = Math.max(minW, snap.line - nextX);
              guideX = snap.guideX;
            }
          }
          if (d.corner === 'nw' || d.corner === 'ne') {
            const snap = getSmartSnappedYLine(nextY, excludedKeys, d.rootH);
            if (snap.guideY != null) {
              nextY = Math.min(bottom - minH, Math.max(0, snap.line));
              nextH = bottom - nextY;
              guideY = snap.guideY;
            }
          } else {
            const snap = getSmartSnappedYLine(nextY + nextH, excludedKeys, d.rootH);
            if (snap.guideY != null) {
              nextH = Math.max(minH, snap.line - nextY);
              guideY = snap.guideY;
            }
          }
        }
        if (resizeShapeCircleShiftSquare && !e.altKey && d.corner) {
          const sq = squareCornerResizeFromProposedRectVisualSquare(
            d.corner,
            d.startX,
            d.startY,
            d.w,
            d.h,
            nextX,
            nextY,
            nextW,
            nextH,
            d.rootW,
            d.rootH,
            minW,
            minH
          );
          nextX = sq.x;
          nextY = sq.y;
          nextW = sq.w;
          nextH = sq.h;
        }
        d.latestX = nextX;
        d.latestY = nextY;
        d.latestW = nextW;
        d.latestH = nextH;
        updateSmartSnapGuides(guideX, guideY);
        const patch = {
          x: Math.round(nextX * 10000) / 10000,
          y: Math.round(nextY * 10000) / 10000,
          w: Math.round(nextW * 10000) / 10000,
          h: Math.round(nextH * 10000) / 10000,
        };
        if (d.kind === 'quizChoice') {
          const setId =
            d.choiceSetId ?? layoutRef.current.quizChoiceOptions.find((opt) => opt.id === d.id)?.setId;
          if (setId && e.altKey && d.choiceSetBBox) {
            patchQuizChoiceSetFromCenter(setId, patch.w, patch.h, d.choiceSetBBox, d.w, d.h);
          } else if (setId && d.corner) patchQuizChoiceSetOptionGeometry(setId, d.id, patch, d.corner);
        } else if (d.kind === 'quiz') patchQuiz(d.id, patch);
        else if (d.kind === 'shape') patchShape(d.id, patch);
        else if (d.kind === 'table') patchTable(d.id, patch);
        else patchButton(d.id, patch);
        return;
      }
      if (d.mode === 'button-radius') {
        const deltaPx = e.clientX - d.startClientX;
        const nextRadius = Math.min(999, Math.max(0, Math.round((d.startRadiusPx ?? 999) + deltaPx)));
        if (d.kind === 'quizChoice' && d.choiceSetId) {
          patchQuizChoiceSetRadius(d.choiceSetId, nextRadius);
        } else if (d.kind === 'quiz') patchQuiz(d.id, { cornerRadiusPx: nextRadius });
        else if (d.kind === 'shape') patchShape(d.id, { cornerRadiusPx: nextRadius });
        else patchButton(d.id, { cornerRadiusPx: nextRadius });
        return;
      }
      if (d.mode === 'image-resize-x' && d.side) {
        const minW = getBlockMinSize(
          d.kind,
          d.kind === 'shape'
            ? {
                slideShapeKind:
                  (layoutRef.current.shapes ?? []).find((s) => s.id === d.id)?.shape ?? d.shapeBlockKind,
              }
            : undefined
        ).minW;
        const right = d.startX + d.w;
        let nextX = d.startX;
        let nextW = d.w;
        if (e.altKey) {
          const centered = resizeHorizontalSideFromCenter(d.startX, d.w, dx, d.side, minW);
          nextX = centered.x;
          nextW = centered.w;
        } else if (d.side === 'right') {
          nextW = Math.min(1 - d.startX, Math.max(minW, d.w + dx));
        } else {
          nextX = Math.min(right - minW, Math.max(0, d.startX + dx));
          nextW = right - nextX;
        }
        let guideX: number | null = null;
        if (smartSnapEnabled && !e.altKey) {
          const excludedKeys = new Set([slideSelectionKey(d.kind, d.id)]);
          if (d.side === 'right') {
            const snap = getSmartSnappedXLine(nextX + nextW, excludedKeys, d.rootW);
            if (snap.guideX != null) {
              nextW = Math.max(minW, snap.line - nextX);
              guideX = snap.guideX;
            }
          } else {
            const snap = getSmartSnappedXLine(nextX, excludedKeys, d.rootW);
            if (snap.guideX != null) {
              nextX = Math.min(right - minW, Math.max(0, snap.line));
              nextW = right - nextX;
              guideX = snap.guideX;
            }
          }
        }
        d.latestX = nextX;
        d.latestW = nextW;
        updateSmartSnapGuides(guideX, null);
        if (d.kind === 'webpage') {
          patchWebpage(d.id, { x: nextX, w: nextW });
        } else if (d.kind === 'button') {
          patchButton(d.id, { x: nextX, w: nextW });
        } else if (d.kind === 'shape') {
          patchShape(d.id, { x: nextX, w: nextW });
        } else {
          const imgKind: SlideImageBlockKind = isSlideBitmapImageKind(d.kind) ? d.kind : 'image';
          const current = findSlideImageBlock(layoutRef.current, imgKind, d.id);
          const hasNatural = current && current.naturalW > 0 && current.naturalH > 0;
          if (hasNatural && d.w > 0) {
            const m0 = d.startAspectWMult ?? 1;
            const nextM = Math.min(20, Math.max(0.05, Math.round(m0 * (nextW / d.w) * 10000) / 10000));
            patchSlideImageByKind(d.id, imgKind, { x: nextX, w: nextW, aspectWMultiplier: nextM });
          } else {
            patchSlideImageByKind(d.id, imgKind, { x: nextX, w: nextW });
          }
        }
        return;
      }
      if (d.mode === 'image-resize-y' && d.vSide) {
        const minH = 0.04;
        const dyN = (e.clientY - d.startClientY) / Math.max(1, d.rootH);
        const bottom = d.startY + d.h;
        let nextY = d.startY;
        let nextH = d.h;
        if (d.vSide === 'bottom') {
          nextH = Math.min(1 - d.startY, Math.max(minH, d.h + dyN));
        } else {
          nextY = Math.min(bottom - minH, Math.max(0, d.startY + dyN));
          nextH = bottom - nextY;
        }
        // 좌우 핸들과 같은 스마트 스냅(가이드 선 표시)
        let guideY: number | null = null;
        if (smartSnapEnabled && !e.altKey) {
          const excludedKeys = new Set([slideSelectionKey(d.kind, d.id)]);
          if (d.vSide === 'bottom') {
            const snap = getSmartSnappedYLine(nextY + nextH, excludedKeys, d.rootH);
            if (snap.guideY != null) {
              nextH = Math.min(1 - nextY, Math.max(minH, snap.line - nextY));
              guideY = snap.guideY;
            }
          } else {
            const snap = getSmartSnappedYLine(nextY, excludedKeys, d.rootH);
            if (snap.guideY != null) {
              nextY = Math.min(bottom - minH, Math.max(0, snap.line));
              nextH = bottom - nextY;
              guideY = snap.guideY;
            }
          }
        }
        updateSmartSnapGuides(null, guideY);
        d.latestY = nextY;
        d.latestH = nextH;
        if (d.kind === 'webpage') {
          patchWebpage(d.id, { y: nextY, h: nextH });
        } else if (d.kind === 'shape') {
          patchShape(d.id, { y: nextY, h: nextH });
        } else {
          // 이미지: 프레임 세로만 변경, aspectWMultiplier 유지 → 원본 비율로 contain(왜곡 없음)
          const imgKind: SlideImageBlockKind = isSlideBitmapImageKind(d.kind) ? d.kind : 'image';
          patchSlideImageByKind(d.id, imgKind, { y: nextY, h: nextH });
        }
        return;
      }
      if (d.mode === 'image-resize' && d.corner) {
        const minW = 0.06;
        const minH = 0.06;
        const aspect = Math.max(0.1, d.w / Math.max(0.001, d.h));
        const right = d.startX + d.w;
        const bottom = d.startY + d.h;
        let nextW = d.w;
        let nextH = d.h;
        let nextX = d.startX;
        let nextY = d.startY;

        if (e.altKey) {
          if (shiftSq) {
            const side = Math.max(d.w, d.h);
            const centered = resizeCornerFromCenterAspect(
              d.startX,
              d.startY,
              side,
              side,
              dx,
              dy,
              d.corner,
              minW,
              minH
            );
            nextX = centered.x;
            nextY = centered.y;
            nextW = centered.w;
            nextH = centered.h;
          } else {
            const centered = resizeCornerFromCenterAspect(
              d.startX,
              d.startY,
              d.w,
              d.h,
              dx,
              dy,
              d.corner,
              minW,
              minH
            );
            nextX = centered.x;
            nextY = centered.y;
            nextW = centered.w;
            nextH = centered.h;
          }
        } else if (shiftSq) {
          const free = freeCornerResizeNoAspect(d.corner, d.startX, d.startY, d.w, d.h, dx, dy, minW, minH);
          const minS = Math.max(minW, minH);
          const sq = squareCornerResizeFromProposedRect(
            d.corner,
            d.startX,
            d.startY,
            d.w,
            d.h,
            free.x,
            free.y,
            free.w,
            free.h,
            minS
          );
          nextX = sq.x;
          nextY = sq.y;
          nextW = sq.w;
          nextH = sq.h;
        } else {
          const horizontalW =
            d.corner === 'nw' || d.corner === 'sw'
              ? Math.max(minW, right - Math.max(0, d.startX + dx))
              : Math.max(minW, d.w + dx);
          const verticalH =
            d.corner === 'nw' || d.corner === 'ne'
              ? Math.max(minH, bottom - Math.max(0, d.startY + dy))
              : Math.max(minH, d.h + dy);
          nextW = Math.max(horizontalW, verticalH * aspect);
          nextH = nextW / aspect;
          if (d.corner === 'nw' || d.corner === 'sw') nextW = Math.min(nextW, right);
          else nextW = Math.min(nextW, 1 - d.startX);
          nextH = nextW / aspect;
          if (d.corner === 'nw' || d.corner === 'ne') nextH = Math.min(nextH, bottom);
          else nextH = Math.min(nextH, 1 - d.startY);
          nextW = nextH * aspect;
          nextX = d.corner === 'nw' || d.corner === 'sw' ? right - nextW : d.startX;
          nextY = d.corner === 'nw' || d.corner === 'ne' ? bottom - nextH : d.startY;
        }
        let guideX: number | null = null;
        let guideY: number | null = null;
        if (smartSnapEnabled && !e.altKey) {
          const snapAspect = shiftSq ? 1 : aspect;
          const snapped = getSmartSnappedAspectResize({
            rawX: nextX,
            rawY: nextY,
            rawW: nextW,
            rawH: nextH,
            corner: d.corner,
            startLeft: d.startX,
            startTop: d.startY,
            startRight: right,
            startBottom: bottom,
            aspect: snapAspect,
            minW: snapAspect === 1 ? Math.max(minW, minH) : Math.max(minW, minH * aspect),
            excludedKeys: new Set([slideSelectionKey(d.kind, d.id)]),
            rootW: d.rootW,
            rootH: d.rootH,
          });
          nextX = snapped.x;
          nextY = snapped.y;
          nextW = snapped.w;
          nextH = snapped.h;
          guideX = snapped.guideX;
          guideY = snapped.guideY;
        }
        d.latestX = nextX;
        d.latestY = nextY;
        d.latestW = nextW;
        d.latestH = nextH;
        updateSmartSnapGuides(guideX, guideY);
        if (d.kind === 'webpage') {
          patchWebpage(d.id, { x: nextX, y: nextY, w: nextW, h: nextH });
        } else {
          patchSlideImageByKind(d.id, isSlideBitmapImageKind(d.kind) ? d.kind : 'image', {
            x: nextX,
            y: nextY,
            w: nextW,
            h: nextH,
          });
        }
        return;
      }
      if (d.mode === 'video-resize' && d.corner) {
        const minW = 0.08;
        const minH = 0.06;
        const aspect = Math.max(0.1, d.w / Math.max(0.001, d.h));
        const right = d.startX + d.w;
        const bottom = d.startY + d.h;
        let nextW = d.w;
        let nextH = d.h;
        let nextX = d.startX;
        let nextY = d.startY;

        if (e.altKey) {
          if (shiftSq) {
            const side = Math.max(d.w, d.h);
            const centered = resizeCornerFromCenterAspect(
              d.startX,
              d.startY,
              side,
              side,
              dx,
              dy,
              d.corner,
              minW,
              minH
            );
            nextX = centered.x;
            nextY = centered.y;
            nextW = centered.w;
            nextH = centered.h;
          } else {
            const centered = resizeCornerFromCenterAspect(
              d.startX,
              d.startY,
              d.w,
              d.h,
              dx,
              dy,
              d.corner,
              minW,
              minH
            );
            nextX = centered.x;
            nextY = centered.y;
            nextW = centered.w;
            nextH = centered.h;
          }
        } else if (shiftSq) {
          const free = freeCornerResizeNoAspect(d.corner, d.startX, d.startY, d.w, d.h, dx, dy, minW, minH);
          const minS = Math.max(minW, minH);
          const sq = squareCornerResizeFromProposedRect(
            d.corner,
            d.startX,
            d.startY,
            d.w,
            d.h,
            free.x,
            free.y,
            free.w,
            free.h,
            minS
          );
          nextX = sq.x;
          nextY = sq.y;
          nextW = sq.w;
          nextH = sq.h;
        } else {
          const horizontalW =
            d.corner === 'nw' || d.corner === 'sw'
              ? Math.max(minW, right - Math.max(0, d.startX + dx))
              : Math.max(minW, d.w + dx);
          const verticalH =
            d.corner === 'nw' || d.corner === 'ne'
              ? Math.max(minH, bottom - Math.max(0, d.startY + dy))
              : Math.max(minH, d.h + dy);
          nextW = Math.max(horizontalW, verticalH * aspect);
          nextH = nextW / aspect;
          if (d.corner === 'nw' || d.corner === 'sw') nextW = Math.min(nextW, right);
          else nextW = Math.min(nextW, 1 - d.startX);
          nextH = nextW / aspect;
          if (d.corner === 'nw' || d.corner === 'ne') nextH = Math.min(nextH, bottom);
          else nextH = Math.min(nextH, 1 - d.startY);
          nextW = nextH * aspect;
          nextX = d.corner === 'nw' || d.corner === 'sw' ? right - nextW : d.startX;
          nextY = d.corner === 'nw' || d.corner === 'ne' ? bottom - nextH : d.startY;
        }
        let guideX: number | null = null;
        let guideY: number | null = null;
        if (smartSnapEnabled && !e.altKey) {
          const snapAspect = shiftSq ? 1 : aspect;
          const snapped = getSmartSnappedAspectResize({
            rawX: nextX,
            rawY: nextY,
            rawW: nextW,
            rawH: nextH,
            corner: d.corner,
            startLeft: d.startX,
            startTop: d.startY,
            startRight: right,
            startBottom: bottom,
            aspect: snapAspect,
            minW: snapAspect === 1 ? Math.max(minW, minH) : Math.max(minW, minH * aspect),
            excludedKeys: new Set([slideSelectionKey('video', d.id)]),
            rootW: d.rootW,
            rootH: d.rootH,
          });
          nextX = snapped.x;
          nextY = snapped.y;
          nextW = snapped.w;
          nextH = snapped.h;
          guideX = snapped.guideX;
          guideY = snapped.guideY;
        }
        d.latestX = nextX;
        d.latestY = nextY;
        d.latestW = nextW;
        d.latestH = nextH;
        updateSmartSnapGuides(guideX, guideY);
        patchVideo(d.id, { x: nextX, y: nextY, w: nextW, h: nextH });
        return;
      }
      if (d.mode === 'crop-move' && d.crop) {
        const nextCrop = {
          ...d.crop,
          x: Math.min(1 - d.crop.w, Math.max(0, d.crop.x + dx)),
          y: Math.min(1 - d.crop.h, Math.max(0, d.crop.y + dy)),
        };
        patchSlideImageByKind(d.id, isSlideBitmapImageKind(d.kind) ? d.kind : 'image', { crop: nextCrop });
        return;
      }
      if (d.mode === 'crop-resize' && d.crop) {
        const minCrop = 0.05;
        const cropRight = d.crop.x + d.crop.w;
        const cropBottom = d.crop.y + d.crop.h;
        let nextX = d.crop.x;
        let nextY = d.crop.y;
        let nextW = d.crop.w;
        let nextH = d.crop.h;
        if (d.corner === 'nw' || d.corner === 'sw') {
          nextX = Math.min(cropRight - minCrop, Math.max(0, d.crop.x + dx));
          nextW = cropRight - nextX;
        }
        if (d.corner === 'ne' || d.corner === 'se') {
          nextW = Math.min(1 - d.crop.x, Math.max(minCrop, d.crop.w + dx));
        }
        if (d.corner === 'nw' || d.corner === 'ne') {
          nextY = Math.min(cropBottom - minCrop, Math.max(0, d.crop.y + dy));
          nextH = cropBottom - nextY;
        }
        if (d.corner === 'sw' || d.corner === 'se') {
          nextH = Math.min(1 - d.crop.y, Math.max(minCrop, d.crop.h + dy));
        }
        patchSlideImageByKind(d.id, isSlideBitmapImageKind(d.kind) ? d.kind : 'image', {
          crop: {
            x: Math.round(nextX * 10000) / 10000,
            y: Math.round(nextY * 10000) / 10000,
            w: Math.round(nextW * 10000) / 10000,
            h: Math.round(nextH * 10000) / 10000,
          },
        });
        return;
      }
      if (d.mode === 'group-resize' && d.moveGroup && d.moveGroup.length > 1 && (d.corner || d.side)) {
        const startLeft = d.startX;
        const startTop = d.startY;
        const startRight = d.startX + d.w;
        const startBottom = d.startY + d.h;
        const layoutForGroupMin = layoutRef.current;
        const shapeMinOpts = (itemId: string) => ({
          slideShapeKind: (layoutForGroupMin.shapes ?? []).find((s) => s.id === itemId)?.shape,
        });
        const minWidth = Math.max(
          0.04,
          ...d.moveGroup.map(
            (item) =>
              (getBlockMinSize(item.kind, item.kind === 'shape' ? shapeMinOpts(item.id) : undefined).minW /
                Math.max(0.0001, item.w)) *
              d.w
          )
        );
        const verticalResizable = d.moveGroup.filter((item) => item.kind !== 'text');
        const minHeight = Math.max(
          0.04,
          ...(verticalResizable.length
            ? verticalResizable.map(
                (item) =>
                  (getBlockMinSize(item.kind, item.kind === 'shape' ? shapeMinOpts(item.id) : undefined).minH /
                    Math.max(0.0001, item.h)) *
                  d.h
              )
            : [0.04])
        );

        let nextLeft = startLeft;
        let nextTop = startTop;
        let nextWidth = d.w;
        let nextHeight = d.h;

        if (e.altKey && d.side) {
          const centered = resizeHorizontalSideFromCenter(startLeft, d.w, dx, d.side, minWidth);
          nextLeft = centered.x;
          nextWidth = centered.w;
          nextHeight = d.h;
          nextTop = startTop;
        } else if (d.side) {
          if (d.side === 'left') {
            nextLeft = Math.min(startRight - minWidth, Math.max(0, startLeft + dx));
            nextWidth = startRight - nextLeft;
          } else {
            nextWidth = Math.max(minWidth, Math.min(1 - startLeft, d.w + dx));
            nextLeft = startLeft;
          }
          nextHeight = d.h;
          nextTop = startTop;
        } else if (e.altKey && d.corner) {
          const aspect = Math.max(0.1, d.w / Math.max(0.001, d.h));
          const resolvedMinWidth = Math.max(minWidth, minHeight * aspect);
          const resolvedMinHeight = resolvedMinWidth / aspect;
          const centered = resizeCornerFromCenterAspect(
            startLeft,
            startTop,
            d.w,
            d.h,
            dx,
            dy,
            d.corner,
            resolvedMinWidth,
            resolvedMinHeight
          );
          nextLeft = centered.x;
          nextTop = centered.y;
          nextWidth = centered.w;
          nextHeight = centered.h;
        } else if (d.corner) {
          const aspect = Math.max(0.1, d.w / Math.max(0.001, d.h));
          const resolvedMinWidth = Math.max(minWidth, minHeight * aspect);
          const resolvedMinHeight = resolvedMinWidth / aspect;
          const horizontalW =
            d.corner === 'nw' || d.corner === 'sw'
              ? Math.max(resolvedMinWidth, startRight - Math.max(0, startLeft + dx))
              : Math.max(resolvedMinWidth, d.w + dx);
          const verticalH =
            d.corner === 'nw' || d.corner === 'ne'
              ? Math.max(resolvedMinHeight, startBottom - Math.max(0, startTop + dy))
              : Math.max(resolvedMinHeight, d.h + dy);
          nextWidth = Math.max(horizontalW, verticalH * aspect);
          nextHeight = nextWidth / aspect;
          if (d.corner === 'nw' || d.corner === 'sw') nextWidth = Math.min(nextWidth, startRight);
          else nextWidth = Math.min(nextWidth, 1 - startLeft);
          nextHeight = nextWidth / aspect;
          if (d.corner === 'nw' || d.corner === 'ne') nextHeight = Math.min(nextHeight, startBottom);
          else nextHeight = Math.min(nextHeight, 1 - startTop);
          nextWidth = nextHeight * aspect;
          nextLeft = d.corner === 'nw' || d.corner === 'sw' ? startRight - nextWidth : startLeft;
          nextTop = d.corner === 'nw' || d.corner === 'ne' ? startBottom - nextHeight : startTop;
        }
        let guideX: number | null = null;
        let guideY: number | null = null;
        if (smartSnapEnabled && !e.altKey) {
          const excludedKeys = new Set(d.moveGroup.map((item) => slideSelectionKey(item.kind, item.id)));
          if (d.side) {
            if (d.side === 'left') {
              const snap = getSmartSnappedXLine(nextLeft, excludedKeys, d.rootW);
              if (snap.guideX != null) {
                nextLeft = Math.min(startRight - minWidth, Math.max(0, snap.line));
                nextWidth = startRight - nextLeft;
                guideX = snap.guideX;
              }
            } else {
              const snap = getSmartSnappedXLine(nextLeft + nextWidth, excludedKeys, d.rootW);
              if (snap.guideX != null) {
                nextWidth = Math.max(minWidth, snap.line - nextLeft);
                guideX = snap.guideX;
              }
            }
          } else if (d.corner) {
            const aspect = Math.max(0.1, d.w / Math.max(0.001, d.h));
            const snapped = getSmartSnappedAspectResize({
              rawX: nextLeft,
              rawY: nextTop,
              rawW: nextWidth,
              rawH: nextHeight,
              corner: d.corner,
              startLeft,
              startTop,
              startRight,
              startBottom,
              aspect,
              minW: Math.max(minWidth, minHeight * aspect),
              excludedKeys,
              rootW: d.rootW,
              rootH: d.rootH,
            });
            nextLeft = snapped.x;
            nextTop = snapped.y;
            nextWidth = snapped.w;
            nextHeight = snapped.h;
            guideX = snapped.guideX;
            guideY = snapped.guideY;
          }
        }

        const scaleX = nextWidth / Math.max(0.0001, d.w);
        const scaleY = nextHeight / Math.max(0.0001, d.h);
        d.latestX = nextLeft;
        d.latestY = nextTop;
        d.latestW = nextWidth;
        d.latestH = nextHeight;
        updateSmartSnapGuides(guideX, guideY);

        const moveMap = new Map<
          string,
          {
            x: number;
            y: number;
            w: number;
            h: number;
          }
        >();
        d.moveGroup.forEach((item) => {
          const key = slideSelectionKey(item.kind, item.id);
          const nextX = frac4(nextLeft + (item.x - startLeft) * scaleX);
          const minForItem = getBlockMinSize(
            item.kind,
            item.kind === 'shape' ? shapeMinOpts(item.id) : undefined
          );
          const nextWidthForItem = frac4(Math.max(minForItem.minW, item.w * scaleX));
          if (item.kind === 'text') {
            const nextY = frac4(Math.min(1 - item.h, Math.max(0, nextTop + (item.y - startTop) * scaleY)));
            moveMap.set(key, {
              x: Math.min(1 - nextWidthForItem, Math.max(0, nextX)),
              y: nextY,
              w: nextWidthForItem,
              h: item.h,
            });
            return;
          }
          const nextY = frac4(nextTop + (item.y - startTop) * scaleY);
          const nextHeightForItem = frac4(Math.max(minForItem.minH, item.h * scaleY));
          moveMap.set(key, {
            x: Math.min(1 - nextWidthForItem, Math.max(0, nextX)),
            y: Math.min(1 - nextHeightForItem, Math.max(0, nextY)),
            w: nextWidthForItem,
            h: nextHeightForItem,
          });
        });

        const current = layoutRef.current;
        applyLayoutChange({
          v: 1,
          boxes: current.boxes.map((box) => {
            const next = moveMap.get(slideSelectionKey('text', box.id));
            return next ? { ...box, x: next.x, y: next.y, w: next.w } : box;
          }),
          images: current.images.map((image) => {
            const next = moveMap.get(slideSelectionKey('image', image.id));
            return next ? { ...image, x: next.x, y: next.y, w: next.w, h: next.h } : image;
          }),
          clipboardImages: (current.clipboardImages ?? []).map((image) => {
            const next = moveMap.get(slideSelectionKey('clipboardImage', image.id));
            return next ? { ...image, x: next.x, y: next.y, w: next.w, h: next.h } : image;
          }),
          scanImages: (current.scanImages ?? []).map((image) => {
            const next = moveMap.get(slideSelectionKey('scanImage', image.id));
            return next ? { ...image, x: next.x, y: next.y, w: next.w, h: next.h } : image;
          }),
          buttons: current.buttons.map((button) => {
            const next = moveMap.get(slideSelectionKey('button', button.id));
            return next ? { ...button, x: next.x, y: next.y, w: next.w, h: next.h } : button;
          }),
          shapes: (current.shapes ?? []).map((shape) => {
            const next = moveMap.get(slideSelectionKey('shape', shape.id));
            return next ? { ...shape, x: next.x, y: next.y, w: next.w, h: next.h } : shape;
          }),
          polylines: (current.polylines ?? []).map((pl) => {
            const key = slideSelectionKey('polyline', pl.id);
            const item = (d.moveGroup ?? []).find((g) => slideSelectionKey(g.kind, g.id) === key);
            const next = moveMap.get(key);
            if (!item || !next || item.kind !== 'polyline') return pl;
            const startPoints = item.polylinePoints ?? pl.points;
            return {
              ...pl,
              points: scalePolylinePointsFromBox(startPoints, item, next),
            };
          }),
          videos: current.videos.map((video) => {
            const next = moveMap.get(slideSelectionKey('video', video.id));
            return next ? { ...video, x: next.x, y: next.y, w: next.w, h: next.h } : video;
          }),
          tables: (current.tables ?? []).map((table) => {
            const next = moveMap.get(slideSelectionKey('table', table.id));
            return next ? { ...table, x: next.x, y: next.y, w: next.w, h: next.h } : table;
          }),
          webpages: (current.webpages ?? []).map((wp) => {
            const next = moveMap.get(slideSelectionKey('webpage', wp.id));
            return next ? { ...wp, x: next.x, y: next.y, w: next.w, h: next.h } : wp;
          }),
          quizzes: current.quizzes.map((quiz) => {
            const next = moveMap.get(slideSelectionKey('quiz', quiz.id));
            return next ? { ...quiz, x: next.x, y: next.y, w: next.w, h: next.h } : quiz;
          }),
          quizChoiceOptions: current.quizChoiceOptions.map((opt) => {
            const next = moveMap.get(slideSelectionKey('quizChoice', opt.id));
            return next ? { ...opt, x: next.x, y: next.y, w: next.w, h: next.h } : opt;
          }),
        }, 'skip');
        return;
      }
      if (d.mode === 'move' && d.moveGroup && d.moveGroup.length > 1 && d.moveGroupBounds) {
        const bounds = contentBoundsRef.current;
        const boundedDx = Math.min(1 - d.moveGroupBounds.maxRight, Math.max(-d.moveGroupBounds.minX, dx));
        const boundedDy = Math.min(
          bounds.bottom - d.moveGroupBounds.maxBottom,
          Math.max(bounds.top - d.moveGroupBounds.minY, dy)
        );
        const groupWidth = d.moveGroupBounds.maxRight - d.moveGroupBounds.minX;
        const groupHeight = d.moveGroupBounds.maxBottom - d.moveGroupBounds.minY;
        let snappedDx = boundedDx;
        let snappedDy = boundedDy;
        if (smartSnapEnabled) {
          const snapped = getSmartSnappedMoveBounds(
            {
              left: d.moveGroupBounds.minX + boundedDx,
              top: d.moveGroupBounds.minY + boundedDy,
              right: d.moveGroupBounds.minX + boundedDx + groupWidth,
              bottom: d.moveGroupBounds.minY + boundedDy + groupHeight,
              width: groupWidth,
              height: groupHeight,
            },
            new Set(d.moveGroup.map((item) => slideSelectionKey(item.kind, item.id))),
            d.rootW,
            d.rootH
          );
          snappedDx = snapped.left - d.moveGroupBounds.minX;
          snappedDy = snapped.top - d.moveGroupBounds.minY;
          updateSmartSnapGuides(snapped.guideX, snapped.guideY);
        } else {
          updateSmartSnapGuides(null, null);
        }
        d.latestX = Math.min(1 - d.w, Math.max(0, d.startX + snappedDx));
        d.latestY = Math.min(
          contentBoundsRef.current.bottom - d.h,
          Math.max(contentBoundsRef.current.top, d.startY + snappedDy)
        );
        const patch = buildTranslatePatchFromMoveGroup(
          layoutRef.current,
          d.moveGroup,
          snappedDx,
          snappedDy
        );
        if (patch) applyLayoutChange(patch, 'skip');
        return;
      }
      let nx = Math.min(1 - d.w, Math.max(0, d.startX + dx));
      let ny = Math.min(
        contentBoundsRef.current.bottom - d.h,
        Math.max(contentBoundsRef.current.top, d.startY + dy)
      );
      if (smartSnapEnabled) {
        const snapped = getSmartSnappedMoveBounds(
          {
            left: nx,
            top: ny,
            right: nx + d.w,
            bottom: ny + d.h,
            width: d.w,
            height: d.h,
          },
          new Set([slideSelectionKey(d.kind, d.id)]),
          d.rootW,
          d.rootH
        );
        nx = snapped.left;
        ny = snapped.top;
        updateSmartSnapGuides(snapped.guideX, snapped.guideY);
      } else {
        updateSmartSnapGuides(null, null);
      }
      d.latestX = nx;
      d.latestY = ny;
      if (d.kind === 'image' || isSlideBitmapImageKind(d.kind)) {
        patchSlideImageByKind(d.id, d.kind, { x: nx, y: ny });
      } else if (d.kind === 'button') {
        patchButton(d.id, { x: nx, y: ny });
      } else if (d.kind === 'shape') {
        patchShape(d.id, { x: nx, y: ny });
      } else if (d.kind === 'table') {
        patchTable(d.id, { x: nx, y: ny });
      } else if (d.kind === 'polyline' && d.polylinePoints0) {
        const ddx = nx - d.startX;
        const ddy = ny - d.startY;
        patchPolyline(d.id, {
          points: d.polylinePoints0.map((p) => ({ x: frac4(p.x + ddx), y: frac4(p.y + ddy) })),
        });
      } else if (d.kind === 'video') {
        patchVideo(d.id, { x: nx, y: ny });
      } else if (d.kind === 'webpage') {
        patchWebpage(d.id, { x: nx, y: ny });
      } else if (d.kind === 'quiz') {
        patchQuiz(d.id, { x: nx, y: ny });
      } else if (d.kind === 'quizChoice') {
        patchQuizChoiceOption(d.id, { x: nx, y: ny });
      } else {
        patchBox(d.id, { x: nx, y: ny });
      }
    },
    [
      getSmartSnappedAspectResize,
      getSmartSnappedMoveBounds,
      getSmartSnappedXLine,
      getSmartSnappedYLine,
      tryActivateCtrlCopyDuringMove,
      patchBox,
      patchButton,
      patchShape,
      patchTable,
      patchPolyline,
      patchImage,
      patchSlideImageByKind,
      patchClipboardImage,
      patchQuiz,
      patchWebpage,
      patchQuizChoiceOption,
      patchQuizChoiceSetOptionGeometry,
      patchQuizChoiceSetFromCenter,
      patchQuizChoiceSetRadius,
      patchVideo,
      smartSnapEnabled,
      updateSmartSnapGuides,
      canvasInnerEl,
    ]
  );

  const endDrag = React.useCallback(() => {
    resizeShiftKeyListenerCleanupRef.current?.();
    resizeShiftKeyListenerCleanupRef.current = null;
    resizeShiftKeyboardRef.current = false;
    ctrlCopyKeyListenerCleanupRef.current?.();
    ctrlCopyKeyListenerCleanupRef.current = null;
    const d = dragRef.current;
    dragRef.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    window.removeEventListener('pointercancel', endDrag);
    updateSmartSnapGuides(null, null);
    if (d?.mode === 'resize') {
      window.requestAnimationFrame(() => adjustBoxHeightToContent(d.id));
    }
    if (d?.mode === 'group-resize' && d.moveGroup?.length) {
      const textIds = d.moveGroup.filter((item) => item.kind === 'text').map((item) => item.id);
      if (textIds.length) {
        window.requestAnimationFrame(() => {
          textIds.forEach((id) => adjustBoxHeightToContent(id));
        });
      }
    }
    if (d?.mode === 'move' && d.keptGroupUnitSelection) {
      const movePx = Math.hypot((d.latestX - d.startX) * d.rootW, (d.latestY - d.startY) * d.rootH);
      if (movePx < 4) {
        applySelectionState([{ kind: d.kind, id: d.id }], { kind: d.kind, id: d.id });
      }
    }
    commitGestureHistory();
  }, [adjustBoxHeightToContent, applySelectionState, commitGestureHistory, onPointerMove, updateSmartSnapGuides]);

  const startDrag = React.useCallback(
    (e: React.PointerEvent, box: ProgramSlideTextBoxV1) => {
      if (!canEdit) return;
      if (box.locked) {
        e.preventDefault();
        e.stopPropagation();
        selectSingleBlock('text', box.id);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      let dragBox = box;
      if (e.ctrlKey || e.metaKey) {
        const dup = duplicateBlockInPlaceForDrag('text', box.id);
        if (dup) {
          const found = layoutRef.current.boxes.find((b) => b.id === dup.id);
          if (found) dragBox = found;
        }
      }
      const textCtrlCopyActive = dragBox.id !== box.id;
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      const moveGroup = getSelectedMoveGroup('text', dragBox.id);
      const keepGroupUnitSel = moveGroup.length > 1 && shouldKeepSlideGroupUnitSelectionOnDrag('text', dragBox.id);
      dragRef.current = {
        id: dragBox.id,
        mode: 'move',
        kind: 'text',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: dragBox.x,
        startY: dragBox.y,
        w: dragBox.w,
        h: dragBox.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: dragBox.x,
        latestY: dragBox.y,
        latestW: dragBox.w,
        latestH: dragBox.h,
        ctrlCopyActive: textCtrlCopyActive,
        moveGroup: moveGroup.length > 1 ? moveGroup : undefined,
        moveGroupBounds:
          moveGroup.length > 1
            ? {
                minX: Math.min(...moveGroup.map((item) => item.x)),
                minY: Math.min(...moveGroup.map((item) => item.y)),
                maxRight: Math.max(...moveGroup.map((item) => item.x + item.w)),
                maxBottom: Math.max(...moveGroup.map((item) => item.y + item.h)),
              }
            : undefined,
        keptGroupUnitSelection: keepGroupUnitSel,
      };
      if (moveGroup.length > 1) {
        if (!keepGroupUnitSel) {
          applySelectionState(
            moveGroup.map((g) => ({ kind: g.kind, id: g.id })),
            { kind: 'text', id: dragBox.id }
          );
        }
      } else {
        if (!shouldSkipSelectSingleOnDragStart('text', dragBox.id)) {
          selectSingleBlock('text', dragBox.id);
        }
      }
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [
      applySelectionState,
      beginGestureHistory,
      canEdit,
      duplicateBlockInPlaceForDrag,
      endDrag,
      exitCropMode,
      getSelectedMoveGroup,
      onPointerMove,
      selectSingleBlock,
      shouldKeepSlideGroupUnitSelectionOnDrag,
      shouldSkipSelectSingleOnDragStart,
    ]
  );

  const startQuizDrag = React.useCallback(
    (e: React.PointerEvent, quiz: ProgramSlideQuizShortAnswerV1) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const r = root.getBoundingClientRect();
      const moveGroup = getSelectedMoveGroup('quiz', quiz.id);
      const keepGroupUnitSel = moveGroup.length > 1 && shouldKeepSlideGroupUnitSelectionOnDrag('quiz', quiz.id);
      dragRef.current = {
        id: quiz.id,
        mode: 'move',
        kind: 'quiz',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: quiz.x,
        startY: quiz.y,
        w: quiz.w,
        h: quiz.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: quiz.x,
        latestY: quiz.y,
        latestW: quiz.w,
        latestH: quiz.h,
        moveGroup: moveGroup.length > 1 ? moveGroup : undefined,
        moveGroupBounds:
          moveGroup.length > 1
            ? {
                minX: Math.min(...moveGroup.map((item) => item.x)),
                minY: Math.min(...moveGroup.map((item) => item.y)),
                maxRight: Math.max(...moveGroup.map((item) => item.x + item.w)),
                maxBottom: Math.max(...moveGroup.map((item) => item.y + item.h)),
              }
            : undefined,
        keptGroupUnitSelection: keepGroupUnitSel,
      };
      if (moveGroup.length > 1) {
        if (!keepGroupUnitSel) {
          applySelectionState(
            moveGroup.map((g) => ({ kind: g.kind, id: g.id })),
            { kind: 'quiz', id: quiz.id }
          );
        }
      } else {
        if (!shouldSkipSelectSingleOnDragStart('quiz', quiz.id)) {
          selectSingleBlock('quiz', quiz.id);
        }
      }
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [applySelectionState, beginGestureHistory, canEdit, endDrag, exitCropMode, getSelectedMoveGroup, onPointerMove, selectSingleBlock, shouldKeepSlideGroupUnitSelectionOnDrag, shouldSkipSelectSingleOnDragStart]
  );

  const startQuizResize = React.useCallback(
    (e: React.PointerEvent, quiz: ProgramSlideQuizShortAnswerV1, corner: ImageResizeCorner) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: quiz.id,
        mode: 'button-resize',
        kind: 'quiz',
        corner,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: quiz.x,
        startY: quiz.y,
        w: quiz.w,
        h: quiz.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: quiz.x,
        latestY: quiz.y,
        latestW: quiz.w,
        latestH: quiz.h,
      };
      selectSingleBlock('quiz', quiz.id);
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startQuizChoiceOptionDrag = React.useCallback(
    (e: React.PointerEvent, option: ProgramSlideQuizChoiceOptionV1) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const r = root.getBoundingClientRect();
      const moveGroup = getSelectedMoveGroup('quizChoice', option.id);
      const keepGroupUnitSel = moveGroup.length > 1 && shouldKeepSlideGroupUnitSelectionOnDrag('quizChoice', option.id);
      dragRef.current = {
        id: option.id,
        mode: 'move',
        kind: 'quizChoice',
        choiceSetId: option.setId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: option.x,
        startY: option.y,
        w: option.w,
        h: option.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: option.x,
        latestY: option.y,
        latestW: option.w,
        latestH: option.h,
        moveGroup: moveGroup.length > 1 ? moveGroup : undefined,
        moveGroupBounds:
          moveGroup.length > 1
            ? {
                minX: Math.min(...moveGroup.map((item) => item.x)),
                minY: Math.min(...moveGroup.map((item) => item.y)),
                maxRight: Math.max(...moveGroup.map((item) => item.x + item.w)),
                maxBottom: Math.max(...moveGroup.map((item) => item.y + item.h)),
              }
            : undefined,
        keptGroupUnitSelection: keepGroupUnitSel,
      };
      if (moveGroup.length > 1) {
        if (!keepGroupUnitSel) {
          applySelectionState(
            moveGroup.map((g) => ({ kind: g.kind, id: g.id })),
            { kind: 'quizChoice', id: option.id }
          );
        }
      } else {
        if (!shouldSkipSelectSingleOnDragStart('quizChoice', option.id)) {
          selectSingleBlock('quizChoice', option.id);
        }
      }
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [applySelectionState, beginGestureHistory, canEdit, endDrag, exitCropMode, getSelectedMoveGroup, onPointerMove, selectSingleBlock, shouldKeepSlideGroupUnitSelectionOnDrag, shouldSkipSelectSingleOnDragStart]
  );

  const startQuizChoiceOptionResize = React.useCallback(
    (e: React.PointerEvent, option: ProgramSlideQuizChoiceOptionV1, corner: ImageResizeCorner) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const r = root.getBoundingClientRect();
      const setOpts = getQuizChoiceOptionsForSet(layoutRef.current, option.setId);
      const choiceSetBBox =
        setOpts.length > 0
          ? {
              left: Math.min(...setOpts.map((o) => o.x)),
              top: Math.min(...setOpts.map((o) => o.y)),
              right: Math.max(...setOpts.map((o) => o.x + o.w)),
              bottom: Math.max(...setOpts.map((o) => o.y + o.h)),
            }
          : undefined;
      dragRef.current = {
        id: option.id,
        mode: 'button-resize',
        kind: 'quizChoice',
        choiceSetId: option.setId,
        choiceSetBBox,
        corner,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: option.x,
        startY: option.y,
        w: option.w,
        h: option.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: option.x,
        latestY: option.y,
        latestW: option.w,
        latestH: option.h,
      };
      selectSingleBlock('quizChoice', option.id);
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startQuizChoiceRadiusResize = React.useCallback(
    (e: React.PointerEvent, option: ProgramSlideQuizChoiceOptionV1) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const r = root.getBoundingClientRect();
      const optWpx = Math.max(1, option.w * r.width);
      const optHpx = Math.max(1, option.h * r.height);
      const effectiveRadiusPx = Math.min(option.cornerRadiusPx ?? 8, Math.min(optWpx, optHpx) / 2);
      dragRef.current = {
        id: option.id,
        mode: 'button-radius',
        kind: 'quizChoice',
        choiceSetId: option.setId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: option.x,
        startY: option.y,
        w: option.w,
        h: option.h,
        startRadiusPx: effectiveRadiusPx,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: option.x,
        latestY: option.y,
        latestW: option.w,
        latestH: option.h,
      };
      selectSingleBlock('quizChoice', option.id);
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startResize = React.useCallback(
    (e: React.PointerEvent, box: ProgramSlideTextBoxV1, side: ResizeSide) => {
      if (!canEdit) return;
      if (box.locked) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: box.id,
        mode: 'resize',
        kind: 'text',
        side,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: box.x,
        startY: box.y,
        w: box.w,
        h: box.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: box.x,
        latestY: box.y,
        latestW: box.w,
        latestH: box.h,
      };
      selectSingleBlock('text', box.id);
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startButtonDrag = React.useCallback(
    (e: React.PointerEvent, button: ProgramSlideButtonBlockV1) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      const moveGroup = getSelectedMoveGroup('button', button.id);
      const keepGroupUnitSel = moveGroup.length > 1 && shouldKeepSlideGroupUnitSelectionOnDrag('button', button.id);
      dragRef.current = {
        id: button.id,
        mode: 'move',
        kind: 'button',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: button.x,
        startY: button.y,
        w: button.w,
        h: button.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: button.x,
        latestY: button.y,
        latestW: button.w,
        latestH: button.h,
        moveGroup: moveGroup.length > 1 ? moveGroup : undefined,
        moveGroupBounds:
          moveGroup.length > 1
            ? {
                minX: Math.min(...moveGroup.map((item) => item.x)),
                minY: Math.min(...moveGroup.map((item) => item.y)),
                maxRight: Math.max(...moveGroup.map((item) => item.x + item.w)),
                maxBottom: Math.max(...moveGroup.map((item) => item.y + item.h)),
              }
            : undefined,
        keptGroupUnitSelection: keepGroupUnitSel,
      };
      if (moveGroup.length > 1) {
        if (!keepGroupUnitSel) {
          applySelectionState(
            moveGroup.map((g) => ({ kind: g.kind, id: g.id })),
            { kind: 'button', id: button.id }
          );
        }
      } else {
        if (!shouldSkipSelectSingleOnDragStart('button', button.id)) {
          selectSingleBlock('button', button.id);
        }
      }
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [applySelectionState, beginGestureHistory, canEdit, endDrag, exitCropMode, getSelectedMoveGroup, onPointerMove, selectSingleBlock, shouldKeepSlideGroupUnitSelectionOnDrag, shouldSkipSelectSingleOnDragStart]
  );

  const startButtonResize = React.useCallback(
    (e: React.PointerEvent, button: ProgramSlideButtonBlockV1, corner: ImageResizeCorner) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: button.id,
        mode: 'button-resize',
        kind: 'button',
        corner,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: button.x,
        startY: button.y,
        w: button.w,
        h: button.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: button.x,
        latestY: button.y,
        latestW: button.w,
        latestH: button.h,
      };
      selectSingleBlock('button', button.id);
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startBoxHorizontalResize = React.useCallback(
    (
      e: React.PointerEvent,
      kind: 'button' | 'shape',
      id: string,
      block: { x: number; y: number; w: number; h: number; locked?: boolean },
      side: ResizeSide
    ) => {
      if (!canEdit) return;
      if (block.locked) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id,
        mode: 'image-resize-x',
        kind,
        side,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: block.x,
        startY: block.y,
        w: block.w,
        h: block.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: block.x,
        latestY: block.y,
        latestW: block.w,
        latestH: block.h,
      };
      selectSingleBlock(kind, id);
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startButtonRadiusResize = React.useCallback(
    (e: React.PointerEvent, button: ProgramSlideButtonBlockV1) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      const buttonWpx = Math.max(1, button.w * r.width);
      const buttonHpx = Math.max(1, button.h * r.height);
      const effectiveRadiusPx = Math.min(button.cornerRadiusPx ?? 999, Math.min(buttonWpx, buttonHpx) / 2);
      dragRef.current = {
        id: button.id,
        mode: 'button-radius',
        kind: 'button',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: button.x,
        startY: button.y,
        w: button.w,
        h: button.h,
        startRadiusPx: effectiveRadiusPx,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: button.x,
        latestY: button.y,
        latestW: button.w,
        latestH: button.h,
      };
      selectSingleBlock('button', button.id);
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startShapeDrag = React.useCallback(
    (e: React.PointerEvent, shape: ProgramSlideShapeBlockV1) => {
      if (!canEdit) return;
      if (shape.locked) {
        e.preventDefault();
        e.stopPropagation();
        selectSingleBlock('shape', shape.id);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      let dragShape = shape;
      if (e.ctrlKey || e.metaKey) {
        const dup = duplicateBlockInPlaceForDrag('shape', shape.id);
        if (dup) {
          const found = (layoutRef.current.shapes ?? []).find((s) => s.id === dup.id);
          if (found) dragShape = found;
        }
      }
      const shapeCtrlCopyActive = dragShape.id !== shape.id;
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      const moveGroup = getSelectedMoveGroup('shape', dragShape.id);
      const keepGroupUnitSel = moveGroup.length > 1 && shouldKeepSlideGroupUnitSelectionOnDrag('shape', dragShape.id);
      dragRef.current = {
        id: dragShape.id,
        mode: 'move',
        kind: 'shape',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: dragShape.x,
        startY: dragShape.y,
        w: dragShape.w,
        h: dragShape.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: dragShape.x,
        latestY: dragShape.y,
        latestW: dragShape.w,
        latestH: dragShape.h,
        ctrlCopyActive: shapeCtrlCopyActive,
        moveGroup: moveGroup.length > 1 ? moveGroup : undefined,
        moveGroupBounds:
          moveGroup.length > 1
            ? {
                minX: Math.min(...moveGroup.map((item) => item.x)),
                minY: Math.min(...moveGroup.map((item) => item.y)),
                maxRight: Math.max(...moveGroup.map((item) => item.x + item.w)),
                maxBottom: Math.max(...moveGroup.map((item) => item.y + item.h)),
              }
            : undefined,
        keptGroupUnitSelection: keepGroupUnitSel,
      };
      if (moveGroup.length > 1) {
        if (!keepGroupUnitSel) {
          applySelectionState(
            moveGroup.map((g) => ({ kind: g.kind, id: g.id })),
            { kind: 'shape', id: dragShape.id }
          );
        }
      } else {
        if (!shouldSkipSelectSingleOnDragStart('shape', dragShape.id)) {
          selectSingleBlock('shape', dragShape.id);
        }
      }
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [applySelectionState, beginGestureHistory, canEdit, duplicateBlockInPlaceForDrag, endDrag, exitCropMode, getSelectedMoveGroup, onPointerMove, selectSingleBlock, shouldKeepSlideGroupUnitSelectionOnDrag, shouldSkipSelectSingleOnDragStart]
  );

  const applyTableSelectionRect = React.useCallback(
    (
      tableId: string,
      rect: TableSelectionRect,
      opts?: { notifyParent?: boolean; expandAnchors?: boolean }
    ) => {
      const notifyParent = opts?.notifyParent ?? true;
      const expandAnchors = opts?.expandAnchors ?? true;
      const table = (layoutRef.current.tables ?? []).find((t) => t.id === tableId) ?? null;
      const normalized =
        expandAnchors && table ? expandSelectionToAnchorBounds(table, rect) : normalizeTableSelectionRect(rect);
      const cur = tableCellSelectionRef.current;
      const unchanged =
        cur?.tableId === tableId &&
        cur.rowStart === normalized.rowStart &&
        cur.colStart === normalized.colStart &&
        cur.rowEnd === normalized.rowEnd &&
        cur.colEnd === normalized.colEnd;
      if (!unchanged) {
        const next = { tableId, ...normalized };
        tableCellSelectionRef.current = next;
        commitTableCellSelection(next);
      }
      if (notifyParent) {
        notifyTableSelection(tableId, normalized);
      }
    },
    [notifyTableSelection, commitTableCellSelection]
  );

  React.useEffect(() => {
    if (!isEvaluationSheetPreset || !syncedTableCellSelection) return;
    if (tableCellDragRef.current) return;
    const sel = syncedTableCellSelection;
    const key = `${sel.tableId}:${sel.rowStart},${sel.colStart},${sel.rowEnd},${sel.colEnd}`;
    const internalKey = tableCellSelectionRef.current
      ? `${tableCellSelectionRef.current.tableId}:${tableCellSelectionRef.current.rowStart},${tableCellSelectionRef.current.colStart},${tableCellSelectionRef.current.rowEnd},${tableCellSelectionRef.current.colEnd}`
      : '';
    if (key === internalKey) return;
    tableCellSelectionRef.current = sel;
    commitTableCellSelection(sel);
    setSelectedId(sel.tableId);
    setSelectedKind('table');
    tableSelectionAnchorRef.current = { tableId: sel.tableId, row: sel.rowStart, col: sel.colStart };
    notifyTableSelection(sel.tableId, sel);
  }, [isEvaluationSheetPreset, notifyTableSelection, syncedTableCellSelection]);

  const exitTableCellEditMode = React.useCallback(() => {
    setEditingTableCellId(null);
    const active = document.activeElement;
    if (active instanceof HTMLTextAreaElement || active instanceof HTMLInputElement) {
      active.blur();
    }
  }, []);

  const enterTableCellEdit = React.useCallback((cellId: string) => {
    suppressTableCellBlurRef.current = true;
    setEditingTableCellId(cellId);
    window.requestAnimationFrame(() => {
      suppressTableCellBlurRef.current = false;
    });
  }, []);

  const handleTableCellPointerDown = React.useCallback(
    (e: React.PointerEvent, table: ProgramSlideTableBlockV1, row: number, col: number, cellId: string) => {
      if (!canEdit) return;
      if (table.locked) {
        e.preventDefault();
        e.stopPropagation();
        selectSingleBlock('table', table.id);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      tableCellPointerDownRef.current = true;
      selectSingleBlock('table', table.id);

      if (e.shiftKey && tableSelectionAnchorRef.current?.tableId === table.id) {
        exitTableCellEditMode();
        const anchor = tableSelectionAnchorRef.current;
        applyTableSelectionRect(
          table.id,
          selectionRectFromEndpoints(anchor.row, anchor.col, row, col)
        );
        tableCellDragRef.current = null;
        return;
      }

      exitTableCellEditMode();
      const anchorSelection = selectionRectForAnchor(table, row, col);
      const selectionRow = anchorSelection?.rowStart ?? row;
      const selectionCol = anchorSelection?.colStart ?? col;
      tableSelectionAnchorRef.current = { tableId: table.id, row: selectionRow, col: selectionCol };
      tableCellDragRef.current = {
        tableId: table.id,
        anchorRow: selectionRow,
        anchorCol: selectionCol,
        cellId,
        active: false,
        startClientX: e.clientX,
        startClientY: e.clientY,
      };
      if (anchorSelection) {
        applyTableSelectionRect(table.id, anchorSelection);
      } else {
        applyTableSelectionRect(table.id, { rowStart: row, colStart: col, rowEnd: row, colEnd: col });
      }
    },
    [applyTableSelectionRect, canEdit, exitTableCellEditMode, selectSingleBlock]
  );

  const handleTableCellDoubleClick = React.useCallback(
    (e: React.MouseEvent, table: ProgramSlideTableBlockV1, row: number, col: number, cellId: string) => {
      if (!canEdit) return;
      if (table.locked) return;
      e.preventDefault();
      e.stopPropagation();
      tableCellDragRef.current = null;
      tableCellPointerDownRef.current = false;
      selectSingleBlock('table', table.id);
      tableSelectionAnchorRef.current = { tableId: table.id, row, col };
      enterTableCellEdit(cellId);
      applyTableSelectionRect(table.id, { rowStart: row, colStart: col, rowEnd: row, colEnd: col });
    },
    [applyTableSelectionRect, canEdit, enterTableCellEdit, selectSingleBlock]
  );

  React.useEffect(() => {
    const DRAG_THRESHOLD_PX = 4;

    const resolveTableCellFromPoint = (clientX: number, clientY: number) => {
      const hit = document.elementFromPoint(clientX, clientY);
      const el = hit?.closest('[data-table-cell-row][data-table-cell-col]') as HTMLElement | null;
      if (!el) return null;
      const row = Number(el.dataset.tableCellRow);
      const col = Number(el.dataset.tableCellCol);
      if (!Number.isFinite(row) || !Number.isFinite(col)) return null;
      return { row, col };
    };

    const onPointerMove = (e: PointerEvent) => {
      const drag = tableCellDragRef.current;
      if (!drag) return;

      const cell = resolveTableCellFromPoint(e.clientX, e.clientY);
      if (!drag.active) {
        const dx = e.clientX - drag.startClientX;
        const dy = e.clientY - drag.startClientY;
        const movedEnough = Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX;
        if (!cell) return;
        const differentCell = cell.row !== drag.anchorRow || cell.col !== drag.anchorCol;
        if (!movedEnough && !differentCell) return;
        tableCellDragRef.current = { ...drag, active: true };
        exitTableCellEditMode();
        applyTableSelectionRect(
          drag.tableId,
          selectionRectFromEndpoints(drag.anchorRow, drag.anchorCol, cell.row, cell.col),
          { notifyParent: false, expandAnchors: false }
        );
        return;
      }

      if (!cell) return;
      applyTableSelectionRect(
        drag.tableId,
        selectionRectFromEndpoints(drag.anchorRow, drag.anchorCol, cell.row, cell.col),
        { notifyParent: false, expandAnchors: false }
      );
    };

    const endTableCellInteraction = () => {
      const drag = tableCellDragRef.current;
      tableCellPointerDownRef.current = false;
      if (drag?.active) {
        const cur = tableCellSelectionRef.current;
        if (cur && cur.tableId === drag.tableId) {
          applyTableSelectionRect(
            drag.tableId,
            {
              rowStart: cur.rowStart,
              colStart: cur.colStart,
              rowEnd: cur.rowEnd,
              colEnd: cur.colEnd,
            },
            { notifyParent: true, expandAnchors: true }
          );
        }
      } else if (drag && !drag.active) {
        enterTableCellEdit(drag.cellId);
      }
      tableCellDragRef.current = null;
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endTableCellInteraction);
    window.addEventListener('pointercancel', endTableCellInteraction);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endTableCellInteraction);
      window.removeEventListener('pointercancel', endTableCellInteraction);
    };
  }, [applyTableSelectionRect, enterTableCellEdit, exitTableCellEditMode]);

  const startTableDrag = React.useCallback(
    (e: React.PointerEvent, table: ProgramSlideTableBlockV1) => {
      if (!canEdit) return;
      if (table.locked) {
        e.preventDefault();
        e.stopPropagation();
        selectSingleBlock('table', table.id);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      let dragTable = table;
      if (e.ctrlKey || e.metaKey) {
        const dup = duplicateBlockInPlaceForDrag('table', table.id);
        if (dup) {
          const found = (layoutRef.current.tables ?? []).find((t) => t.id === dup.id);
          if (found) dragTable = found;
        }
      }
      const tableCtrlCopyActive = dragTable.id !== table.id;
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const r = (canvasInnerEl ?? root).getBoundingClientRect();
      const moveGroup = getSelectedMoveGroup('table', dragTable.id);
      const keepGroupUnitSel = moveGroup.length > 1 && shouldKeepSlideGroupUnitSelectionOnDrag('table', dragTable.id);
      dragRef.current = {
        id: dragTable.id,
        mode: 'move',
        kind: 'table',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: dragTable.x,
        startY: dragTable.y,
        w: dragTable.w,
        h: dragTable.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: dragTable.x,
        latestY: dragTable.y,
        latestW: dragTable.w,
        latestH: dragTable.h,
        ctrlCopyActive: tableCtrlCopyActive,
        moveGroup: moveGroup.length > 1 ? moveGroup : undefined,
        moveGroupBounds:
          moveGroup.length > 1
            ? {
                minX: Math.min(...moveGroup.map((item) => item.x)),
                minY: Math.min(...moveGroup.map((item) => item.y)),
                maxRight: Math.max(...moveGroup.map((item) => item.x + item.w)),
                maxBottom: Math.max(...moveGroup.map((item) => item.y + item.h)),
              }
            : undefined,
        keptGroupUnitSelection: keepGroupUnitSel,
      };
      if (moveGroup.length > 1) {
        if (!keepGroupUnitSel) {
          applySelectionState(
            moveGroup.map((g) => ({ kind: g.kind, id: g.id })),
            { kind: 'table', id: dragTable.id }
          );
        }
      } else if (!shouldSkipSelectSingleOnDragStart('table', dragTable.id)) {
        selectSingleBlock('table', dragTable.id);
      }
      setEditingTableCellId(null);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [
      applySelectionState,
      beginGestureHistory,
      canEdit,
      canvasInnerEl,
      duplicateBlockInPlaceForDrag,
      endDrag,
      getSelectedMoveGroup,
      notifyTableSelection,
      onPointerMove,
      selectSingleBlock,
      shouldKeepSlideGroupUnitSelectionOnDrag,
      shouldSkipSelectSingleOnDragStart,
    ]
  );

  const startTableResize = React.useCallback(
    (e: React.PointerEvent, table: ProgramSlideTableBlockV1, corner: ImageResizeCorner) => {
      if (!canEdit) return;
      if (table.locked) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: table.id,
        mode: 'button-resize',
        kind: 'table',
        corner,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: table.x,
        startY: table.y,
        w: table.w,
        h: table.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: table.x,
        latestY: table.y,
        latestW: table.w,
        latestH: table.h,
      };
      selectSingleBlock('table', table.id);
      setEditingTableCellId(null);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, onPointerMove, selectSingleBlock]
  );

  React.useEffect(() => {
    const focusId = focusTableId ?? innerFocusTableId;
    if (!focusId || !canEdit) return;
    const tbl = (layout.tables ?? []).find((t) => t.id === focusId);
    if (!tbl) {
      onFocusTableHandled?.();
      return;
    }
    selectSingleBlock('table', focusId);
    onFocusTableHandled?.();
    setInnerFocusTableId(null);
  }, [canEdit, focusTableId, innerFocusTableId, layout.tables, onFocusTableHandled, selectSingleBlock]);

  const startPolylineDrag = React.useCallback(
    (e: React.PointerEvent, pl: ProgramSlidePolylineBlockV1) => {
      if (!canEdit) return;
      if (pl.locked) {
        e.preventDefault();
        e.stopPropagation();
        selectSingleBlock('polyline', pl.id);
        return;
      }
      const b0 = polylineBBox(pl.points);
      if (!b0) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      let dragPl = pl;
      if (e.ctrlKey || e.metaKey) {
        const dup = duplicateBlockInPlaceForDrag('polyline', pl.id);
        if (dup) {
          const found = (layoutRef.current.polylines ?? []).find((p) => p.id === dup.id);
          if (found) dragPl = found;
        }
      }
      const polylineCtrlCopyActive = dragPl.id !== pl.id;
      const b = polylineBBox(dragPl.points);
      if (!b) return;
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      const moveGroup = getSelectedMoveGroup('polyline', dragPl.id);
      const points0 = dragPl.points.map((p) => ({ x: p.x, y: p.y }));
      const keepGroupUnitSel = moveGroup.length > 1 && shouldKeepSlideGroupUnitSelectionOnDrag('polyline', dragPl.id);
      dragRef.current = {
        id: dragPl.id,
        mode: 'move',
        kind: 'polyline',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: b.x,
        startY: b.y,
        w: b.w,
        h: b.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: b.x,
        latestY: b.y,
        latestW: b.w,
        latestH: b.h,
        polylinePoints0: moveGroup.length > 1 ? undefined : points0,
        moveGroup: moveGroup.length > 1 ? moveGroup : undefined,
        moveGroupBounds:
          moveGroup.length > 1
            ? {
                minX: Math.min(...moveGroup.map((item) => item.x)),
                minY: Math.min(...moveGroup.map((item) => item.y)),
                maxRight: Math.max(...moveGroup.map((item) => item.x + item.w)),
                maxBottom: Math.max(...moveGroup.map((item) => item.y + item.h)),
              }
            : undefined,
        keptGroupUnitSelection: keepGroupUnitSel,
        ctrlCopyActive: polylineCtrlCopyActive,
      };
      if (moveGroup.length > 1) {
        if (!keepGroupUnitSel) {
          applySelectionState(
            moveGroup.map((g) => ({ kind: g.kind, id: g.id })),
            { kind: 'polyline', id: dragPl.id }
          );
        }
      } else {
        if (!shouldSkipSelectSingleOnDragStart('polyline', dragPl.id)) {
          selectSingleBlock('polyline', dragPl.id);
        }
      }
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [applySelectionState, beginGestureHistory, canEdit, duplicateBlockInPlaceForDrag, endDrag, exitCropMode, getSelectedMoveGroup, onPointerMove, selectSingleBlock, shouldKeepSlideGroupUnitSelectionOnDrag, shouldSkipSelectSingleOnDragStart]
  );

  const startShapeResize = React.useCallback(
    (e: React.PointerEvent, shape: ProgramSlideShapeBlockV1, corner: ImageResizeCorner) => {
      if (!canEdit) return;
      if (shape.locked) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: shape.id,
        mode: 'button-resize',
        kind: 'shape',
        shapeBlockKind: shape.shape,
        corner,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: shape.x,
        startY: shape.y,
        w: shape.w,
        h: shape.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: shape.x,
        latestY: shape.y,
        latestW: shape.w,
        latestH: shape.h,
      };
      selectSingleBlock('shape', shape.id);
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startShapeRadiusResize = React.useCallback(
    (e: React.PointerEvent, shape: ProgramSlideShapeBlockV1) => {
      if (!canEdit || shape.shape === 'circle' || shape.locked) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      const wpx = Math.max(1, shape.w * r.width);
      const hpx = Math.max(1, shape.h * r.height);
      const effectiveRadiusPx = Math.min(shape.cornerRadiusPx ?? 14, Math.min(wpx, hpx) / 2);
      dragRef.current = {
        id: shape.id,
        mode: 'button-radius',
        kind: 'shape',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: shape.x,
        startY: shape.y,
        w: shape.w,
        h: shape.h,
        startRadiusPx: effectiveRadiusPx,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: shape.x,
        latestY: shape.y,
        latestW: shape.w,
        latestH: shape.h,
      };
      selectSingleBlock('shape', shape.id);
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startQuizRadiusResize = React.useCallback(
    (e: React.PointerEvent, quiz: ProgramSlideQuizShortAnswerV1) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      const quizWpx = Math.max(1, quiz.w * r.width);
      const quizHpx = Math.max(1, quiz.h * r.height);
      const effectiveRadiusPx = Math.min(quiz.cornerRadiusPx ?? 999, Math.min(quizWpx, quizHpx) / 2);
      dragRef.current = {
        id: quiz.id,
        mode: 'button-radius',
        kind: 'quiz',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: quiz.x,
        startY: quiz.y,
        w: quiz.w,
        h: quiz.h,
        startRadiusPx: effectiveRadiusPx,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: quiz.x,
        latestY: quiz.y,
        latestW: quiz.w,
        latestH: quiz.h,
      };
      selectSingleBlock('quiz', quiz.id);
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startImageDrag = React.useCallback(
    (
      e: React.PointerEvent,
      image: ProgramSlideImageBlockV1,
      blockKind: SlideImageBlockKind = 'image'
    ) => {
      if (!canEdit) return;
      if (image.locked) {
        e.preventDefault();
        e.stopPropagation();
        selectSingleBlock(blockKind, image.id);
        return;
      }
      if (cropEditingId === image.id) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      let dragImage = image;
      if (e.ctrlKey || e.metaKey) {
        const dup = duplicateBlockInPlaceForDrag(blockKind, image.id);
        if (dup) {
          const found = findSlideImageBlock(layoutRef.current, blockKind, dup.id);
          if (found) dragImage = found;
        }
      }
      const imageCtrlCopyActive = dragImage.id !== image.id;
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      const moveGroup = getSelectedMoveGroup(blockKind, dragImage.id);
      const keepGroupUnitSel = moveGroup.length > 1 && shouldKeepSlideGroupUnitSelectionOnDrag(blockKind, dragImage.id);
      dragRef.current = {
        id: dragImage.id,
        mode: 'move',
        kind: blockKind,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: dragImage.x,
        startY: dragImage.y,
        w: dragImage.w,
        h: dragImage.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: dragImage.x,
        latestY: dragImage.y,
        latestW: dragImage.w,
        latestH: dragImage.h,
        ctrlCopyActive: imageCtrlCopyActive,
        moveGroup: moveGroup.length > 1 ? moveGroup : undefined,
        moveGroupBounds:
          moveGroup.length > 1
            ? {
                minX: Math.min(...moveGroup.map((item) => item.x)),
                minY: Math.min(...moveGroup.map((item) => item.y)),
                maxRight: Math.max(...moveGroup.map((item) => item.x + item.w)),
                maxBottom: Math.max(...moveGroup.map((item) => item.y + item.h)),
              }
            : undefined,
        keptGroupUnitSelection: keepGroupUnitSel,
      };
      if (moveGroup.length > 1) {
        if (!keepGroupUnitSel) {
          applySelectionState(
            moveGroup.map((g) => ({ kind: g.kind, id: g.id })),
            { kind: blockKind, id: dragImage.id }
          );
        }
      } else {
        if (!shouldSkipSelectSingleOnDragStart(blockKind, dragImage.id)) {
          selectSingleBlock(blockKind, dragImage.id);
        }
      }
      setEditingId(null);
      if (cropEditingId && cropEditingId !== dragImage.id) {
        exitCropMode();
      }
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [applySelectionState, beginGestureHistory, canEdit, cropEditingId, duplicateBlockInPlaceForDrag, endDrag, exitCropMode, getSelectedMoveGroup, onPointerMove, selectSingleBlock, shouldKeepSlideGroupUnitSelectionOnDrag, shouldSkipSelectSingleOnDragStart]
  );

  const startImageResize = React.useCallback(
    (
      e: React.PointerEvent,
      image: ProgramSlideImageBlockV1,
      corner: ImageResizeCorner,
      blockKind: SlideImageBlockKind = 'image'
    ) => {
      if (!canEdit) return;
      if (image.locked) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: image.id,
        mode: 'image-resize',
        kind: blockKind,
        corner,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: image.x,
        startY: image.y,
        w: image.w,
        h: image.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: image.x,
        latestY: image.y,
        latestW: image.w,
        latestH: image.h,
      };
      selectSingleBlock(blockKind, image.id);
      setEditingId(null);
      if (cropEditingId && cropEditingId !== image.id) {
        exitCropMode();
      }
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, cropEditingId, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startImageResizeHorizontal = React.useCallback(
    (
      e: React.PointerEvent,
      image: ProgramSlideImageBlockV1,
      side: ResizeSide,
      blockKind: SlideImageBlockKind = 'image'
    ) => {
      if (!canEdit) return;
      if (image.locked) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: image.id,
        mode: 'image-resize-x',
        kind: blockKind,
        side,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: image.x,
        startY: image.y,
        w: image.w,
        h: image.h,
        startAspectWMult: image.aspectWMultiplier > 0 ? image.aspectWMultiplier : 1,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: image.x,
        latestY: image.y,
        latestW: image.w,
        latestH: image.h,
      };
      selectSingleBlock(blockKind, image.id);
      setEditingId(null);
      if (cropEditingId && cropEditingId !== image.id) {
        exitCropMode();
      }
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, cropEditingId, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startVideoDrag = React.useCallback(
    (e: React.PointerEvent, video: ProgramSlideVideoBlockV1) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      const moveGroup = getSelectedMoveGroup('video', video.id);
      const keepGroupUnitSel = moveGroup.length > 1 && shouldKeepSlideGroupUnitSelectionOnDrag('video', video.id);
      dragRef.current = {
        id: video.id,
        mode: 'move',
        kind: 'video',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: video.x,
        startY: video.y,
        w: video.w,
        h: video.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: video.x,
        latestY: video.y,
        latestW: video.w,
        latestH: video.h,
        moveGroup: moveGroup.length > 1 ? moveGroup : undefined,
        moveGroupBounds:
          moveGroup.length > 1
            ? {
                minX: Math.min(...moveGroup.map((item) => item.x)),
                minY: Math.min(...moveGroup.map((item) => item.y)),
                maxRight: Math.max(...moveGroup.map((item) => item.x + item.w)),
                maxBottom: Math.max(...moveGroup.map((item) => item.y + item.h)),
              }
            : undefined,
        keptGroupUnitSelection: keepGroupUnitSel,
      };
      if (moveGroup.length > 1) {
        if (!keepGroupUnitSel) {
          applySelectionState(
            moveGroup.map((g) => ({ kind: g.kind, id: g.id })),
            { kind: 'video', id: video.id }
          );
        }
      } else {
        if (!shouldSkipSelectSingleOnDragStart('video', video.id)) {
          selectSingleBlock('video', video.id);
        }
      }
      setEditingId(null);
      stopVideoPlaybackInEditModeRef.current();
      exitCropMode();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [applySelectionState, beginGestureHistory, canEdit, endDrag, exitCropMode, getSelectedMoveGroup, onPointerMove, selectSingleBlock, shouldKeepSlideGroupUnitSelectionOnDrag, shouldSkipSelectSingleOnDragStart]
  );

  const startWebpageDrag = React.useCallback(
    (e: React.PointerEvent, webpage: ProgramSlideWebpageBlockV1) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const r = root.getBoundingClientRect();
      const moveGroup = getSelectedMoveGroup('webpage', webpage.id);
      const keepGroupUnitSel = moveGroup.length > 1 && shouldKeepSlideGroupUnitSelectionOnDrag('webpage', webpage.id);
      dragRef.current = {
        id: webpage.id,
        mode: 'move',
        kind: 'webpage',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: webpage.x,
        startY: webpage.y,
        w: webpage.w,
        h: webpage.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: webpage.x,
        latestY: webpage.y,
        latestW: webpage.w,
        latestH: webpage.h,
        moveGroup: moveGroup.length > 1 ? moveGroup : undefined,
        moveGroupBounds:
          moveGroup.length > 1
            ? {
                minX: Math.min(...moveGroup.map((item) => item.x)),
                minY: Math.min(...moveGroup.map((item) => item.y)),
                maxRight: Math.max(...moveGroup.map((item) => item.x + item.w)),
                maxBottom: Math.max(...moveGroup.map((item) => item.y + item.h)),
              }
            : undefined,
        keptGroupUnitSelection: keepGroupUnitSel,
      };
      if (moveGroup.length > 1) {
        if (!keepGroupUnitSel) {
          applySelectionState(
            moveGroup.map((g) => ({ kind: g.kind, id: g.id })),
            { kind: 'webpage', id: webpage.id }
          );
        }
      } else if (!shouldSkipSelectSingleOnDragStart('webpage', webpage.id)) {
        selectSingleBlock('webpage', webpage.id);
      }
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [
      applySelectionState,
      beginGestureHistory,
      canEdit,
      endDrag,
      exitCropMode,
      getSelectedMoveGroup,
      onPointerMove,
      selectSingleBlock,
      shouldKeepSlideGroupUnitSelectionOnDrag,
      shouldSkipSelectSingleOnDragStart,
    ]
  );

  const startWebpageResize = React.useCallback(
    (e: React.PointerEvent, webpage: ProgramSlideWebpageBlockV1, corner: ImageResizeCorner) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: webpage.id,
        mode: 'image-resize',
        kind: 'webpage',
        corner,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: webpage.x,
        startY: webpage.y,
        w: webpage.w,
        h: webpage.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: webpage.x,
        latestY: webpage.y,
        latestW: webpage.w,
        latestH: webpage.h,
      };
      selectSingleBlock('webpage', webpage.id);
      setEditingId(null);
      exitCropMode();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startWebpageResizeHorizontal = React.useCallback(
    (e: React.PointerEvent, webpage: ProgramSlideWebpageBlockV1, side: ResizeSide) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: webpage.id,
        mode: 'image-resize-x',
        kind: 'webpage',
        side,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: webpage.x,
        startY: webpage.y,
        w: webpage.w,
        h: webpage.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: webpage.x,
        latestY: webpage.y,
        latestW: webpage.w,
        latestH: webpage.h,
      };
      selectSingleBlock('webpage', webpage.id);
      setEditingId(null);
      exitCropMode();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  /** 소스코드 블럭: 위·아래 변으로 세로 길이만 조정 */
  const startWebpageResizeVertical = React.useCallback(
    (
      e: React.PointerEvent,
      webpage: { id: string; x: number; y: number; w: number; h: number; locked?: boolean },
      vSide: 'top' | 'bottom',
      blockKind: 'webpage' | 'shape' | SlideImageBlockKind = 'webpage'
    ) => {
      if (!canEdit) return;
      if (webpage.locked) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: webpage.id,
        mode: 'image-resize-y',
        kind: blockKind,
        vSide,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: webpage.x,
        startY: webpage.y,
        w: webpage.w,
        h: webpage.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: webpage.x,
        latestY: webpage.y,
        latestW: webpage.w,
        latestH: webpage.h,
      };
      selectSingleBlock(blockKind, webpage.id);
      setEditingId(null);
      exitCropMode();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startVideoResize = React.useCallback(
    (e: React.PointerEvent, video: ProgramSlideVideoBlockV1, corner: ImageResizeCorner) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: video.id,
        mode: 'video-resize',
        kind: 'video',
        corner,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: video.x,
        startY: video.y,
        w: video.w,
        h: video.h,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: video.x,
        latestY: video.y,
        latestW: video.w,
        latestH: video.h,
      };
      selectSingleBlock('video', video.id);
      setEditingId(null);
      exitCropMode();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, onPointerMove, selectSingleBlock]
  );

  const startGroupMove = React.useCallback(
    (e: React.PointerEvent, bounds: SelectionBounds) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const selectedGroup = getSelectedBlockGeometrySnapshot();
      if (selectedGroup.length <= 1) return;
      const coordEl = canvasInnerEl ?? root;
      const r = coordEl.getBoundingClientRect();
      const layoutR = clientRectToLayoutRect(r);
      const hitNorm = clientDeltaToElementNorm(e.clientX, e.clientY, r.left, r.top, coordEl);
      const hit = [...selectedGroup]
        .reverse()
        .find(
          (g) =>
            hitNorm.dx >= g.x &&
            hitNorm.dx <= g.x + g.w &&
            hitNorm.dy >= g.y &&
            hitNorm.dy <= g.y + g.h
        );
      const primary = hit ?? selectedGroup[selectedGroup.length - 1]!;
      dragRef.current = {
        id: primary.id,
        kind: primary.kind,
        mode: 'move',
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: bounds.left,
        startY: bounds.top,
        w: bounds.width,
        h: bounds.height,
        rootW: Math.max(1, layoutR.width),
        rootH: Math.max(1, layoutR.height),
        latestX: bounds.left,
        latestY: bounds.top,
        latestW: bounds.width,
        latestH: bounds.height,
        moveGroup: selectedGroup,
        moveGroupBounds: {
          minX: bounds.left,
          minY: bounds.top,
          maxRight: bounds.right,
          maxBottom: bounds.bottom,
        },
        keptGroupUnitSelection: !!selectionRef.current.selectedGroupId,
      };
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [
      beginGestureHistory,
      canEdit,
      canvasInnerEl,
      endDrag,
      exitCropMode,
      getSelectedBlockGeometrySnapshot,
      onPointerMove,
    ]
  );

  const startGroupResize = React.useCallback(
    (e: React.PointerEvent, bounds: SelectionBounds, handle: { corner?: ImageResizeCorner; side?: ResizeSide }) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      root.focus({ preventScroll: true });
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const selectedGroup = getSelectedBlockGeometrySnapshot();
      if (selectedGroup.length <= 1) return;
      const r = root.getBoundingClientRect();
      dragRef.current = {
        id: selectedGroup[selectedGroup.length - 1]?.id ?? '',
        kind: selectedGroup[selectedGroup.length - 1]?.kind ?? 'text',
        mode: 'group-resize',
        corner: handle.corner,
        side: handle.side,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: bounds.left,
        startY: bounds.top,
        w: bounds.width,
        h: bounds.height,
        rootW: Math.max(1, r.width),
        rootH: Math.max(1, r.height),
        latestX: bounds.left,
        latestY: bounds.top,
        latestW: bounds.width,
        latestH: bounds.height,
        moveGroup: selectedGroup,
        moveGroupBounds: {
          minX: bounds.left,
          minY: bounds.top,
          maxRight: bounds.right,
          maxBottom: bounds.bottom,
        },
      };
      setEditingId(null);
      exitCropMode();
      stopVideoPlaybackInEditModeRef.current();
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, exitCropMode, getSelectedBlockGeometrySnapshot, onPointerMove]
  );

  const toggleCropMode = React.useCallback(
    (image: ProgramSlideImageBlockV1, blockKind: SlideImageBlockKind = 'image') => {
      if (!canEdit) return;
      selectSingleBlock(blockKind, image.id);
      setEditingId(null);
      const exiting = cropEditingId === image.id;
      if (exiting) {
        exitCropMode();
        return;
      }

      const block = findSlideImageBlock(layout, blockKind, image.id);
      if (!block?.src || block.naturalW <= 0 || block.naturalH <= 0) {
        setCropEditingId(image.id);
        return;
      }

      const didMigrate = !block.cropUsesFrameFrac;
      if (didMigrate) {
        const mapImg = (img: ProgramSlideImageBlockV1) =>
          img.id === block.id
            ? {
                ...img,
                crop: letterboxCropToFrameFrac(block, getContainMetrics),
                cropUsesFrameFrac: true,
              }
            : img;
        const nextLayout: ProgramSlideLayoutPatch = isSlideBitmapImageKind(blockKind)
          ? {
              v: 1,
              boxes: layout.boxes,
              images: layout.images,
              [bitmapImagesLayoutKey(blockKind)]: getBitmapImages(layout, blockKind).map(mapImg),
              buttons: layout.buttons,
              videos: layout.videos,
              quizzes: layout.quizzes,
            }
          : {
              v: 1,
              boxes: layout.boxes,
              images: layout.images.map(mapImg),
              buttons: layout.buttons,
              videos: layout.videos,
              quizzes: layout.quizzes,
            };
        layoutRef.current = mergeSlideLayoutArrays(layout, nextLayout);
        applyLayoutChange(nextLayout, 'skip');
      }

      setCropEditingId(image.id);
      const expandFrame = () => applySlideImageFrameToOriginal(image.id, blockKind);
      if (didMigrate) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(expandFrame);
        });
      } else {
        window.requestAnimationFrame(expandFrame);
      }
    },
    [
      applySlideImageFrameToOriginal,
      canEdit,
      cropEditingId,
      exitCropMode,
      getContainMetrics,
      layout.boxes,
      layout.buttons,
      layout.clipboardImages,
      layout.scanImages,
      layout.images,
      layout.videos,
      applyLayoutChange,
      selectSingleBlock,
    ]
  );

  const startCropMove = React.useCallback(
    (
      e: React.PointerEvent,
      image: ProgramSlideImageBlockV1,
      blockKind: SlideImageBlockKind = 'image'
    ) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      const useFrame = image.cropUsesFrameFrac;
      const { dispW, dispH } = useFrame ? { dispW: 1, dispH: 1 } : getContainMetrics(image);
      dragRef.current = {
        id: image.id,
        mode: 'crop-move',
        kind: blockKind,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: image.x,
        startY: image.y,
        w: image.w,
        h: image.h,
        crop: image.crop,
        rootW: Math.max(1, image.w * r.width * (useFrame ? 1 : dispW)),
        rootH: Math.max(1, image.h * r.height * (useFrame ? 1 : dispH)),
        latestX: image.x,
        latestY: image.y,
        latestW: image.w,
        latestH: image.h,
      };
      selectSingleBlock(blockKind, image.id);
      setEditingId(null);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, getContainMetrics, onPointerMove, selectSingleBlock]
  );

  const startCropResize = React.useCallback(
    (
      e: React.PointerEvent,
      image: ProgramSlideImageBlockV1,
      corner: ImageResizeCorner,
      blockKind: SlideImageBlockKind = 'image'
    ) => {
      if (!canEdit) return;
      e.preventDefault();
      e.stopPropagation();
      beginGestureHistory();
      const root = rootRef.current;
      if (!root) return;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* ignore unsupported capture */
      }
      const r = root.getBoundingClientRect();
      const useFrame = image.cropUsesFrameFrac;
      const { dispW, dispH } = useFrame ? { dispW: 1, dispH: 1 } : getContainMetrics(image);
      dragRef.current = {
        id: image.id,
        mode: 'crop-resize',
        kind: blockKind,
        corner,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: image.x,
        startY: image.y,
        w: image.w,
        h: image.h,
        crop: image.crop,
        rootW: Math.max(1, image.w * r.width * (useFrame ? 1 : dispW)),
        rootH: Math.max(1, image.h * r.height * (useFrame ? 1 : dispH)),
        latestX: image.x,
        latestY: image.y,
        latestW: image.w,
        latestH: image.h,
      };
      selectSingleBlock(blockKind, image.id);
      setEditingId(null);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);
    },
    [beginGestureHistory, canEdit, endDrag, getContainMetrics, onPointerMove, selectSingleBlock]
  );

  React.useEffect(
    () => () => {
      endDrag();
      clearDeleteHoldTimer();
    },
    [clearDeleteHoldTimer, endDrag]
  );

  const openButtonHref = React.useCallback((rawHref: string) => {
    const raw = rawHref.trim();
    if (!raw) return;
    let url = '';
    try {
      const parsed = new URL(raw);
      if (parsed.protocol === 'http:' || parsed.protocol === 'https:') url = parsed.toString();
    } catch {
      try {
        const parsed = new URL(`https://${raw}`);
        if (parsed.protocol === 'https:') url = parsed.toString();
      } catch {
        url = '';
      }
    }
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const dismissButtonInView = React.useCallback((id: string) => {
    setFadingDismissButtonIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    window.setTimeout(() => {
      setHiddenDismissButtonIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setFadingDismissButtonIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }, 220);
  }, []);

  const postVideoIframeCommand = React.useCallback((id: string, renderSource: VideoRenderSource, command: 'play' | 'pause' | 'time') => {
    if (renderSource.kind !== 'iframe') return;
    const iframe = iframeRefs.current[id];
    const target = iframe?.contentWindow;
    if (!target) return;
    if (renderSource.provider === 'youtube') {
      const func = command === 'play' ? 'playVideo' : command === 'pause' ? 'pauseVideo' : 'getCurrentTime';
      target.postMessage(JSON.stringify({ event: 'listening', id }), '*');
      target.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*');
      return;
    }
    const method = command === 'play' ? 'play' : command === 'pause' ? 'pause' : 'getCurrentTime';
    target.postMessage({ method }, '*');
  }, []);

  const pauseVideoPlaybackForBlock = React.useCallback(
    (id: string) => {
      const block = layoutRef.current.videos.find((v) => v.id === id);
      if (!block?.src) return;
      const renderSource = getVideoRenderSource(block.src, videoEmbedStarts[id]);
      if (!renderSource) return;
      if (renderSource.kind === 'video') {
        videoRefs.current[id]?.pause();
      } else if (renderSource.kind === 'iframe') {
        postVideoIframeCommand(id, renderSource, 'pause');
      }
    },
    [postVideoIframeCommand, videoEmbedStarts]
  );

  const stopVideoPlaybackInEditMode = React.useCallback(() => {
    const id = videoControlEnabledIdRef.current;
    if (!id) return;
    pauseVideoPlaybackForBlock(id);
    setVideoControlEnabledId(null);
  }, [pauseVideoPlaybackForBlock]);

  stopVideoPlaybackInEditModeRef.current = stopVideoPlaybackInEditMode;

  React.useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const found = Object.entries(iframeRefs.current).find(([, iframe]) => iframe?.contentWindow === event.source);
      const id = found?.[0];
      if (!id) return;
      let data: unknown = event.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          return;
        }
      }
      if (!data || typeof data !== 'object') return;
      const o = data as {
        event?: unknown;
        info?: { currentTime?: unknown };
        value?: unknown;
        data?: { seconds?: unknown };
      };
      const rawTime =
        typeof o.info?.currentTime === 'number'
          ? o.info.currentTime
          : typeof o.value === 'number'
          ? o.value
          : typeof o.data?.seconds === 'number'
          ? o.data.seconds
          : undefined;
      if (typeof rawTime !== 'number' || !Number.isFinite(rawTime) || rawTime < 0) return;
      setVideoEmbedCurrentTimes((cur) => ({ ...cur, [id]: Math.round(rawTime * 100) / 100 }));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  React.useEffect(() => {
    if (!videoControlEnabledId) return;
    const block = layoutRef.current.videos.find((video) => video.id === videoControlEnabledId);
    const renderSource = block ? getVideoRenderSource(block.src, videoEmbedStarts[videoControlEnabledId]) : null;
    if (!renderSource || renderSource.kind !== 'iframe') return;
    const timer = window.setInterval(() => {
      postVideoIframeCommand(videoControlEnabledId, renderSource, 'time');
    }, 500);
    postVideoIframeCommand(videoControlEnabledId, renderSource, 'time');
    return () => window.clearInterval(timer);
  }, [postVideoIframeCommand, videoControlEnabledId, videoEmbedStarts]);

  const addVideoBookmark = React.useCallback(
    (id: string) => {
      const block = layoutRef.current.videos.find((video) => video.id === id);
      if (!block?.src) return;
      const renderSource = getVideoRenderSource(block.src);
      if (!renderSource) return;
      let timeSec = 0;
      if (renderSource.kind === 'iframe') {
        timeSec = Math.max(0, Math.round((videoEmbedCurrentTimes[id] ?? videoEmbedStarts[id] ?? 0) * 100) / 100);
      } else {
        const videoEl = videoRefs.current[id];
        timeSec = Math.max(0, Math.round((videoEl?.currentTime ?? 0) * 100) / 100);
      }
      const nextBookmarks = [
        ...block.bookmarks,
        {
          id: genSlideBoxId(block.bookmarks.length + 1),
          label: formatVideoBookmarkTime(timeSec),
          timeSec,
        },
      ]
        .sort((a, b) => a.timeSec - b.timeSec)
        .slice(0, 20);
      patchVideo(id, {
        bookmarks: nextBookmarks,
      });
    },
    [patchVideo, videoEmbedCurrentTimes, videoEmbedStarts]
  );

  const deleteVideoBookmark = React.useCallback(
    (videoId: string, bookmarkId: string) => {
      const block = layoutRef.current.videos.find((video) => video.id === videoId);
      if (!block) return;
      patchVideo(videoId, {
        bookmarks: block.bookmarks.filter((bookmark) => bookmark.id !== bookmarkId),
      });
    },
    [patchVideo]
  );

  const jumpToVideoBookmark = React.useCallback((id: string, timeSec: number) => {
    const videoEl = videoRefs.current[id];
    if (videoEl) {
      setVideoControlEnabledId(id);
      videoEl.currentTime = Math.max(0, timeSec);
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          /* 브라우저 자동 재생 정책상 실패할 수 있음 */
        });
      }
      return;
    }
    setVideoControlEnabledId(id);
    setVideoEmbedStarts((cur) => ({ ...cur, [id]: Math.max(0, timeSec) }));
  }, []);

  const toggleVideoPlaybackInEditMode = React.useCallback((id: string, renderSource: VideoRenderSource | null) => {
    if (!renderSource) return;
    selectSingleBlock('video', id);
    setEditingId(null);
    exitCropMode();
    if (videoControlEnabledId === id) {
      pauseVideoPlaybackForBlock(id);
      setVideoControlEnabledId(null);
      return;
    }
    setVideoControlEnabledId(id);
    if (renderSource.kind === 'iframe') {
      window.requestAnimationFrame(() => {
        postVideoIframeCommand(id, renderSource, 'play');
        postVideoIframeCommand(id, renderSource, 'time');
      });
      return;
    }
    window.requestAnimationFrame(() => {
      const videoEl = videoRefs.current[id];
      if (!videoEl) return;
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          /* 브라우저 자동 재생 정책상 실패할 수 있음 */
        });
      }
    });
  }, [exitCropMode, pauseVideoPlaybackForBlock, postVideoIframeCommand, selectSingleBlock, videoControlEnabledId]);

  const displayedMultiSelectionBounds = React.useMemo(() => {
    const drag = dragRef.current;
    if (drag?.mode === 'group-resize') {
      return {
        left: drag.latestX,
        top: drag.latestY,
        right: drag.latestX + drag.latestW,
        bottom: drag.latestY + drag.latestH,
        width: drag.latestW,
        height: drag.latestH,
      };
    }
    return multiSelectionBounds;
  }, [layout, multiSelectionBounds]);

  const batchToolbarAction =
    <A extends unknown[]>(kind: SlideSelectionKind, fn: (id: string, ...args: A) => void) =>
    (primaryId: string, ...args: A) =>
      forToolbarTargets(kind, primaryId, (id) => fn(id, ...args));
  const patchBoxTargets = batchToolbarAction('text', patchBox);
  const patchBoxAndAutofitTargets = batchToolbarAction('text', patchBoxAndAutofit);
  const applyFontSizeTargets = batchToolbarAction('text', applyFontSizeAndAutofit);
  const applyTextColorTargets = batchToolbarAction('text', applySlideTextColorFromToolbar);
  const patchButtonTargets = batchToolbarAction('button', patchButton);
  const patchShapeTargets = batchToolbarAction('shape', patchShape);
  const patchPolylineTargets = batchToolbarAction('polyline', patchPolyline);
  const stepBoxFontSizeTargets = (primaryId: string, delta: number) =>
    forToolbarTargets('text', primaryId, (id) => {
      const cur = layoutRef.current.boxes.find((b) => b.id === id);
      if (cur) applyFontSizeAndAutofit(id, cur.fontSizePx + delta);
    });
  const stepBoxLineHeightTargets = (primaryId: string, delta: number) =>
    forToolbarTargets('text', primaryId, (id) => {
      const cur = layoutRef.current.boxes.find((b) => b.id === id);
      if (cur) applyLineHeightAndAutofit(id, (cur.lineHeight ?? 1.05) + delta);
    });
  const stepButtonFontSizeTargets = (primaryId: string, delta: number) =>
    forToolbarTargets('button', primaryId, (id) => {
      const cur = layoutRef.current.buttons.find((b) => b.id === id);
      if (cur) patchButton(id, { fontSizePx: Math.min(96, Math.max(8, cur.fontSizePx + delta)) });
    });
  const stepShapeFontSizeTargets = (primaryId: string, delta: number) =>
    forToolbarTargets('shape', primaryId, (id) => {
      const cur = (layoutRef.current.shapes ?? []).find((s) => s.id === id);
      if (cur) patchShape(id, { fontSizePx: Math.min(96, Math.max(8, cur.fontSizePx + delta)) });
    });
  /** 일괄 수정 중이면 미니 툴바를 선택 영역 전체 기준으로 띄운다 */
  const toolbarAnchorFor = (kind: SlideSelectionKind, x: number, y: number, h: number) => {
    const bounds = isToolbarBatchFor(kind) ? displayedMultiSelectionBounds : null;
    return bounds ? { x: bounds.left, y: bounds.top, h: bounds.height } : { x, y, h };
  };

  const mountExternalToolbar = React.useCallback(
    (node: React.ReactNode, host: HTMLElement | null | undefined) =>
      host ? createPortal(node, host) : node,
    []
  );

  const innerActiveTable = innerTableId ? (layout.tables ?? []).find((t) => t.id === innerTableId) ?? null : null;
  const handleInnerCreateTable = (input: { rowCount: number; colCount: number }) => {
    const cur = layoutRef.current;
    const zs = [
      ...cur.boxes.map((b) => b.zIndex),
      ...cur.images.map((b) => b.zIndex),
      ...(cur.shapes ?? []).map((b) => b.zIndex),
      ...(cur.tables ?? []).map((b) => b.zIndex),
    ];
    const table = createTableBlock({
      rowCount: input.rowCount,
      colCount: input.colCount,
      defaultFontSizePx: isEvaluationSheetPreset ? EVALUATION_PLAN_SHEET_DEFAULT_TEXT_FONT_SIZE_PX : 24,
      defaultTextColorHex: resolvedDefaultTextColorHex,
      x: 0.1,
      y: 0.2,
      w: 0.8,
      h: 0.5,
      zIndex: zs.length ? Math.max(...zs) + 1 : 0,
    });
    beginGestureHistory();
    applyLayoutChange({ ...cur, tables: [...(cur.tables ?? []), table] });
    commitGestureHistory();
    setInnerTableCreateOpen(false);
    setInnerTableId(table.id);
    setInnerTableEditOpen(true);
    setInnerFocusTableId(table.id);
  };
  const handleInnerPatchTable = (table: ProgramSlideTableBlockV1, selection?: TableSelectionRect | null) => {
    const cur = layoutRef.current;
    const normalized = normalizeTableGrid(table);
    applyLayoutChange({ ...cur, tables: (cur.tables ?? []).map((t) => (t.id === normalized.id ? normalized : t)) });
    if (selection) setInnerTableSel(selection);
  };
  const handleInnerDeleteTable = () => {
    if (!innerTableId) return;
    const cur = layoutRef.current;
    beginGestureHistory();
    applyLayoutChange({ ...cur, tables: (cur.tables ?? []).filter((t) => t.id !== innerTableId) });
    commitGestureHistory();
    setInnerTableId(null);
    setInnerTableSel(null);
    setInnerTableAnchor(null);
  };
  const innerTableUi =
    useInternalTableUi && canEdit && typeof document !== 'undefined'
      ? createPortal(
          <>
            <EvaluationPlanSheetTableCreateModal
              open={innerTableCreateOpen}
              backdropZIndex={floatedToolbarZIndex + 10}
              onClose={() => setInnerTableCreateOpen(false)}
              onSubmit={handleInnerCreateTable}
            />
            <EvaluationPlanSheetTableEditMiniModal
              open={!!innerActiveTable && innerTableEditOpen}
              table={innerActiveTable}
              selection={innerTableSel}
              anchorRect={innerTableAnchor}
              containerRect={viewportContainerRect ?? null}
              panelZIndex={floatedToolbarZIndex + 10}
              onClose={() => setInnerTableEditOpen(false)}
              onPatchTable={handleInnerPatchTable}
              onDeleteTable={handleInnerDeleteTable}
            />
          </>,
          document.body
        )
      : null;

  return (
    <>
    {innerTableUi}
    <div
      className={
        'cp-program-slide-canvas' +
        (viewMode ? ' is-view-mode' : '') +
        (canEdit ? ' is-slide-canvas-editable' : '') +
        (canEdit && !viewMode && placeCanvasTool === 'line' ? ' cp-program-slide-canvas--line-draw-cursor' : '') +
        (webcamHitThrough
          ? ' cp-program-slide-canvas--webcam-hit-through cp-program-slide-canvas--webcam-over-stage'
          : '') +
        (layoutLayerRole === 'background' ? ' cp-program-slide-canvas--layout-background' : '') +
        (layoutLayerRole === 'foreground' ? ' cp-program-slide-canvas--layout-foreground' : '') +
        (canvasAspectRatio === 'a4' ? ' cp-program-slide-canvas--a4' : '') +
        (isEvaluationSheetPreset ? ' cp-program-slide-canvas--evaluation-sheet' : '')
      }
      ref={rootRef}
      role="presentation"
      tabIndex={0}
      onCopyCapture={handleBlockCopy}
      onCutCapture={(e) => {
        if (!canEdit) return;
        if (shouldDeferToNativeSlideCopy(e.target)) return;
        const refs = getEffectiveSelectedRefs();
        const okRefs = refs.filter(
          (r) =>
            !isSlideBlockLockedInLayout(layoutRef.current, r.kind, r.id) &&
            !!serializeSlideBlockSelection(r.kind, r.id, layoutRef.current)
        );
        if (!okRefs.length) return;
        const payload =
          okRefs.length === 1
            ? serializeSlideBlockSelection(okRefs[0]!.kind, okRefs[0]!.id, layoutRef.current)
            : serializeSlideMultiBlockClipboard(okRefs, layoutRef.current);
        if (!payload) return;
        e.preventDefault();
        e.stopPropagation();
        e.clipboardData?.setData('text/plain', payload);
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
          void navigator.clipboard.writeText(payload).catch(() => undefined);
        }
        onBlockClipboardCopyRef.current?.();
        if (refs.length > 1) deleteSelectedBlocks();
        else {
          const r = okRefs[0]!;
          deleteSelectedBlock(r.kind, r.id);
        }
      }}
      onPasteCapture={onCanvasPaste}
      onPointerDownCapture={(e) => {
        if (!canEdit) return;
        if (e.target === rootRef.current) {
          ensureCanvasPasteFocus();
        }
      }}
      onKeyDown={(e) => {
        if (viewMode && e.key === 'Enter') {
          const ae = e.target as HTMLElement;
          if (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA') return;
          if (ae.isContentEditable || ae.closest('[contenteditable="true"]')) return;
          e.preventDefault();
          submitChoiceQuizzes();
          return;
        }
        if (!canEdit) return;
        const key = e.key.toLowerCase();
        const isModifierPressed = e.metaKey || e.ctrlKey;
        if (isModifierPressed && !e.altKey) {
          if (key === 'z') {
            e.preventDefault();
            e.stopPropagation();
            if (e.shiftKey) handleRedoHistory();
            else handleUndoHistory();
            return;
          }
          if (key === 'y') {
            e.preventDefault();
            e.stopPropagation();
            handleRedoHistory();
            return;
          }
        }
        if (key === 'delete' || key === 'backspace') {
          if (editingIdRef.current) return;
          const ae = e.target as HTMLElement;
          if (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA') return;
          if (ae.isContentEditable || ae.closest('[contenteditable="true"]')) return;
          if (!getEffectiveSelectedRefs().length) return;
          e.preventDefault();
          e.stopPropagation();
          deleteSelectedBlocks();
          return;
        }
        if (key === 'escape') {
          if (editingIdRef.current) return;
          const ae = e.target as HTMLElement;
          if (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA') return;
          if (ae.isContentEditable || ae.closest('[contenteditable="true"]')) return;
          if (placeCanvasToolRef.current != null || lineDraftPointsRef.current != null || shapeAddFlyoutOpen) {
            e.preventDefault();
            e.stopPropagation();
            setPlaceCanvasTool(null);
            setLineDraftPoints(null);
            setLinePointerNorm(null);
            setShapeAddFlyoutOpen(false);
            return;
          }
        }
        if (e.key === 'Enter' && placeCanvasToolRef.current === 'line') {
          const pts = lineDraftPointsRef.current;
          if (pts && pts.length >= 2) {
            const ae = e.target as HTMLElement;
            if (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA') return;
            if (ae.isContentEditable || ae.closest('[contenteditable="true"]')) return;
            e.preventDefault();
            e.stopPropagation();
            commitPolylineDraft();
            return;
          }
        }
        if (editingId || e.key !== 'Enter' || !selectedId || !hasSingleSelection) return;
        if (selectedKind !== 'text' && selectedKind !== 'button' && selectedKind !== 'shape') return;
        e.preventDefault();
        if (selectedKind === 'button') enterButtonEditMode(selectedId);
        else if (selectedKind === 'shape') enterShapeEditMode(selectedId);
        else enterEditMode(selectedId);
      }}
    >
          {canEdit
        ? mountExternalToolbar(
            <div
              className={
                'cp-program-slide-canvas-toolbar-wrap' +
                (externalToolbarLeftHost ? ' cp-program-slide-canvas-toolbar-wrap--external' : '')
              }
              data-tutorial-slide="toolbar-left"
            >
          <div className="cp-program-slide-canvas-toolbar-text-shape-col">
            <span className="cp-program-slide-canvas-add-wrap">
              <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
                텍스트 블럭
              </span>
              <button
                type="button"
                className="cp-program-slide-canvas-add"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={addBox}
                aria-label="텍스트 블럭 추가"
              >
                <img src="/text.svg" alt="" width={22} height={22} aria-hidden />
              </button>
            </span>
            <div
              className={
                'cp-program-slide-canvas-shape-tool-wrap' + (shapeAddFlyoutOpen ? ' is-shape-flyout-open' : '')
              }
              onMouseEnter={() => setShapeAddFlyoutOpen(true)}
              onMouseLeave={() => setShapeAddFlyoutOpen(false)}
            >
              <span className="cp-program-slide-canvas-shape-main-wrap">
                <button
                  type="button"
                  className={
                    'cp-program-slide-canvas-add' +
                    (shapeAddFlyoutOpen || placeCanvasTool === 'line' ? ' is-on' : '')
                  }
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    shapeAddMainButtonPointerTypeRef.current = e.pointerType;
                  }}
                  onClick={() => {
                    // 마우스는 호버로 열리므로 같은 클릭에서 토글하지 않음(터치·펜·키보드 등만 토글)
                    if (shapeAddMainButtonPointerTypeRef.current === 'mouse') return;
                    setShapeAddFlyoutOpen((open) => !open);
                  }}
                  aria-label="도형 블럭 추가 메뉴"
                  aria-expanded={shapeAddFlyoutOpen}
                >
                  <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden focusable="false">
                    <rect x="3" y="5" width="18" height="14" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
                    <path d="M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </span>
              {shapeAddFlyoutOpen ? (
                <div className="cp-program-slide-canvas-shape-flyout" role="group" aria-label="도형 종류 선택">
                  <span className="cp-program-slide-canvas-add-wrap">
                    <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
                      직사각형 도형
                    </span>
                    <button
                      type="button"
                      className="cp-program-slide-canvas-add cp-program-slide-canvas-add--subtool"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => {
                        placeShapeFromCanvasClick(0.5, 0.5, 'rect');
                        setLineDraftPoints(null);
                        setLinePointerNorm(null);
                        setPlaceCanvasTool(null);
                        setShapeAddFlyoutOpen(false);
                      }}
                      aria-label="직사각형 도형 삽입"
                    >
                      <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden focusable="false">
                        <rect x="4" y="7" width="16" height="11" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
                      </svg>
                    </button>
                  </span>
                  <span className="cp-program-slide-canvas-add-wrap">
                    <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
                      원형 도형
                    </span>
                    <button
                      type="button"
                      className="cp-program-slide-canvas-add cp-program-slide-canvas-add--subtool"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => {
                        placeShapeFromCanvasClick(0.5, 0.5, 'circle');
                        setLineDraftPoints(null);
                        setLinePointerNorm(null);
                        setPlaceCanvasTool(null);
                        setShapeAddFlyoutOpen(false);
                      }}
                      aria-label="원형 도형 삽입, Shift+코너 조절 시 정원"
                    >
                      <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden focusable="false">
                        <ellipse cx="12" cy="12" rx="8" ry="6" fill="none" stroke="currentColor" strokeWidth="2" />
                      </svg>
                    </button>
                  </span>
                  <span className="cp-program-slide-canvas-add-wrap">
                    <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
                      직선도형
                    </span>
                    <button
                      type="button"
                      className={
                        'cp-program-slide-canvas-add cp-program-slide-canvas-add--subtool' +
                        (placeCanvasTool === 'line' ? ' is-on' : '')
                      }
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => {
                        setPlaceCanvasTool('line');
                        setLineDraftPoints([]);
                        setLinePointerNorm(null);
                        setShapeAddFlyoutOpen(false);
                      }}
                      aria-pressed={placeCanvasTool === 'line'}
                      aria-label="직선도형 그리기, Shift로 수평·수직 맞춤"
                    >
                      <img src="/line.svg" alt="" width={18} height={18} aria-hidden />
                    </button>
                  </span>
                </div>
              ) : null}
            </div>
          </div>
          {/* 글쓰기·표: 모든 편집기에서 도형 바로 아래 */}
          {true ? (
          <>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              글쓰기 블럭
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => {
                placeShapeFromCanvasClick(0.5, 0.5, 'ruled');
                setPlaceCanvasTool(null);
                setShapeAddFlyoutOpen(false);
              }}
              aria-label="글쓰기 블럭 추가"
            >
              <img src="/writing-block.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              표 블럭
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => (onRequestCreateTable ? onRequestCreateTable() : setInnerTableCreateOpen(true))}
              aria-label="표 블럭 추가"
            >
              <img src="/table.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          </>
          ) : null}
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              링크 이미지 블럭
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={addImageBlock}
              aria-label="링크 이미지 블럭 추가"
            >
              <img src="/imagelink.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              클립보드 이미지 블럭
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={addClipboardImageBlock}
              aria-label="클립보드 이미지 블럭 추가 (Ctrl+V로 붙여넣기)"
            >
              <img src="/ClipboardCopy.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              스캔 이미지 블럭
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={addScanImageBlock}
              aria-label="스캔 이미지 블럭 추가"
            >
              <img src="/webcam.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          {isEvaluationSheetPreset ? (
            <span className="cp-program-slide-canvas-add-wrap">
              <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
                소스코드 블럭
              </span>
              <button
                type="button"
                className="cp-program-slide-canvas-add"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={addCodeWebpageBlock}
                aria-label="소스코드 블럭 추가"
              >
                <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true" focusable="false">
                  <path
                    fill="currentColor"
                    d="M9.2 16.7a1 1 0 0 1-1.4 0l-4.1-4.1a1 1 0 0 1 0-1.4l4.1-4.1a1 1 0 1 1 1.4 1.4L5.8 12l3.4 3.3a1 1 0 0 1 0 1.4Zm5.6 0a1 1 0 0 1 0-1.4l3.4-3.3-3.4-3.3a1 1 0 1 1 1.4-1.4l4.1 4.1a1 1 0 0 1 0 1.4l-4.1 4.1a1 1 0 0 1-1.4 0ZM10.9 19a1 1 0 0 1-.97-1.27l2.2-11a1 1 0 1 1 1.96.39l-2.2 11A1 1 0 0 1 10.9 19Z"
                  />
                </svg>
              </button>
            </span>
          ) : null}
          {!isEvaluationSheetPreset ? (
          <>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              동영상 블럭
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={addVideoBlock}
              aria-label="동영상 블럭 추가"
            >
              <img src="/Video.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              웹페이지 블럭
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={addWebpageBlock}
              aria-label="웹페이지 블럭 추가"
            >
              <img src="/webpagelink.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              소스코드 블럭
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={addCodeWebpageBlock}
              aria-label="소스코드 블럭 추가"
            >
              <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden="true" focusable="false">
                <path
                  fill="currentColor"
                  d="M9.2 16.7a1 1 0 0 1-1.4 0l-4.1-4.1a1 1 0 0 1 0-1.4l4.1-4.1a1 1 0 1 1 1.4 1.4L5.8 12l3.4 3.3a1 1 0 0 1 0 1.4Zm5.6 0a1 1 0 0 1 0-1.4l3.4-3.3-3.4-3.3a1 1 0 1 1 1.4-1.4l4.1 4.1a1 1 0 0 1 0 1.4l-4.1 4.1a1 1 0 0 1-1.4 0ZM10.9 19a1 1 0 0 1-.97-1.27l2.2-11a1 1 0 1 1 1.96.39l-2.2 11A1 1 0 0 1 10.9 19Z"
                />
              </svg>
            </button>
          </span>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              링크 버튼
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={addButtonBlock}
              aria-label="링크 버튼 추가"
            >
              <img src="/Link.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              가림막 버튼
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={addDismissButtonBlock}
              aria-label="가림막 버튼 추가"
            >
              <img src="/Question.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          <div className="cp-program-slide-canvas-toolbar-divider" aria-hidden />
          {/* 퀴즈: 도형 버튼과 같은 호버 플라이아웃(단답형·선택형) */}
          <div
            className={'cp-program-slide-canvas-shape-tool-wrap' + (quizAddFlyoutOpen ? ' is-shape-flyout-open' : '')}
            onMouseEnter={() => setQuizAddFlyoutOpen(true)}
            onMouseLeave={() => setQuizAddFlyoutOpen(false)}
          >
            <span className="cp-program-slide-canvas-shape-main-wrap">
              <button
                type="button"
                className={'cp-program-slide-canvas-add' + (quizAddFlyoutOpen ? ' is-on' : '')}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  quizAddMainButtonPointerTypeRef.current = e.pointerType;
                }}
                onClick={() => {
                  if (quizAddMainButtonPointerTypeRef.current === 'mouse') return;
                  setQuizAddFlyoutOpen((open) => !open);
                }}
                aria-label="퀴즈 블럭 추가 메뉴"
                aria-expanded={quizAddFlyoutOpen}
              >
                <svg viewBox="0 0 24 24" width={22} height={22} aria-hidden focusable="false">
                  <path d="M5 4h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-7l-4.5 3.5V17H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M10 8.6a2.1 2.1 0 1 1 3 1.9c-.6.3-1 .8-1 1.5v.3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  <circle cx="12" cy="14.6" r="1.05" fill="currentColor" />
                </svg>
              </button>
            </span>
            {quizAddFlyoutOpen ? (
              <div className="cp-program-slide-canvas-shape-flyout" role="group" aria-label="퀴즈 종류 선택">
                  <span className="cp-program-slide-canvas-add-wrap">
                    <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
                      단답형 퀴즈
                    </span>
                    <button
                      type="button"
                      className="cp-program-slide-canvas-add cp-program-slide-canvas-add--subtool"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => {
                        addQuizBlock();
                        setQuizAddFlyoutOpen(false);
                      }}
                      aria-label="단답형 퀴즈 추가"
                    >
                      <img src="/short%20answer.svg" alt="" width={22} height={22} aria-hidden />
                    </button>
                  </span>
                  <span className="cp-program-slide-canvas-add-wrap">
                    <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
                      선택형 퀴즈
                    </span>
                    <button
                      type="button"
                      className="cp-program-slide-canvas-add cp-program-slide-canvas-add--subtool"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={() => {
                        addQuizChoiceSet();
                        setQuizAddFlyoutOpen(false);
                      }}
                      aria-label="선택형 퀴즈 추가"
                    >
                      <img src="/optional%20Quiz.svg" alt="" width={22} height={22} aria-hidden />
                    </button>
                  </span>
              </div>
            ) : null}
          </div>
          </>
          ) : null}
            </div>,
            externalToolbarLeftHost
          )
        : null}
      {canEdit
        ? mountExternalToolbar(
            <div
              className={
                'cp-program-slide-canvas-toolbar-wrap cp-program-slide-canvas-toolbar-wrap--right' +
                (externalToolbarRightHost ? ' cp-program-slide-canvas-toolbar-wrap--external' : '')
              }
              data-tutorial-slide="toolbar-right"
            >
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              되돌리기
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add cp-program-slide-canvas-add--subtool"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={handleUndoHistory}
              aria-label="편집 되돌리기"
              disabled={!canUndoHistory}
            >
              <img src="/Arrow%20Back%20Up).svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              다시 실행
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add cp-program-slide-canvas-add--subtool"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={handleRedoHistory}
              aria-label="편집 다시 실행"
              disabled={!canRedoHistory}
            >
              <img src="/Arrow%20Forward%20Up.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              {smartSnapEnabled ? '스마트 스냅 켜짐' : '스마트 스냅 꺼짐'}
            </span>
            <button
              type="button"
              className={'cp-program-slide-canvas-add cp-program-slide-canvas-add--subtool' + (smartSnapEnabled ? ' is-on' : '')}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => setSmartSnapEnabled((prev) => !prev)}
              aria-label={smartSnapEnabled ? '스마트 스냅 끄기' : '스마트 스냅 켜기'}
              aria-pressed={smartSnapEnabled}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                {renderSmartSnapIcon()}
              </svg>
            </button>
          </span>
          <div className="cp-program-slide-canvas-toolbar-divider" aria-hidden />
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              개체 맨앞으로 보내기
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add cp-program-slide-canvas-add--subtool"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => applyObjectStackOrder('front')}
              aria-label="개체 맨앞으로 보내기"
              disabled={selectionCount < 1}
            >
              <img src="/Forward.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          <span className="cp-program-slide-canvas-add-wrap">
            <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
              개체 맨뒤로 보내기
            </span>
            <button
              type="button"
              className="cp-program-slide-canvas-add cp-program-slide-canvas-add--subtool"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={() => applyObjectStackOrder('back')}
              aria-label="개체 맨뒤로 보내기"
              disabled={selectionCount < 1}
            >
              <img src="/Backward.svg" alt="" width={22} height={22} aria-hidden />
            </button>
          </span>
          <div className="cp-program-slide-canvas-align-actions" onPointerDown={(e) => e.stopPropagation()}>
            {OBJECT_ALIGN_ACTIONS.map((action) => {
              const disabled = selectionCount < action.minSelectionCount;
              return (
                <span key={action.id} className="cp-program-slide-canvas-add-wrap">
                  <span className="cp-program-slide-canvas-add-tooltip" aria-hidden>
                    {action.label}
                  </span>
                  <button
                    type="button"
                    className="cp-program-slide-canvas-add cp-program-slide-canvas-add--subtool"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => applyObjectAlignment(action.id)}
                    aria-label={action.label}
                    disabled={disabled}
                  >
                    <img src={getObjectAlignIconSrc(action.id)} alt="" width={22} height={22} aria-hidden />
                  </button>
                </span>
              );
            })}
          </div>
            </div>,
            externalToolbarRightHost
          )
        : null}
      <div
        className="cp-program-slide-canvas-inner"
        ref={setCanvasInnerEl}
        style={
          shouldPaintCanvasInnerBackground
            ? { backgroundColor: slideDisplayColors.bg(resolvedCanvasBackgroundColorHex) }
            : undefined
        }
        onPointerDownCapture={(e) => {
          if (!canEdit || viewMode || e.button !== 0) return;
          if (placeCanvasToolRef.current !== 'line') return;
          tryConsumeLinePlacePointerDown(e);
        }}
        onPointerDown={startMarqueeSelection}
        onPointerMoveCapture={(e) => {
          updateLineDraftHoverFromInnerPointer(e);
        }}
        onPointerLeave={() => {
          if (placeCanvasToolRef.current !== 'line') return;
          setLinePointerNorm(null);
        }}
      >
        {fixedHeaderBand ? (
          <div
            ref={headerBandElRef}
            className="cp-program-slide-canvas-header-band cp-evaluation-plan-sheet-canvas-header-band"
            aria-hidden={readOnly ? true : undefined}
          >
            <div className="cp-evaluation-plan-web-doc-title-row">
              <div
                className={
                  'cp-evaluation-plan-web-doc-title-badge' +
                  (fixedHeaderBand.pageNumber != null ? ' has-page-badge' : '') +
                  (!readOnly &&
                  !viewMode &&
                  fixedHeaderBand.titleEditable &&
                  fixedHeaderBand.onTitleChange
                    ? ' cp-evaluation-plan-web-doc-title-badge--editable'
                    : '')
                }
              >
                {!readOnly &&
                !viewMode &&
                fixedHeaderBand.titleEditable &&
                fixedHeaderBand.onTitleChange ? (
                  <input
                    type="text"
                    className="cp-evaluation-plan-web-doc-title-input"
                    value={fixedHeaderBand.title}
                    onChange={(e) => fixedHeaderBand.onTitleChange?.(e.target.value)}
                    onFocus={() => {
                      worksheetTitleBeforeEditRef.current = fixedHeaderBand.title;
                    }}
                    onBlur={(e) => {
                      if (!e.target.value.trim()) {
                        fixedHeaderBand.onTitleChange?.(worksheetTitleBeforeEditRef.current);
                      }
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    aria-label={imageStorageKind === 'learning-worksheet' ? '활동지 제목' : '평가지 제목'}
                  />
                ) : (
                  <div className="cp-evaluation-plan-web-doc-title-readonly">{fixedHeaderBand.title}</div>
                )}
                {fixedHeaderBand.pageNumber != null ? (
                  <span className="cp-learning-worksheet-title-page-badge" aria-hidden>
                    {fixedHeaderBand.pageNumber}
                  </span>
                ) : null}
              </div>
            </div>
            {fixedHeaderBand.academicYearLabel ? (
              imageStorageKind === 'learning-worksheet' || imageStorageKind === 'evaluation-sheet' ? (
                <div className="cp-learning-worksheet-school-year-row" aria-hidden={readOnly ? true : undefined}>
                  <span className="cp-learning-worksheet-school-year-label">{fixedHeaderBand.academicYearLabel}</span>
                  <div className="cp-learning-worksheet-student-info-fields">
                    <span className="cp-learning-worksheet-student-info-item cp-learning-worksheet-student-info-item--box-first">
                      <span className="cp-learning-worksheet-student-info-blank cp-learning-worksheet-student-info-blank--grade" />
                      <span className="cp-learning-worksheet-student-info-label">학년</span>
                    </span>
                    <span className="cp-learning-worksheet-student-info-item cp-learning-worksheet-student-info-item--box-first">
                      <span className="cp-learning-worksheet-student-info-blank cp-learning-worksheet-student-info-blank--class" />
                      <span className="cp-learning-worksheet-student-info-label">반</span>
                    </span>
                    <span className="cp-learning-worksheet-student-info-item cp-learning-worksheet-student-info-item--label-first">
                      <span className="cp-learning-worksheet-student-info-label">번호</span>
                      <span className="cp-learning-worksheet-student-info-blank cp-learning-worksheet-student-info-blank--number" />
                    </span>
                    <span className="cp-learning-worksheet-student-info-item cp-learning-worksheet-student-info-item--label-first">
                      <span className="cp-learning-worksheet-student-info-label">이름</span>
                      <span className="cp-learning-worksheet-student-info-blank cp-learning-worksheet-student-info-blank--name" />
                    </span>
                  </div>
                </div>
              ) : (
                <p className="cp-evaluation-plan-web-doc-school-year-line">{fixedHeaderBand.academicYearLabel}</p>
              )
            ) : null}
          </div>
        ) : null}
        {canEdit && smartSnapGuides.x != null ? (
          <div
            className="cp-program-slide-canvas-smart-snap-guide cp-program-slide-canvas-smart-snap-guide--vertical"
            style={{ left: `calc(${smartSnapGuides.x * 100}% - 1px)` }}
            aria-hidden
          />
        ) : null}
        {canEdit && smartSnapGuides.y != null ? (
          <div
            className="cp-program-slide-canvas-smart-snap-guide cp-program-slide-canvas-smart-snap-guide--horizontal"
            style={{ top: `calc(${smartSnapGuides.y * 100}% - 1px)` }}
            aria-hidden
          />
        ) : null}
        {boxes.map((box) => (
          (() => {
            const isToolbarOpen =
              canEdit && canOpenToolbarFor('text') && selectedKind === 'text' && selectedId === box.id;
            const toolbarBatch = isToolbarBatchFor('text');
            const toolbarAnchor = toolbarAnchorFor('text', box.x, box.y, box.h);
            const isTextEmpty = getSlideTextBoxPlainText(box).trim().length === 0;
            return (
          <div
            key={box.id}
            className={
              'cp-program-slide-canvas-box' +
              (isBlockSelected('text', box.id) ? ' is-selected' : '') +
              (isTextEmpty ? ' is-empty-text' : '')
            }
            style={{
              left: `${box.x * 100}%`,
              top: `${box.y * 100}%`,
              width: `${box.w * 100}%`,
              height: `${box.h * 100}%`,
              zIndex: getBlockLayerZIndex(blockLayerZ(box.zIndex), isToolbarOpen),
            }}
            onPointerDown={(e) => {
              if (editingId === box.id) {
                e.stopPropagation();
                return;
              }
              if (handleMultiSelectPointerDown(e, 'text', box.id)) return;
              startDrag(e, box);
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              enterEditMode(box.id);
            }}
          >
            {isToolbarOpen ? (
              <ProgramSlideFloatedToolbar
                {...floatedToolbarPortalProps}
                blockX={toolbarAnchor.x}
                blockY={toolbarAnchor.y}
                blockH={toolbarAnchor.h}
                className={'cp-program-slide-canvas-toolbar' + (box.locked ? ' is-locked' : '')}
                placement={resolveToolbarPlacement(toolbarAnchor.y, toolbarAnchor.h, SLIDE_TOOLBAR_SINGLE_ROW_HEIGHT_PX)}
                zIndex={floatedToolbarZIndex}
                onMouseDown={keepTextEditFocusFromToolbar(box.id)}
                onPointerDown={keepTextEditFocusFromToolbar(box.id)}
              >
                <label className="cp-program-slide-canvas-tl">
                  크기
                  <input
                    type="number"
                    min={8}
                    max={96}
                    value={box.fontSizePx}
                    onChange={(e) => applyFontSizeTargets(box.id, Number(e.target.value) || 72)}
                  />
                  <div className="cp-program-slide-canvas-fontstep-group" role="group" aria-label="텍스트 크기 조절">
                    <button
                      type="button"
                      className="cp-program-slide-canvas-fontstep"
                      onClick={() => stepBoxFontSizeTargets(box.id, -2)}
                      aria-label="텍스트 크기 줄이기"
                      title="텍스트 크기 줄이기"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path d="M5 11h14v2H5v-2z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="cp-program-slide-canvas-fontstep"
                      onClick={() => stepBoxFontSizeTargets(box.id, 2)}
                      aria-label="텍스트 크기 키우기"
                      title="텍스트 크기 키우기"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        <path d="M11 5h2v14h-2V5zM5 11h14v2H5v-2z" />
                      </svg>
                    </button>
                  </div>
                </label>
                <label className="cp-program-slide-canvas-tl cp-program-slide-canvas-font-family-field">
                  글꼴
                  <select
                    value={normalizeSlideTextFontFamily(box.fontFamily)}
                    onChange={(e) =>
                      patchBoxAndAutofitTargets(box.id, {
                        fontFamily: normalizeSlideTextFontFamily(e.target.value),
                      })
                    }
                    aria-label="텍스트 글꼴"
                  >
                    {PROGRAM_SLIDE_TEXT_FONT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="cp-program-slide-canvas-tl">
                  색
                  <SlideColorPickerField
                    value={box.colorHex.match(/^#/) ? box.colorHex : '#141414'}
                    ariaLabel="텍스트 색상"
                    recentColors={slideRecentColors}
                    usedColors={slideUsedColors}
                    surface={isEvaluationSheetPreset ? 'light' : 'dark'}
                    onOpen={() => captureSlideTextPlainSelection(box.id)}
                    onPreview={previewSlideColor((hex) => applyTextColorTargets(box.id, hex, 'skip'))}
                    onConfirm={confirmSlideColor((hex) => applyTextColorTargets(box.id, hex, 'skip'))}
                  />
                </label>
                <button
                  type="button"
                  className={'cp-program-slide-canvas-bold' + (box.bold ? ' is-on' : '')}
                  onClick={() => patchBoxTargets(box.id, { bold: !box.bold })}
                >
                  B
                </button>
                <div className="cp-program-slide-canvas-lineheight-group" role="group" aria-label="줄간격 조절">
                  <button
                    type="button"
                    className="cp-program-slide-canvas-lineheight"
                    onClick={() => stepBoxLineHeightTargets(box.id, -0.1)}
                    aria-label="줄간격 줄이기"
                    title="줄간격 줄이기"
                  >
                    <img src="/line1.svg" alt="" aria-hidden className="cp-program-slide-canvas-lineheight-icon" />
                  </button>
                  <button
                    type="button"
                    className="cp-program-slide-canvas-lineheight"
                    onClick={() => stepBoxLineHeightTargets(box.id, 0.1)}
                    aria-label="줄간격 늘리기"
                    title="줄간격 늘리기"
                  >
                    <img src="/line2.svg" alt="" aria-hidden className="cp-program-slide-canvas-lineheight-icon" />
                  </button>
                </div>
                <div className="cp-program-slide-canvas-align-group" role="group" aria-label="텍스트 정렬">
                  {PROGRAM_SLIDE_TEXT_ALIGN_OPTIONS.map((align) => (
                    <button
                      key={align}
                      type="button"
                      className={
                        'cp-program-slide-canvas-align' +
                        ((box.textAlign ?? 'left') === align ? ' is-on' : '')
                      }
                      onClick={() => patchBoxTargets(box.id, { textAlign: align })}
                      aria-label={programSlideTextAlignLabel(align)}
                      title={programSlideTextAlignLabel(align)}
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                        {PROGRAM_SLIDE_TEXT_ALIGN_ICONS[align]}
                      </svg>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className={'cp-program-slide-canvas-lock' + (box.locked ? ' is-on' : '')}
                  onClick={() => toggleLockTargets('text', box.id)}
                  aria-label={box.locked ? '잠금 해제' : '잠금'}
                  title={box.locked ? '잠금 해제' : '잠금'}
                >
                  <SlideLockIcon locked={!!box.locked} />
                </button>
                {toolbarBatch ? null : (
                <ProgramSlideTrashDeleteControl
                  isHolding={holdingDeleteId === box.id}
                  showConfirm={
                    !!deleteConfirmTarget &&
                    deleteConfirmTarget.id === box.id &&
                    deleteConfirmTarget.kind === 'text'
                  }
                  disabled={!!box.locked}
                  ariaLabel="텍스트 상자 삭제: 짧게 확인, 길게 바로 삭제"
                  onPointerDown={(e) => startDeleteHold(e, box.id)}
                  onPointerUp={() => endDeleteHold(true)}
                  onPointerLeave={() => endDeleteHold(false)}
                  onPointerCancel={() => endDeleteHold(false)}
                  onCancelConfirm={() => setDeleteConfirmTarget(null)}
                  onConfirmDelete={() => {
                    const target = deleteConfirmTarget;
                    setDeleteConfirmTarget(null);
                    if (target) executeDeleteBlock(target.id, target.kind);
                  }}
                />
                )}
              </ProgramSlideFloatedToolbar>
            ) : null}
            {canEdit && !box.locked ? (
              <>
                <div
                  className="cp-program-slide-canvas-drag-edge cp-program-slide-canvas-drag-edge--top"
                  onPointerDown={(e) => startDrag(e, box)}
                  title="드래그하여 이동 (Ctrl: 복사)"
                />
                <div
                  className="cp-program-slide-canvas-drag-edge cp-program-slide-canvas-drag-edge--right"
                  onPointerDown={(e) => startDrag(e, box)}
                  title="드래그하여 이동 (Ctrl: 복사)"
                />
                <div
                  className="cp-program-slide-canvas-drag-edge cp-program-slide-canvas-drag-edge--bottom"
                  onPointerDown={(e) => startDrag(e, box)}
                  title="드래그하여 이동 (Ctrl: 복사)"
                />
                <div
                  className="cp-program-slide-canvas-drag-edge cp-program-slide-canvas-drag-edge--left"
                  onPointerDown={(e) => startDrag(e, box)}
                  title="드래그하여 이동 (Ctrl: 복사)"
                />
              </>
            ) : null}
            {(() => {
              const isEditingText = canEdit && editingId === box.id;
              const textViewSig = `${getSlideTextBoxPlainText(box)}|${box.colorHex}|${JSON.stringify(box.textRuns ?? null)}|${slideSurfaceTheme}`;
              return (
          <div
            key={isEditingText ? `${box.id}__edit__${slideSurfaceTheme}` : `${box.id}__view__${textViewSig}`}
              ref={(el) => {
                textBlockRefs.current[box.id] = el;
              }}
              role="textbox"
              aria-multiline="true"
              aria-label="슬라이드 텍스트"
              contentEditable={isEditingText}
              suppressContentEditableWarning
              className={'cp-program-slide-canvas-text' + (isEditingText ? ' is-editing' : '')}
              style={{
                fontSize: `${resolveFontPx(box.fontSizePx)}px`,
                fontFamily: slideTextFontCssFamily(box.fontFamily),
                color: slideDisplayColors.fg(slideDisplayHex(box.colorHex, '#141414')),
                fontWeight: box.bold ? 700 : 400,
                textAlign: box.textAlign ?? 'left',
                lineHeight: box.lineHeight ?? 1.05,
                ...(typographyBoxPadding ? { padding: typographyBoxPadding } : null),
              }}
              {...(isEditingText
                ? {}
                : {
                    dangerouslySetInnerHTML: {
                      __html: runsToEditableHtml(box.textRuns, box.text, box.colorHex, slideDisplayColors.fg),
                    },
                  })}
              onInput={() => {
                if (!isEditingText) return;
                const el = textBlockRefs.current[box.id];
                const cur = layoutRef.current.boxes.find((b) => b.id === box.id);
                if (!el || !cur) return;
                const parsed = parseRunsFromContentEditableRoot(el, cur.colorHex);
                patchBox(box.id, compactSlideTextBoxContent({ ...cur, ...parsed }), 'skip');
                window.requestAnimationFrame(() => adjustBoxHeightToContent(box.id));
              }}
              onPaste={(e) => {
                if (!isEditingText) return;
                e.preventDefault();
                const t = e.clipboardData?.getData('text/plain') ?? '';
                document.execCommand('insertText', false, t);
              }}
              onKeyDown={(e) => {
                if (isEditingText && e.key === 'Enter' && !e.shiftKey) {
                  if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                  e.preventDefault();
                  e.stopPropagation();
                  document.execCommand('insertLineBreak');
                  return;
                }
                if (isEditingText && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
                  e.stopPropagation();
                }
              }}
              onPointerDown={(e) => {
                if (isEditingText) {
                  e.stopPropagation();
                  return;
                }
                if (handleMultiSelectPointerDown(e, 'text', box.id)) {
                  e.stopPropagation();
                  return;
                }
                /* 편집 캔버스에서는 CSS로 비편집 텍스트에 pointer-events:none → 상자에서 드래그·더블클릭 처리 */
              }}
              onBlur={(e) => {
                if (!canEdit) return;
                const boxContainer = e.currentTarget.closest('.cp-program-slide-canvas-box') as HTMLElement | null;
                window.requestAnimationFrame(() => {
                  if (isFocusInsideTextBoxToolbar(boxContainer)) return;
                  const el = textBlockRefs.current[box.id];
                  const cur = layoutRef.current.boxes.find((b) => b.id === box.id);
                  if (el && cur) {
                    const parsed = parseRunsFromContentEditableRoot(el, cur.colorHex);
                    patchBox(box.id, compactSlideTextBoxContent({ ...cur, ...parsed }), 'skip');
                  }
                  commitGestureHistory();
                  slideTextPlainSelectionRef.current = null;
                  setEditingId((curId) => (curId === box.id ? null : curId));
                  adjustBoxHeightToContent(box.id);
                });
              }}
              spellCheck={false}
            />
              );
            })()}
          </div>
            );
          })()
        ))}
        {buttons.map((button) => {
          const isDismissButton = button.kind === 'dismiss';
          const isToolbarOpen =
            selectedKind === 'button' && selectedId === button.id && canEdit && canOpenToolbarFor('button');
          const toolbarBatch = isToolbarBatchFor('button');
          const toolbarAnchor = toolbarAnchorFor('button', button.x, button.y, button.h);
          if (isDismissButton && hiddenDismissButtonIds.has(button.id)) return null;
          const radius = button.cornerRadiusPx ?? 999;
          return (
          <div
            key={button.id}
            className={
              'cp-program-slide-canvas-button' +
              (isDismissButton ? ' cp-program-slide-canvas-button--dismiss' : ' cp-program-slide-canvas-button--link') +
              (isBlockSelected('button', button.id) ? ' is-selected' : '') +
              (isDismissButton && fadingDismissButtonIds.has(button.id) ? ' is-fading-out' : '')
            }
            style={{
              left: `${button.x * 100}%`,
              top: `${button.y * 100}%`,
              width: `${button.w * 100}%`,
              height: `${button.h * 100}%`,
              backgroundColor: slideDisplayColors.bg(slideDisplayHex(button.backgroundColorHex, '#ffffff')),
              borderRadius: `${radius}px`,
              zIndex: getBlockLayerZIndex(blockLayerZ(button.zIndex), isToolbarOpen),
            }}
            role={viewMode ? (isDismissButton ? 'button' : 'link') : undefined}
            tabIndex={viewMode ? 0 : undefined}
            onPointerDown={(e) => {
              if (editingId === button.id) {
                e.stopPropagation();
                return;
              }
              if (handleMultiSelectPointerDown(e, 'button', button.id)) return;
              startButtonDrag(e, button);
            }}
            onClick={(e) => {
              if (!viewMode) return;
              e.stopPropagation();
              if (isDismissButton) dismissButtonInView(button.id);
              else openButtonHref(button.href);
            }}
            onKeyDown={(e) => {
              if (!viewMode || (e.key !== 'Enter' && e.key !== ' ')) return;
              e.preventDefault();
              e.stopPropagation();
              if (isDismissButton) dismissButtonInView(button.id);
              else openButtonHref(button.href);
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              enterButtonEditMode(button.id);
            }}
          >
            {isToolbarOpen ? (
              <ProgramSlideFloatedToolbar
                {...floatedToolbarPortalProps}
                blockX={toolbarAnchor.x}
                blockY={toolbarAnchor.y}
                blockH={toolbarAnchor.h}
                className={
                  'cp-program-slide-canvas-toolbar cp-program-slide-canvas-button-toolbar' +
                  (isDismissButton || toolbarBatch ? ' cp-program-slide-canvas-button-toolbar--compact' : '')
                }
                placement={resolveToolbarPlacement(
                  toolbarAnchor.y,
                  toolbarAnchor.h,
                  isDismissButton || toolbarBatch ? SLIDE_TOOLBAR_SINGLE_ROW_HEIGHT_PX : SLIDE_TOOLBAR_DOUBLE_ROW_HEIGHT_PX
                )}
                zIndex={floatedToolbarZIndex}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <div className="cp-program-slide-canvas-button-toolbar-row">
                  <label className="cp-program-slide-canvas-tl">
                    크기
                    <input
                      type="number"
                      min={8}
                      max={96}
                      value={button.fontSizePx}
                      onChange={(e) =>
                        patchButtonTargets(button.id, {
                          fontSizePx: Math.min(96, Math.max(8, Math.round(Number(e.target.value) || 32))),
                        })
                      }
                    />
                    <div className="cp-program-slide-canvas-fontstep-group" role="group" aria-label="버튼 텍스트 크기 조절">
                      <button
                        type="button"
                        className="cp-program-slide-canvas-fontstep"
                        onClick={() => stepButtonFontSizeTargets(button.id, -2)}
                        aria-label="버튼 텍스트 크기 줄이기"
                        title="버튼 텍스트 크기 줄이기"
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path d="M5 11h14v2H5v-2z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="cp-program-slide-canvas-fontstep"
                        onClick={() => stepButtonFontSizeTargets(button.id, 2)}
                        aria-label="버튼 텍스트 크기 키우기"
                        title="버튼 텍스트 크기 키우기"
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path d="M11 5h2v14h-2V5zM5 11h14v2H5v-2z" />
                        </svg>
                      </button>
                    </div>
                  </label>
                  <label className="cp-program-slide-canvas-tl cp-program-slide-canvas-font-family-field">
                    글꼴
                    <select
                      value={normalizeSlideTextFontFamily(button.fontFamily)}
                      onChange={(e) =>
                        patchButtonTargets(button.id, {
                          fontFamily: normalizeSlideTextFontFamily(e.target.value),
                        })
                      }
                      aria-label="버튼 텍스트 글꼴"
                    >
                      {PROGRAM_SLIDE_TEXT_FONT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="cp-program-slide-canvas-tl">
                    텍스트색
                    <SlideColorPickerField
                      value={button.colorHex.match(/^#/) ? button.colorHex : '#141414'}
                      ariaLabel="버튼 텍스트 색상"
                      recentColors={slideRecentColors}
                      usedColors={slideUsedColors}
                    surface={isEvaluationSheetPreset ? 'light' : 'dark'}
                      onPreview={previewSlideColor((hex) => patchButtonTargets(button.id, { colorHex: hex }, 'skip'))}
                      onConfirm={confirmSlideColor((hex) => patchButtonTargets(button.id, { colorHex: hex }, 'skip'))}
                    />
                  </label>
                  <label className="cp-program-slide-canvas-tl">
                    배경색
                    <SlideColorPickerField
                      value={button.backgroundColorHex?.match(/^#/) ? button.backgroundColorHex : '#ffffff'}
                      fallback="#ffffff"
                      ariaLabel="버튼 배경색"
                      recentColors={slideRecentColors}
                      usedColors={slideUsedColors}
                    surface={isEvaluationSheetPreset ? 'light' : 'dark'}
                      onPreview={previewSlideColor((hex) =>
                        patchButtonTargets(button.id, { backgroundColorHex: hex }, 'skip')
                      )}
                      onConfirm={confirmSlideColor((hex) =>
                        patchButtonTargets(button.id, { backgroundColorHex: hex }, 'skip')
                      )}
                    />
                  </label>
                  <button
                    type="button"
                    className={'cp-program-slide-canvas-bold' + (button.bold ? ' is-on' : '')}
                    onClick={() => patchButtonTargets(button.id, { bold: !button.bold })}
                  >
                    B
                  </button>
                  {toolbarBatch ? null : (
                  <ProgramSlideTrashDeleteControl
                    isHolding={holdingDeleteId === button.id}
                    showConfirm={
                      !!deleteConfirmTarget &&
                      deleteConfirmTarget.id === button.id &&
                      deleteConfirmTarget.kind === 'button'
                    }
                    ariaLabel="링크 버튼 삭제: 짧게 확인, 길게 바로 삭제"
                    onPointerDown={(e) => startDeleteHold(e, button.id, 'button')}
                    onPointerUp={() => endDeleteHold(true)}
                    onPointerLeave={() => endDeleteHold(false)}
                    onPointerCancel={() => endDeleteHold(false)}
                    onCancelConfirm={() => setDeleteConfirmTarget(null)}
                    onConfirmDelete={() => {
                      const target = deleteConfirmTarget;
                      setDeleteConfirmTarget(null);
                      if (target) executeDeleteBlock(target.id, target.kind);
                    }}
                  />
                  )}
                </div>
                {!isDismissButton && !toolbarBatch ? (
                  <label className="cp-program-slide-canvas-tl cp-program-slide-canvas-link-field">
                    링크
                    <input
                      type="text"
                      value={button.href}
                      onChange={(e) => patchButton(button.id, { href: e.target.value })}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder="https://..."
                      aria-label="버튼 링크"
                    />
                    <button
                      type="button"
                      className="cp-program-slide-canvas-link-open-btn"
                      onMouseDown={(e) => e.preventDefault()}
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openButtonHref(button.href);
                      }}
                      disabled={!button.href.trim()}
                      title="링크 바로가기"
                      aria-label="링크 바로가기"
                    >
                      바로가기
                    </button>
                  </label>
                ) : null}
              </ProgramSlideFloatedToolbar>
            ) : null}
            <textarea
              className={'cp-program-slide-canvas-button-text' + (editingId === button.id ? ' is-editing' : '')}
              style={{
                fontSize: `${resolveFontPx(button.fontSizePx)}px`,
                fontFamily: slideTextFontCssFamily(button.fontFamily),
                color: slideDisplayHex(button.colorHex, '#141414'),
                fontWeight: button.bold ? 700 : 400,
                backgroundColor: slideDisplayColors.bg(slideDisplayHex(button.backgroundColorHex, '#ffffff')),
                borderRadius: `${radius}px`,
              }}
              value={button.text}
              readOnly={!canEdit || editingId !== button.id}
              onChange={(e) => patchButton(button.id, { text: e.target.value })}
              onKeyDown={(e) => {
                if (editingId === button.id && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) e.stopPropagation();
              }}
              ref={(el) => {
                textareaRefs.current[button.id] = el;
              }}
              onPointerDown={(e) => {
                if (editingId === button.id) {
                  e.stopPropagation();
                  return;
                }
                if (handleMultiSelectPointerDown(e, 'button', button.id)) return;
                startButtonDrag(e, button);
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                enterButtonEditMode(button.id);
              }}
              onBlur={() => {
                if (!canEdit) return;
                setEditingId((cur) => (cur === button.id ? null : cur));
              }}
              placeholder="링크 버튼"
              spellCheck={false}
            />
          </div>
          );
        })}
        {shapes.map((shape) => {
          const isToolbarOpen =
            canEdit && canOpenToolbarFor('shape') && selectedKind === 'shape' && selectedId === shape.id;
          const toolbarBatch = isToolbarBatchFor('shape');
          const toolbarAnchor = toolbarAnchorFor('shape', shape.x, shape.y, shape.h);
          const sw = Math.max(0, shape.strokeWidthPx ?? 2);
          const fill = shape.fillColorHex?.trim() ? shape.fillColorHex : 'transparent';
          const displayFill =
            fill === 'transparent' ? fill : slideDisplayColors.bg(slideDisplayHex(fill, '#ffffff'));
          const borderRadiusCss =
            shape.shape === 'circle' ? '50%' : `${Math.min(shape.cornerRadiusPx ?? 14, 999)}px`;
          const taAlign: ProgramSlideTextAlign =
            shape.textAlign === 'justify' ||
            shape.textAlign === 'left' ||
            shape.textAlign === 'right' ||
            shape.textAlign === 'center'
              ? shape.textAlign
              : 'center';
          const stroke = shape.strokeColorHex?.match(/^#/) ? shape.strokeColorHex : '#bdbdbd';
          const displayStroke = slideDisplayColors.stroke(stroke);
          const outerBorder = sw > 0;
          return (
            <div
              key={shape.id}
              className={'cp-program-slide-canvas-shape' + (isBlockSelected('shape', shape.id) ? ' is-selected' : '')}
              style={{
                left: `${shape.x * 100}%`,
                top: `${shape.y * 100}%`,
                width: `${shape.w * 100}%`,
                height: `${shape.h * 100}%`,
                zIndex: getBlockLayerZIndex(blockLayerZ(shape.zIndex), isToolbarOpen),
                borderColor: outerBorder ? displayStroke : 'transparent',
                borderStyle: outerBorder ? 'solid' : 'none',
                borderWidth: outerBorder ? `${sw}px` : 0,
                borderRadius: borderRadiusCss,
                backgroundColor: displayFill,
                pointerEvents: viewMode ? 'none' : undefined,
              }}
              onPointerDown={(e) => {
                if (!canEdit) return;
                if (editingId === shape.id) {
                  e.stopPropagation();
                  return;
                }
                if (handleMultiSelectPointerDown(e, 'shape', shape.id)) return;
                startShapeDrag(e, shape);
              }}
            >
              {isToolbarOpen ? (
                <ProgramSlideFloatedToolbar
                  {...floatedToolbarPortalProps}
                  blockX={toolbarAnchor.x}
                  blockY={toolbarAnchor.y}
                  blockH={toolbarAnchor.h}
                  className={
                    'cp-program-slide-canvas-toolbar cp-program-slide-canvas-shape-toolbar' +
                    (shape.locked ? ' is-locked' : '')
                  }
                  placement={resolveToolbarPlacement(
                    toolbarAnchor.y,
                    toolbarAnchor.h,
                    SLIDE_TOOLBAR_SHAPE_TWO_ROW_HEIGHT_PX
                  )}
                  zIndex={floatedToolbarZIndex}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <div className="cp-program-slide-canvas-button-toolbar-row">
                    <span className="cp-program-slide-canvas-tl">{shape.shape === 'ruled' ? '글쓰기' : '도형'}</span>
                    <label className="cp-program-slide-canvas-tl">
                      채우기
                      <SlideColorPickerField
                        value={fill.match(/^#/) ? fill : '#ffffff'}
                        fallback="#ffffff"
                        ariaLabel="도형 채우기 색"
                        recentColors={slideRecentColors}
                        usedColors={slideUsedColors}
                    surface={isEvaluationSheetPreset ? 'light' : 'dark'}
                        swatchStyle={{
                          backgroundColor: fill !== 'transparent' && fill.match(/^#/) ? fill : 'transparent',
                          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.25)',
                        }}
                        onPreview={previewSlideColor((hex) => patchShapeTargets(shape.id, { fillColorHex: hex }, 'skip'))}
                        onConfirm={confirmSlideColor((hex) => patchShapeTargets(shape.id, { fillColorHex: hex }, 'skip'))}
                      />
                    </label>
                    <label className="cp-program-slide-canvas-tl">
                      테두리
                      <SlideColorPickerField
                        value={stroke}
                        ariaLabel="도형 테두리 색"
                        recentColors={slideRecentColors}
                        usedColors={slideUsedColors}
                    surface={isEvaluationSheetPreset ? 'light' : 'dark'}
                        onPreview={previewSlideColor((hex) =>
                          patchShapeTargets(shape.id, { strokeColorHex: hex }, 'skip')
                        )}
                        onConfirm={confirmSlideColor((hex) =>
                          patchShapeTargets(shape.id, { strokeColorHex: hex }, 'skip')
                        )}
                      />
                    </label>
                    <label className="cp-program-slide-canvas-tl">
                      선굵기
                      <input
                        type="number"
                        min={0}
                        max={16}
                        value={sw}
                        onChange={(e) =>
                          patchShapeTargets(shape.id, { strokeWidthPx: Math.min(16, Math.max(0, Math.round(Number(e.target.value) || 0))) })
                        }
                      />
                    </label>
                  </div>
                  {shape.shape === 'ruled' ? (
                    <div className="cp-program-slide-canvas-button-toolbar-row">
                      <span className="cp-slide-ruled-seg" role="radiogroup" aria-label="글쓰기 배경">
                        {([['lines', '줄'], ['grid', '격자 선'], ['dots', '격자 점']] as const).map(([v, label]) => (
                          <button
                            key={v}
                            type="button"
                            role="radio"
                            aria-checked={(shape.ruledBackground ?? 'lines') === v}
                            className={(shape.ruledBackground ?? 'lines') === v ? 'is-active' : ''}
                            onClick={() => patchShapeTargets(shape.id, { ruledBackground: v })}
                          >
                            {label}
                          </button>
                        ))}
                      </span>
                      {true ? (
                      <>
                      <label className="cp-program-slide-canvas-tl">
                        {(shape.ruledBackground ?? 'lines') === 'dots' ? '점 간격' : (shape.ruledBackground ?? 'lines') === 'grid' ? '칸 간격' : '줄간격'}
                        <input
                          type="number"
                          min={SLIDE_RULED_MIN_SPACING_PX}
                          max={SLIDE_RULED_MAX_SPACING_PX}
                          value={shape.ruledLineSpacingPx ?? SLIDE_RULED_DEFAULT_SPACING_PX}
                          onChange={(e) =>
                            patchShapeTargets(shape.id, {
                              ruledLineSpacingPx: Math.min(
                                SLIDE_RULED_MAX_SPACING_PX,
                                Math.max(SLIDE_RULED_MIN_SPACING_PX, Math.round(Number(e.target.value) || SLIDE_RULED_DEFAULT_SPACING_PX))
                              ),
                            })
                          }
                        />
                      </label>
                      <label className="cp-program-slide-canvas-tl">
                        {(shape.ruledBackground ?? 'lines') === 'dots' ? '점 색' : (shape.ruledBackground ?? 'lines') === 'grid' ? '선 색' : '줄 색'}
                        <SlideColorPickerField
                          value={shape.ruledLineColorHex || '#c8c8c8'}
                          fallback="#c8c8c8"
                          ariaLabel="글쓰기 줄 색"
                          recentColors={slideRecentColors}
                          usedColors={slideUsedColors}
                          surface={isEvaluationSheetPreset ? 'light' : 'dark'}
                          swatchStyle={{ backgroundColor: shape.ruledLineColorHex || '#c8c8c8', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.25)' }}
                          onPreview={previewSlideColor((hex) => patchShapeTargets(shape.id, { ruledLineColorHex: hex }, 'skip'))}
                          onConfirm={confirmSlideColor((hex) => patchShapeTargets(shape.id, { ruledLineColorHex: hex }, 'skip'))}
                        />
                      </label>
                      {(shape.ruledBackground ?? 'lines') !== 'dots' ? (
                      <label className="cp-program-slide-canvas-tl">
                        <input
                          type="checkbox"
                          checked={!!shape.ruledLineDashed}
                          onChange={(e) => patchShapeTargets(shape.id, { ruledLineDashed: e.target.checked })}
                        />
                        점선
                      </label>
                      ) : null}
                      </>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="cp-program-slide-canvas-button-toolbar-row">
                    <label className="cp-program-slide-canvas-tl">
                      크기
                      <input
                        type="number"
                        min={8}
                        max={96}
                        value={shape.fontSizePx}
                        onChange={(e) =>
                          patchShapeTargets(shape.id, {
                            fontSizePx: Math.min(96, Math.max(8, Math.round(Number(e.target.value) || 32))),
                          })
                        }
                      />
                      <div className="cp-program-slide-canvas-fontstep-group" role="group" aria-label="도형 텍스트 크기 조절">
                        <button
                          type="button"
                          className="cp-program-slide-canvas-fontstep"
                          onClick={() => stepShapeFontSizeTargets(shape.id, -2)}
                          aria-label="도형 텍스트 크기 줄이기"
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M5 11h14v2H5v-2z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="cp-program-slide-canvas-fontstep"
                          onClick={() => stepShapeFontSizeTargets(shape.id, 2)}
                          aria-label="도형 텍스트 크기 키우기"
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M11 5h2v14h-2V5zM5 11h14v2H5v-2z" />
                          </svg>
                        </button>
                      </div>
                    </label>
                    <label className="cp-program-slide-canvas-tl cp-program-slide-canvas-font-family-field">
                      글꼴
                      <select
                        value={normalizeSlideTextFontFamily(shape.fontFamily)}
                        onChange={(e) => patchShapeTargets(shape.id, { fontFamily: normalizeSlideTextFontFamily(e.target.value) })}
                        aria-label="도형 텍스트 글꼴"
                      >
                        {PROGRAM_SLIDE_TEXT_FONT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="cp-program-slide-canvas-tl">
                      텍스트색
                      <SlideColorPickerField
                        value={shape.colorHex.match(/^#/) ? shape.colorHex : '#000000'}
                        fallback="#000000"
                        ariaLabel="도형 텍스트 색상"
                        recentColors={slideRecentColors}
                        usedColors={slideUsedColors}
                    surface={isEvaluationSheetPreset ? 'light' : 'dark'}
                        onPreview={previewSlideColor((hex) => patchShapeTargets(shape.id, { colorHex: hex }, 'skip'))}
                        onConfirm={confirmSlideColor((hex) => patchShapeTargets(shape.id, { colorHex: hex }, 'skip'))}
                      />
                    </label>
                    <button
                      type="button"
                      className={'cp-program-slide-canvas-bold' + (shape.bold ? ' is-on' : '')}
                      onClick={() => patchShapeTargets(shape.id, { bold: !shape.bold })}
                    >
                      B
                    </button>
                    <div className="cp-program-slide-canvas-align-group" role="group" aria-label="도형 텍스트 정렬">
                      {PROGRAM_SLIDE_TEXT_ALIGN_OPTIONS.map((al) => (
                        <button
                          key={al}
                          type="button"
                          className={'cp-program-slide-canvas-align' + (taAlign === al ? ' is-on' : '')}
                          onClick={() => patchShapeTargets(shape.id, { textAlign: al })}
                          aria-label={programSlideTextAlignLabel(al)}
                          title={programSlideTextAlignLabel(al)}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            {PROGRAM_SLIDE_TEXT_ALIGN_ICONS[al]}
                          </svg>
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      className={'cp-program-slide-canvas-lock' + (shape.locked ? ' is-on' : '')}
                      onClick={() => toggleLockTargets('shape', shape.id)}
                      aria-label={shape.locked ? '잠금 해제' : '잠금'}
                      title={shape.locked ? '잠금 해제' : '잠금'}
                    >
                      <SlideLockIcon locked={!!shape.locked} />
                    </button>
                    {toolbarBatch ? null : (
                    <ProgramSlideTrashDeleteControl
                      isHolding={holdingDeleteId === shape.id}
                      showConfirm={
                        !!deleteConfirmTarget &&
                        deleteConfirmTarget.id === shape.id &&
                        deleteConfirmTarget.kind === 'shape'
                      }
                      disabled={!!shape.locked}
                      ariaLabel="도형 삭제: 짧게 확인, 길게 바로 삭제"
                      onPointerDown={(e) => startDeleteHold(e, shape.id, 'shape')}
                      onPointerUp={() => endDeleteHold(true)}
                      onPointerLeave={() => endDeleteHold(false)}
                      onPointerCancel={() => endDeleteHold(false)}
                      onCancelConfirm={() => setDeleteConfirmTarget(null)}
                      onConfirmDelete={() => {
                        const target = deleteConfirmTarget;
                        setDeleteConfirmTarget(null);
                        if (target) executeDeleteBlock(target.id, target.kind);
                      }}
                    />
                    )}
                  </div>
                </ProgramSlideFloatedToolbar>
              ) : null}
              {shape.shape === 'ruled'
                ? (() => {
                    // 줄 수 = floor(안쪽 높이 / 줄간격). 위치는 블럭 높이 대비 % → 캡처(클론 크기 변경)에도 안전
                    const rootHpx = Math.max(1, canvasCoordRect?.height ?? 720);
                    const rootWpx = Math.max(1, canvasCoordRect?.width ?? 794);
                    // 평가지/활동지: A4 폭 794 기준 / 프레젠테이션: 글자 크기와 같은 배율
                    const scale = isEvaluationSheetPreset ? rootWpx / 794 : resolveFontPx(100) / 100;
                    const sp = Math.max(4, (shape.ruledLineSpacingPx ?? SLIDE_RULED_DEFAULT_SPACING_PX) * scale);
                    const hPx = Math.max(1, shape.h * rootHpx - sw * 2);
                    const padB = 6 * scale;
                    const bg = shape.ruledBackground ?? 'lines';
                    const color = slideDisplayColors.stroke(shape.ruledLineColorHex || '#c8c8c8');
                    if (bg === 'dots') {
                      // 격자 점: 실제 div(원) — html2canvas 캡처 안전. 위치는 % → 클론 크기 변경에도 동일
                      const padX = 10 * scale;
                      const wPx = Math.max(1, shape.w * rootWpx - sw * 2 - padX * 2);
                      const cols = Math.max(0, Math.floor(wPx / sp));
                      const rows = Math.max(0, Math.floor((hPx - padB) / sp));
                      const dotPx = Math.max(2, Math.round(2.4 * scale));
                      const offX = (wPx - cols * sp) / 2;
                      const dots = [];
                      if (cols * rows <= 2500) {
                        for (let r = 1; r <= rows; r += 1)
                          for (let c = 0; c <= cols; c += 1)
                            dots.push(
                              <div
                                key={`${r}:${c}`}
                                className="cp-slide-ruled-dot"
                                style={{
                                  left: `calc(${((offX + c * sp) / wPx) * 100}% - ${dotPx / 2}px)`,
                                  top: `calc(${((r * sp) / hPx) * 100}% - ${dotPx / 2}px)`,
                                  width: dotPx,
                                  height: dotPx,
                                  background: color,
                                }}
                              />
                            );
                      }
                      return (
                        <div className="cp-slide-ruled-lines cp-slide-ruled-dots" aria-hidden>
                          {dots}
                        </div>
                      );
                    }
                    const n = Math.max(0, Math.floor((hPx - padB) / sp));
                    const lines = [];
                    const lineStyle = `1px ${shape.ruledLineDashed ? 'dashed' : 'solid'} ${color}`;
                    if (bg === 'grid') {
                      // 격자 선(모눈): 블럭 가득(여백 없음), 안쪽 선만 — 바깥 틀은 블럭 테두리뿐. 모서리 칸은 잘려도 됨
                      const wPx = Math.max(1, shape.w * rootWpx - sw * 2);
                      for (let y = sp; y < hPx - 0.5; y += sp)
                        lines.push(
                          <div key={`h${y}`} className="cp-slide-ruled-line" style={{ top: `${(y / hPx) * 100}%`, borderTop: lineStyle }} />
                        );
                      for (let x = sp; x < wPx - 0.5; x += sp)
                        lines.push(
                          <div key={`v${x}`} className="cp-slide-ruled-vline" style={{ left: `${(x / wPx) * 100}%`, borderLeft: lineStyle }} />
                        );
                      return (
                        <div className="cp-slide-ruled-lines cp-slide-ruled-grid" aria-hidden>
                          {lines}
                        </div>
                      );
                    }
                    for (let li = 1; li <= n; li += 1) {
                      lines.push(
                        <div
                          key={li}
                          className="cp-slide-ruled-line"
                          style={{
                            top: `${((li * sp) / hPx) * 100}%`,
                            borderTop: `1px ${shape.ruledLineDashed ? 'dashed' : 'solid'} ${color}`,
                          }}
                        />
                      );
                    }
                    return (
                      <div className="cp-slide-ruled-lines" aria-hidden>
                        {lines}
                      </div>
                    );
                  })()
                : null}
              {shape.shape === 'ruled' ? null : (
              <div className="cp-program-slide-canvas-shape-text-wrap">
                <textarea
                  className={'cp-program-slide-canvas-shape-text' + (editingId === shape.id ? ' is-editing' : '')}
                  rows={1}
                  style={{
                    fontSize: `${resolveFontPx(shape.fontSizePx)}px`,
                    fontFamily: slideTextFontCssFamily(shape.fontFamily),
                    color: slideDisplayHex(shape.colorHex, '#000000'),
                    fontWeight: shape.bold ? 700 : 400,
                    textAlign: taAlign,
                  }}
                  value={shape.text}
                  readOnly={!canEdit || editingId !== shape.id}
                  onChange={(e) => patchShape(shape.id, { text: e.target.value })}
                  onKeyDown={(e) => {
                    if (editingId === shape.id && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) e.stopPropagation();
                  }}
                  ref={(el) => {
                    textareaRefs.current[shape.id] = el;
                  }}
                  onPointerDown={(e) => {
                    if (!canEdit) return;
                    if (editingId === shape.id) {
                      e.stopPropagation();
                      return;
                    }
                    if (handleMultiSelectPointerDown(e, 'shape', shape.id)) return;
                    startShapeDrag(e, shape);
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    enterShapeEditMode(shape.id);
                  }}
                  onBlur={() => {
                    if (!canEdit) return;
                    setEditingId((cur) => (cur === shape.id ? null : cur));
                  }}
                  spellCheck={false}
                />
              </div>
              )}
            </div>
          );
        })}
        {true
          ? tables.map((table) => {
              const isSelected = isBlockSelected('table', table.id);
              const selection =
                tableCellSelection?.tableId === table.id
                  ? {
                      rowStart: tableCellSelection.rowStart,
                      colStart: tableCellSelection.colStart,
                      rowEnd: tableCellSelection.rowEnd,
                      colEnd: tableCellSelection.colEnd,
                    }
                  : null;
              return (
                <ProgramSlideTableBlockView
                  key={table.id}
                  table={table}
                  readOnly={!canEdit}
                  isTableSelected={isSelected}
                  layerZIndex={getBlockLayerZIndex(blockLayerZ(table.zIndex), isSelected)}
                  selection={selection}
                  editingCellId={editingTableCellId}
                  canvasHeightPx={Math.max(1, canvasCoordRect?.height ?? 720)}
                  onAutoResizeTable={
                    canEdit && !table.locked
                      ? (nextTable) => {
                          patchTable(
                            table.id,
                            { h: nextTable.h, rowHeightsFrac: nextTable.rowHeightsFrac },
                            'skip'
                          );
                        }
                      : undefined
                  }
                  onTableLayoutChange={
                    canEdit
                      ? () => {
                          window.requestAnimationFrame(() => syncTableAnchorRect(table.id));
                        }
                      : undefined
                  }
                  blockRef={(el) => {
                    tableBlockElRefs.current[table.id] = el;
                  }}
                  onBorderPointerDown={(e) => {
                    if (!canEdit) return;
                    e.stopPropagation();
                    if (handleMultiSelectPointerDown(e, 'table', table.id)) return;
                    startTableDrag(e, table);
                  }}
                  onCellPointerDown={(e, row, col, cellId) =>
                    handleTableCellPointerDown(e, table, row, col, cellId)
                  }
                  onCellDoubleClick={(e, row, col, cellId) =>
                    handleTableCellDoubleClick(e, table, row, col, cellId)
                  }
                  onCellTextChange={(cellId, text) => {
                    const nextCells = table.cells.map((c) => (c.id === cellId ? { ...c, text } : c));
                    patchTable(table.id, { cells: nextCells }, 'skip');
                  }}
                  onCellEditBlur={(cellId) => {
                    if (suppressTableCellBlurRef.current || tableCellPointerDownRef.current) return;
                    setEditingTableCellId((cur) => (cur === cellId ? null : cur));
                    const live = (layoutRef.current.tables ?? []).find((t) => t.id === table.id);
                    if (live) replaceTable(live, 'record');
                  }}
                />
              );
            })
          : null}
        {polylines.map((pl) => {
          const b = polylineBBox(pl.points);
          if (!b) return null;
          const pts = pl.points.map((p) => `${p.x},${p.y}`).join(' ');
          const sel = isBlockSelected('polyline', pl.id);
          const isToolbarOpen =
            canEdit && !viewMode && canOpenToolbarFor('polyline') && selectedKind === 'polyline' && selectedId === pl.id;
          const toolbarBatch = isToolbarBatchFor('polyline');
          const swPx = Math.max(1, Math.round(pl.strokeWidthPx ?? defaultPolylineStrokeWidthPx));
          const strokeCol = slideDisplayColors.stroke(
            pl.strokeColorHex?.match(/^#/) ? pl.strokeColorHex : '#000000'
          );
          const pList = pl.points;
          const cr = rootRef.current?.getBoundingClientRect();
          const canvasArrowW = Math.max(1, cr?.width ?? 1280);
          const canvasArrowH = Math.max(1, cr?.height ?? 720);
          const p0 = pList[0];
          const p1 = pList[1];
          const startArrowPath =
            pl.startArrow && pList.length >= 2
              ? polylineChevronCapPath(
                  p0,
                  { x: p0.x - p1.x, y: p0.y - p1.y },
                  swPx,
                  canvasArrowW,
                  canvasArrowH
                )
              : '';
          const pn1 = pList[pList.length - 1];
          const pn2 = pList[pList.length - 2];
          const endArrowPath =
            pl.endArrow && pList.length >= 2
              ? polylineChevronCapPath(
                  pn1,
                  { x: pn1.x - pn2.x, y: pn1.y - pn2.y },
                  swPx,
                  canvasArrowW,
                  canvasArrowH
                )
              : '';
          const layoutH = Math.max(b.h, 0.06);
          const toolbarAnchor = toolbarAnchorFor('polyline', b.x, b.y, layoutH);
          const tp = resolveToolbarPlacement(toolbarAnchor.y, toolbarAnchor.h, SLIDE_TOOLBAR_SINGLE_ROW_HEIGHT_PX);
          return (
            <React.Fragment key={pl.id}>
              <svg
                className={'cp-program-slide-canvas-polyline-svg' + (sel ? ' is-selected' : '')}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  overflow: 'visible',
                  zIndex: getBlockLayerZIndex(blockLayerZ(pl.zIndex), isToolbarOpen),
                }}
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
                aria-hidden
              >
                <polyline
                  fill="none"
                  stroke={strokeCol}
                  strokeWidth={`${swPx}px`}
                  vectorEffect="non-scaling-stroke"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  points={pts}
                  style={{
                    pointerEvents: viewMode ? 'none' : 'stroke',
                    cursor: viewMode ? 'default' : 'grab',
                  }}
                  onPointerDown={(e) => {
                    if (!canEdit || viewMode) return;
                    e.stopPropagation();
                    if (handleMultiSelectPointerDown(e, 'polyline', pl.id)) return;
                    startPolylineDrag(e, pl);
                  }}
                />
                {startArrowPath ? (
                  <path
                    d={startArrowPath}
                    fill="none"
                    stroke={strokeCol}
                    strokeWidth={`${swPx}px`}
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    strokeLinejoin="miter"
                    strokeMiterlimit={2.8}
                    pointerEvents="none"
                  />
                ) : null}
                {endArrowPath ? (
                  <path
                    d={endArrowPath}
                    fill="none"
                    stroke={strokeCol}
                    strokeWidth={`${swPx}px`}
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    strokeLinejoin="miter"
                    strokeMiterlimit={2.8}
                    pointerEvents="none"
                  />
                ) : null}
              </svg>
              {isToolbarOpen ? (
                <ProgramSlideFloatedToolbar
                  {...floatedToolbarPortalProps}
                  blockX={toolbarAnchor.x}
                  blockY={toolbarAnchor.y}
                  blockH={toolbarAnchor.h}
                  className={
                    'cp-program-slide-canvas-toolbar cp-program-slide-canvas-shape-toolbar' +
                    (pl.locked ? ' is-locked' : '')
                  }
                  placement={tp}
                  zIndex={floatedToolbarZIndex}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                    <div className="cp-program-slide-canvas-button-toolbar-row">
                      <span className="cp-program-slide-canvas-tl">선</span>
                      <span className="cp-program-slide-canvas-polyline-arrow-toggle-group">
                        <button
                          type="button"
                          className={
                            'cp-program-slide-canvas-polyline-arrow-btn' + (pl.startArrow ? ' is-on' : '')
                          }
                          aria-pressed={Boolean(pl.startArrow)}
                          aria-label="시작점 화살표"
                          title="시작점 화살표"
                          onClick={() => patchPolylineTargets(pl.id, { startArrow: !pl.startArrow })}
                        >
                          <svg
                            className="cp-program-slide-canvas-polyline-arrow-btn-icon"
                            viewBox="0 0 16 16"
                            width={16}
                            height={16}
                            aria-hidden
                          >
                            <path
                              d="M 10 2.5 L 4.5 8 L 10 13.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.25"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className={'cp-program-slide-canvas-polyline-arrow-btn' + (pl.endArrow ? ' is-on' : '')}
                          aria-pressed={Boolean(pl.endArrow)}
                          aria-label="끝점 화살표"
                          title="끝점 화살표"
                          onClick={() => patchPolylineTargets(pl.id, { endArrow: !pl.endArrow })}
                        >
                          <svg
                            className="cp-program-slide-canvas-polyline-arrow-btn-icon"
                            viewBox="0 0 16 16"
                            width={16}
                            height={16}
                            aria-hidden
                          >
                            <path
                              d="M 6 2.5 L 11.5 8 L 6 13.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.25"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </span>
                        <label className="cp-program-slide-canvas-tl">
                        색
                        <SlideColorPickerField
                          value={strokeCol}
                          ariaLabel="선 색"
                          recentColors={slideRecentColors}
                          usedColors={slideUsedColors}
                    surface={isEvaluationSheetPreset ? 'light' : 'dark'}
                          onPreview={previewSlideColor((hex) =>
                            patchPolylineTargets(pl.id, { strokeColorHex: hex }, 'skip')
                          )}
                          onConfirm={confirmSlideColor((hex) =>
                            patchPolylineTargets(pl.id, { strokeColorHex: hex }, 'skip')
                          )}
                        />
                      </label>
                      <label className="cp-program-slide-canvas-tl">
                        굵기
                        <input
                          type="number"
                          min={1}
                          max={16}
                          value={pl.strokeWidthPx ?? defaultPolylineStrokeWidthPx}
                          onChange={(e) =>
                            patchPolylineTargets(pl.id, {
                              strokeWidthPx: Math.min(
                                16,
                                Math.max(1, Math.round(Number(e.target.value) || defaultPolylineStrokeWidthPx))
                              ),
                            })
                          }
                          aria-label="선 굵기(px)"
                        />
                      </label>
                      <button
                        type="button"
                        className={'cp-program-slide-canvas-lock' + (pl.locked ? ' is-on' : '')}
                        onClick={() => toggleLockTargets('polyline', pl.id)}
                        aria-label={pl.locked ? '잠금 해제' : '잠금'}
                        title={pl.locked ? '잠금 해제' : '잠금'}
                      >
                        <SlideLockIcon locked={!!pl.locked} />
                      </button>
                      {toolbarBatch ? null : (
                      <ProgramSlideTrashDeleteControl
                        isHolding={holdingDeleteId === pl.id}
                        showConfirm={
                          !!deleteConfirmTarget &&
                          deleteConfirmTarget.id === pl.id &&
                          deleteConfirmTarget.kind === 'polyline'
                        }
                        disabled={!!pl.locked}
                        ariaLabel="선 삭제: 짧게 확인, 길게 바로 삭제"
                        onPointerDown={(e) => startDeleteHold(e, pl.id, 'polyline')}
                        onPointerUp={() => endDeleteHold(true)}
                        onPointerLeave={() => endDeleteHold(false)}
                        onPointerCancel={() => endDeleteHold(false)}
                        onCancelConfirm={() => setDeleteConfirmTarget(null)}
                        onConfirmDelete={() => {
                          const target = deleteConfirmTarget;
                          setDeleteConfirmTarget(null);
                          if (target) executeDeleteBlock(target.id, target.kind);
                        }}
                      />
                      )}
                    </div>
                </ProgramSlideFloatedToolbar>
              ) : null}
            </React.Fragment>
          );
        })}
        {canEdit && placeCanvasTool === 'line' && lineDraftPoints && lineDraftPoints.length > 0 ? (
          <svg
            className="cp-program-slide-canvas-polyline-draft-svg"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              overflow: 'visible',
              zIndex: activeToolbarBlockLayer + 2,
            }}
            viewBox="0 0 1 1"
            preserveAspectRatio="none"
            aria-hidden
          >
            {lineDraftPoints.length >= 2 ? (
              <polyline
                fill="none"
                stroke={slideDisplayColors.stroke('#757575')}
                strokeWidth="2px"
                vectorEffect="non-scaling-stroke"
                strokeDasharray="6 4"
                strokeOpacity={0.92}
                strokeLinecap="round"
                strokeLinejoin="round"
                points={lineDraftPoints.map((p) => `${p.x},${p.y}`).join(' ')}
              />
            ) : null}
            {linePointerNorm ? (
              <line
                x1={lineDraftPoints[lineDraftPoints.length - 1].x}
                y1={lineDraftPoints[lineDraftPoints.length - 1].y}
                x2={linePointerNorm.x}
                y2={linePointerNorm.y}
                stroke={slideDisplayColors.stroke('#757575')}
                strokeWidth="2px"
                vectorEffect="non-scaling-stroke"
                strokeDasharray="6 4"
                strokeOpacity={0.92}
                strokeLinecap="round"
              />
            ) : null}
          </svg>
        ) : null}
        {quizzes.map((quiz) => {
          const isToolbarOpen = selectedKind === 'quiz' && selectedId === quiz.id && canEdit && !hasMultiSelection;
          const isSucceeded = quizSucceededIds.has(quiz.id);
          const isWrongFlash = quizWrongFlashIds.has(quiz.id);
          const isEditingPlaceholder = canEdit && !viewMode && editingId === quiz.id;
          const radius = quiz.cornerRadiusPx ?? 999;
          const viewValue = quizDraftById[quiz.id] ?? '';
          return (
            <div
              key={quiz.id}
              className={
                'cp-program-slide-canvas-quiz' +
                (isBlockSelected('quiz', quiz.id) ? ' is-selected' : '') +
                (isWrongFlash ? ' is-wrong-flash' : '') +
                (isSucceeded ? ' is-succeeded' : '')
              }
              style={{
                left: `${quiz.x * 100}%`,
                top: `${quiz.y * 100}%`,
                width: `${quiz.w * 100}%`,
                height: `${quiz.h * 100}%`,
                backgroundColor: slideDisplayColors.bg(slideDisplayHex(quiz.backgroundColorHex, '#ffffff')),
                borderRadius: `${radius}px`,
                zIndex: getBlockLayerZIndex(blockLayerZ(quiz.zIndex), isToolbarOpen),
              }}
              onPointerDown={(e) => {
                if (!canEdit) return;
                if (isEditingPlaceholder) return;
                if (handleMultiSelectPointerDown(e, 'quiz', quiz.id)) return;
                startQuizDrag(e, quiz);
              }}
              onDoubleClick={(e) => {
                if (!canEdit || viewMode) return;
                e.stopPropagation();
                enterQuizPlaceholderEditMode(quiz.id);
              }}
            >
              {isToolbarOpen ? (
                <ProgramSlideFloatedToolbar
                  {...floatedToolbarPortalProps}
                  blockX={quiz.x}
                  blockY={quiz.y}
                  blockH={quiz.h}
                  className="cp-program-slide-canvas-toolbar cp-program-slide-canvas-quiz-toolbar"
                  placement={resolveToolbarPlacement(quiz.y, quiz.h, SLIDE_TOOLBAR_DOUBLE_ROW_HEIGHT_PX)}
                  zIndex={floatedToolbarZIndex}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <div className="cp-program-slide-canvas-quiz-toolbar-row">
                    <label className="cp-program-slide-canvas-tl">
                      크기
                      <input
                        type="number"
                        min={8}
                        max={96}
                        value={quiz.fontSizePx}
                        onChange={(e) =>
                          patchQuiz(quiz.id, {
                            fontSizePx: Math.min(96, Math.max(8, Math.round(Number(e.target.value) || 72))),
                          })
                        }
                      />
                      <div className="cp-program-slide-canvas-fontstep-group" role="group" aria-label="퀴즈 텍스트 크기 조절">
                        <button
                          type="button"
                          className="cp-program-slide-canvas-fontstep"
                          onClick={() => patchQuiz(quiz.id, { fontSizePx: Math.max(8, quiz.fontSizePx - 2) })}
                          aria-label="퀴즈 텍스트 크기 줄이기"
                          title="퀴즈 텍스트 크기 줄이기"
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M5 11h14v2H5v-2z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="cp-program-slide-canvas-fontstep"
                          onClick={() => patchQuiz(quiz.id, { fontSizePx: Math.min(96, quiz.fontSizePx + 2) })}
                          aria-label="퀴즈 텍스트 크기 키우기"
                          title="퀴즈 텍스트 크기 키우기"
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                            <path d="M11 5h2v14h-2V5zM5 11h14v2H5v-2z" />
                          </svg>
                        </button>
                      </div>
                    </label>
                    <label className="cp-program-slide-canvas-tl cp-program-slide-canvas-font-family-field">
                      글꼴
                      <select
                        value={normalizeSlideTextFontFamily(quiz.fontFamily)}
                        onChange={(e) =>
                          patchQuiz(quiz.id, {
                            fontFamily: normalizeSlideTextFontFamily(e.target.value),
                          })
                        }
                        aria-label="퀴즈 글꼴"
                      >
                        {PROGRAM_SLIDE_TEXT_FONT_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="cp-program-slide-canvas-tl">
                      텍스트색
                      <SlideColorPickerField
                        value={quiz.colorHex.match(/^#/) ? quiz.colorHex : QUIZ_DEFAULT_TEXT_COLOR_HEX}
                        fallback={QUIZ_DEFAULT_TEXT_COLOR_HEX}
                        ariaLabel="퀴즈 텍스트 색상"
                        recentColors={slideRecentColors}
                        usedColors={slideUsedColors}
                    surface={isEvaluationSheetPreset ? 'light' : 'dark'}
                        onPreview={previewSlideColor((hex) => patchQuiz(quiz.id, { colorHex: hex }, 'skip'))}
                        onConfirm={confirmSlideColor((hex) => patchQuiz(quiz.id, { colorHex: hex }, 'skip'))}
                      />
                    </label>
                    <label className="cp-program-slide-canvas-tl">
                      배경색
                      <SlideColorPickerField
                        value={quiz.backgroundColorHex?.match(/^#/) ? quiz.backgroundColorHex : '#ffffff'}
                        fallback="#ffffff"
                        ariaLabel="퀴즈 배경색"
                        recentColors={slideRecentColors}
                        usedColors={slideUsedColors}
                    surface={isEvaluationSheetPreset ? 'light' : 'dark'}
                        onPreview={previewSlideColor((hex) =>
                          patchQuiz(quiz.id, { backgroundColorHex: hex }, 'skip')
                        )}
                        onConfirm={confirmSlideColor((hex) =>
                          patchQuiz(quiz.id, { backgroundColorHex: hex }, 'skip')
                        )}
                      />
                    </label>
                    <button
                      type="button"
                      className={'cp-program-slide-canvas-bold' + (quiz.bold ? ' is-on' : '')}
                      onClick={() => patchQuiz(quiz.id, { bold: !quiz.bold })}
                    >
                      B
                    </button>
                    <ProgramSlideTrashDeleteControl
                      isHolding={holdingDeleteId === quiz.id}
                      showConfirm={
                        !!deleteConfirmTarget &&
                        deleteConfirmTarget.id === quiz.id &&
                        deleteConfirmTarget.kind === 'quiz'
                      }
                      ariaLabel="단답형 퀴즈 삭제: 짧게 확인, 길게 바로 삭제"
                      onPointerDown={(e) => startDeleteHold(e, quiz.id, 'quiz')}
                      onPointerUp={() => endDeleteHold(true)}
                      onPointerLeave={() => endDeleteHold(false)}
                      onPointerCancel={() => endDeleteHold(false)}
                      onCancelConfirm={() => setDeleteConfirmTarget(null)}
                      onConfirmDelete={() => {
                        const target = deleteConfirmTarget;
                        setDeleteConfirmTarget(null);
                        if (target) executeDeleteBlock(target.id, target.kind);
                      }}
                    />
                  </div>
                  <label className="cp-program-slide-canvas-tl cp-program-slide-canvas-link-field">
                    정답
                    <input
                      type="text"
                      value={quiz.correctAnswer}
                      onChange={(e) => patchQuiz(quiz.id, { correctAnswer: e.target.value })}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder="#정답1 #정답2"
                      aria-label="퀴즈 정답"
                    />
                  </label>
                </ProgramSlideFloatedToolbar>
              ) : null}
              {(() => {
                const guidanceText = quiz.placeholder ?? '?';
                const showGuidancePreview = !viewMode && !isEditingPlaceholder;
                const inputValue = viewMode
                  ? viewValue
                  : isEditingPlaceholder
                    ? (quiz.placeholder ?? '')
                    : showGuidancePreview
                      ? guidanceText
                      : '';
                return (
              <input
                type="text"
                className={
                  'cp-program-slide-canvas-quiz-input' +
                  (isEditingPlaceholder ? ' is-editing' : '') +
                  (showGuidancePreview ? ' is-showing-guidance' : '')
                }
                style={{
                  fontSize: `${resolveFontPx(quiz.fontSizePx)}px`,
                  fontFamily: slideTextFontCssFamily(quiz.fontFamily),
                  color: showGuidancePreview
                    ? undefined
                    : slideDisplayHex(quiz.colorHex, QUIZ_DEFAULT_TEXT_COLOR_HEX),
                  backgroundColor: slideDisplayColors.bg(slideDisplayHex(quiz.backgroundColorHex, '#ffffff')),
                  borderRadius: `${radius}px`,
                  fontWeight: quiz.bold ? 700 : isSucceeded ? 600 : 400,
                }}
                value={inputValue}
                readOnly={viewMode ? isSucceeded : !isEditingPlaceholder}
                placeholder={viewMode ? guidanceText : showGuidancePreview ? '' : guidanceText}
                onChange={(e) => {
                  if (isEditingPlaceholder) {
                    patchQuiz(quiz.id, { placeholder: e.target.value });
                    return;
                  }
                  if (!viewMode || isSucceeded) return;
                  setQuizDraftById((prev) => ({ ...prev, [quiz.id]: e.target.value }));
                }}
                onKeyDown={(e) => {
                  if (isEditingPlaceholder) {
                    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') e.stopPropagation();
                    return;
                  }
                  if (!viewMode || isSucceeded) return;
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    submitQuizAnswer(quiz.id);
                    return;
                  }
                  e.stopPropagation();
                }}
                onPointerDown={(e) => {
                  if (viewMode || isEditingPlaceholder) e.stopPropagation();
                }}
                onBlur={() => {
                  if (!canEdit) return;
                  setEditingId((cur) => (cur === quiz.id ? null : cur));
                }}
                ref={(el) => {
                  quizPlaceholderInputRefs.current[quiz.id] = el;
                }}
                spellCheck={false}
                autoComplete="off"
                aria-label={viewMode ? '단답형 퀴즈 답 입력' : '퀴즈 안내문'}
              />
                );
              })()}
            </div>
          );
        })}
        <ProgramSlideQuizChoiceLayer
          sets={quizChoiceSets}
          options={quizChoiceOptions}
          viewMode={viewMode}
          canEdit={canEdit}
          hasMultiSelection={hasMultiSelection}
          holdingDeleteId={holdingDeleteId}
          choicePickedBySetId={choicePickedBySetId}
          choiceSucceededSetIds={choiceSucceededSetIds}
          choiceWrongFlashSetIds={choiceWrongFlashSetIds}
          getBlockLayerZIndex={(baseZ, toolbarOpen) =>
            getBlockLayerZIndex(blockZIndexBase + baseZ, toolbarOpen)
          }
          resolveToolbarPlacement={resolveToolbarPlacement}
          onToggleCorrectIndex={toggleQuizChoiceCorrect}
          onAddOption={addQuizChoiceOption}
          onRemoveOption={removeQuizChoiceOption}
          onStartDeleteHold={(e, setId) => startDeleteHold(e, setId, 'quizChoice')}
          onEndDeleteHold={endDeleteHold}
          deleteConfirmSetId={
            deleteConfirmTarget?.kind === 'quizChoice' ? deleteConfirmTarget.id : null
          }
          onCancelDeleteConfirm={() => setDeleteConfirmTarget(null)}
          onConfirmDelete={() => {
            const target = deleteConfirmTarget;
            setDeleteConfirmTarget(null);
            if (target) executeDeleteBlock(target.id, target.kind);
          }}
          onStartDrag={startQuizChoiceOptionDrag}
          onToggleViewPick={toggleChoiceViewPick}
          onSelectOption={(optionId) => selectSingleBlock('quizChoice', optionId)}
          onMultiSelectPointerDown={(e, optionId) => handleMultiSelectPointerDown(e, 'quizChoice', optionId)}
          onEditLabel={enterChoiceLabelEditMode}
          onPatchLabel={(optionId, label) => patchQuizChoiceOption(optionId, { label })}
          onEndEditLabel={() => setEditingChoiceLabelOptionId(null)}
          editingLabelOptionId={editingChoiceLabelOptionId}
          choiceMinOptions={QUIZ_CHOICE_MIN_OPTIONS}
          choiceMaxOptions={QUIZ_CHOICE_MAX_OPTIONS}
          isOptionSelected={(optionId) => isBlockSelected('quizChoice', optionId)}
          isSetPeerActive={(setId) =>
            quizChoiceOptions.some(
              (o) => o.setId === setId && isBlockSelected('quizChoice', o.id)
            )
          }
          selectedSlideBlockGroupId={selectedGroupId}
          slideTextFontCssFamily={slideTextFontCssFamily}
          toolbarRowHeightPx={SLIDE_TOOLBAR_SINGLE_ROW_HEIGHT_PX}
          mapBackgroundColor={slideDisplayColors.bg}
          canvasInnerEl={canvasInnerEl}
          activeToolbarZIndex={activeToolbarHandleLayer + 5}
        />
        {images.map((image) => {
          const isSelected = isBlockSelected('image', image.id);
          const isCropping = cropEditingId === image.id;
          const isToolbarOpen = isSelected && canEdit && !hasMultiSelection;
          const crop = image.crop;
          const { dispW, dispH, offX, offY } = getContainMetrics(image);
          const croppedImageStyle: React.CSSProperties | undefined = image.src
            ? {
                width: `${100 / crop.w}%`,
                height: `${100 / crop.h}%`,
                left: `${(-crop.x / crop.w) * 100}%`,
                top: `${(-crop.y / crop.h) * 100}%`,
              }
            : undefined;
          const cropBoxStyle: React.CSSProperties | undefined =
            isCropping && image.src
              ? image.cropUsesFrameFrac
                ? {
                    left: `${crop.x * 100}%`,
                    top: `${crop.y * 100}%`,
                    width: `${crop.w * 100}%`,
                    height: `${crop.h * 100}%`,
                  }
                : {
                    left: `${(offX + crop.x * dispW) * 100}%`,
                    top: `${(offY + crop.y * dispH) * 100}%`,
                    width: `${crop.w * dispW * 100}%`,
                    height: `${crop.h * dispH * 100}%`,
                  }
              : undefined;
          return (
            <div
              key={image.id}
              className={
                'cp-program-slide-canvas-image' +
                (isSelected ? ' is-selected' : '') +
                (isCropping ? ' is-cropping' : '')
              }
              style={{
                left: `${image.x * 100}%`,
                top: `${image.y * 100}%`,
                width: `${image.w * 100}%`,
                height: `${image.h * 100}%`,
                zIndex: getBlockLayerZIndex(blockLayerZ(image.zIndex), isToolbarOpen || isCropping),
              }}
              onPointerDown={(e) => {
                if (isCropping) return;
                if (handleMultiSelectPointerDown(e, 'image', image.id)) return;
                startImageDrag(e, image);
              }}
            >
              {isToolbarOpen ? (
                <ProgramSlideFloatedToolbar
                  {...floatedToolbarPortalProps}
                  blockX={image.x}
                  blockY={image.y}
                  blockH={image.h}
                  className={
                    'cp-program-slide-canvas-image-toolbar' + (image.locked ? ' is-locked' : '')
                  }
                  placement={resolveToolbarPlacement(image.y, image.h, SLIDE_TOOLBAR_SINGLE_ROW_HEIGHT_PX)}
                  zIndex={floatedToolbarZIndex}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <input
                    type="url"
                    value={imageUrlDrafts[image.id] ?? image.src}
                    onChange={(e) => setImageUrlDrafts((cur) => ({ ...cur, [image.id]: e.target.value }))}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder="이미지 URL"
                    aria-label="삽입할 이미지 URL"
                    disabled={!!image.locked}
                  />
                  <button type="button" onClick={() => loadImageIntoBlock(image.id)} disabled={!!image.locked}>
                    삽입
                  </button>
                  <button
                    type="button"
                    className={'cp-program-slide-canvas-image-cut' + (isCropping ? ' is-on' : '')}
                    onClick={() => toggleCropMode(image)}
                    aria-label="이미지 잘라내기 영역 조절"
                    title="이미지 잘라내기 영역 조절"
                    disabled={!image.src || !!image.locked}
                  >
                    <img src="/Crop.svg" alt="" aria-hidden className="cp-program-slide-canvas-image-cut-icon" />
                  </button>
                  <button
                    type="button"
                    className={'cp-program-slide-canvas-lock' + (image.locked ? ' is-on' : '')}
                    onClick={() => toggleBlockLocked('image', image.id)}
                    aria-label={image.locked ? '잠금 해제' : '잠금'}
                    title={image.locked ? '잠금 해제' : '잠금'}
                  >
                    <SlideLockIcon locked={!!image.locked} />
                  </button>
                  <ProgramSlideTrashDeleteControl
                    isHolding={holdingDeleteId === image.id}
                    showConfirm={
                      !!deleteConfirmTarget &&
                      deleteConfirmTarget.id === image.id &&
                      deleteConfirmTarget.kind === 'image'
                    }
                    disabled={!!image.locked}
                    ariaLabel="이미지 프레임 삭제: 짧게 확인, 길게 바로 삭제"
                    onPointerDown={(e) => startDeleteHold(e, image.id, 'image')}
                    onPointerUp={() => endDeleteHold(true)}
                    onPointerLeave={() => endDeleteHold(false)}
                    onPointerCancel={() => endDeleteHold(false)}
                    onCancelConfirm={() => setDeleteConfirmTarget(null)}
                    onConfirmDelete={() => {
                      const target = deleteConfirmTarget;
                      setDeleteConfirmTarget(null);
                      if (target) executeDeleteBlock(target.id, target.kind);
                    }}
                  />
                </ProgramSlideFloatedToolbar>
              ) : null}
              <div className="cp-program-slide-canvas-image-body">
                {image.src ? (
                  isCropping ? (
                    <>
                      <div className="cp-program-slide-canvas-image-full-clip" aria-hidden>
                        <img
                          src={image.src}
                          alt=""
                          className="cp-program-slide-canvas-image-crop-fill"
                          draggable={false}
                        />
                      </div>
                      <div
                        className="cp-program-slide-canvas-crop-box"
                        style={cropBoxStyle}
                        onPointerDown={(e) => startCropMove(e, image)}
                        title="드래그하여 잘라내기 영역 이동"
                      />
                    </>
                  ) : (
                    <img
                      src={image.src}
                      alt=""
                      className="cp-program-slide-canvas-image-img"
                      style={croppedImageStyle}
                      draggable={false}
                    />
                  )
                ) : (
                  <div className="cp-program-slide-canvas-image-placeholder">이미지 URL을 입력해 주세요</div>
                )}
              </div>
              {isCropping && cropBoxStyle ? (
                <div className="cp-program-slide-canvas-crop-handles" style={cropBoxStyle}>
                  {(['nw', 'ne', 'sw', 'se'] as const).map((corner) => (
                    <button
                      key={`${image.id}:crop:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-crop-resize cp-program-slide-canvas-crop-resize--${corner}`}
                      onPointerDown={(e) => startCropResize(e, image, corner)}
                      aria-label="이미지 잘라내기 영역 크기 조정"
                      title="드래그하여 잘라내기 영역 크기 조정"
                    />
                  ))}
                </div>
              ) : null}
              {imageLoadErrors[image.id] ? (
                <div className="cp-program-slide-canvas-image-error">{imageLoadErrors[image.id]}</div>
              ) : null}
            </div>
          );
        })}
        {clipboardImages.map((image) => {
          const isSelected = isBlockSelected('clipboardImage', image.id);
          const isCropping = cropEditingId === image.id;
          const isToolbarOpen = isSelected && canEdit && !hasMultiSelection;
          const isUploading = !!clipboardImageUploading[image.id];
          const crop = image.crop;
          const { dispW, dispH, offX, offY } = getContainMetrics(image);
          const croppedImageStyle: React.CSSProperties | undefined = image.src
            ? {
                width: `${100 / crop.w}%`,
                height: `${100 / crop.h}%`,
                left: `${(-crop.x / crop.w) * 100}%`,
                top: `${(-crop.y / crop.h) * 100}%`,
              }
            : undefined;
          const cropBoxStyle: React.CSSProperties | undefined =
            isCropping && image.src
              ? image.cropUsesFrameFrac
                ? {
                    left: `${crop.x * 100}%`,
                    top: `${crop.y * 100}%`,
                    width: `${crop.w * 100}%`,
                    height: `${crop.h * 100}%`,
                  }
                : {
                    left: `${(offX + crop.x * dispW) * 100}%`,
                    top: `${(offY + crop.y * dispH) * 100}%`,
                    width: `${crop.w * dispW * 100}%`,
                    height: `${crop.h * dispH * 100}%`,
                  }
              : undefined;
          return (
            <div
              key={image.id}
              className={
                'cp-program-slide-canvas-image cp-program-slide-canvas-clipboard-image' +
                (isSelected ? ' is-selected' : '') +
                (isCropping ? ' is-cropping' : '')
              }
              style={{
                left: `${image.x * 100}%`,
                top: `${image.y * 100}%`,
                width: `${image.w * 100}%`,
                height: `${image.h * 100}%`,
                zIndex: getBlockLayerZIndex(blockLayerZ(image.zIndex), isToolbarOpen || isCropping),
              }}
              onPointerDown={(e) => {
                if (isCropping) return;
                if (handleMultiSelectPointerDown(e, 'clipboardImage', image.id)) return;
                startImageDrag(e, image, 'clipboardImage');
              }}
            >
              {isToolbarOpen ? (
                <ProgramSlideFloatedToolbar
                  {...floatedToolbarPortalProps}
                  blockX={image.x}
                  blockY={image.y}
                  blockH={image.h}
                  className={
                    'cp-program-slide-canvas-image-toolbar' + (image.locked ? ' is-locked' : '')
                  }
                  placement={resolveToolbarPlacement(image.y, image.h, SLIDE_TOOLBAR_SINGLE_ROW_HEIGHT_PX)}
                  zIndex={floatedToolbarZIndex}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => {
                      void pasteClipboardImageToBlock(image.id);
                    }}
                    disabled={isUploading || !!image.locked}
                  >
                    클립보드 이미지 붙여넣기
                  </button>
                  <button
                    type="button"
                    className={'cp-program-slide-canvas-image-cut' + (isCropping ? ' is-on' : '')}
                    onClick={() => toggleCropMode(image, 'clipboardImage')}
                    aria-label="이미지 잘라내기"
                    title="이미지 잘라내기"
                    disabled={!image.src || !!image.locked}
                  >
                    <img src="/Crop.svg" alt="" aria-hidden className="cp-program-slide-canvas-image-cut-icon" />
                  </button>
                  <button
                    type="button"
                    className={'cp-program-slide-canvas-lock' + (image.locked ? ' is-on' : '')}
                    onClick={() => toggleBlockLocked('clipboardImage', image.id)}
                    aria-label={image.locked ? '잠금 해제' : '잠금'}
                    title={image.locked ? '잠금 해제' : '잠금'}
                  >
                    <SlideLockIcon locked={!!image.locked} />
                  </button>
                  <ProgramSlideTrashDeleteControl
                    isHolding={holdingDeleteId === image.id}
                    showConfirm={
                      !!deleteConfirmTarget &&
                      deleteConfirmTarget.id === image.id &&
                      deleteConfirmTarget.kind === 'clipboardImage'
                    }
                    disabled={!!image.locked}
                    ariaLabel="클립보드 이미지 삭제: 짧게 확인, 길게 바로 삭제"
                    onPointerDown={(e) => startDeleteHold(e, image.id, 'clipboardImage')}
                    onPointerUp={() => endDeleteHold(true)}
                    onPointerLeave={() => endDeleteHold(false)}
                    onPointerCancel={() => endDeleteHold(false)}
                    onCancelConfirm={() => setDeleteConfirmTarget(null)}
                    onConfirmDelete={() => {
                      const target = deleteConfirmTarget;
                      setDeleteConfirmTarget(null);
                      if (target) executeDeleteBlock(target.id, target.kind);
                    }}
                  />
                </ProgramSlideFloatedToolbar>
              ) : null}
              <div className="cp-program-slide-canvas-image-body">
                {isUploading ? (
                  <div className="cp-program-slide-canvas-clipboard-uploading">처리 중…</div>
                ) : image.src ? (
                  isCropping ? (
                    <>
                      <div className="cp-program-slide-canvas-image-full-clip" aria-hidden>
                        <img
                          src={image.src}
                          alt=""
                          className="cp-program-slide-canvas-image-crop-fill"
                          loading="lazy"
                          decoding="async"
                          draggable={false}
                        />
                      </div>
                      <div
                        className="cp-program-slide-canvas-crop-box"
                        style={cropBoxStyle}
                        onPointerDown={(e) => startCropMove(e, image, 'clipboardImage')}
                      />
                    </>
                  ) : (
                    <img
                      src={image.src}
                      alt=""
                      className="cp-program-slide-canvas-image-img"
                      style={croppedImageStyle}
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />
                  )
                ) : (
                  <div className="cp-program-slide-canvas-image-placeholder">
                    클립보드에서 붙여넣기 (Ctrl+V)
                  </div>
                )}
              </div>
              {isCropping && cropBoxStyle ? (
                <div className="cp-program-slide-canvas-crop-handles" style={cropBoxStyle}>
                  {(['nw', 'ne', 'sw', 'se'] as const).map((corner) => (
                    <button
                      key={`${image.id}:crop:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-crop-resize cp-program-slide-canvas-crop-resize--${corner}`}
                      onPointerDown={(e) => startCropResize(e, image, corner, 'clipboardImage')}
                      aria-label="이미지 잘라내기 영역 크기 조정"
                      title="드래그하여 잘라내기 영역 크기 조정"
                    />
                  ))}
                </div>
              ) : null}
              {clipboardImageLoadErrors[image.id] ? (
                <div className="cp-program-slide-canvas-image-error">{clipboardImageLoadErrors[image.id]}</div>
              ) : null}
            </div>
          );
        })}
        {scanImages.map((image) => {
          const isSelected = isBlockSelected('scanImage', image.id);
          const isCropping = cropEditingId === image.id;
          const isLivePreview = scanLiveBlockId === image.id;
          const isToolbarOpen = isSelected && canEdit && !hasMultiSelection;
          const isUploading = !!clipboardImageUploading[image.id];
          const crop = image.crop;
          const { dispW, dispH, offX, offY } = getContainMetrics(image);
          const croppedImageStyle: React.CSSProperties | undefined = image.src
            ? {
                width: `${100 / crop.w}%`,
                height: `${100 / crop.h}%`,
                left: `${(-crop.x / crop.w) * 100}%`,
                top: `${(-crop.y / crop.h) * 100}%`,
              }
            : undefined;
          const cropBoxStyle: React.CSSProperties | undefined =
            isCropping && image.src
              ? image.cropUsesFrameFrac
                ? {
                    left: `${crop.x * 100}%`,
                    top: `${crop.y * 100}%`,
                    width: `${crop.w * 100}%`,
                    height: `${crop.h * 100}%`,
                  }
                : {
                    left: `${(offX + crop.x * dispW) * 100}%`,
                    top: `${(offY + crop.y * dispH) * 100}%`,
                    width: `${crop.w * dispW * 100}%`,
                    height: `${crop.h * dispH * 100}%`,
                  }
              : undefined;
          return (
            <div
              key={image.id}
              className={
                'cp-program-slide-canvas-image cp-program-slide-canvas-scan-image cp-program-slide-canvas-clipboard-image' +
                (isSelected ? ' is-selected' : '') +
                (isCropping ? ' is-cropping' : '') +
                (isLivePreview ? ' is-scan-live' : '')
              }
              style={{
                left: `${image.x * 100}%`,
                top: `${image.y * 100}%`,
                width: `${image.w * 100}%`,
                height: `${image.h * 100}%`,
                zIndex:
                  scanLiveInputMenuBlockId === image.id
                    ? activeToolbarHandleLayer + 40
                    : getBlockLayerZIndex(blockLayerZ(image.zIndex), isToolbarOpen || isCropping),
              }}
              onPointerDown={(e) => {
                if (isLivePreview) {
                  e.stopPropagation();
                  return;
                }
                if (isCropping) return;
                if (handleMultiSelectPointerDown(e, 'scanImage', image.id)) return;
                startImageDrag(e, image, 'scanImage');
              }}
            >
              {isToolbarOpen ? (
                <ProgramSlideFloatedToolbar
                  {...floatedToolbarPortalProps}
                  blockX={image.x}
                  blockY={image.y}
                  blockH={image.h}
                  className={
                    'cp-program-slide-canvas-image-toolbar' + (image.locked ? ' is-locked' : '')
                  }
                  placement={resolveToolbarPlacement(image.y, image.h, SLIDE_TOOLBAR_SINGLE_ROW_HEIGHT_PX)}
                  zIndex={floatedToolbarZIndex}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (isLivePreview) stopScanLivePreview();
                      else startScanLivePreview(image.id);
                    }}
                    disabled={isUploading || !!image.locked}
                  >
                    {isLivePreview ? '촬영 취소' : '웹캠 촬영'}
                  </button>
                  <div
                    className="cp-program-slide-webcam-source-wrap cp-program-slide-canvas-scan-source-wrap"
                    ref={scanLiveInputMenuBlockId === image.id ? scanLiveInputMenuWrapRef : undefined}
                  >
                    <button
                      type="button"
                      className="cp-program-slide-webcam-swap cp-program-slide-canvas-scan-toolbar-swap"
                      title="카메라·화면 입력 바꾸기"
                      aria-label="카메라 또는 모니터·창 화면 입력 바꾸기"
                      aria-expanded={scanLiveInputMenuBlockId === image.id}
                      aria-haspopup="true"
                      disabled={isUploading}
                      onClick={(e) => {
                        e.stopPropagation();
                        setScanLiveInputMenuBlockId((cur) => (cur === image.id ? null : image.id));
                      }}
                    >
                      <img
                        src="/change.svg"
                        alt=""
                        className="cp-program-slide-webcam-swap-icon"
                        width={20}
                        height={20}
                        aria-hidden
                      />
                    </button>
                    {scanLiveInputMenuBlockId === image.id ? (
                      <div
                        className="cp-program-slide-canvas-scan-source-menu"
                        role="menu"
                        aria-label="비디오 입력 선택"
                      >
                        {scanLiveInputDeviceList.length === 0 ? (
                          <div className="cp-program-slide-canvas-scan-source-menu-empty">목록 불러오는 중…</div>
                        ) : (
                            scanLiveInputDeviceList.map((d) => {
                              const label = simplifyVideoInputDeviceLabel(d.label.trim() || '카메라');
                            const selected =
                              scanLiveCaptureMode === 'camera' && scanLiveCameraDeviceId === d.deviceId;
                            return (
                              <button
                                key={d.deviceId}
                                type="button"
                                role="menuitem"
                                className={
                                  'cp-program-slide-canvas-scan-source-menu-item' + (selected ? ' is-selected' : '')
                                }
                                title={label}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setScanLiveCaptureMode('camera');
                                  setScanLiveCameraDeviceId(d.deviceId);
                                  setScanLiveInputMenuBlockId(null);
                                }}
                              >
                                {label}
                              </button>
                            );
                          })
                        )}
                        <div className="cp-program-slide-canvas-scan-source-menu-divider" role="separator" />
                        <button
                          type="button"
                          role="menuitem"
                          className={
                            'cp-program-slide-canvas-scan-source-menu-item' +
                            (scanLiveCaptureMode === 'screen' ? ' is-selected' : '')
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            setScanLiveCaptureMode('screen');
                            setScanLiveInputMenuBlockId(null);
                          }}
                        >
                          화면 공유
                        </button>
                      </div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={'cp-program-slide-canvas-image-cut' + (isCropping ? ' is-on' : '')}
                    onClick={() => toggleCropMode(image, 'scanImage')}
                    aria-label="이미지 잘라내기"
                    title="이미지 잘라내기"
                    disabled={!image.src || !!image.locked}
                  >
                    <img src="/Crop.svg" alt="" aria-hidden className="cp-program-slide-canvas-image-cut-icon" />
                  </button>
                  <button
                    type="button"
                    className={'cp-program-slide-canvas-lock' + (image.locked ? ' is-on' : '')}
                    onClick={() => toggleBlockLocked('scanImage', image.id)}
                    aria-label={image.locked ? '잠금 해제' : '잠금'}
                    title={image.locked ? '잠금 해제' : '잠금'}
                  >
                    <SlideLockIcon locked={!!image.locked} />
                  </button>
                  <ProgramSlideTrashDeleteControl
                    isHolding={holdingDeleteId === image.id}
                    showConfirm={
                      !!deleteConfirmTarget &&
                      deleteConfirmTarget.id === image.id &&
                      deleteConfirmTarget.kind === 'scanImage'
                    }
                    disabled={!!image.locked}
                    ariaLabel="스캔 이미지 삭제: 짧게 확인, 길게 바로 삭제"
                    onPointerDown={(e) => startDeleteHold(e, image.id, 'scanImage')}
                    onPointerUp={() => endDeleteHold(true)}
                    onPointerLeave={() => endDeleteHold(false)}
                    onPointerCancel={() => endDeleteHold(false)}
                    onCancelConfirm={() => setDeleteConfirmTarget(null)}
                    onConfirmDelete={() => {
                      const target = deleteConfirmTarget;
                      setDeleteConfirmTarget(null);
                      if (target) executeDeleteBlock(target.id, target.kind);
                    }}
                  />
                </ProgramSlideFloatedToolbar>
              ) : null}
              <div className="cp-program-slide-canvas-image-body">
                {isUploading ? (
                  <div className="cp-program-slide-canvas-clipboard-uploading">처리 중…</div>
                ) : isLivePreview ? (
                  <ScanImageBlockLiveCapture
                    active
                    captureMode={scanLiveCaptureMode}
                    cameraDeviceId={scanLiveCameraDeviceId}
                    onCaptureModeChange={setScanLiveCaptureMode}
                    onCapture={(blob) => handleScanLiveCapture(image.id, blob)}
                    onError={handleScanLivePreviewError}
                  />
                ) : image.src ? (
                  isCropping ? (
                    <>
                      <div className="cp-program-slide-canvas-image-full-clip" aria-hidden>
                        <img
                          src={image.src}
                          alt=""
                          className="cp-program-slide-canvas-image-crop-fill"
                          loading="lazy"
                          decoding="async"
                          draggable={false}
                        />
                      </div>
                      <div
                        className="cp-program-slide-canvas-crop-box"
                        style={cropBoxStyle}
                        onPointerDown={(e) => startCropMove(e, image, 'scanImage')}
                      />
                    </>
                  ) : (
                    <img
                      src={image.src}
                      alt=""
                      className="cp-program-slide-canvas-image-img"
                      style={croppedImageStyle}
                      loading="lazy"
                      decoding="async"
                      draggable={false}
                    />
                  )
                ) : (
                  <div className="cp-program-slide-canvas-image-placeholder cp-program-slide-canvas-scan-placeholder-static">
                    크기를 조정한 뒤 「웹캠 촬영」을 누르세요
                  </div>
                )}
              </div>
              {isCropping && cropBoxStyle ? (
                <div className="cp-program-slide-canvas-crop-handles" style={cropBoxStyle}>
                  {(['nw', 'ne', 'sw', 'se'] as const).map((corner) => (
                    <button
                      key={`scan:${image.id}:crop:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-crop-resize cp-program-slide-canvas-crop-resize--${corner}`}
                      onPointerDown={(e) => startCropResize(e, image, corner, 'scanImage')}
                      aria-label="이미지 잘라내기 영역 크기 조정"
                      title="드래그하여 잘라내기 영역 크기 조정"
                    />
                  ))}
                </div>
              ) : null}
              {clipboardImageLoadErrors[image.id] ? (
                <div className="cp-program-slide-canvas-image-error">{clipboardImageLoadErrors[image.id]}</div>
              ) : null}
            </div>
          );
        })}
        {webpages.map((webpage) => {
          const isCodeBlock = webpage.mode === 'code';
          const isCodeTransparent = webpage.codeTransparentBackground !== false;
          const codeTrimmed = (webpage.sourceCode ?? '').trim();
          /** 코드 미입력: 비었거나 생성 시 기본 예시 코드 그대로 */
          const isCodeUntouched = isCodeBlock && (!codeTrimmed || codeTrimmed === DEFAULT_CODE_BLOCK_SOURCE.trim());
          const isSelected = isBlockSelected('webpage', webpage.id);
          const isToolbarOpen = isSelected && canEdit && !hasMultiSelection;
          const embedFailed = webpageEmbedFailed[webpage.id] ?? false;
          const isPending = isCodeBlock
            ? !(webpage.sourceCode ?? '').trim()
            : !webpage.url.trim() && !webpage.thumbSrc.trim();
          const isPreviewLoading = webpagePreviewLoadingId === webpage.id;
          return (
            <div
              key={webpage.id}
              className={
                'cp-program-slide-canvas-webpage' +
                (isCodeBlock ? ' is-code-block' : '') +
                (isCodeBlock && isCodeTransparent ? ' is-code-transparent' : '') +
                (isCodeBlock && isCodeTransparent && isCodeUntouched && (isEvaluationSheetPreset || !viewMode)
                  ? ' is-code-empty-hatch'
                  : '') +
                (isSelected ? ' is-selected' : '') +
                (isPending ? ' is-pending' : '')
              }
              style={{
                left: `${webpage.x * 100}%`,
                top: `${webpage.y * 100}%`,
                width: `${webpage.w * 100}%`,
                height: `${webpage.h * 100}%`,
                zIndex: getBlockLayerZIndex(blockLayerZ(webpage.zIndex), isToolbarOpen),
              }}
              onPointerDown={(e) => {
                if (handleMultiSelectPointerDown(e, 'webpage', webpage.id)) return;
                startWebpageDrag(e, webpage);
              }}
            >
              {isPreviewLoading && isPending ? (
                <div className="cp-program-slide-canvas-clipboard-uploading">미리보기 생성 중…</div>
              ) : null}
              {isCodeBlock && isEvaluationSheetPreset && !isCodeUntouched ? (
                // 평가지(인쇄용): 코드가 있으면 정리된 HTML 을 정적 렌더(스크립트 실행 없음) — 미리보기·인쇄·PDF 동일
                <SlideCodeStaticHtml
                  sourceCode={webpage.sourceCode ?? ''}
                  blockWidthNorm={webpage.w}
                  transparent={isCodeTransparent}
                />
              ) : isCodeBlock && (isEvaluationSheetPreset || !viewMode) ? (
                // 편집 캔버스·평가지(빈 코드): 반투명 자리 표시. 프레젠테이션 재생(viewMode)은 실제 코드 실행 유지
                <SlideCodeBlockPlaceholder />
              ) : (
              <ProgramSlideWebpageBlockView
                block={webpage}
                viewMode={viewMode}
                isPending={isPending && !viewMode}
                embedFailed={embedFailed}
                onOpenUrl={() => {
                  if (isCodeBlock) return;
                  openButtonHref(webpage.url);
                }}
                onEmbedError={() => setWebpageEmbedFailed((cur) => ({ ...cur, [webpage.id]: true }))}
              />
              )}
            </div>
          );
        })}
        {videos.map((video) => {
          const isSelected = isBlockSelected('video', video.id);
          const renderSource = getVideoRenderSource(video.src, videoEmbedStarts[video.id]);
          const isVideoControlEnabled = videoControlEnabledId === video.id;
          const isToolbarOpen = isSelected && canEdit && !hasMultiSelection;
          return (
            <div
              key={video.id}
              className={
                'cp-program-slide-canvas-video' +
                (isSelected ? ' is-selected' : '') +
                (isVideoControlEnabled ? ' is-control-enabled' : '')
              }
              style={{
                left: `${video.x * 100}%`,
                top: `${video.y * 100}%`,
                width: `${video.w * 100}%`,
                height: `${video.h * 100}%`,
                zIndex: getBlockLayerZIndex(blockLayerZ(video.zIndex), isToolbarOpen),
              }}
              onPointerDown={(e) => {
                if (handleMultiSelectPointerDown(e, 'video', video.id)) return;
                startVideoDrag(e, video);
              }}
            >
              {isToolbarOpen ? (
                <ProgramSlideFloatedToolbar
                  {...floatedToolbarPortalProps}
                  blockX={video.x}
                  blockY={video.y}
                  blockH={video.h}
                  className="cp-program-slide-canvas-image-toolbar cp-program-slide-canvas-video-toolbar"
                  placement={resolveToolbarPlacement(video.y, video.h, SLIDE_TOOLBAR_DOUBLE_ROW_HEIGHT_PX)}
                  zIndex={floatedToolbarZIndex}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <div className="cp-program-slide-canvas-video-toolbar-row">
                    <input
                      type="url"
                      value={videoUrlDrafts[video.id] ?? video.src}
                      onChange={(e) => setVideoUrlDrafts((cur) => ({ ...cur, [video.id]: e.target.value }))}
                      onKeyDown={(e) => e.stopPropagation()}
                      placeholder="동영상 URL"
                      aria-label="삽입할 동영상 URL"
                    />
                    <button type="button" onClick={() => loadVideoIntoBlock(video.id)}>
                      삽입
                    </button>
                    <ProgramSlideTrashDeleteControl
                      isHolding={holdingDeleteId === video.id}
                      showConfirm={
                        !!deleteConfirmTarget &&
                        deleteConfirmTarget.id === video.id &&
                        deleteConfirmTarget.kind === 'video'
                      }
                      ariaLabel="동영상 블럭 삭제: 짧게 확인, 길게 바로 삭제"
                      onPointerDown={(e) => startDeleteHold(e, video.id, 'video')}
                      onPointerUp={() => endDeleteHold(true)}
                      onPointerLeave={() => endDeleteHold(false)}
                      onPointerCancel={() => endDeleteHold(false)}
                      onCancelConfirm={() => setDeleteConfirmTarget(null)}
                      onConfirmDelete={() => {
                        const target = deleteConfirmTarget;
                        setDeleteConfirmTarget(null);
                        if (target) executeDeleteBlock(target.id, target.kind);
                      }}
                    />
                  </div>
                  <div className="cp-program-slide-canvas-video-bookmark-row" aria-label="동영상 북마크">
                    <span className="cp-program-slide-canvas-video-play-control">
                      <span className="cp-program-slide-canvas-video-play-label">재생</span>
                      <button
                        type="button"
                        className={
                          'team-auto-approve-toggle cp-program-slide-canvas-video-play-toggle' +
                          (isVideoControlEnabled ? ' on' : '')
                        }
                        onClick={() => toggleVideoPlaybackInEditMode(video.id, renderSource)}
                        disabled={!video.src || !renderSource}
                        aria-pressed={isVideoControlEnabled}
                        aria-label={
                          isVideoControlEnabled
                            ? '편집 모드 동영상 재생 및 탐색 끄기'
                            : '편집 모드 동영상 재생 및 탐색 켜기'
                        }
                        title={isVideoControlEnabled ? 'OFF: 블럭 편집 및 이동' : 'ON: 동영상 재생바 조작'}
                      >
                        <span className="team-auto-approve-toggle-slider" data-on={!isVideoControlEnabled}>
                          OFF
                        </span>
                        <span className="team-auto-approve-toggle-slider" data-on={isVideoControlEnabled}>
                          ON
                        </span>
                      </button>
                    </span>
                    <button
                      type="button"
                      className="cp-program-slide-canvas-video-bookmark-add"
                      onClick={() => addVideoBookmark(video.id)}
                      disabled={!video.src || !renderSource || !isVideoControlEnabled}
                      aria-label="현재 재생 위치를 북마크로 추가"
                      title={
                        isVideoControlEnabled
                          ? '현재 재생 중이거나 일시정지한 위치를 북마크로 저장합니다'
                          : '재생 ON으로 전환한 뒤 북마크를 추가할 수 있습니다'
                      }
                    >
                      북마크 추가
                    </button>
                    <div className="cp-program-slide-canvas-video-bookmark-list">
                      {video.bookmarks.map((bookmark) => (
                        <span key={bookmark.id} className="cp-program-slide-canvas-video-bookmark-wrap">
                          <button
                            type="button"
                            className="cp-program-slide-canvas-video-bookmark"
                            onClick={() => jumpToVideoBookmark(video.id, bookmark.timeSec)}
                            title={formatVideoBookmarkTime(bookmark.timeSec)}
                          >
                            {formatVideoBookmarkTime(bookmark.timeSec)}
                          </button>
                          <button
                            type="button"
                            className="cp-program-slide-canvas-video-bookmark-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteVideoBookmark(video.id, bookmark.id);
                            }}
                            aria-label={`${formatVideoBookmarkTime(bookmark.timeSec)} 북마크 삭제`}
                            title="북마크 삭제"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                </ProgramSlideFloatedToolbar>
              ) : null}
              {!canEdit && video.bookmarks.length ? (
                <div
                  className="cp-program-slide-canvas-video-view-bookmarks"
                  aria-label="동영상 북마크"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <div className="cp-program-slide-canvas-video-bookmark-list">
                    {video.bookmarks.map((bookmark) => (
                      <button
                        key={bookmark.id}
                        type="button"
                        className="cp-program-slide-canvas-video-bookmark"
                        onClick={() => jumpToVideoBookmark(video.id, bookmark.timeSec)}
                        title={formatVideoBookmarkTime(bookmark.timeSec)}
                      >
                        {formatVideoBookmarkTime(bookmark.timeSec)}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="cp-program-slide-canvas-video-body">
                {renderSource?.kind === 'video' ? (
                  <video
                    ref={(el) => {
                      videoRefs.current[video.id] = el;
                    }}
                    className="cp-program-slide-canvas-video-el"
                    src={renderSource.src}
                    controls
                    playsInline
                    onPointerDown={(e) => {
                      if (canEdit && isVideoControlEnabled) e.stopPropagation();
                    }}
                    onLoadedData={() => setVideoLoadErrors((cur) => ({ ...cur, [video.id]: '' }))}
                    onError={() =>
                      setVideoLoadErrors((cur) => ({
                        ...cur,
                        [video.id]: '동영상을 불러올 수 없습니다. mp4/webm 같은 직접 동영상 파일 주소인지 확인해 주세요.',
                      }))
                    }
                  />
                ) : renderSource?.kind === 'iframe' ? (
                  <iframe
                    key={`${video.id}:${videoEmbedStarts[video.id] ?? 0}`}
                    ref={(el) => {
                      iframeRefs.current[video.id] = el;
                    }}
                    className="cp-program-slide-canvas-video-el"
                    src={renderSource.src}
                    title="삽입된 동영상"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    onLoad={() => {
                      if (isVideoControlEnabled) postVideoIframeCommand(video.id, renderSource, 'time');
                    }}
                    onPointerDown={(e) => {
                      if (canEdit && isVideoControlEnabled) e.stopPropagation();
                    }}
                  />
                ) : (
                  <div className="cp-program-slide-canvas-image-placeholder">동영상 URL을 입력해 주세요</div>
                )}
              </div>
              {videoLoadErrors[video.id] ? (
                <div className="cp-program-slide-canvas-image-error">{videoLoadErrors[video.id]}</div>
              ) : null}
            </div>
          );
        })}
        {canEdit && showMultiSelectionBoundsOverlay && displayedMultiSelectionBounds ? (
          <>
            <div
              className={
                'cp-program-slide-canvas-group-bounds' +
                (hasGroupAsUnitSelection ? '' : ' cp-program-slide-canvas-group-bounds--plain-multi') +
                (hasMultiSelection && !isDrilledGroupMemberSelection
                  ? ' cp-program-slide-canvas-group-bounds--move-target'
                  : '')
              }
              style={{
                left: `${displayedMultiSelectionBounds.left * 100}%`,
                top: `${displayedMultiSelectionBounds.top * 100}%`,
                width: `${displayedMultiSelectionBounds.width * 100}%`,
                height: `${displayedMultiSelectionBounds.height * 100}%`,
                zIndex: activeToolbarHandleLayer,
              }}
              aria-hidden
              onPointerDown={
                hasMultiSelection && !isDrilledGroupMemberSelection && multiSelectionBounds
                  ? (e) => startGroupMove(e, multiSelectionBounds)
                  : undefined
              }
            />
            {(['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
              const left =
                corner === 'nw' || corner === 'sw'
                  ? displayedMultiSelectionBounds.left
                  : displayedMultiSelectionBounds.right;
              const top =
                corner === 'nw' || corner === 'ne'
                  ? displayedMultiSelectionBounds.top
                  : displayedMultiSelectionBounds.bottom;
              return (
                <button
                  key={`group-resize:${corner}`}
                  type="button"
                  className={`cp-program-slide-canvas-image-resize cp-program-slide-canvas-image-resize--${corner} cp-program-slide-canvas-group-resize${plainMultiGroupResizeClass}`}
                  style={{
                    left: `calc(${left * 100}% - 5px)`,
                    top: `calc(${top * 100}% - 5px)`,
                    zIndex: activeToolbarHandleLayer,
                  }}
                  onPointerDown={(e) =>
                    multiSelectionBounds ? startGroupResize(e, multiSelectionBounds, { corner }) : undefined
                  }
                  aria-label="선택한 개체 묶음 크기 조정"
                  title="드래그하여 선택한 개체 묶음을 비율 유지로 크기 조정"
                />
              );
            })}
            {(['left', 'right'] as const).map((side) => (
              <button
                key={`group-resize-x:${side}`}
                type="button"
                className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--${side} is-selected cp-program-slide-canvas-group-resize${plainMultiGroupResizeClass}`}
                style={{
                  left:
                    side === 'left'
                      ? `calc(${displayedMultiSelectionBounds.left * 100}% - 5px)`
                      : `calc(${displayedMultiSelectionBounds.right * 100}% - 5px)`,
                  top: `calc(${displayedMultiSelectionBounds.top * 100}% + ${(displayedMultiSelectionBounds.height * 100) / 2}%)`,
                  zIndex: activeToolbarHandleLayer,
                }}
                onPointerDown={(e) =>
                  multiSelectionBounds ? startGroupResize(e, multiSelectionBounds, { side }) : undefined
                }
                aria-label="선택한 개체 묶음 가로 크기 조정"
                title="드래그하여 선택한 개체 묶음의 가로 길이 조정"
              />
            ))}
          </>
        ) : null}
        {canEdit && !hasMultiSelection
          ? boxes
              .filter((box) => selectedKind === 'text' && box.id === selectedId)
              .flatMap((box) =>
                (['left', 'right'] as const).map((side) => (
                  <button
                    key={`${box.id}:${side}`}
                    type="button"
                    className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--${side} is-selected`}
                    style={{
                      left:
                        side === 'left'
                          ? `calc(${box.x * 100}% - 5px)`
                          : `calc(${(box.x + box.w) * 100}% - 5px)`,
                      top: `calc(${box.y * 100}% + ${(box.h * 100) / 2}%)`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startResize(e, box, side)}
                    aria-label="텍스트 상자 가로 크기 조정"
                    title="드래그하여 가로 크기 조정"
                  />
                ))
              )
          : null}
        {canEdit && !hasMultiSelection
          ? buttons
              .filter((button) => selectedKind === 'button' && button.id === selectedId)
              .flatMap((button) => {
                const rootRect = rootRef.current?.getBoundingClientRect();
                const buttonWpx = Math.max(1, button.w * (rootRect?.width ?? 1280));
                const buttonHpx = Math.max(1, button.h * (rootRect?.height ?? 720));
                const effectiveRadiusPx = Math.min(button.cornerRadiusPx ?? 999, Math.min(buttonWpx, buttonHpx) / 2);
                const radiusHandleOffsetPx = Math.round(7 + effectiveRadiusPx);
                const cornerHandles = (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                  const left = corner === 'nw' || corner === 'sw' ? button.x : button.x + button.w;
                  const top = corner === 'nw' || corner === 'ne' ? button.y : button.y + button.h;
                  return (
                    <button
                      key={`${button.id}:resize:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-image-resize cp-program-slide-canvas-image-resize--${corner}`}
                      style={{
                        left: `calc(${left * 100}% - 5px)`,
                        top: `calc(${top * 100}% - 5px)`,
                        zIndex: activeToolbarHandleLayer,
                      }}
                      onPointerDown={(e) => startButtonResize(e, button, corner)}
                      aria-label="링크 버튼 가로세로 크기 조정"
                      title="드래그하여 버튼 크기 조정"
                    />
                  );
                });
                const radiusHandle = (
                  <button
                    key={`${button.id}:radius`}
                    type="button"
                    className="cp-program-slide-canvas-button-radius"
                    style={{
                      left: `calc(${button.x * 100}% + ${radiusHandleOffsetPx}px)`,
                      top: `calc(${button.y * 100}% - 5px)`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startButtonRadiusResize(e, button)}
                    aria-label="버튼 모서리 둥글기 조정"
                    title="좌우로 드래그하여 모서리 둥글기 조정"
                  />
                );
                const horiz = (['left', 'right'] as const).map((side) => (
                  <button
                    key={`${button.id}:x:${side}`}
                    type="button"
                    className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--${side} is-selected`}
                    style={{
                      left:
                        side === 'left'
                          ? `calc(${button.x * 100}% - 5px)`
                          : `calc(${(button.x + button.w) * 100}% - 5px)`,
                      top: `calc(${button.y * 100}% + ${(button.h * 100) / 2}%)`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startBoxHorizontalResize(e, 'button', button.id, button, side)}
                    aria-label="버튼 가로 크기 조정"
                    title="드래그하여 가로 크기 조정"
                  />
                ));
                return [...horiz, ...cornerHandles, radiusHandle];
              })
          : null}
        {canEdit && !hasMultiSelection
          ? shapes
              .filter((shape) => selectedKind === 'shape' && shape.id === selectedId && !shape.locked)
              .flatMap((shape) => {
                const rootRect = rootRef.current?.getBoundingClientRect();
                const wpx = Math.max(1, shape.w * (rootRect?.width ?? 1280));
                const hpx = Math.max(1, shape.h * (rootRect?.height ?? 720));
                const effectiveRadiusPx = Math.min(shape.cornerRadiusPx ?? 14, Math.min(wpx, hpx) / 2);
                const radiusHandleOffsetPx = Math.round(7 + effectiveRadiusPx);
                const cornerHandles = (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                  const left = corner === 'nw' || corner === 'sw' ? shape.x : shape.x + shape.w;
                  const top = corner === 'nw' || corner === 'ne' ? shape.y : shape.y + shape.h;
                  return (
                    <button
                      key={`${shape.id}:resize:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-image-resize cp-program-slide-canvas-image-resize--${corner}`}
                      style={{
                        left: `calc(${left * 100}% - 5px)`,
                        top: `calc(${top * 100}% - 5px)`,
                        zIndex: activeToolbarHandleLayer,
                      }}
                      onPointerDown={(e) => startShapeResize(e, shape, corner)}
                      aria-label="도형 크기 조정"
                      title="드래그하여 도형 크기 조정"
                    />
                  );
                });
                const horiz =
                  shape.shape !== 'circle'
                    ? (['left', 'right'] as const).map((side) => (
                        <button
                          key={`${shape.id}:x:${side}`}
                          type="button"
                          className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--${side} is-selected`}
                          style={{
                            left:
                              side === 'left'
                                ? `calc(${shape.x * 100}% - 5px)`
                                : `calc(${(shape.x + shape.w) * 100}% - 5px)`,
                            top: `calc(${shape.y * 100}% + ${(shape.h * 100) / 2}%)`,
                            zIndex: activeToolbarHandleLayer,
                          }}
                          onPointerDown={(e) => startBoxHorizontalResize(e, 'shape', shape.id, shape, side)}
                          aria-label="도형 가로 크기 조정"
                          title="드래그하여 가로 크기 조정"
                        />
                      ))
                    : [];
                const vert =
                  shape.shape === 'ruled'
                    ? (['top', 'bottom'] as const).map((side) => (
                        <button
                          key={`${shape.id}:y:${side}`}
                          type="button"
                          className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--y cp-program-slide-canvas-resize--${side} is-selected`}
                          style={{
                            left: `calc(${shape.x * 100}% + ${(shape.w * 100) / 2}%)`,
                            top:
                              side === 'top'
                                ? `calc(${shape.y * 100}% - 5px)`
                                : `calc(${(shape.y + shape.h) * 100}% - 5px)`,
                            zIndex: activeToolbarHandleLayer,
                          }}
                          onPointerDown={(e) => startWebpageResizeVertical(e, shape, side, 'shape')}
                          aria-label="글쓰기 블럭 세로 크기 조정"
                          title="드래그하여 세로 크기 조정(줄 수가 바뀝니다)"
                        />
                      ))
                    : [];
                if (shape.shape === 'circle') return [...horiz, ...cornerHandles];
                const radiusHandle = (
                  <button
                    key={`${shape.id}:radius`}
                    type="button"
                    className="cp-program-slide-canvas-button-radius"
                    style={{
                      left: `calc(${shape.x * 100}% + ${radiusHandleOffsetPx}px)`,
                      top: `calc(${shape.y * 100}% - 5px)`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startShapeRadiusResize(e, shape)}
                    aria-label="도형 모서리 둥글기 조정"
                    title="좌우로 드래그하여 모서리 둥글기 조정"
                  />
                );
                return [...horiz, ...vert, ...cornerHandles, radiusHandle];
              })
          : null}
        {canEdit && !hasMultiSelection && isEvaluationSheetPreset
          ? tables
              .filter((table) => selectedKind === 'table' && table.id === selectedId && !table.locked)
              .flatMap((table) =>
                (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                  const left = corner === 'nw' || corner === 'sw' ? table.x : table.x + table.w;
                  const top = corner === 'nw' || corner === 'ne' ? table.y : table.y + table.h;
                  return (
                    <button
                      key={`${table.id}:resize:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-image-resize cp-program-slide-canvas-image-resize--${corner}`}
                      style={{
                        left: `calc(${left * 100}% - 5px)`,
                        top: `calc(${top * 100}% - 5px)`,
                        zIndex: activeToolbarHandleLayer,
                      }}
                      onPointerDown={(e) => startTableResize(e, table, corner)}
                      aria-label="표 크기 조정"
                      title="드래그하여 표 크기 조정"
                    />
                  );
                })
              )
          : null}
        {canEdit && !hasMultiSelection
          ? images
              .filter(
                (image) =>
                  selectedKind === 'image' &&
                  image.id === selectedId &&
                  !image.locked &&
                  cropEditingId !== image.id
              )
              .flatMap((image) => {
                const corners = (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                  const left = corner === 'nw' || corner === 'sw' ? image.x : image.x + image.w;
                  const top = corner === 'nw' || corner === 'ne' ? image.y : image.y + image.h;
                  return (
                    <button
                      key={`${image.id}:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-image-resize cp-program-slide-canvas-image-resize--${corner}`}
                      style={{
                        left: `calc(${left * 100}% - 5px)`,
                        top: `calc(${top * 100}% - 5px)`,
                        zIndex: activeToolbarHandleLayer,
                      }}
                      onPointerDown={(e) => startImageResize(e, image, corner)}
                      aria-label="이미지 비율 유지 크기 조정"
                      title="드래그하여 이미지 크기 조정"
                    />
                  );
                });
                const horiz = (['left', 'right'] as const).map((side) => (
                  <button
                    key={`${image.id}:x:${side}`}
                    type="button"
                    className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--${side} is-selected`}
                    style={{
                      left:
                        side === 'left'
                          ? `calc(${image.x * 100}% - 5px)`
                          : `calc(${(image.x + image.w) * 100}% - 5px)`,
                      top: `calc(${image.y * 100}% + ${(image.h * 100) / 2}%)`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startImageResizeHorizontal(e, image, side)}
                    aria-label="이미지 가로·원본 비율 조정"
                    title="드래그하여 가로 길이·원본 비율 조정 (세로는 유지)"
                  />
                ));
                const vert = (['top', 'bottom'] as const).map((vSide) => (
                  <button
                    key={`${image.id}:y:${vSide}`}
                    type="button"
                    className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--y cp-program-slide-canvas-resize--${vSide} is-selected`}
                    style={{
                      left: `calc(${image.x * 100}% + ${(image.w * 100) / 2}%)`,
                      top: vSide === 'top' ? `${image.y * 100}%` : `${(image.y + image.h) * 100}%`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startWebpageResizeVertical(e, image, vSide, 'image')}
                    aria-label="이미지 세로 크기 조정"
                    title="드래그하여 세로 길이 조정 (이미지는 비율 유지)"
                  />
                ));
                return [...horiz, ...vert, ...corners];
              })
          : null}
        {canEdit && !hasMultiSelection
          ? clipboardImages
              .filter(
                (image) =>
                  selectedKind === 'clipboardImage' &&
                  image.id === selectedId &&
                  !image.locked &&
                  cropEditingId !== image.id
              )
              .flatMap((image) => {
                const corners = (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                  const left = corner === 'nw' || corner === 'sw' ? image.x : image.x + image.w;
                  const top = corner === 'nw' || corner === 'ne' ? image.y : image.y + image.h;
                  return (
                    <button
                      key={`cb:${image.id}:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-image-resize cp-program-slide-canvas-image-resize--${corner}`}
                      style={{
                        left: `calc(${left * 100}% - 5px)`,
                        top: `calc(${top * 100}% - 5px)`,
                        zIndex: activeToolbarHandleLayer,
                      }}
                      onPointerDown={(e) => startImageResize(e, image, corner, 'clipboardImage')}
                      aria-label="클립보드 이미지 크기 조정"
                      title="드래그하여 이미지 크기 조정"
                    />
                  );
                });
                const horiz = (['left', 'right'] as const).map((side) => (
                  <button
                    key={`cb:${image.id}:x:${side}`}
                    type="button"
                    className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--${side} is-selected`}
                    style={{
                      left:
                        side === 'left'
                          ? `calc(${image.x * 100}% - 5px)`
                          : `calc(${(image.x + image.w) * 100}% - 5px)`,
                      top: `calc(${image.y * 100}% + ${(image.h * 100) / 2}%)`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startImageResizeHorizontal(e, image, side, 'clipboardImage')}
                    aria-label="클립보드 이미지 가로 조정"
                    title="드래그하여 가로 길이·원본 비율 조정"
                  />
                ));
                const vert = (['top', 'bottom'] as const).map((vSide) => (
                  <button
                    key={`${image.id}:y:${vSide}`}
                    type="button"
                    className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--y cp-program-slide-canvas-resize--${vSide} is-selected`}
                    style={{
                      left: `calc(${image.x * 100}% + ${(image.w * 100) / 2}%)`,
                      top: vSide === 'top' ? `${image.y * 100}%` : `${(image.y + image.h) * 100}%`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startWebpageResizeVertical(e, image, vSide, 'clipboardImage')}
                    aria-label="이미지 세로 크기 조정"
                    title="드래그하여 세로 길이 조정 (이미지는 비율 유지)"
                  />
                ));
                return [...horiz, ...vert, ...corners];
              })
          : null}
        {canEdit && !hasMultiSelection
          ? scanImages
              .filter(
                (image) =>
                  selectedKind === 'scanImage' &&
                  image.id === selectedId &&
                  !image.locked &&
                  cropEditingId !== image.id
              )
              .flatMap((image) => {
                const corners = (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                  const left = corner === 'nw' || corner === 'sw' ? image.x : image.x + image.w;
                  const top = corner === 'nw' || corner === 'ne' ? image.y : image.y + image.h;
                  return (
                    <button
                      key={`scan:${image.id}:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-image-resize cp-program-slide-canvas-image-resize--${corner}`}
                      style={{
                        left: `calc(${left * 100}% - 5px)`,
                        top: `calc(${top * 100}% - 5px)`,
                        zIndex: activeToolbarHandleLayer,
                      }}
                      onPointerDown={(e) => startImageResize(e, image, corner, 'scanImage')}
                      aria-label="스캔 이미지 크기 조정"
                      title="드래그하여 이미지 크기 조정"
                    />
                  );
                });
                const horiz = (['left', 'right'] as const).map((side) => (
                  <button
                    key={`scan:${image.id}:x:${side}`}
                    type="button"
                    className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--${side} is-selected`}
                    style={{
                      left:
                        side === 'left'
                          ? `calc(${image.x * 100}% - 5px)`
                          : `calc(${(image.x + image.w) * 100}% - 5px)`,
                      top: `calc(${image.y * 100}% + ${(image.h * 100) / 2}%)`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startImageResizeHorizontal(e, image, side, 'scanImage')}
                    aria-label="스캔 이미지 가로 조정"
                    title="드래그하여 가로 길이·원본 비율 조정"
                  />
                ));
                const vert = (['top', 'bottom'] as const).map((vSide) => (
                  <button
                    key={`${image.id}:y:${vSide}`}
                    type="button"
                    className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--y cp-program-slide-canvas-resize--${vSide} is-selected`}
                    style={{
                      left: `calc(${image.x * 100}% + ${(image.w * 100) / 2}%)`,
                      top: vSide === 'top' ? `${image.y * 100}%` : `${(image.y + image.h) * 100}%`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startWebpageResizeVertical(e, image, vSide, 'scanImage')}
                    aria-label="이미지 세로 크기 조정"
                    title="드래그하여 세로 길이 조정 (이미지는 비율 유지)"
                  />
                ));
                return [...horiz, ...vert, ...corners];
              })
          : null}
        {canEdit && !hasMultiSelection
          ? videos
              .filter((video) => selectedKind === 'video' && video.id === selectedId)
              .flatMap((video) =>
                (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                  const left = corner === 'nw' || corner === 'sw' ? video.x : video.x + video.w;
                  const top = corner === 'nw' || corner === 'ne' ? video.y : video.y + video.h;
                  return (
                    <button
                      key={`${video.id}:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-image-resize cp-program-slide-canvas-image-resize--${corner}`}
                      style={{
                        left: `calc(${left * 100}% - 5px)`,
                        top: `calc(${top * 100}% - 5px)`,
                        zIndex: activeToolbarHandleLayer,
                      }}
                      onPointerDown={(e) => startVideoResize(e, video, corner)}
                      aria-label="동영상 비율 유지 크기 조정"
                      title="드래그하여 동영상 크기 조정"
                    />
                  );
                })
              )
          : null}
        {canEdit && !hasMultiSelection
          ? webpages
              .filter((wp) => selectedKind === 'webpage' && wp.id === selectedId)
              .map((wp) => {
                const previewError = webpagePreviewErrorByBlockId[wp.id];
                const isCodeBlock = wp.mode === 'code';
                const isCodeTransparent = wp.codeTransparentBackground !== false;
                const toolbarHeightPx = isCodeBlock
                  ? SLIDE_TOOLBAR_CODE_BLOCK_HEIGHT_PX
                  : previewError
                    ? SLIDE_TOOLBAR_DOUBLE_ROW_HEIGHT_PX
                    : SLIDE_TOOLBAR_SINGLE_ROW_HEIGHT_PX;
                const placement = resolveToolbarPlacement(wp.y, wp.h, toolbarHeightPx);
                const urlDraft = webpageUrlDrafts[wp.id] ?? wp.url;
                const codeDraft = webpageCodeDrafts[wp.id] ?? wp.sourceCode ?? '';
                const isPreviewLoading = webpagePreviewLoadingId === wp.id;
                return (
                  <ProgramSlideFloatedToolbar
                    key={`webpage-toolbar:${wp.id}`}
                    {...floatedToolbarPortalProps}
                    blockX={wp.x}
                    blockY={wp.y}
                    blockH={wp.h}
                    className={
                      'cp-program-slide-canvas-image-toolbar cp-program-slide-canvas-webpage-toolbar' +
                      (isCodeBlock ? ' cp-program-slide-canvas-webpage-code-toolbar' : '')
                    }
                    placement={placement}
                    zIndex={floatedToolbarZIndex}
                    onPointerDown={(e) => e.stopPropagation()}
                  >
                    {isCodeBlock ? (
                      <>
                        <div className="cp-program-slide-canvas-webpage-code-toolbar-row">
                          <span className="cp-program-slide-canvas-tl">소스코드</span>
                          <textarea
                            className="cp-program-slide-canvas-webpage-code-input"
                            value={codeDraft}
                            onChange={(e) => setWebpageCodeDrafts((cur) => ({ ...cur, [wp.id]: e.target.value }))}
                            onKeyDown={(e) => e.stopPropagation()}
                            placeholder="<!DOCTYPE html> 또는 <style>...</style><script>...</script>"
                            aria-label="소스코드 블럭 HTML/CSS/JS"
                            spellCheck={false}
                          />
                        </div>
                        <div className="cp-program-slide-canvas-webpage-code-actions-row">
                          <span className="cp-program-slide-canvas-webpage-code-transparent-control">
                            <span className="cp-program-slide-canvas-video-play-label">배경 투명</span>
                            <button
                              type="button"
                              className={
                                'team-auto-approve-toggle cp-program-slide-canvas-webpage-code-transparent-toggle' +
                                (isCodeTransparent ? ' on' : '')
                              }
                              onClick={() => {
                                patchWebpage(
                                  wp.id,
                                  { codeTransparentBackground: !isCodeTransparent },
                                  'record'
                                );
                              }}
                              aria-pressed={isCodeTransparent}
                              aria-label={
                                isCodeTransparent
                                  ? '소스코드 블럭 배경 투명 끄기'
                                  : '소스코드 블럭 배경 투명 켜기'
                              }
                              title={isCodeTransparent ? 'OFF: 블럭·iframe 배경 표시' : 'ON: 슬라이드 배경이 비침'}
                            >
                              <span className="team-auto-approve-toggle-slider" data-on={!isCodeTransparent}>
                                OFF
                              </span>
                              <span className="team-auto-approve-toggle-slider" data-on={isCodeTransparent}>
                                ON
                              </span>
                            </button>
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              patchWebpage(
                                wp.id,
                                {
                                  mode: 'code',
                                  sourceCode: codeDraft.slice(0, 120_000),
                                  embedSrc: undefined,
                                  url: '',
                                  title: wp.title || '소스코드 블럭',
                                  thumbSrc: '',
                                },
                                'record'
                              );
                              setWebpageEmbedFailed((cur) => ({ ...cur, [wp.id]: false }));
                            }}
                            disabled={!codeDraft.trim()}
                          >
                            적용
                          </button>
                          <ProgramSlideTrashDeleteControl
                            isHolding={holdingDeleteId === wp.id}
                            showConfirm={
                              !!deleteConfirmTarget &&
                              deleteConfirmTarget.id === wp.id &&
                              deleteConfirmTarget.kind === 'webpage'
                            }
                            ariaLabel="소스코드 블럭 삭제: 짧게 확인, 길게 바로 삭제"
                            onPointerDown={(e) => startDeleteHold(e, wp.id, 'webpage')}
                            onPointerUp={() => endDeleteHold(true)}
                            onPointerLeave={() => endDeleteHold(false)}
                            onPointerCancel={() => endDeleteHold(false)}
                            onCancelConfirm={() => setDeleteConfirmTarget(null)}
                            onConfirmDelete={() => {
                              const target = deleteConfirmTarget;
                              setDeleteConfirmTarget(null);
                              if (target) executeDeleteBlock(target.id, target.kind);
                            }}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="cp-program-slide-canvas-video-toolbar-row">
                        <input
                          type="url"
                          value={urlDraft}
                          onChange={(e) => {
                            setWebpageUrlDrafts((cur) => ({ ...cur, [wp.id]: e.target.value }));
                            if (previewError) {
                              setWebpagePreviewErrorByBlockId((cur) => {
                                if (!cur[wp.id]) return cur;
                                const next = { ...cur };
                                delete next[wp.id];
                                return next;
                              });
                            }
                          }}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              void submitWebpageUrlForBlock(wp.id, urlDraft);
                            }
                          }}
                          placeholder="https://..."
                          aria-label="웹페이지 URL"
                          disabled={isPreviewLoading}
                          ref={(el) => {
                            if (el && webpageToolbarInputFocusIdRef.current === wp.id) {
                              webpageToolbarInputFocusIdRef.current = null;
                              window.setTimeout(() => el.focus(), 0);
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => void submitWebpageUrlForBlock(wp.id, urlDraft)}
                          disabled={isPreviewLoading || !urlDraft.trim()}
                        >
                          {isPreviewLoading ? '처리 중…' : '적용'}
                        </button>
                        <ProgramSlideTrashDeleteControl
                          isHolding={holdingDeleteId === wp.id}
                          showConfirm={
                            !!deleteConfirmTarget &&
                            deleteConfirmTarget.id === wp.id &&
                            deleteConfirmTarget.kind === 'webpage'
                          }
                          disabled={isPreviewLoading}
                          ariaLabel="웹페이지 블럭 삭제: 짧게 확인, 길게 바로 삭제"
                          onPointerDown={(e) => startDeleteHold(e, wp.id, 'webpage')}
                          onPointerUp={() => endDeleteHold(true)}
                          onPointerLeave={() => endDeleteHold(false)}
                          onPointerCancel={() => endDeleteHold(false)}
                          onCancelConfirm={() => setDeleteConfirmTarget(null)}
                          onConfirmDelete={() => {
                            const target = deleteConfirmTarget;
                            setDeleteConfirmTarget(null);
                            if (target) executeDeleteBlock(target.id, target.kind);
                          }}
                        />
                      </div>
                    )}
                    {!isCodeBlock && previewError ? (
                      <p className="cp-program-slide-canvas-webpage-toolbar-error" role="alert">
                        {previewError}
                      </p>
                    ) : null}
                  </ProgramSlideFloatedToolbar>
                );
              })
          : null}
        {canEdit && !hasMultiSelection
          ? webpages
              .filter((wp) => selectedKind === 'webpage' && wp.id === selectedId)
              .flatMap((wp) => {
                const corners = (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                  const left = corner === 'nw' || corner === 'sw' ? wp.x : wp.x + wp.w;
                  const top = corner === 'nw' || corner === 'ne' ? wp.y : wp.y + wp.h;
                  return (
                    <button
                      key={`webpage-resize:${wp.id}:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-image-resize cp-program-slide-canvas-image-resize--${corner}`}
                      style={{
                        left: `calc(${left * 100}% - 5px)`,
                        top: `calc(${top * 100}% - 5px)`,
                        zIndex: activeToolbarHandleLayer,
                      }}
                      onPointerDown={(e) => startWebpageResize(e, wp, corner)}
                      aria-label="웹페이지 블럭 크기 조정"
                      title="드래그하여 웹페이지 블럭 크기 조정"
                    />
                  );
                });
                const horiz = (['left', 'right'] as const).map((side) => (
                  <button
                    key={`webpage-resize:${wp.id}:x:${side}`}
                    type="button"
                    className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--${side} is-selected`}
                    style={{
                      left:
                        side === 'left'
                          ? `calc(${wp.x * 100}% - 5px)`
                          : `calc(${(wp.x + wp.w) * 100}% - 5px)`,
                      top: `calc(${wp.y * 100}% + ${(wp.h * 100) / 2}%)`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startWebpageResizeHorizontal(e, wp, side)}
                    aria-label="웹페이지 블럭 가로 크기 조정"
                    title="드래그하여 가로 길이 조정 (세로는 유지)"
                  />
                ));
                const vert = (['top', 'bottom'] as const).map((vSide) => (
                        <button
                          key={`webpage-resize:${wp.id}:y:${vSide}`}
                          type="button"
                          className={`cp-program-slide-canvas-resize cp-program-slide-canvas-resize--y cp-program-slide-canvas-resize--${vSide} is-selected`}
                          style={{
                            left: `calc(${wp.x * 100}% + ${(wp.w * 100) / 2}%)`,
                            top: vSide === 'top' ? `${wp.y * 100}%` : `${(wp.y + wp.h) * 100}%`,
                            zIndex: activeToolbarHandleLayer,
                          }}
                          onPointerDown={(e) => startWebpageResizeVertical(e, wp, vSide)}
                          aria-label="웹페이지·소스코드 블럭 세로 크기 조정"
                          title="드래그하여 세로 길이 조정 (가로는 유지)"
                        />
                      ));
                return [...horiz, ...vert, ...corners];

              })
          : null}
        {canEdit && !hasMultiSelection
          ? quizzes
              .filter((quiz) => selectedKind === 'quiz' && quiz.id === selectedId)
              .flatMap((quiz) => {
                const rootRect = rootRef.current?.getBoundingClientRect();
                const quizWpx = Math.max(1, quiz.w * (rootRect?.width ?? 1280));
                const quizHpx = Math.max(1, quiz.h * (rootRect?.height ?? 720));
                const effectiveRadiusPx = Math.min(quiz.cornerRadiusPx ?? 999, Math.min(quizWpx, quizHpx) / 2);
                const radiusHandleOffsetPx = Math.round(7 + effectiveRadiusPx);
                const cornerHandles = (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                  const left = corner === 'nw' || corner === 'sw' ? quiz.x : quiz.x + quiz.w;
                  const top = corner === 'nw' || corner === 'ne' ? quiz.y : quiz.y + quiz.h;
                  return (
                    <button
                      key={`${quiz.id}:resize:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-image-resize cp-program-slide-canvas-image-resize--${corner}`}
                      style={{
                        left: `calc(${left * 100}% - 5px)`,
                        top: `calc(${top * 100}% - 5px)`,
                        zIndex: activeToolbarHandleLayer,
                      }}
                      onPointerDown={(e) => startQuizResize(e, quiz, corner)}
                      aria-label="단답형 퀴즈 크기 조정"
                      title="드래그하여 퀴즈 크기 조정"
                    />
                  );
                });
                const radiusHandle = (
                  <button
                    key={`${quiz.id}:radius`}
                    type="button"
                    className="cp-program-slide-canvas-button-radius"
                    style={{
                      left: `calc(${quiz.x * 100}% + ${radiusHandleOffsetPx}px)`,
                      top: `calc(${quiz.y * 100}% - 5px)`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startQuizRadiusResize(e, quiz)}
                    aria-label="퀴즈 모서리 둥글기 조정"
                    title="좌우로 드래그하여 모서리 둥글기 조정"
                  />
                );
                return [...cornerHandles, radiusHandle];
              })
          : null}
        {canEdit && !hasMultiSelection
          ? quizChoiceOptions
              .filter((opt) => isBlockSelected('quizChoice', opt.id))
              .flatMap((option) => {
                const rootRect = rootRef.current?.getBoundingClientRect();
                const optWpx = Math.max(1, option.w * (rootRect?.width ?? 1280));
                const optHpx = Math.max(1, option.h * (rootRect?.height ?? 720));
                const effectiveRadiusPx = Math.min(option.cornerRadiusPx ?? 8, Math.min(optWpx, optHpx) / 2);
                const radiusHandleOffsetPx = Math.round(7 + effectiveRadiusPx);
                const cornerHandles = (['nw', 'ne', 'sw', 'se'] as const).map((corner) => {
                  const left = corner === 'nw' || corner === 'sw' ? option.x : option.x + option.w;
                  const top = corner === 'nw' || corner === 'ne' ? option.y : option.y + option.h;
                  return (
                    <button
                      key={`${option.id}:resize:${corner}`}
                      type="button"
                      className={`cp-program-slide-canvas-image-resize cp-program-slide-canvas-image-resize--${corner}`}
                      style={{
                        left: `calc(${left * 100}% - 5px)`,
                        top: `calc(${top * 100}% - 5px)`,
                        zIndex: activeToolbarHandleLayer,
                      }}
                      onPointerDown={(e) => startQuizChoiceOptionResize(e, option, corner)}
                      aria-label="선택형 퀴즈 크기 조정"
                      title="드래그하여 선택지 크기 조정 (세트 전체 동기)"
                    />
                  );
                });
                const radiusHandle = (
                  <button
                    key={`${option.id}:radius`}
                    type="button"
                    className="cp-program-slide-canvas-button-radius"
                    style={{
                      left: `calc(${option.x * 100}% + ${radiusHandleOffsetPx}px)`,
                      top: `calc(${option.y * 100}% - 5px)`,
                      zIndex: activeToolbarHandleLayer,
                    }}
                    onPointerDown={(e) => startQuizChoiceRadiusResize(e, option)}
                    aria-label="선택형 퀴즈 모서리 둥글기 조정"
                    title="좌우로 드래그하여 모서리 둥글기 조정"
                  />
                );
                return [...cornerHandles, radiusHandle];
              })
          : null}
        {!boxes.length &&
        !images.length &&
        !clipboardImages.length &&
        !scanImages.length &&
        !buttons.length &&
        !videos.length &&
        !webpages.length &&
        !shapes.length &&
        !tables.length &&
        !polylines.length &&
        !quizzes.length &&
        !quizChoiceSets.length &&
        !quizChoiceOptions.length &&
        !canEdit &&
        !webcamHitThrough ? (
          <div className="cp-program-slide-canvas-empty" aria-hidden>
            내용 없음
          </div>
        ) : null}
        {marqueeViewportRect && canEdit && typeof document !== 'undefined'
          ? createPortal(
              <div
                className="cp-program-slide-canvas-marquee cp-program-slide-canvas-marquee--viewport"
                style={{
                  left: marqueeViewportRect.left,
                  top: marqueeViewportRect.top,
                  width: marqueeViewportRect.width,
                  height: marqueeViewportRect.height,
                  zIndex: 200000,
                }}
                aria-hidden
              />,
              document.body
            )
          : null}
        {quizCorrectOverlay ? (
          <div
            className="cp-program-slide-canvas-quiz-correct-overlay"
            style={{ zIndex: quizResultOverlayZIndex }}
            aria-hidden
          >
            <span className="cp-program-slide-canvas-quiz-correct-mark">O</span>
          </div>
        ) : null}
        {quizResultOverlay === 'x' ? (
          <div
            className="cp-program-slide-canvas-quiz-correct-overlay"
            style={{ zIndex: quizResultOverlayZIndex }}
            aria-hidden
          >
            <span className="cp-program-slide-canvas-quiz-wrong-mark">X</span>
          </div>
        ) : null}
      </div>
      {clipboardImageToast ? (
        <div className="cp-program-slide-clipboard-toast" role="status">
          {clipboardImageToast}
        </div>
      ) : null}
    </div>
    </>
  );
});

export default ProgramSlideCanvas;

export function cloneSlideLayout(layout: ProgramSlideLayoutV1 | undefined): ProgramSlideLayoutV1 {
  if (!layout || layout.v !== 1) {
    return {
      v: 1,
      defaultTextColorHex: '#141414',
      defaultBackgroundColorHex: '#ffffff',
      boxes: [],
      images: [],
      clipboardImages: [],
      scanImages: [],
      buttons: [],
      shapes: [],
      polylines: [],
      tables: [],
      videos: [],
      webpages: [],
      quizzes: [],
      quizChoiceSets: [],
      quizChoiceOptions: [],
    };
  }
  return {
    v: 1,
    appliedLayoutTemplateId: (layout.appliedLayoutTemplateId ?? '').trim() || undefined,
    defaultTextColorHex:
      (layout.defaultTextColorHex ?? '').trim().match(/^#[0-9a-f]{6}$/i) ? layout.defaultTextColorHex?.toLowerCase() : undefined,
    defaultBackgroundColorHex:
      (layout.defaultBackgroundColorHex ?? '').trim().match(/^#[0-9a-f]{6}$/i)
        ? layout.defaultBackgroundColorHex?.toLowerCase()
        : undefined,
    pageBackgroundColorHex:
      (layout.pageBackgroundColorHex ?? '').trim().match(/^#[0-9a-f]{6}$/i)
        ? layout.pageBackgroundColorHex?.toLowerCase()
        : undefined,
    pageTextColorHex:
      (layout.pageTextColorHex ?? '').trim().match(/^#[0-9a-f]{6}$/i)
        ? layout.pageTextColorHex?.toLowerCase()
        : undefined,
    boxes: layout.boxes.map((b) => ({
      ...b,
      textRuns: b.textRuns?.map((r) => ({ ...r })),
    })),
    images: layout.images.map((img) => ({ ...img, crop: { ...img.crop } })),
    clipboardImages: (layout.clipboardImages ?? []).map((img) => ({ ...img, crop: { ...img.crop } })),
    scanImages: (layout.scanImages ?? []).map((img) => ({ ...img, crop: { ...img.crop } })),
    buttons: layout.buttons.map((btn) => ({
      ...btn,
      kind: btn.kind ?? 'link',
      href: btn.kind === 'dismiss' ? '' : btn.href,
      fontFamily: normalizeSlideTextFontFamily(btn.fontFamily),
      backgroundColorHex: btn.backgroundColorHex ?? '#ffffff',
      cornerRadiusPx: typeof btn.cornerRadiusPx === 'number' ? btn.cornerRadiusPx : 999,
    })),
    shapes: (layout.shapes ?? []).map((s) => ({
      ...s,
      fontFamily: normalizeSlideTextFontFamily(s.fontFamily),
      strokeWidthPx: typeof s.strokeWidthPx === 'number' ? s.strokeWidthPx : 2,
      cornerRadiusPx: typeof s.cornerRadiusPx === 'number' ? s.cornerRadiusPx : 12,
    })),
    polylines: (layout.polylines ?? []).map((p) => ({
      ...p,
      points: p.points.map((pt) => ({ ...pt })),
      strokeWidthPx: typeof p.strokeWidthPx === 'number' ? p.strokeWidthPx : 12,
    })),
    tables: (layout.tables ?? []).map((tbl) => ({
      ...tbl,
      cells: tbl.cells.map((c) => ({ ...c })),
      rowHeightsFrac: [...tbl.rowHeightsFrac],
      colWidthsFrac: [...tbl.colWidthsFrac],
      coveredBy: tbl.coveredBy ? { ...tbl.coveredBy } : {},
    })),
    videos: layout.videos.map((video) => ({
      ...video,
      bookmarks: video.bookmarks.map((bookmark) => ({ ...bookmark })),
    })),
    webpages: (layout.webpages ?? []).map((wp) => ({ ...wp })),
    quizzes: layout.quizzes.map((quiz) => ({
      ...quiz,
      fontFamily: normalizeSlideTextFontFamily(quiz.fontFamily),
      backgroundColorHex: quiz.backgroundColorHex ?? '#ffffff',
      bold: Boolean(quiz.bold),
      cornerRadiusPx: typeof quiz.cornerRadiusPx === 'number' ? quiz.cornerRadiusPx : 999,
    })),
    quizChoiceSets: (layout.quizChoiceSets ?? []).map((set) => ({
      ...set,
      correctOptionIndices: [...(set.correctOptionIndices ?? [])],
    })),
    quizChoiceOptions: (layout.quizChoiceOptions ?? []).map((opt) => ({
      ...opt,
      fontFamily: normalizeSlideTextFontFamily(opt.fontFamily),
    })),
  };
}
